/**
 * Translation catalog.
 *
 * Keys are dot-namespaced strings; values are the literal text for each
 * supported language. Add new keys as you internationalise more surfaces —
 * an unknown key falls back to the key itself, so it's safe to ship a
 * partial dictionary.
 *
 * Conventions:
 *   - Group by surface (topbar.*, profile.*, dashboard.*).
 *   - Use sentence case in `en`; mirror in `es`.
 *   - Static UI text only. Don't translate user-generated content.
 */

import type { AppLanguage } from '@/lib/prefs/usePrefsStore';

type Dict = Record<string, string>;

const EN: Dict = {
  // ── Common ─────────────────────────────────────────────────────────
  'common.edit': 'Edit',
  'common.comingSoon': 'Coming soon',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.done': 'Done',
  'profile.privacy.eyebrow': 'Privacy',
  'profile.privacy.profileLabel': 'Profile card',
  'profile.privacy.profileHint': 'Who can see your name, bio and theme.',
  'profile.privacy.activityLabel': 'Activity timeline',
  'profile.privacy.activityHint': 'Whether your calendar and event activity appear across accounts.',
  'profile.privacy.public': 'Public',
  'profile.privacy.private': 'Private',
  'profile.privacy.saved': 'Privacy preference saved',
  'profile.privacy.saveError': 'Could not save privacy preference',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.create': 'Create',
  'common.signOut': 'Sign out',
  'datePicker.placeholder': 'Select a date',
  'datePicker.clear': 'Clear date',
  'datePicker.time': 'Time',
  'tour.start': 'Take a tour',
  'tour.next': 'Next',
  'tour.prev': 'Back',
  'tour.done': 'Done',
  'tour.progress': '{{current}} of {{total}}',
  'tour.restart': 'Restart product tours',
  'tour.restartHint': 'Replay the guided walkthroughs as you visit each page.',
  'tour.restartDone': "Tours reset — they'll show again as you browse.",
  'journeyPreview.title': 'Live preview',
  'brandTree.label': 'Browse projects, events & stations',
  'brandTree.loadError': 'Could not load',
  'brandTree.empty.projects': 'No projects yet',
  'brandTree.empty.events': 'No events',
  'brandTree.empty.stations': 'No stations',
  'common.unassigned': 'Unassigned',
  'common.upcoming': 'Upcoming',
  'common.live': 'Live',
  'common.idle': 'Idle',
  'common.export': 'Export',
  'common.search': 'Search…',
  'common.loading': 'Loading…',

  // ── TopBar ─────────────────────────────────────────────────────────
  'topbar.workspace': 'ACCOUNT',
  'topbar.agency': 'AGENCY',
  'topbar.atledi': 'Atledi',
  'topbar.atlediSoon': 'Atledi assistant coming soon',
  'topbar.resetDemo': 'Reset demo data',
  'topbar.theme': 'Theme',
  'topbar.language': 'Language',
  'topbar.openMenu': 'Open menu',
  'topbar.closeMenu': 'Close menu',
  'topbar.help': 'Help & documentation',

  // ── App switcher (the 3×3 grid) ────────────────────────────────────
  'apps.open': 'Onxite apps',
  'apps.title': 'Onxite apps',
  'apps.current': 'You are here',
  'apps.comingSoon': 'Soon',
  'apps.lockedHint': 'Not included in your plan — ask us about adding it',
  'apps.opening': 'Opening {app}…',
  'apps.switchFailed': "Couldn't open that app. Try again.",

  // ── Workspace switcher (left-aligned brand picker pill) ────────────
  'topbar.workspaceSwitcher.searchPlaceholder': 'Search brands…',
  'topbar.workspaceSwitcher.empty': 'No matching brands',
  'topbar.workspaceSwitcher.clear': 'Clear search',
  'topbar.workspaceSwitcher.noBrand': 'Pick a brand',

  // ── Sidebar brand tree (selected brand + projects/events) ──────────
  'sidebar.brandTree.projects': 'Projects',
  'sidebar.brandTree.events': 'Events',
  'sidebar.brandTree.noProjects': 'No projects yet',
  'sidebar.brandTree.noEvents': 'No events yet',
  'sidebar.brandTree.noStations': 'No stations yet',
  'sidebar.brandTree.expand': 'Expand',
  'sidebar.brandTree.collapse': 'Collapse',

  // ── Brand profile tabs (Projects · Xotskies · Leftovers) ───────────
  'brand.tabs.projects': 'Projects',
  'brand.tabs.xotskies': 'Merch',
  'brand.tabs.inventory': 'Inventory',
  'brand.tabs.leftovers': 'Leftovers',
  'brand.leftovers.title': 'Leftover inventory from past events',
  'brand.leftovers.subtitle': 'Past events with unused merch units still allocated to their stations.',
  'brand.leftovers.emptyTitle': 'Nothing leftover',
  'brand.leftovers.emptyBody': 'No past event finished with available units. Once an event ends with leftovers, it will show up here.',
  'brand.leftovers.noBrand': 'No brand selected',
  'brand.leftovers.noBrandBody': 'Pick a brand from the brand switcher to see its leftover inventory.',
  'brand.leftovers.totalLeftover': 'Total leftover',
  'brand.leftovers.units': 'units',
  // Legacy — kept for any deep links still referencing the old label.
  'brand.tabs.prizes': 'Inventory',
  'brand.xotskies.title': 'Brand merch',
  'brand.xotskies.subtitle': 'Catalog of merch owned by this brand. Add or bulk-import from a ZIP.',
  'brand.xotskies.add': 'Add merch',
  'brand.xotskies.import': 'Bulk import',
  'brand.xotskies.emptyTitle': 'No merch yet',
  'brand.xotskies.emptyBody': 'Add the first merch item for this brand or bulk-import a ZIP.',
  'brand.xotskies.noBrand': 'No brand selected',
  'brand.xotskies.noBrandBody': 'Pick a brand from the brand switcher to manage its merch.',
  'brand.xotskies.agencyCatalog': 'Available from agency catalog',

  // Los cuatro contadores de la tarjeta de My Merch. La etiqueta va abreviada
  // porque la celda mide 9 px; la explicación vive en el tooltip, que es donde
  // cabe. Las dos se traducen: antes estaban en inglés dentro del componente.
  'brand.xotskies.counter.total.label': 'Total',
  'brand.xotskies.counter.total.help':
    'Units registered for this item. Restocking raises it.',
  'brand.xotskies.counter.allocated.label': 'Alloc.',
  'brand.xotskies.counter.allocated.help':
    'Committed: units in play right now at open events. They come back to the warehouse when the event closes and they were not won.',
  'brand.xotskies.counter.delivered.label': 'Deliv.',
  'brand.xotskies.counter.delivered.help':
    'Delivered: handed to a visitor, per the prize ledger of every event of this brand. This one does not come back.',
  'brand.xotskies.counter.available.label': 'Avail.',
  'brand.xotskies.counter.available.help':
    'What the brand owns right now: the warehouse plus whatever is still in play. Subtract Committed to know what is free to allocate.',
  'brand.xotskies.agencyCatalogEmpty': 'All agency merch already adopted, or the catalog is empty.',
  'brand.xotskies.deleteAria': 'Delete merch',
  'brand.xotskies.deleteBlocked': 'Cannot delete merch',
  'brand.xotskies.deleteBlockedHint': 'Has active allocations on a station — remove those allocations before deleting.',
  'brand.xotskies.writeOffBlocked': 'Cannot write off more than {available} available unit(s).',

  // ── Theme menu ─────────────────────────────────────────────────────
  'theme.brand': 'Xotski',
  'plan.banner.dismiss': 'Dismiss',
  'rightRail.title': 'Panel',
  'auth.login.orContinueWith': 'or continue with',
  'brandPulse.projectOne': 'project',
  'brandPulse.projectMany': 'projects',
  'brandPulse.eventOne': 'event',
  'brandPulse.eventMany': 'events',
  'brandPulse.live': 'live',
  'brandPulse.liveNow': '{name} is running right now.',
  'brandPulse.nextUp': 'Next up: {name} · {when}',
  'brandPulse.nothingScheduled': 'Nothing scheduled yet.',
  'brandPulse.watchLive': 'Watch live',
  'brandPulse.openNext': 'Open it',
  'brandPulse.scheduleEvent': 'Schedule an event',
  'sampleGame.cta': 'See a sample game',
  'sampleGame.title': 'A sample game',
  'sampleGame.description': 'Your colours, made-up prizes. Nothing is saved and no merch is spent — this is just to show what guests will see.',
  'sampleGame.won': 'You landed on {name}. A real guest would get their prize here.',
  'sampleGame.again': 'Spin again',
  'onboarding.kit.optionalHint': 'Optional — you can add it later.',
  'onboarding.kit.defaultCampaign': 'My first campaign',
  'onboarding.kit.changeLater': 'All of this lives in Brand Studio. Change it whenever you like.',
  'register.toast.tooMany': 'Too many signups from this connection',
  'register.toast.tooManyBody': 'Signups are limited for a short while. Try again in a few minutes, or sign in if you already have an account.',
  'prizes.toast.adopted': 'Merch adopted',
  'prizes.toast.adoptFailed': "Couldn't adopt it",
  'prizes.toast.restocked': 'Inventory updated',
  'prizes.toast.restockFailed': "Couldn't update the inventory",
  'prizes.toast.removed': 'Removed from the brand',
  'prizes.toast.removeFailed': "Couldn't remove it",
  'prizes.counter.total': 'Total',
  'prizes.counter.allocated': 'At stations',
  'prizes.counter.delivered': 'Handed out',
  'prizes.counter.available': 'Free',
  'prizes.restock.placeholder': '+ units',
  'prizes.restock.action': 'Update inventory',
  'prizes.restock.hint': 'A positive number adds units; a negative one writes them off.',
  'validation.nameRequired': 'A name is required',
  'validation.quantityMin': 'Enter at least 1',
  'validation.companyNameRequired': 'A company name is required',
  'validation.contactNameRequired': 'A contact name is required',
  'validation.industryRequired': 'Pick an industry',
  'validation.campaignNameRequired': 'A campaign name is required',
  'validation.logoRequired': 'A logo is required',
  'validation.hexColor': 'Use a HEX colour, like #C8E530',
  'forms.imageEditor.useOriginal': 'Use as is',
  'roleForm.description': 'Description',
  'roleForm.optional': '(optional)',
  'roleForm.namePlaceholder': 'brand-admin, viewer…',
  'roleForm.descriptionPlaceholder': 'What this role is for, when to assign it…',
  'roleForm.descriptionHint': "Shown in the roles list and on the member's role chip.",
  'roleForm.brandScope': 'Brands ({n})',
  'roleForm.brandScopeHint': 'People with this role see all of these brands, and everything under them.',
  'roleForm.noBrands': 'No brands you can manage.',
  'roleForm.presets': 'Quick-start presets',
  'roleForm.presetsHint': 'A preset adds its capabilities to what is already selected — stack several, then adjust.',
  'roleForm.clear': 'Clear',
  'roleForm.clearHint': 'Remove every capability from this role',
  'roleForm.titleCreate': 'Create role',
  'roleForm.titleEdit': 'Edit role · {name}',
  'roleForm.titleClone': 'Clone role · {name}',
  'roleForm.subtitleCreate': 'Define a reusable role for your account. Use the two lists to grant capabilities per area.',
  'roleForm.subtitleEdit': 'Update the role and what it can do. Changes are saved together or not at all.',
  'roleForm.subtitleClone': 'A new role with the capabilities of "{name}". Pick a name and the brands this copy applies to.',
  'roleForm.name': 'Name',
  'roleForm.nameRequired': 'A name is required',
  'roleForm.companyRequired': 'Pick at least one brand for this role',
  'roleForm.created': 'Role created',
  'roleForm.updated': 'Role updated',
  'roleForm.save': 'Save changes',
  'roleForm.createClone': 'Create clone',
  'quickStart.title': 'Three steps to a live game',
  'quickStart.step1': 'Add merch',
  'quickStart.step1Hint': 'The merch guests can win.',
  'quickStart.step1Cta': 'Add',
  'quickStart.step2': 'Create an event',
  'quickStart.step2Hint': 'Where and when you are giving it away.',
  'quickStart.step2Cta': 'Create',
  'quickStart.step3': 'Get your game link',
  'quickStart.step3Hint': 'We set up the station, journey and prize game for you.',
  'quickStart.step3Cta': 'Get link',
  'quickStart.linkReady': 'Your game is live. Share this with guests.',
  'quickStart.copy': 'Copy',
  'quickStart.copied': 'Copied',
  'quickStart.preview': 'Preview',
  'quickStart.failed': "Couldn't set up your game",
  'quickStart.needEvent': 'Create an event first',
  'quickStart.noTemplate': 'No guest journey starting point is available yet',
  'planner.pool.title': 'Commit {name} to this project',
  'planner.pool.description': 'The total this project may draw, not an amount to add. Its stations allocate from here, not straight from the brand.',
  'planner.pool.field': 'Total units for this project',
  'planner.pool.brandHas': 'The brand has {n} available',
  'planner.pool.range': 'Between {min} (already at this project\u2019s stations) and {max} \u2014 the brand still has {free} free',
  'planner.pool.atLeast': 'At least {min}, already at this project\u2019s stations',
  'planner.pool.belowAllocated': '{n} already sit at this project\u2019s stations',
  'planner.pool.aboveBrand': 'At most {max}: the brand only has {free} free beyond what its stations already hold',
  'planner.pool.currently': 'Now: {committed} committed \u00b7 {allocated} at stations \u00b7 {remaining} left',
  'planner.pool.commit': 'Commit',
  'planner.pool.commitTip': 'Commit units of this merch item to the project',
  'planner.pool.committed': 'Committed {n} \u00d7 {name} to this project',
  'planner.pool.commitFailed': "Couldn't commit those units",
  'planner.xotskiCarousel.availableInProject': '{n} left in this project\u2019s pool',
  'liveMonitor.title': 'Live now',
  'eventReport.sections.label': 'Show',
  'eventReport.sections.reach': 'Interactions & leads',
  'eventReport.sections.prizes': 'Prizes delivered',
  'eventReport.sections.costs': 'Cost per result',
  'eventReport.sections.topAnswers': 'Top answers',
  'eventReport.sections.demographics': 'Demographics',
  'assistant.comingSoon': 'Coming soon',
  'entityHome.atledi.comingSoonBody': "Atledi will read your events and tell you what changed and what to do next. It isn't switched on yet — this is where it will live.",
  'access.roles.advanced': 'Advanced — pick individual capabilities',
  'access.roles.advancedHint': 'Only needed when a preset is close but not exact. Capabilities are grouped by what they let someone do.',
  'planner.gameGlance.title': 'The game running here and how many slots it has',
  'planner.gameGlance.slotOne': 'slot',
  'planner.gameGlance.slotMany': 'slots',
  'planner.gameGlance.game.wheel': 'Prize Wheel',
  'planner.gameGlance.game.scratch': 'Scratch Card',
  'planner.gameGlance.game.xinkit': 'Xinkit',
  'planner.gameGlance.game.pirinola': 'Pirinola',
  'planner.gameGlance.game.slots': 'Slots',
  'planner.gameGlance.game.randomDraw': 'Random Draw',
  'planner.gameGlance.game.soccer': 'Penalty Shot',
  // The deployment left the game to the survey. Saying which one would mean
  // guessing: the chip doesn't have the survey tree in front of it.
  'planner.gameGlance.game.fromSurvey': "The survey's game",
  'planner.properties.journeyFromProject': "This event uses the project's guest journey. Change it on the project's Design Journey tab and it applies everywhere.",
  'stations.detail.allocationsReadonly': "What this station is carrying. Read-only here — mid-event isn't the moment to re-plan inventory.",
  'stations.detail.allocationsEditLink': 'Edit on the project plan board',
  'planner.station.editAllocations': 'Edit merch at this station',
  'planner.station.allocationsTitle': 'Merch · {name}',
  'planner.drop.xotskiNoStations': 'Add a station to this event first — merch lives at stations.',
  'planner.drop.xotskiNoStock': 'No {name} left to hand out — every unit is already assigned.',
  'planner.drop.xotskiSpread': 'Spread {total} × {name} across {count} station(s) — adjust any of them in the panel.',
  'rightRail.collapse': 'Collapse panel',
  'rightRail.expand': 'Expand panel',
  'rightRail.empty': 'Select a project, event or station to edit it here.',
  'stations.detail.playGameHint': 'Open the live game preview — dry run, no merch touched',
  'stations.detail.editJourneyHint': 'Edit this game in Design Journey',
  'stations.qrPrint.downloadOrPrint': 'Download / print',
  'designJourney.tabLabel': 'Design Journey',
  'theme.dark': 'Dark',
  'theme.light': 'Light',

  // ── Dashboard / Home ───────────────────────────────────────────────
  'dashboard.portfolio': 'Brand portfolio',
  'dashboard.brandHub': 'Brand Hub',
  'dashboard.brandsLower': 'brands',
  'dashboard.kpi.liveActivations': 'Live activations',
  'dashboard.kpi.spend': 'Spend',
  'dashboard.kpi.peopleReached': 'People reached (YTD)',
  'dashboard.subBrands': 'Sub-brands',
  'dashboard.brandManagers': 'Brand managers',
  'dashboard.newBrand': 'New brand',
  'dashboard.noBrandsTitle': 'No brands yet',
  'dashboard.noBrandsBody': 'Add a brand to start running projects in your account.',
  'dashboard.noManagers': 'No managers assigned yet. Invite teammates from Settings.',

  // ── SubBrand card ──────────────────────────────────────────────────
  'subbrand.projects': 'Projects',
  'subbrand.events': 'Events',
  'subbrand.active': 'Active',
  'subbrand.ownedBy': 'Owned by',
  'subbrand.ytd': 'YTD',

  // ── Brand profile page ─────────────────────────────────────────────
  'brand.workspace': 'Brand',
  'brand.editInStudio': 'Edit in Brand Studio',
  'brand.new.cta': 'New brand',
  'brand.list.cta': 'All brands',
  'brandsGallery.title': 'Brands',
  'brandsGallery.subtitle': 'Every brand you can work in, painted with its own identity and with what it holds. Open one to work in it, or edit its identity in Brand Studio.',
  'brandsGallery.empty': 'No brands yet.',
  'brandsGallery.root': 'Account',
  'brandsGallery.current': 'Current',
  'brandsGallery.open': 'Open brand',
  'brandsGallery.editIdentity': 'Edit identity',
  'brandsGallery.noKit': 'No identity yet',
  'brandsGallery.count.projects': 'Projects',
  'brandsGallery.count.events': 'Events',
  'brandsGallery.count.stations': 'Stations',
  'brandsGallery.count.merch': 'Merch',
  'brandsGallery.count.contacts': 'Contacts',
  'brand.new.title': 'Create a new brand',
  'brand.new.subtitle': 'Just the basics — you’ll customise the rest in Brand Studio next.',
  'brand.new.nameLabel': 'Brand name',
  'brand.new.namePlaceholder': 'e.g. Acme Soda',
  'brand.new.industryLabel': 'Industry',
  'brand.new.industryPlaceholder': 'e.g. Beverages (optional)',
  'brand.new.industryPick': 'Pick one (optional)',
  'brand.new.logoLabel': 'Brand logo',
  'brand.new.logoPick': 'Upload a logo',
  'brand.new.logoReplace': 'Replace',
  'brand.new.logoUploading': 'Uploading…',
  'brand.new.logoHint': 'PNG or SVG on a transparent background reads best. Optional — you can add it later in Brand Studio.',
  'brand.new.logoFailed': 'The logo could not be uploaded',
  'brand.new.studioHint': 'After creating it we’ll take you straight to Brand Studio to add the logo, colours and fonts.',
  'brand.new.submit': 'Create brand',
  'brand.new.creating': 'Creating…',
  'brand.new.created': 'Brand “{name}” created',
  'brand.new.failed': 'Could not create brand',
  'brand.viewTrends': 'View trends',
  'brand.recentEvents': 'Recent events',
  'brand.recentEvents.count': 'Showing {shown} of {total}',
  'common.open': 'Open',
  'brand.activeProjects': 'Active projects',
  'brand.newProject': 'New project',
  'brand.noProjectsTitle': 'No projects yet',
  'brand.noProjectsBody': 'Start your first campaign to set up events, stations, and merch.',
  'brand.createProject': 'Create project',
  'brand.kpi.activeXotskies': 'Active merch',
  'brand.kpi.distributed': 'Distributed YTD',
  'brand.kpi.engagement': 'Engagement rate',
  'brand.kpi.health': 'Brand health',

  // ── Profile page ───────────────────────────────────────────────────
  'profile.tabs.events': 'Events',
  'profile.tabs.brands': 'Brands',
  'profile.tabs.projects': 'Projects',
  'profile.tabs.stations': 'Stations',
  'profile.tabs.team': 'Team',
  'profile.events.upcoming': 'Upcoming events',
  'profile.events.past': 'Past events',
  'profile.events.searchPlaceholder': 'Search events…',
  'profile.events.emptyUpcomingTitle': 'No upcoming events',
  'profile.events.emptyUpcomingSubtitle': 'Create a project to get started',
  'profile.events.emptyPastTitle': 'No past events recorded yet.',
  'profile.brands.emptyTitle': 'Brands you manage',
  'profile.brands.emptySubtitle': 'Brands linked to your account will appear here.',
  'profile.brands.nextEvent': 'Next event',
  'profile.brands.noUpcoming': 'No upcoming events',
  'profile.team.emptyTitle': 'Team members',
  'profile.team.emptySubtitle': 'Invite collaborators to see them here.',
  'profile.activity.emptyTitle': 'No activity yet',
  'profile.activity.emptySubtitle':
    'Resources you create, edit or delete will show up here.',
  'profile.activity.loadErrorTitle': 'Could not load activity',
  'profile.activity.loadErrorSubtitle': 'Please try again in a moment.',
  'profile.activity.lastActivity': 'Last activity {date}',
  'profile.activity.historyToggle': 'History ({count})',
  'profile.activity.badgeDeleted': 'Deleted',
  'profile.activity.noun': 'resources',
  'profile.activity.history.created': 'Created this {type}',
  'profile.activity.history.updated': 'Edited {fields}',
  'profile.activity.history.updatedGeneric': 'Edited this {type}',
  'profile.activity.history.deleted': 'Deleted this {type}',
  'profile.activity.history.restored': 'Restored this {type}',
  'profile.activity.type.brand': 'brand',
  'profile.activity.type.event': 'event',
  'profile.activity.type.project': 'project',
  'profile.activity.type.station': 'station',
  'profile.sidebar.experience': 'My experience',
  'profile.sidebar.contact': 'Contact information',
  'profile.sidebar.professional': 'Professional',
  'profile.sidebar.additional': 'Additional info',
  'profile.sidebar.preferences': 'Preferences',
  'profile.sidebar.languages': 'Languages',
  'profile.sidebar.interests': 'Interests',
  'profile.sidebar.editProfile': 'Edit profile',
  'profile.sidebar.changePassword': 'Change password',
  'profile.sidebar.theme': 'Theme',
  'profile.sidebar.language': 'Language',
  'profile.sidebar.noBio':
    'No bio yet. Click "Edit profile" to add your experience.',

  // ── Pagination ─────────────────────────────────────────────────────
  'pagination.previous': 'Previous',
  'pagination.next': 'Next',
  'pagination.previousPage': 'Previous page',
  'pagination.nextPage': 'Next page',
  'pagination.showing': 'Showing',
  'pagination.of': 'of',
  'pagination.items': 'items',

  // ── Global search ──────────────────────────────────────────────────
  'globalSearch.placeholder': 'Search anything — events, brands, stations, merch',
  'globalSearch.clear': 'Clear search',
  'globalSearch.noMatches': 'No matches for',
  'globalSearch.kind.brand': 'Brands',
  'globalSearch.kind.event': 'Events',
  'globalSearch.kind.station': 'Stations',
  'globalSearch.kind.stock': 'Merch',
  'globalSearch.subBrand': 'Brand',
  'globalSearch.subEvent': 'Event',
  'globalSearch.subStation': 'Station',
  'globalSearch.subStockItem': 'Merch item',

  // ── Right sidebar / AI Assistant ───────────────────────────────────
  'assistant.title': 'Atledi',
  'assistant.collapse': 'Collapse assistant',
  'assistant.expand': 'Open assistant',
  // Caso 07 — global launcher + drawer aria labels.
  'assistant.launcher.aria': 'Open AI assistant',
  'assistant.drawer.aria': 'AI assistant',
  'assistant.kickoffPending': 'Reading page context…',
  'assistant.placeholder': 'Ask anything about this page…',
  'assistant.send': 'Send',
  'assistant.suggestions': 'Suggestions',
  'assistant.error': "Couldn't reach the assistant. Try again.",
  'assistant.error.unavailable': 'The assistant is temporarily unavailable. Please try again in a moment.',
  'assistant.retry': 'Try again',

  // ── Export XLSX ────────────────────────────────────────────────────
  'export.button': 'Export XLSX',
  'export.exporting': 'Exporting…',
  'export.nothingTitle': 'Nothing to export',
  'export.nothingBody': 'The list is empty.',
  'export.readyTitle': 'Export ready',
  'export.readyBodyOne': 'row downloaded.',
  'export.readyBodyMany': 'rows downloaded.',
  'export.failedTitle': 'Export failed',

  // ── Forms / file uploader ──────────────────────────────────────────
  'forms.colorField.pickColor': 'Pick color',
  'forms.uploader.formats': 'PNG, JPG, WEBP or SVG',
  'forms.uploader.uploadFailed': 'Upload failed',
  'forms.uploader.cancel': 'Cancel',
  'forms.uploader.edit': 'Edit',
  'forms.uploader.remove': 'Remove',
  'forms.uploader.retry': 'Retry',
  'forms.imageEditor.title': 'Edit image',
  'forms.imageEditor.zoomOut': 'Zoom out',
  'forms.imageEditor.zoomIn': 'Zoom in',
  'forms.imageEditor.rotateLeft': 'Rotate left',
  'forms.imageEditor.rotateRight': 'Rotate right',
  'forms.imageEditor.zoom': 'Zoom',
  'forms.imageEditor.brightness': 'Brightness',
  'forms.imageEditor.contrast': 'Contrast',
  'forms.imageEditor.saturation': 'Saturation',
  'forms.imageEditor.reset': 'Reset',
  'forms.imageEditor.cancel': 'Cancel',
  'forms.imageEditor.apply': 'Apply crop',
  'forms.imageEditor.hint': 'Drag to reposition · Scroll to zoom · ± buttons for fine control',
  'forms.imageEditor.loadError':
    'The image could not be loaded. It may have been deleted, or the file server may be unreachable.',
  'forms.rating.starOne': '1 star',
  'forms.rating.starMany': 'stars',

  // ── Common buttons / states ────────────────────────────────────────
  'common.saving': 'Saving…',
  'common.next': 'Next',
  'common.back': 'Back',
  'common.continue': 'Continue',
  'common.skip': 'Skip',
  'common.submit': 'Submit',
  'common.confirm': 'Confirm',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.update': 'Update',
  'common.optional': 'Optional',
  'common.required': 'Required',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.retry': 'Retry',
  'common.viewAll': 'View all',
  'common.error': 'Something went wrong',
  'common.tryAgain': 'Please try again.',
  'common.empty': 'Nothing here yet',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.draft': 'Draft',
  'common.active': 'Active',
  'common.disabled': 'Disabled',
  'common.enabled': 'Enabled',
  'common.preview': 'Preview',
  'common.print': 'Print',
  'common.download': 'Download',
  'common.duplicate': 'Duplicate',
  'common.archive': 'Archive',
  'common.locked': 'Locked',
  'common.unlocked': 'Unlocked',

  // ── Onboarding ─────────────────────────────────────────────────────
  'onboarding.brandProfile.title': 'Tell us about your brand',
  'onboarding.brandProfile.subtitle': 'A few details so we can tailor your account.',
  'onboarding.brandProfile.brandName': 'Brand name',
  'onboarding.brandProfile.brandNamePlaceholder': 'e.g. Atledi',
  'onboarding.brandProfile.industry': 'Industry',
  'onboarding.brandProfile.industryPlaceholder': 'Select an industry',
  'onboarding.brandProfile.website': 'Website',
  'onboarding.brandProfile.websitePlaceholder': 'https://yourbrand.com',
  'onboarding.brandProfile.about': 'About',
  'onboarding.brandProfile.aboutPlaceholder': 'Short description of what you do.',
  'onboarding.brandProfile.continue': 'Continue',
  // ── Guía de los primeros días (My Merch) ────────────────────────────────
  'nextSteps.title': 'What comes next',
  'nextSteps.subtitle': 'Your merch is only half the story — it needs an event to be given away at.',
  'nextSteps.merch': 'Add your merch',
  'nextSteps.merchHint': 'The prizes and products guests will play for.',
  'nextSteps.project': 'Create a project',
  'nextSteps.projectHint': 'A project groups the events of one campaign — a tour, a season, a launch.',
  'nextSteps.event': 'Schedule an event',
  'nextSteps.eventHint': 'The event is what goes live: its stations, its QR and its prizes.',
  'onboarding.profile.title': 'Tell us about your brand',
  'onboarding.profile.subtitle': "We'll set up your free account right after.",
  'onboarding.profile.companyName': 'Brand name',
  'onboarding.profile.companyNamePlaceholder': 'Lime Soda Co.',
  'onboarding.profile.yourName': 'Your name',
  'onboarding.profile.yourNamePlaceholder': 'Jane Founder',
  // La industria ya no se pregunta en el alta (vive en la marca); las dos claves
  // siguientes se quedan porque el Centro de ayuda y los ajustes las usan.
  'onboarding.profile.industry': 'Industry',
  'onboarding.profile.pickOne': 'Pick one',
  'onboarding.profile.kind': 'What best describes you?',
  'onboarding.profile.kind.Agency': 'Agency',
  'onboarding.profile.kind.Agency.hint': 'You run activations for other brands.',
  'onboarding.profile.kind.Company': 'Brand',
  'onboarding.profile.kind.Company.hint': 'You run activations for your own brand.',
  'onboarding.profile.kind.Property': 'Property',
  'onboarding.profile.kind.Property.hint': 'You host other people’s activations at your venue.',
  'onboarding.profile.continue': 'Continue',
  'onboarding.profile.savedTitle': 'Profile saved',
  'onboarding.profile.savedBody': 'Your free plan is now active. One more step.',
  'onboarding.profile.errorTitle': 'Could not save',
  'onboarding.kit.title': 'Build your BrandKit',
  'onboarding.kit.subtitle': "We'll use these defaults across journeys, the wheel and printables.",
  'onboarding.kit.campaignName': 'Campaign name',
  'onboarding.kit.campaignNamePlaceholder': 'Summer Tour 2026',
  'onboarding.kit.primary': 'Primary',
  'onboarding.kit.secondary': 'Secondary',
  'onboarding.kit.font': 'Font',
  'onboarding.kit.clientLogo': 'Client logo',
  'onboarding.kit.finish': 'Enter Xotski',
  'onboarding.kit.savedTitle': 'BrandKit saved!',
  'onboarding.kit.savedBody': 'Welcome to Xotski.',
  'onboarding.kit.errorTitle': 'Could not save BrandKit',

  // ── Stock ──────────────────────────────────────────────────────────
  'stock.title': 'Merch',
  'stock.subtitle': 'Manage the items you offer at activations.',
  'stock.addButton': 'Add merch',
  'stock.empty.title': 'No merch yet',
  'stock.empty.body': 'Add your first merch item to start running activations.',
  'stock.empty.cta': 'Add merch',
  'stock.column.name': 'Name',
  'stock.column.rating': 'Rating',
  'stock.column.classification': 'Classification',
  'stock.column.status': 'Status',
  'stock.column.actions': 'Actions',
  'stock.searchPlaceholder': 'Search merch…',
  'stock.confirmDelete': 'Delete this merch item?',
  'stock.deletedTitle': 'Merch deleted',
  'stock.savedTitle': 'Merch saved',
  'stock.errorTitle': 'Couldn\'t save merch',
  'stock.dialog.addTitle': 'Add merch',
  'stock.dialog.editTitle': 'Edit merch',
  'stock.dialog.name': 'Name',
  'stock.dialog.namePlaceholder': 'Sample bottle, postcard, sticker…',
  'stock.dialog.translate': 'Spanish name',
  'stock.dialog.translatePlaceholder': 'Optional translation',
  'stock.dialog.image': 'Image',
  'stock.dialog.logo': 'Logo',
  'stock.dialog.rating': 'Game probability',
  'stock.dialog.classification': 'Classification',
  'stock.dialog.classificationPlaceholder': 'Beverage, swag, voucher…',
  'stock.dialog.status': 'Status',
  'stock.list.title': 'My Merch',
  'stock.list.subtitle': 'The merch items that show up on your games and printables.',
  'stock.list.addButton': 'Add merch',
  'stock.list.filter': 'Filter',
  'stock.list.allClassifications': 'All classifications',
  'stock.list.unclassified': 'Unclassified',
  'stock.list.itemOne': 'item',
  'stock.list.itemMany': 'items',
  'stock.list.emptyTitle': 'No merch yet',
  'stock.list.emptyBody': 'Add the products and merch your guests can win.',
  'stock.list.allClassified': 'Everything is classified',
  'stock.list.allClassifiedBody': 'Every item has a SEE / ENGAGE / TRY / ACT label.',
  'stock.list.noneInClass': 'No merch classified as',
  'stock.list.editToSetClass': 'Edit a merch item and set its classification to',
  'stock.list.editToSetClassSuffix': 'to see it here.',
  'stock.list.createdTitle': 'Merch added to inventory',
  'stock.list.updatedTitle': 'Merch updated',
  'stock.list.createFailed': 'Create failed',
  'stock.list.updateFailed': 'Update failed',
  'stock.card.edit': 'Edit',
  'stock.card.delete': 'Delete',
  'stock.card.rarityTier': 'Rarity tier',
  'stock.card.rarityLabel.1': 'Common',
  'stock.card.rarityLabel.2': 'Uncommon',
  'stock.card.rarityLabel.3': 'Rare',
  'stock.card.rarityLabel.4': 'Epic',
  'stock.card.rarityLabel.5': 'Legendary',
  'stock.card.qty': 'Qty',
  'stock.card.ratingOf': 'Rating',
  'stock.dialog.editingTitle': 'Edit merch',
  'stock.dialog.addingTitle': 'Add merch',
  'stock.dialog.updateHeading': 'Update',
  'stock.dialog.updateHeadingFallback': 'merch',
  'stock.dialog.newHeading': "What's the new merch?",
  'stock.dialog.editLead': 'Tweak the basics — changes apply everywhere this merch item appears.',
  'stock.dialog.newLead': "Set the basics. You'll assign quantities to specific stations from My Merch later.",
  'stock.dialog.section.identity': 'Identity',
  'stock.dialog.section.visuals': 'Visuals',
  'stock.dialog.section.inventory': 'Inventory',
  'stock.dialog.section.funnel': 'Funnel stage',
  'stock.dialog.section.rarity': 'Rarity',
  'stock.dialog.field.name': 'Name',
  'stock.dialog.field.namePlaceholder': 'e.g. Joyly enamel pin',
  'stock.dialog.field.translate': 'Spanish name (translate)',
  'stock.dialog.field.translatePlaceholder': 'Shown when journey runs in en-es',
  'stock.dialog.field.description': 'Description',
  'stock.dialog.field.descriptionPlaceholder': 'A short blurb shown on the prize-won screen…',
  'stock.dialog.field.productImage': 'Product image',
  'stock.dialog.field.wheelLogo': 'Game logo',
  'stock.dialog.field.initialQty': 'Initial quantity',
  'stock.dialog.field.unitCost': 'Unit value (USD)',
  'stock.dialog.field.unitCostHint': 'Optional. What one unit is worth. Feeds the distributed value in reports and closing records.',
  'stock.dialog.destination.create': 'This merch will be added to {brand}.',
  'stock.dialog.destination.edit': 'This merch belongs to {brand}.',
  'stock.dialog.destination.confirm': 'Yes, it belongs to {brand} (your account has more than one brand).',
  'stock.list.addedTo': 'Added to {brand}',
  'stock.list.seeInventory': 'See brand inventory',
  'stock.dialog.field.wheelLogoLibrary': 'Pick from library',
  'stock.dialog.field.wheelLogoLibraryEmpty':
    'No curated logos yet. Drop files into ox-platform/uploads/wheel-logos/ to populate the library.',
  'stock.library.title': 'Logo library',
  'stock.library.count': '{n} logos on file. Pick one and it fills the logo slot; you can still upload your own.',
  'stock.library.search': 'Search by name',
  'stock.library.empty': 'The library is empty.',
  'stock.library.noMatch': 'Nothing matches «{q}».',
  'stock.library.pickedHint': 'From the library: {name}',
  'stock.outline.pick': 'Pick an outline',
  'stock.outline.title': 'Merch outlines',
  'stock.outline.description': 'Placeholders by item type, in three inks. The outline stands in until the real product photo is uploaded; items still on an outline are flagged in My Merch and in Track.',
  'stock.outline.empty': 'No outlines loaded yet. Drop the set into ox-platform/uploads/merch-outlines/<category>/.',
  'stock.outline.placeholderHint': 'Outline placeholder. Upload the real product photo when you have it.',
  'stock.outline.badge': 'Outline',
  'stock.warehouse.title': 'What is in the warehouse',
  'stock.warehouse.ledgerChip': 'Computed from {n}+ ledger movements',
  'stock.warehouse.portfolio': 'Portfolio',
  'stock.warehouse.portfolioHint': '{n} items · {value} at cost',
  'stock.warehouse.portfolioPending': '{n} items · value pending on {k} item(s)',
  'stock.warehouse.committed': 'Committed',
  'stock.warehouse.committedHint': '{p}% of units owned, across {n} open event(s)',
  'stock.warehouse.restock': 'Needs restock',
  'stock.warehouse.restockHint': 'Most urgent: {name}',
  'stock.warehouse.restockNone': 'Nothing below threshold',
  'stock.warehouse.unaccounted': 'Unaccounted',
  'stock.warehouse.unaccountedHint': '{value} at cost · last reconciled {last}',
  'stock.warehouse.costPending': 'cost pending',
  'stock.warehouse.never': 'never',
  'stock.warehouse.chipAction': 'Action',
  'stock.warehouse.chipDisposition': 'Needs disposition',
  'stock.warehouse.action.costs': 'Add unit costs to {k} of {n} items to unlock portfolio value and cost per lead.',
  'stock.warehouse.action.costsCta': 'Add costs',
  'stock.warehouse.action.reallocate': '{name} has nothing left in the warehouse: re-allocate what is still in play or restock.',
  'stock.warehouse.action.reallocateCta': 'Re-allocate',
  'stock.warehouse.action.counts': '{n} ended event(s) still have no closing count.',
  'stock.warehouse.action.countsCta': 'Enter counts',
  'stock.warehouse.action.moreWaiting': '{n} more waiting',
  'stock.warehouse.action.nothingElse': 'nothing else waiting',
  'stock.dispo.kicker': 'Unaccounted · needs a disposition',
  'stock.dispo.head': '{units} units across {lots} lot(s) still need a disposition',
  'stock.dispo.allClear': 'Every unaccounted unit has a disposition on record',
  'stock.dispo.sub': 'Resolving a lot writes a new movement. The ledger is append-only, so the original count stays on record.',
  'stock.dispo.basis': '{out} units out · {p}%',
  'stock.dispo.needs': 'Needs a disposition',
  'stock.dispo.blurb': 'Pick what actually happened. Until then it counts against the event.',
  'stock.dispo.kind.off_station': 'Handed off-station',
  'stock.dispo.kind.operations': 'Operations use',
  'stock.dispo.kind.write_off': 'Write off',
  'stock.dispo.kind.recount': 'Recount',
  'stock.dispo.kindBlurb.off_station': 'Given out at the stage or gate without a scan. Stays a delivered unit, just not a game outcome.',
  'stock.dispo.kindBlurb.operations': 'Staff uniform, sample or venue courtesy. An inventory fact, not shrinkage.',
  'stock.dispo.kindBlurb.write_off': 'Genuinely gone. Hits distributed value and the shrinkage line for the quarter.',
  'stock.dispo.kindBlurb.recount': 'They were still in the box: the units go back to the warehouse.',
  'stock.dispo.units': 'Units (up to {max})',
  'stock.dispo.note': 'Note',
  'stock.dispo.notePlaceholder': 'Stage giveaway, organizer request…',
  'stock.dispo.appendOnly': 'This writes a new movement. A correction later is another row, never an edit.',
  'stock.dispo.confirm': 'Record it',
  'stock.dispo.savedToast': 'Disposition recorded',
  'stock.dispo.failedToast': 'The disposition could not be recorded',
  'stock.movements.title': 'Recent movements',
  'stock.movements.appendOnly': 'Append only. A correction is a new row, never an edit.',
  'stock.movements.empty': 'No movements yet. Allocations, deliveries, closings and dispositions show up here as they happen.',
  'stock.movements.noNote': 'no note',
  'stock.movements.noun': 'movements',
  'stock.movements.state.warehouse': 'warehouse',
  'stock.movements.state.allocated': 'allocated',
  'stock.movements.state.in play': 'in play',
  'stock.movements.state.won': 'won',
  'stock.movements.state.unaccounted': 'unaccounted',
  'stock.movements.state.off-station': 'off-station',
  'stock.movements.state.operations': 'operations',
  'stock.movements.state.written off': 'written off',
  'stock.dialog.field.restockThreshold': 'Restock threshold',
  'stock.dialog.field.restockPlaceholder': 'Alert below…',
  'stock.dialog.field.classification': 'Classification (SEE · ENGAGE · TRY · ACT)',
  'stock.dialog.field.classUnset': 'Unset',
  'stock.dialog.field.rarityTier': 'Rarity tier (drives game probability)',
  'stock.dialog.weight.weightOn': 'Weight on the game:',
  'stock.dialog.weight.hint': '1 = common (frequent win), 5 = legendary (rare drop).',
  'stock.dialog.fixFieldsOne': 'Please fix 1 field before saving:',
  'stock.dialog.fixFieldsMany': 'Please fix {count} fields before saving:',
  'stock.dialog.cancel': 'Cancel',
  'stock.dialog.saving': 'Saving…',
  'stock.dialog.saveChanges': 'Save changes',
  'stock.dialog.addToInventory': 'Add to inventory',
  'stock.dialog.error.nameLen': 'Name must be at least 2 characters',
  'stock.dialog.error.qtyGt0': 'Initial quantity must be greater than 0',
  'stock.dialog.error.qtyInt': 'Must be a whole number',
  'stock.dialog.error.costNeg': 'Unit value cannot be negative',
  'stock.dialog.error.costInvalid': 'Enter a number with up to two decimals, or leave it blank',
  'stock.dialog.error.threshNeg': 'Threshold cannot be negative',
  'stock.dialog.error.threshExceeds': 'Threshold cannot exceed initial quantity',
  'stock.dialog.error.ratingRange': 'Rating must be between 1 and 5',
  'stock.dialog.classHint.SEE': 'Awareness — visibility only',
  'stock.dialog.classHint.ENGAGE': 'Engagement — small touchpoint reward',
  'stock.dialog.classHint.TRY': 'Trial — sampling / tasting',
  'stock.dialog.classHint.ACT': 'Action — high-intent conversion',

  // ── Stations ───────────────────────────────────────────────────────
  'stations.title': 'Stations',
  'stations.subtitle': 'Where your team distributes merch on event day.',
  'stations.addButton': 'New station',
  'stations.empty.title': 'No stations yet',
  'stations.empty.body': 'Add a station to assign merch and capture interactions.',
  'stations.empty.cta': 'Create station',
  'stations.searchPlaceholder': 'Search stations…',
  'stations.column.name': 'Name',
  'stations.column.event': 'Event',
  'stations.column.surveys': 'Journeys',
  'stations.column.status': 'Status',
  'stations.confirmDelete': 'Delete this station?',
  'stations.deletedTitle': 'Station deleted',
  'stations.savedTitle': 'Station saved',
  'stations.dialog.addTitle': 'New station',
  'stations.dialog.editTitle': 'Edit station',
  'stations.dialog.name': 'Station name',
  'stations.dialog.event': 'Event',
  'stations.dialog.surveys': 'Journeys',
  'stations.dialog.status': 'Status',
  'stations.detail.qrCode': 'QR code',
  'stations.detail.printQr': 'Print QR',
  'stations.detail.copyLink': 'Copy link',
  'stations.detail.linkCopied': 'Link copied to clipboard',
  'stations.detail.stockAssigned': 'Merch assigned',
  'stations.detail.noStock': 'No merch assigned to this station.',
  'stations.detail.responses': 'Interactions',
  'stations.detail.noResponses': 'No interactions captured yet.',
  'stations.print.title': 'Print station QR codes',
  'stations.print.instructions': 'Use your browser\'s print dialog to send these QR codes to a printer or save as PDF.',
  'stations.list.subtitleLong': 'Every QR code lives on a station. Each station belongs to an event.',
  'stations.list.emptyDesc': "Create stations inside a project's events.",
  'stations.list.goToProjects': 'Go to projects',
  'stations.list.allStations': 'All stations',
  'stations.list.column.survey': 'Journey',
  'stations.list.column.actions': 'Actions',
  'stations.list.allocate': 'Open station',
  'stations.list.column.allocated': 'Allocated',
  'stations.list.allocatedCell': '{units} units · {items} prizes',
  'stations.list.allocatedNone': 'Nothing allocated',
  'stations.list.allocatedLink': 'Change in Plan',
  'stations.list.qrShort': 'QR',
  'stations.detail.notFound': 'Station not found',
  'stations.detail.back': 'Back',
  'stations.detail.eventLabel': 'Event',
  'stations.detail.surveyLabel': 'Journey',
  'stations.detail.edit': 'Edit',
  'stations.detail.viewQr': 'View QR code',
  'stations.detail.allocationsTitle': 'Merch at this station',
  'stations.detail.stock': 'Merch',
  'stations.detail.loadingStocks': 'Loading merch catalog…',
  'stations.detail.allAllocated': 'All brand merch is already allocated to this station',
  'stations.detail.noStocksForBrand': 'No merch yet for this brand',
  'stations.detail.createStocksCta': 'Create merch',
  'stations.detail.available': 'available',
  'stations.detail.pickStock': 'Pick a merch item',
  'stations.detail.quantity': 'Quantity',
  'stations.detail.qtyError': 'Must be ≥ 1',
  'stations.detail.overrideRating': 'Override rating',
  'stations.detail.usesDefault': 'Uses the merch default',
  'stations.detail.wheelWeight': 'Game weight:',
  'stations.detail.allocate': 'Allocate',
  'stations.detail.noAllocations': 'No allocations yet.',
  'stations.detail.stockCol': 'Merch',
  'stations.detail.ratingCol': 'Rating',
  'stations.detail.availTotalCol': 'Available / total',
  'stations.detail.out': 'Out',
  'stations.detail.removeAria': 'Remove',
  'stations.detail.allocatedToast': 'Merch allocated',
  'stations.detail.allocateFailed': 'Allocate failed',
  'stations.detail.missingData': 'Missing data',
  // PRD §9 — two-tier brand inventory: the station picker shows how many
  // units the brand still has in its pool and blocks allocations beyond it.
  'stations.detail.brandPool': 'Brand pool',
  'stations.detail.exceedsBrandPool': 'Exceeds brand pool',
  // PRD §9 v2 — Event Prize Pool picker (eligible brand prizes for an event).
  'eventPrizePool.title': 'Eligible prizes for this event',
  'eventPrizePool.subtitle':
    "Pick which of the brand's prizes can be allocated to this event's stations. Leave empty to allow all brand prizes (back-compat).",
  'eventPrizePool.brandEmpty':
    'The brand has no adopted prizes yet. Open Brand Studio → Prizes to adopt from the agency catalog.',
  'eventPrizePool.noPoolHint':
    'No prizes selected yet — stations will see the full brand catalog.',
  'eventPrizePool.unitsRemain': 'units',
  'eventPrizePool.savedToast': 'Event prize pool updated',
  'eventPrizePool.saveFailed': 'Failed to update prize pool',
  // ── Station Detail v2 (header + tabs) ────────────────────────────
  'stations.detail.inventoryTab': 'Merch',
  'stations.detail.responsesTab': 'Interactions',
  'stations.detail.dashboardTab': 'Dashboard',
  'stations.detail.usedCol': 'Used',
  'stations.detail.totalAllocated': 'Total',
  'stations.detail.totalUsed': 'Used',
  'stations.detail.totalRemaining': 'Remaining',
  'stations.detail.noSurveyAssigned': 'No journey assigned to this station yet.',
  'stations.detail.responsesOverTime': 'Interactions over time',
  'stations.detail.stockDistribution': 'Merch won by item',
  'stations.detail.inventoryDonut': 'Inventory used vs remaining',
  'stations.detail.topAnswers': 'Top answers',
  'stations.detail.dashboardEmpty': 'Not enough data to draw this chart yet — come back once respondents start submitting.',
  'stations.dash.heroTitle': 'What happened at this station',
  'stations.dash.usedChip': '{pct}% of the merch used',
  'stations.dash.interactions': 'Interactions',
  'stations.dash.interactionsHint': 'survey sessions started at this station',
  'stations.dash.unitsOut': 'Units out',
  'stations.dash.ofAllocated': 'of {n} allocated',
  'stations.dash.remainingHint': 'available for the games',
  'stations.dash.last24h': 'Last 24 h',
  'stations.dash.last7dHint': '{n} in the last 7 days',
  'stations.dash.chip': '{n} interactions · by {unit}',
  'stations.dash.unitHour': 'hour',
  'stations.dash.unitDay': 'day',
  'stations.dash.peakCap': 'Peak {n} per {unit}',
  'stations.dash.peakWhen': 'Busiest {unit}',
  'stations.dash.perUnit': 'Peak per {unit}',
  'stations.dash.total': 'Total',
  'stations.dash.avg': 'Average per block',
  'stations.dash.tooltip': '{n} interactions · {phase}',
  'stations.dash.legendPeak': 'Busiest block',
  'stations.dash.rowUsed': '{used} of {total}',
  'stations.dash.soldOut': 'Sold out',
  'stations.dash.answered': '{n} answers',
  'stations.detail.responseTimestamp': 'When',
  'stations.detail.responseSummary': 'Preview',
  'stations.detail.prevPage': 'Previous page',
  'stations.detail.nextPage': 'Next page',
  'stations.editDialog.title': 'Edit station',
  'stations.editDialog.description': 'Rename or re-attach the journey. Allocations stay intact.',
  'stations.editDialog.stationName': 'Station name',
  'stations.editDialog.nameRequired': 'Station name is required',
  'stations.editDialog.survey': 'Journey',
  'stations.editDialog.surveyDisabledTitle': "The journey is set on the event's Design Journey tab. Open the event to change it.",
  'stations.editDialog.noSurvey': '— no journey —',
  'stations.editDialog.template': 'journey',
  'stations.editDialog.global': 'global',
  'stations.editDialog.surveyHintPre': "Set on the event's",
  'stations.editDialog.surveyHintLink': 'Design Journey',
  'stations.editDialog.surveyHintPost': 'tab — applies to all stations of this event.',
  'stations.editDialog.confirmHeader': 'Delete station',
  'stations.editDialog.confirmBody': 'The station and its merch allocations will be hidden from listings.',
  'stations.editDialog.confirmInstruction': 'Click',
  'stations.editDialog.confirmInstructionEmph': 'Confirm delete',
  'stations.editDialog.confirmInstructionEnd': 'to proceed.',
  'stations.editDialog.deleting': 'Deleting…',
  'stations.editDialog.confirmDelete': 'Confirm delete',
  'stations.editDialog.delete': 'Delete',
  'stations.editDialog.cancel': 'Cancel',
  'stations.editDialog.save': 'Save',
  'stations.editDialog.updatedToast': 'Station updated',
  'stations.editDialog.updateFailed': 'Update failed',
  'stations.editDialog.deletedToast': 'Station deleted',
  'stations.editDialog.deleteFailed': 'Delete failed',
  'stations.qrPrint.noSurvey': 'This station has no journey wired yet. Start one in the event\u2019s Design Journey tab.',
  'stations.qrPrint.back': 'Back',
  'stations.qrPrint.title': 'QR code',
  'stations.qrPrint.shortUrl': 'Short URL',
  'stations.qrPrint.sharedNote': 'This station shares a QR with others — the code below opens the shared station picker. Scanning it lets the consumer choose their station first.',
  'stations.qrPrint.fullUrl': 'Full URL',
  'stations.qrPrint.downloadSvg': 'Download SVG',
  'stations.qrPrint.print': 'Print',
  'stations.qrPrint.openNewTab': 'Open in new tab',

  // ── Events ─────────────────────────────────────────────────────────
  'events.detail.startDate': 'Start',
  'events.detail.endDate': 'End',
  'events.detail.address': 'Address',
  'events.detail.codeRef': 'Reference code',
  'events.detail.sectionCount': 'Sections',
  'events.detail.stations': 'Stations',
  'events.detail.noStations': 'No stations yet for this event.',
  'events.detail.addStation': 'Add station',
  'events.detail.savedTitle': 'Event saved',
  'events.detail.deletedTitle': 'Event deleted',
  'events.detail.confirmDelete': 'Delete this event?',
  'events.dialog.editTitle': 'Edit event',
  'events.dialog.name': 'Event name',
  'events.dialog.address': 'Address',
  'events.dialog.startDate': 'Start date',
  'events.dialog.endDate': 'End date',
  'events.dialog.sectionCount': 'Section count',
  'events.dialog.codeRef': 'Reference code',
  'events.dialog.status': 'Status',
  'events.survey.title': 'Journey configuration',
  'events.survey.subtitle': 'Design the guest journey for this event.',
  'events.survey.outputs': 'Outputs',
  'events.survey.preview': 'Preview',
  'events.survey.savedTitle': 'Configuration saved',
  'events.survey.endpoint': 'Endpoint URL',
  'events.survey.endpointPlaceholder': 'https://hooks.example.com/onxite',
  'events.survey.translatable': 'Translatable',
  'events.survey.translatableHint': 'Show language toggle to respondents.',
  'events.survey.locked': 'Lock journey',
  'events.survey.lockedHint': 'Prevents accidental edits to the journey body.',
  'events.survey.preview.empty': 'Add steps and components to see a live preview.',
  'events.detail.notFound': 'Event not found',
  'events.detail.backToProject': 'Back to project',
  'events.detail.delete': 'Delete event',
  'events.detail.deleteTitle': 'Delete "{name}"?',
  'events.detail.deleteBody':
    'The event disappears from the project and from every list, and its public links stop working. The responses already collected are kept. Its stations are not removed — they stay in the Stations list until you delete them there.',
  'events.detail.deletedToast': 'Event "{name}" deleted',
  'events.detail.deleteFailed': 'The event could not be deleted',
  'events.detail.unknown': 'Unknown',
  'events.detail.tbd': 'TBD',
  'events.detail.sectionOne': 'section',
  'events.detail.sectionMany': 'sections',
  'events.detail.editEvent': 'Edit event',
  'events.detail.viewReport': 'View report',
  // Caso 10 — Promote live monitor to the event header so on-site staff
  // reach it in one click.
  'events.detail.openLiveMonitor': 'Live monitor',
  'events.detail.liveTitle': 'Live monitoring',
  'events.detail.reportTitle': 'Event report',
  // Caso 12 — Event-goal card.
  'goals.title': 'Targets & progress',
  'goals.description':
    'Set per-event targets. Progress is computed from the same metrics as the event report.',
  'goals.empty': 'No targets set yet. Pick a metric below to start.',
  'goals.targetPlaceholder': 'Target',
  'goals.editor.eventTitle': 'Targets for this event',
  'goals.editor.eventHint': 'Inherited whole from the project unless you set one here. An event target overrides the project target; leave a field empty to keep measuring without scoring.',
  'goals.editor.projectTitle': 'Project targets',
  'goals.editor.projectHint': 'Every event in the project inherits these whole (they are not split). An event can override any of them from its properties panel.',
  'goals.editor.own': 'This event',
  'goals.editor.inherited': 'From the project',
  'goals.editor.inheritedHint': 'Type a number to override it for this event.',
  'goals.editor.useProject': 'Use the project target',
  'goals.editor.remove': 'Remove target',
  'goals.editor.none': 'No target',
  'goals.editor.saved': 'Target saved',
  'goals.editor.removed': 'Target removed',
  'goals.editor.failed': 'The target could not be saved',
  'goals.readOnlyHint': 'Targets are set in the project planner (Plan) and in the event properties panel. This card only reports progress.',
  'goals.editInPlan': 'Edit targets in Plan',
  'goals.source.project': 'from the project',
  'goals.metric.samples': 'Samples',
  'goals.metric.responses': 'Interactions',
  'goals.metric.leads': 'Leads',
  'goals.metric.prizesDelivered': 'Prizes delivered',
  'goals.metric.completionRate': 'Completion rate',
  'goals.status.on-track': 'On track',
  'goals.status.at-risk': 'At risk',
  'goals.status.met': 'Met',
  // Caso 11 — Booth-operator's mobile-first station view.
  'operator.title': 'Operator mode',
  'operator.edit.cta': 'Edit availability',
  'operator.edit.unlockTitle': 'Station code',
  'operator.edit.unlockBody': 'Ask the organizer for this station’s 6-digit code. It unlocks editing on this phone for 8 hours.',
  'operator.edit.codeLabel': 'Code',
  'operator.edit.unlock': 'Unlock',
  'operator.edit.wrongCode': 'Wrong code',
  'operator.edit.tooMany': 'Too many attempts — wait a minute and try again.',
  'operator.edit.unlocked': 'Editing unlocked',
  'operator.edit.editing': 'Editing',
  'operator.edit.saved': 'Saved',
  'operator.edit.saveFailed': 'Could not save',
  'operator.edit.notEnabled': 'Editing is not enabled for this station. Ask the organizer.',
  'operator.back': 'Back to stations',
  'operator.invalidId': 'Invalid station id.',
  'operator.unknownEvent': 'Unknown event',
  'operator.surveyActive': 'Active journey: {name}',
  'operator.noSurvey': 'No journey assigned yet.',
  'operator.cta.redeem': 'Redeem',
  'operator.cta.showQr': 'Show QR',
  'operator.stock.title': 'Prizes left',
  'operator.stock.totalRemaining': '{count} total',
  'operator.stock.left': 'left',
  'operator.stock.empty': 'No prizes allocated to this station.',
  'operator.stock.lowAlert':
    '{count} prize(s) running low — flag to your coordinator.',
  'stations.detail.operatorMode': 'Operator mode',
  'stations.detail.operatorEdit.label': 'Operators can edit availability and take prizes out of the games',
  'stations.detail.operatorEdit.hint': 'They unlock it on their phone with this station’s code. Without it, the operator view is read-only.',
  'stations.detail.operatorEdit.code': 'Station code',
  'stations.detail.operatorEdit.copy': 'Copy code',
  'stations.detail.operatorEdit.copied': 'Code copied',
  'stations.detail.operatorEdit.rotate': 'New code',
  'stations.detail.operatorEdit.rotated': 'New code generated — the previous one no longer works',
  'stations.detail.operatorEdit.failed': 'Could not update operator access',
  // Caso 11 v3 — Operator access tokens.
  'stations.detail.generateOperatorLink': 'Operator link',
  'stations.detail.tokenReady': 'Operator link ready',
  'stations.detail.tokenWarning':
    "This URL grants access to the operator view. Copy it now — we won't show it again.",
  'stations.detail.tokenExpiresAt': 'Expires {when}',
  'stations.detail.tokenCopy': 'Copy URL',
  'stations.detail.tokenCopied': 'Copied to clipboard.',
  'stations.detail.tokenCopyFailed': "Couldn't copy. Select the URL manually.",
  'stations.detail.tokenMintFailed': "Couldn't generate the operator link.",
  // Caso 11 v4 — operator login (token exchange) UX.
  'operatorLogin.title': 'Signing you in…',
  'operatorLogin.subtitle': "Exchanging your link for an operator session.",
  'operatorLogin.errorTitle': "Couldn't sign you in",
  'operatorLogin.missingToken': 'This link is missing the access token.',
  'operatorLogin.exchangeFailed':
    'The link is invalid, revoked, or expired. Ask the manager for a new one.',
  // Caso 16 — Breadcrumb labels.
  'breadcrumbs.brand': 'Brand',
  'breadcrumbs.project': 'Project',
  'breadcrumbs.event': 'Event',
  'breadcrumbs.settings': 'Settings',
  // Caso 16 — Entity-type chip next to the page title (EntityTypeBadge).
  'entityType.project': 'Project',
  'entityType.event': 'Event',
  'entityType.station': 'Station',

  // ── Caso 09 — Demographic aggregation panel ────────────────────
  'demographics.title': 'Demographics',
  'demographics.subtitle':
    '{count} contacts with demographic data on file.',
  'demographics.ageRange': 'Age range',
  'demographics.gender': 'Gender',
  'demographics.ethnicity': 'Ethnicity',
  'demographics.city': 'Top cities',
  'demographics.state': 'Top states',
  'demographics.language': 'Top languages',
  'demographics.genderValue.female': 'Female',
  'demographics.genderValue.male': 'Male',
  'demographics.genderValue.non-binary': 'Non-binary',
  'demographics.genderValue.other': 'Other',
  'demographics.genderValue.undisclosed': 'Undisclosed',
  'demographics.brand.noEvents':
    'This brand has no events yet — demographics will appear once you run an event with a journey that collects age, gender or location.',
  'demographics.brand.noData':
    'Scanned {count} event(s); none have demographic interactions yet. Make sure the brand\'s journeys collect age, gender or location.',
  // Caso 10 — Live monitor.
  'liveMonitor.invalidId': 'Invalid event id.',
  'liveMonitor.back': 'Back to report',
  'liveMonitor.live': 'LIVE',
  'liveMonitor.refreshing': 'Refreshing…',
  // Caso 10 v2 — transport indicator (WS push vs polling fallback).
  'liveMonitor.transport.live': 'Realtime',
  'liveMonitor.transport.polling': 'Polling',
  'liveMonitor.unknown': 'Event',
  'liveMonitor.kpi.interactions': 'Interactions',
  'liveMonitor.kpi.last24h': 'Last 24 h',
  'liveMonitor.kpi.leads': 'Leads',
  'liveMonitor.kpi.prizesDistributed': 'Prizes delivered',
  'liveMonitor.alert.lowStock': 'Only {count} prize(s) left across stations.',
  'eventReport.openLiveMonitor': 'Open live monitor',
  'events.detail.stationsTab': 'Stations',
  'events.detail.surveyTab': 'Design Journey',
  // Next-step nudge: stations ready → go design the Xurvey.
  'events.djNudge.title': 'Stations ready — now design your Xurvey',
  'events.djNudge.body':
    'Your {n} station(s) are running the default survey. Give it your brand, steps and prizes in the Design Journey.',
  'events.djNudge.cta': 'Design Journey',
  // Design Journey — the brand has not written a legal text this survey asks for.
  'designJourney.consentMissing.title': 'A legal text is missing from this brand',
  'designJourney.consentMissing.general':
    'This survey has a consent step, but the brand has no terms and conditions written yet. Respondents would be asked to accept a document that does not exist.',
  'designJourney.consentMissing.photo':
    'This survey asks for image consent, but the brand has no image consent text written yet. Respondents would be asked to accept being photographed with nothing to read.',
  'designJourney.consentMissing.both':
    'This survey asks for consent — including image consent — and the brand has neither text written yet. Respondents would be asked to accept documents that do not exist.',
  'designJourney.consentMissing.cta': 'Write them in Settings › Brand',
  // Design Journey — la capa legal derivada del despliegue (sept 2026).
  'designJourney.tab.steps': 'Journeys',
  'designJourney.tab.consent': 'Consent',
  'designJourney.legalLocked': 'Managed automatically from what this survey collects and the brand\u2019s Legal page. It cannot be hidden by hand.',
  'designJourney.consent.title': 'Consent',
  'designJourney.consent.description':
    'What the guest will be asked to accept is derived: contact, mobile and prize from the survey; age gate, marketing and SMS from the brand\u2019s Legal & Consent page. Only photography and minors are decided per deployment.',
  'designJourney.consent.blockedTitle': 'This deployment cannot go live yet',
  'designJourney.consent.cta': 'Open Legal & Consent',
  'designJourney.consent.photos': 'Photography at this activation',
  'designJourney.consent.photos.none': 'No photos or video',
  'designJourney.consent.photos.ambient': 'Ambient only (crowd, atmosphere) \u2014 notice',
  'designJourney.consent.photos.featured': 'Featured (people, likeness) \u2014 separate release',
  'designJourney.consent.minors': 'Minors may attend',
  'designJourney.consent.minorsDescription': 'Turns the featured image release off in the digital flow. Use the guardian paper release instead.',
  'designJourney.consent.instrument.notice': 'Privacy notice (always shown)',
  'designJourney.consent.instrument.terms': 'Terms & Conditions checkbox',
  'designJourney.consent.instrument.rules': 'Official Rules link',
  'designJourney.consent.instrument.marketing': 'Email marketing opt-in',
  'designJourney.consent.instrument.sms': 'SMS opt-in (TCPA)',
  'designJourney.consent.instrument.media': 'Featured image release',
  'designJourney.consent.instrument.ageGate': 'Age gate before consent',
  'designJourney.consent.why.noPrize': 'No prize in this deployment.',
  'designJourney.consent.why.noContact': 'The survey collects no contact info \u2014 nothing to send.',
  'designJourney.consent.why.noMobile': 'Needs a mobile number collected with a contact block.',
  'designJourney.consent.why.brandOff': 'Off on the brand\u2019s Legal page.',
  'designJourney.consent.why.noFeatured': 'Photography is not set to featured.',
  'designJourney.consent.why.minors': 'Off because minors may attend.',
  'designJourney.consent.brandEmpty': 'This brand has no legal documents yet; guests only see Onxite\u2019s.',
  'designJourney.consent.descriptionXotski': 'What the guest is asked to accept comes from the survey and from the brand’s Legal & Consent page: the age check, Terms and Privacy, the email marketing opt-in and, with a prize game, the Official Rules. Nothing here has to be set per event.',
  'designJourney.consent.setHereNote': 'This event has its own photo or minors setting, so it shows here. Xotski events normally don’t need one: set photography to “No photos or video” and minors off, and once it’s saved this goes away.',
  // Settings › Brand — company legal texts (migration 089).
  'settings.terms.title': 'Terms and conditions',
  'settings.terms.description':
    'Each brand has its own — a guest must read the terms of the brand running the activation, not the agency\u2019s. Paste them per brand. Every consent step in your deployed surveys reads them from here and shows them under your brand name — surveys cannot rewrite them.',
  'settings.terms.brand': 'Brand these texts belong to',
  'settings.terms.general.label': 'General terms and conditions',
  'settings.terms.general.hint':
    'What respondents accept about how their data is handled. Shown as a link that opens the full text.',
  'settings.terms.general.placeholder': 'Paste your terms and conditions here…',
  'settings.terms.photo.label': 'Image consent',
  'settings.terms.photo.hint':
    'A separate permission: being photographed or filmed, and how those images may be used. Only requested by surveys whose consent step asks for it.',
  'settings.terms.photo.placeholder': 'Paste your image consent text here…',
  'settings.terms.empty': 'Not filled in yet',
  'settings.terms.chars': '{n} characters',
  'settings.terms.legacyUrl':
    'This brand still has an old terms URL ({url}). It stays in use until you paste the text above.',
  // Settings › Legal & Consent — la página legal de la marca (co_company_legal, 116).
  'settings.section.legal': 'Legal & Consent',
  'settings.section.legalDesc': 'Terms, privacy, official rules and image consent, per brand',
  'settings.brand.legalMoved':
    'Legal texts moved to Legal & Consent: one page per brand that every app (Xotski, Xamplin, Xurvey) reads. Brand settings keep identity and inventory only.',
  'settings.brand.legalMovedCta': 'Open Legal & Consent',
  'settings.legal.title': 'Legal & Consent',
  'settings.legal.subtitle':
    'The brand\u2019s legal instruments, in one place. Every consent step in a deployed survey reads its documents from here and shows them under the brand\u2019s name. Surveys cannot rewrite them. The on-screen notice and the acceptance checkbox are always Onxite\u2019s and are not edited here.',
  'settings.legal.roles.title': 'Data roles',
  'settings.legal.roles.brand': 'brand = controller',
  'settings.legal.roles.app': 'Xotski = processor',
  'settings.legal.roles.description':
    'The brand running the activation owns the guest data and its legal terms. Xotski processes on the brand\u2019s behalf. This is why every text below is stored per brand, not per agency and not per app.',
  'settings.legal.brandHint': 'Texts are stored against this brand. A survey deployed for this brand pulls exactly these.',
  'settings.legal.legacyNotice':
    'This brand is still using the texts pasted in the old Brand settings. Save once here and they move to the Legal & Consent page for good.',
  'settings.legal.readOnly': 'You can read this page but not change it: editing requires the brand edit permission.',
  'settings.legal.tag.brand': 'brand-supplied',
  'settings.legal.tag.brandLink': 'brand link or text',
  'settings.legal.tag.ifPrize': 'only if prize',
  'settings.legal.tag.ifPhotos': 'only if photos',
  'settings.legal.tag.config': 'config',
  'settings.legal.tag.onxite': 'Onxite standard',
  'settings.legal.field.url': 'URL',
  'settings.legal.field.text': 'Full text',
  'settings.legal.legalName.title': 'Legal entity',
  'settings.legal.legalName.description':
    'The registered name of the company behind the brand (e.g. Felix Technologies Inc.). Used in acknowledgments and in the guardian release; guests see the brand name.',
  'settings.legal.legalName.placeholder': 'Registered company name',
  'settings.legal.terms.title': 'General Terms & Conditions',
  'settings.legal.terms.description':
    'What guests accept about how their data is handled. Shown as a link that opens the full text; the guest accepts it with the checkbox on the consent step.',
  'settings.legal.terms.urlHint': 'Preferred: a link keeps the document current and onboarding light.',
  'settings.legal.terms.textHint': 'Alternative if the terms are not published anywhere. With text, the link is not used.',
  'settings.legal.privacy.title': 'Privacy Policy',
  'settings.legal.privacy.description':
    'The brand\u2019s live policy. The consent screen links to it as a reference: privacy is notice, not consent, so there is no separate checkbox.',
  'settings.legal.privacy.urlHint': 'If the brand has no policy, turn on the Onxite standard terms below.',
  'settings.legal.rules.title': 'Official Rules',
  'settings.legal.rules.description':
    'Required whenever the activation has a prize game. The brand is Sponsor of record; Onxite provides a template the brand customises and pastes here. Go-live is blocked for prize games until this is filled.',
  'settings.legal.rules.urlHint': 'Link to the published rules.',
  'settings.legal.rules.textHint': 'Or paste the customised rules here.',
  'settings.legal.rules.placeholder': 'Paste the Official Rules here\u2026',
  'settings.legal.media.title': 'Image / Photo consent',
  'settings.legal.media.description':
    'A separate permission for being photographed or filmed and how images may be used. It cannot be a link: it is granted in the flow. Requested only by deployments that take photos.',
  'settings.legal.media.textHint': 'The brand\u2019s own release text. Leave empty to adopt Onxite\u2019s generic release below.',
  'settings.legal.media.adoptLabel': 'Adopt Onxite generic media release',
  'settings.legal.media.adoptDescription':
    'The brand adopts it as its own and accepts sole responsibility for image use.',
  'settings.legal.ageGate.title': 'Age gate',
  'settings.legal.ageGate.description':
    'For regulated brands (alcohol). Enforced before the consent screen, with an immutable attestation record. The brand warrants the setting is correct for the venue jurisdiction.',
  'settings.legal.ageGate.requireLabel': 'Require age gate',
  'settings.legal.ageGate.requireDescription': 'When this is on and the event\u2019s survey has an age step, Xotski guests first see a button (\u201cI\'m 21 or older\u201d or \u201cI\'m 18 or older\u201d) before the consent screen, and one tap takes them to the next step; the screen does not let them go on without it. Xotski never asks for a date of birth. When this is off, Xotski guests get no age question. Surveys deployed for this brand must include an age check step.',
  'settings.legal.ageGate.minAge': 'Legal age for this activation',
  'settings.legal.ageGate.minAgeHint': '18 or 21, depending on the jurisdiction.',
  'settings.legal.marketing.title': 'Marketing channels',
  'settings.legal.marketing.description':
    'Email and SMS consent are separate instruments: two boxes, two records. Neither is ever derived from the other. A box only renders when the survey collects the matching field.',
  'settings.legal.marketing.emailLabel': 'Email marketing opt-in',
  'settings.legal.marketing.emailDescription': 'Shown only when the survey collects contact info.',
  'settings.legal.marketing.smsLabel': 'SMS opt-in (TCPA PEWC)',
  'settings.legal.marketing.smsDescription': 'Shown only when the survey collects a mobile number. Uses prior-express-written-consent wording.',
  'settings.legal.standard.title': 'Standard Tier fallback',
  'settings.legal.standard.description':
    'If the brand supplied no documents, the activation runs on Onxite\u2019s standard terms, operative under the brand\u2019s name.',
  'settings.legal.standard.label': 'Use Onxite standard terms for this brand',
  'settings.legal.standard.labelDescription': 'The representative confirms authority to bind the brand and adopts the standard terms.',
  'settings.legal.lastSaved': 'Last saved {when}',
  'settings.legal.toBrandSettings': 'Brand settings',
  'settings.legal.save': 'Save legal texts',
  'settings.legal.textLimit.counter': '{n} / {max} characters',
  'settings.legal.textLimit.over': 'Too long: up to {max} characters. Shorten it to save.',
  'settings.legal.textLimit.saveBlocked': 'A pasted legal text is over {max} characters. Shorten it to save.',
  'settings.legal.textLimit.toast': '“{field}” is longer than {max} characters. Shorten it and save again.',
  'settings.legal.fieldError.url': 'Check the link in “{field}”: it must be a full address that starts with https://.',
  'settings.legal.fieldError.generic': 'Check “{field}” and save again.',
  'settings.legal.savedToast': 'Legal texts saved',
  'settings.legal.other.title': 'Other apps and older events',
  'settings.legal.other.inUse': 'In use',
  'settings.legal.other.description': 'Image consent, SMS opt-in and the Onxite standard terms. A Xotski event doesn’t need them, but the other apps that run surveys for this brand (Xamplin, xeck-in, DiD, Xurvey) and events set up earlier read them from this same page, so they stay here and can still be edited. Whatever is switched on here still applies everywhere, Xotski included. Nothing is deleted.',
  'settings.legal.marketing.emailTitle': 'Email marketing',
  'settings.legal.marketing.emailOnlyDescription': 'The email opt-in guests can tick. It is a separate consent from SMS, which is under “Other apps and older events”, below.',
  'settings.legal.sms.title': 'SMS marketing (TCPA)',
  'settings.legal.sms.description': 'A separate consent from email, with prior-express-written-consent wording: two boxes, two records, and one is never derived from the other.',
  'settings.legal.privacy.urlHintPanel': 'If the brand has no policy, the Onxite standard terms are under “Other apps and older events”, below.',
  // Reconocimientos del representante (Exhibit F) + historial legal.
  'legalAck.summary': 'Summary (system-generated)',
  'legalAck.name': 'Name',
  'legalAck.title': 'Title',
  'legalAck.titlePlaceholder': 'e.g. Marketing Director',
  'legalAck.recordNote': 'This acknowledgment is stored as an immutable record with your name, title, the configuration shown and the time. Any later change requires a new one.',
  'legalAck.error': 'The acknowledgment could not be recorded',
  'legalAck.setup.title': 'Confirm your legal setup',
  'legalAck.setup.intro': 'You are configuring the legal instruments that participants will see and accept in activations run by {brand}. Review the summary before confirming.',
  'legalAck.setup.check.authority': 'I am authorized to act on behalf of {brand} and to bind it to this configuration.',
  'legalAck.setup.check.controller': '{brand} is the Data Controller for consumer data collected in its activations. Onxite processes that data as a service provider under the Client Terms & Conditions.',
  'legalAck.setup.check.documents': 'Where {brand} supplied its own documents, it warrants they are accurate, lawful and sufficient. Where it adopted Onxite\u2019s generic instruments, it adopts them as its own and accepts sole responsibility for their use.',
  'legalAck.setup.confirm': 'Confirm and save',
  'legalAck.export.title': 'You are taking possession of consumer data',
  'legalAck.export.intro': 'You are about to export consumer-level data ({subject}). This data belongs to {brand} as Data Controller. Once exported, it leaves the Onxite platform and Onxite\u2019s security controls.',
  'legalAck.export.point.security': '{brand} is responsible for securing this data and for its lawful use, consistent with the consent participants gave.',
  'legalAck.export.point.rights': 'Consumer rights requests (access, deletion, opt-out) covering this copy are {brand}\u2019s responsibility to honor. Onxite keeps honoring them for data remaining on the platform.',
  'legalAck.export.point.marketing': 'Marketing use is limited to participants who gave the separate marketing opt-in. Email consent and text (SMS) consent are different: the export marks each per record, and texting a number without its own SMS consent is not permitted.',
  'legalAck.export.point.logged': 'This export is logged (who, what, when) and the log is available to {brand} for its own compliance records.',
  'legalAck.export.check.authority': 'I am authorized to receive this data on behalf of {brand}.',
  'legalAck.export.check.custody': 'I understand {brand} assumes responsibility for this copy of the data upon export.',
  'legalAck.export.confirm': 'Export data',
  'settings.legal.ack.own': 'Own',
  'settings.legal.ack.ownUrl': 'Own documents at URL',
  'settings.legal.ack.ownText': 'Own documents (pasted text)',
  'settings.legal.ack.standard': 'Onxite standard terms (Standard Tier)',
  'settings.legal.ack.generic': 'Onxite generic release adopted',
  'settings.legal.ack.none': 'Not provided',
  'settings.legal.history.title': 'Acknowledgments',
  'settings.legal.history.description': 'Every legal act a representative signed for this brand, newest first. Your own compliance log; nothing here can be edited or deleted.',
  'settings.legal.history.empty': 'No acknowledgments yet. Saving this page records the first one.',
  'settings.legal.history.type.legal_setup_ack': 'Legal setup acknowledgment',
  'settings.legal.history.type.data_export_ack': 'Data export acknowledgment',
  'settings.legal.history.type.media_adopt_generic_ack': 'Generic media release adopted',
  'settings.legal.history.type.standard_tier_ack': 'Onxite standard terms adopted',
  'settings.legal.history.type.dpa': 'Data Processing Agreement',
  'settings.legal.history.type.data_controller_ack': 'Data Controller acknowledgment',
  'contacts.exportAckSubject': 'Contacts export · {n} records',
  'contacts.exportFields': 'Contact fields (name, email, phone, consents) and one column per survey question',
  'events.detail.touchpoints': 'Stations / Touchpoints',
  'events.detail.eventSurvey': 'Event journey:',
  'events.detail.sharedByStations': '· shared by all stations',
  'events.detail.noEventSurveyPre': 'No event journey yet — start one in the',
  'events.detail.noEventSurveyTab': 'Design Journey',
  'events.detail.noEventSurveyPost': 'tab.',
  'events.detail.newStation': 'New station',
  'events.detail.stationName': 'Station name',
  'events.detail.stationNamePlaceholder': 'Main Bar',
  'events.detail.surveyAutoPre': 'auto-assigned from this event\'s Design Journey (set in the',
  'events.detail.surveyAutoTab': 'Design Journey',
  'events.detail.surveyAutoPost': 'tab). All stations of an event share the same journey.',
  'events.detail.surveyLabel': 'Journey:',
  'events.detail.createStation': 'Create station',
  'events.detail.stationCreated': 'Station created',
  'events.detail.createFailed': 'Create failed',
  'events.detail.surveyNone': 'none',
  'events.detail.outOfSync': 'out of sync',
  'events.detail.noSurvey': 'no journey',
  'events.detail.outOfSyncTitle': 'Out of sync — event journey is',
  'events.detail.outOfSyncBody': 'Use "Re-sync stations" in Design Journey.',
  'events.detail.noSurveyTitle': 'No journey assigned. Use "Re-sync stations" in Design Journey.',
  'events.detail.editStationAria': 'Edit station',
  'events.detail.editBtn': 'Edit',
  'events.detail.allocateStock': 'View',
  'events.detail.qrShort': 'QR',
  'events.editDialog.title': 'Edit event',
  'events.editDialog.description': 'Tune the schedule, address, status, and capacity for this event.',
  'events.editDialog.name': 'Event name',
  'events.editDialog.address': 'Address',
  'events.editDialog.starts': 'Starts',
  'events.editDialog.ends': 'Ends',
  'events.editDialog.sections': 'Sections',
  'events.editDialog.codeRef': 'Code ref',
  'events.editDialog.statusField': 'Status',
  'events.editDialog.confirmHeader': 'Delete',
  'events.editDialog.confirmBody': 'The event, its stations and merch allocations will be hidden from all listings.',
  'events.editDialog.confirmInstruction': 'Click',
  'events.editDialog.confirmInstructionEmph': 'Confirm delete',
  'events.editDialog.confirmInstructionEnd': 'to proceed.',
  'events.editDialog.deleting': 'Deleting…',
  'events.editDialog.confirmDelete': 'Confirm delete',
  'events.editDialog.delete': 'Delete',
  'events.editDialog.cancel': 'Cancel',
  'events.editDialog.save': 'Save',
  'events.editDialog.updatedToast': 'Event updated',
  'events.editDialog.updateFailed': 'Update failed',
  'events.editDialog.deletedToast': 'Event deleted',
  'events.editDialog.deleteFailed': 'Delete failed',
  'events.editDialog.error.nameRequired': 'Event name is required',
  'events.editDialog.error.startRequired': 'Start date and time is required',
  'events.editDialog.error.startInvalid': 'Invalid start date',
  'events.editDialog.error.endRequired': 'End date and time is required',
  'events.editDialog.error.endInvalid': 'Invalid end date',
  'events.editDialog.error.endAfterStart': 'End must be after start',
  'events.editDialog.error.outOfRange': 'Dates must fall within the project date range',
  // ── Xurvey deployment (protocol §2) ────────────────────────────────
  'xurveyDeploy.title': 'Xurvey survey',
  'xurveyDeploy.subtitle': 'Pick a published survey and put it live on this event\u2019s stations. Each station gets its own link, which is what ties every answer back to where it came from.',
  'xurveyDeploy.surveyLabel': 'Survey',
  'xurveyDeploy.deployAll': 'Deploy to all stations',
  'xurveyDeploy.deployOne': 'Deploy',
  'xurveyDeploy.open': 'Open',
  'xurveyDeploy.qr': 'QR',
  'xurveyDeploy.done': 'Live on {n} station(s)',
  'xurveyDeploy.pending': '{n} station(s) not live yet',
  'xurveyDeploy.noStations': 'This event has no stations yet. Add one and it can go live.',
  'xurveyDeploy.noSurveys': 'No published survey targets Xotski yet. A draft cannot go live \u2014 publish it in Xurvey first.',
  'xurveyDeploy.overridesTitle': 'How it looks at this event',
  'xurveyDeploy.overridesHint': 'Design only \u2014 the questions belong to the survey.',
  'xurveyDeploy.overridesSaved': 'Saved on every station of this event',
  'xurveyDeploy.appliesTo': 'Applies to {n} station(s) of this event',
  'xurveyDeploy.fromTemplate': 'From the survey',
  'xurveyDeploy.colorPrimary': 'Primary',
  'xurveyDeploy.colorBg': 'Background',
  'xurveyDeploy.colorText': 'Text',
  'xurveyDeploy.font': 'Font',
  'xurveyDeploy.celebration': 'Celebration',
  'xurveyDeploy.celebration.confetti': 'Confetti',
  'xurveyDeploy.celebration.fireworks': 'Fireworks',
  'xurveyDeploy.celebration.sparks': 'Sparks',
  'xurveyDeploy.celebration.none': 'None',
  'xurveyDeploy.game': 'Game',
  'xurveyDeploy.logo': 'Logo',
  'xurveyDeploy.background': 'Background image',
  'xurveyDeploy.locales': 'Languages offered here',
  'xurveyDeploy.previewTitle': 'Survey preview',
  'xurveyDeploy.previewOpen': 'Open the preview in a new tab',
  'xurveyDeploy.previewNotDeployed': 'Nothing to preview yet: deploy the survey to a station and its link appears here.',

  // ── Design Journey (estándar de UI para las apps consumidoras) ─────
  'designJourney.title': 'How it looks at this event',
  'designJourney.cardTitle': 'Design Journey',
  'designJourney.subtitle': 'Design only. Saved to the {n} station link(s) of this event.',
  'designJourney.saved': 'Saved on {n} station(s)',
  'designJourney.autosaving': 'Saving…',
  'designJourney.autosavePending': 'Unsaved changes · saving on its own',
  'designJourney.autosaved': 'Saved at {hora}',
  'designJourney.autosaveOn': 'Saves on its own',
  // XCS-15 (épica #333): el autoguardado se para tras un rechazo y el guardado informa por evento.
  'designJourney.autosavePaused': 'Not saved · paused until you change something',
  'designJourney.autosaveWaiting': 'Not saved · the server asked to wait; trying again at {hora}',
  'designJourney.saveReport.noneSaved': 'The design was not saved',
  'designJourney.saveReport.someLinks': 'Saved on {ok} of {total} links of this event',
  'designJourney.saveReport.events': 'Saved in {ok} of {total} events',
  'designJourney.saveReport.eventReason': '{name}: {reason}',
  'designJourney.saveReport.more': 'and {n} more',
  'designJourney.saveReport.unnamedEvent': 'An event',
  'designJourney.saveError.officialRulesMissing': 'This link has a prize and the brand has no Official Rules. Add them on the brand’s Legal page, or remove the game.',
  'designJourney.saveError.ageGateMissing': 'The brand requires an age check and this survey has no age step. Add one to the survey, or turn the age check off on the Legal page.',
  'designJourney.saveError.drawingNeedsContact': 'A drawing needs contact details to reach the winner. Collect contact details or use instant win.',
  'designJourney.saveError.forbidden': 'Your role can’t change this link.',
  'designJourney.saveError.notFound': 'This link no longer exists. Reload the page.',
  'designJourney.saveError.network': 'Couldn’t reach the server. Try again in a moment.',
  'designJourney.saveError.server': 'The server had a problem. Try again in a moment.',
  'designJourney.saveError.unknown': 'The server didn’t accept the change.',
  'designJourney.saveError.rateLimited': 'Too many changes in a short time. Wait a minute and try again.',
  'designJourney.saveError.sessionExpired': 'Your session has expired. Sign in again.',
  'designJourney.saveError.conflict': 'This survey changed somewhere else or is locked. Reload the page and try again.',
  'designJourney.notAllDeployed': 'Saving applies to the live links. {n} station(s) do not run this survey yet — deploy them to include them.',
  'designJourney.notDeployed': 'Deploy the survey to a station first: the design belongs to the link, and there is no link yet.',
  'designJourney.previewTitle': 'Live preview',
  'xurveyDeploy.deployHeading': 'Survey and links',
  'designJourney.steps': 'Journeys',
  'designJourney.abandonedHere': '{n} left at this step',
  'designJourney.completion': '{ok} of {total} finish ({pct}%)',
  'designJourney.median': 'typically {s}s',
  'designJourney.stepsHint': 'Drag to reorder, click to jump, eye to hide. Questions are edited in Xurvey.',
  'designJourney.reorder': 'Reorder',
  'designJourney.hide': 'Hide at this event',
  'designJourney.cannotHideLast':
    'This is the only step left showing: hiding it would leave the QR with nothing to answer.',
  'designJourney.show': 'Show again',
  'designJourney.tab.apariencia': 'Look',
  'designJourney.tab.estilo': 'Style',
  'designJourney.tab.form': 'Form',
  'designJourney.fromSurvey': 'From the survey',
  'designJourney.backToTheme': 'Back to the survey colour',
  'designJourney.transparent': 'Make it transparent',
  'designJourney.logo': 'Logo',
  'designJourney.background': 'Background',
  'designJourney.backgroundSize': 'Framing',
  'designJourney.opacity': 'Opacity',
  'designJourney.celebration': 'Celebration',
  'dj.testQr.button': 'Test QR',
  'dj.testQr.title': 'Test QR — try it on a real phone',
  'dj.testQr.body':
    'A printable QR that works on any phone but cannot act as a real event: 10 plays, 24 hours, prizes are shown but not awarded, and nothing is saved.',
  'dj.testQr.create': 'Create test QR',
  'dj.testQr.createAgain': 'The last test QR expired — create a new one',
  'dj.testQr.created': 'Test QR ready',
  'dj.testQr.copy': 'Copy link',
  'dj.testQr.copied': 'Link copied',
  'dj.testQr.limits': '10 plays · 24 h · fake prizes',
  'dj.testQr.expires': 'expires {when}',
  'dj.testQr.activate': 'Activate — go live',
  'dj.testQr.activateHint': 'Flow proven? Activation removes the test banner, awards real prizes and starts saving answers.',
  'dj.testQr.activated': 'Deployment activated',
  'dj.testQr.activatedBody': 'The QR is now live: real prizes, answers saved.',
  'dj.testQr.failed': 'Could not complete the action',
  'designJourney.watermark': 'Game watermark logo',
  'designJourney.watermarkHint':
    'Shown faintly inside the games — scratch mosaic, soccer LED board, pirinola surface, xinkit ball. Empty = the header logo is used.',
  'designJourney.game': 'Game',
  'designJourney.gameHint': 'The survey says there is a game here; this event picks which one. Saved to the station links, not to the survey.',
  'designJourney.noGames': 'No games available on your plan yet.',
  'designJourney.celebration.confetti': 'Confetti',
  'designJourney.celebration.fireworks': 'Fireworks',
  'designJourney.celebration.sparks': 'Sparks',
  'designJourney.celebration.none': 'None',
  'designJourney.locales': 'Languages offered here',
  'designJourney.localesHint': 'Only the ones the survey already has. Offering one it lacks would show a half-translated screen.',
  'designJourney.palette': 'Palette',
  'designJourney.typography': 'Typography',
  'designJourney.fontDisplay': 'Headings',
  'designJourney.fontBody': 'Body',
  'designJourney.textOpacity': 'Text opacity',
  'designJourney.textOpacityHint': 'Below 40% text drops under 3:1 on almost any background and stops being readable at an event.',
  'designJourney.container': 'Card',
  'designJourney.cardColor': 'Card colour',
  'designJourney.border': 'Border',
  'designJourney.borderColor': 'Border colour',
  'designJourney.radiusTop': 'Top radius',
  // ── Estilos guardados ────────────────────────────────────────────────
  'designJourney.questionsCount': '{n} of {total} questions',
  'designJourney.stepAutoHidden': 'Every question is hidden, so this step will not be shown.',
  'designJourney.tab.cartel': 'Poster',
  'designJourney.screens': 'Screens and control',
  'designJourney.screenGet': 'Get the screen and control links',
  'designJourney.screenHint': 'One control for the {n} collection point(s) of this event.',
  'designJourney.screenNeedsDeploy': 'Deploy the survey to the stations first: without links there is nothing to show or to switch on.',
  'designJourney.screenUrl': 'Venue screen',
  'designJourney.screenUrlHint': 'Open it on the venue TV. Until the event is on, the QR shows blurred with “starting soon”.',
  'designJourney.controlUrl': 'Agent control',
  'designJourney.controlUrlHint': 'Send it to whoever is on site. No login: the link plus the code below.',
  'designJourney.controlCode': 'Event code',
  'designJourney.controlWarning': 'Switching off closes response collection at every point of this event. Screens change on their own, no reload.',
  'designJourney.screenOn': 'Live',
  'designJourney.screenOff': 'Off',
  'designJourney.screenFailed': 'The control could not be created.',
  'designJourney.posterPreview': 'The poster of this deployment',
  'designJourney.posterBackgroundImage': 'Poster background',
  'designJourney.posterPng': 'Download PNG',
  'designJourney.posterPdf': 'PDF / print',
  'designJourney.posterDownloadHint': 'PNG at 2160×2880 for printing or social. PDF goes through the browser dialog and keeps the QR vector — sharp at any size.',
  'designJourney.posterQrLogo': 'Logo in the middle of the QR',
  'designJourney.posterQrLogoHint': 'It takes a fifth of the code and the QR is drawn with high error correction, so it still scans.',
  'designJourney.posterOpen': 'Open full size',
  'designJourney.posterDownload': 'There you can download it as PNG or PDF to print.',
  'designJourney.posterHint': 'The poster comes from the survey; here you override it for THIS event. Empty = whatever the survey brings. The QR is this station’s.',
  'designJourney.posterTitle': 'Headline',
  'designJourney.posterDescription': 'Description',
  'designJourney.posterCta': 'Text under the QR',
  'designJourney.posterBackground': 'Background colour',
  'designJourney.posterText': 'Text colour',
  'designJourney.posterQrColor': 'QR colour',
  'designJourney.posterQrBackground': 'QR background',
  'designJourney.presets': 'Saved styles',
  'designJourney.presetSave': 'Save this look',
  'designJourney.presetNameHint': 'You will find it under this name on every event of your company and its brands.',
  'designJourney.presetNamePlaceholder': 'Summer 2026 · Joyly',
  'designJourney.presetName': 'Name for this style (e.g. “Summer 2026”)',
  'designJourney.presetsEmpty': 'No saved styles yet. Design this one and save it: you will be able to apply it to any other event.',
  'designJourney.presetsShow': 'Show the {n} saved styles',
  'designJourney.presetsHide': 'Hide the list',
  'designJourney.presetApply': 'Apply here',
  'designJourney.presetApplied': 'Applied',
  'designJourney.presetPick': 'Choose one of {n} saved styles…',
  'designJourney.presetApplyElsewhere': 'Apply to…',
  // Apply a saved style to other events, without opening each one.
  'applyStyle.title': 'Apply “{name}” to other events',
  'applyStyle.description':
    'Only the look travels — colours, font, logo, background, celebration. Hidden steps, step order and the poster of each event stay exactly as they are.',
  'applyStyle.noEvents': 'No events to apply it to yet.',
  'applyStyle.noProject': 'Without a project',
  'applyStyle.allOfProject': 'Select all',
  'applyStyle.noneOfProject': 'Clear',
  'applyStyle.cta': 'Apply to {n} events',
  'applyStyle.applied': 'Style applied to {n} live links, across {e} events.',
  'applyStyle.skipped': '{n} of the events you picked have no survey deployed yet, so nothing changed there.',
  'applyStyle.noneApplied':
    'Nothing changed: none of the events you picked has a survey deployed yet.',
  'applyStyle.failed': 'The style could not be applied everywhere',
  'applyStyle.partial': 'Style applied to {ok} of {total} events',
  'applyStyle.partialHint': 'The events it couldn’t reach stay selected, with the reason under each one.',
  'designJourney.presetShared': 'shared',
  'designJourney.presetDelete': 'Remove style',
  'designJourney.presetDeleteConfirm': 'Remove “{name}”? Events already designed with it keep their look.',
  'designJourney.presetAppliedHint': 'Applied to the preview. It only reaches the event’s links once you press Save.',
  'designJourney.presetSaveFailed': 'The style could not be saved.',
  'designJourney.presetDeleteFailed': 'The style could not be removed.',
  'designJourney.posterPresets': 'Saved poster designs',
  'designJourney.posterPresetSave': 'Save this design',
  'designJourney.posterPresetNameHint': 'Background, colours and the QR look. The poster\u2019s texts stay with each event. You will find it under this name on every event of your company and its brands.',
  'designJourney.posterPresetNamePlaceholder': 'Summer 2026 poster \u00b7 Joyly',
  'designJourney.posterPresetsEmpty': 'No saved poster designs yet. Design this one and save it: you will be able to reuse it on any other event.',
  'designJourney.posterPresetPick': 'Choose one of {n} saved designs\u2026',
  'designJourney.posterPresetDelete': 'Remove design',
  'designJourney.posterPresetDeleteConfirm': 'Remove \u201c{name}\u201d? Events already using it keep their poster.',
  'designJourney.posterPresetSaveFailed': 'The poster design could not be saved.',
  'designJourney.posterPresetDeleteFailed': 'The poster design could not be removed.',
  'applyPoster.title': 'Apply poster \u201c{name}\u201d to other events',
  'applyPoster.description': 'Only the poster\u2019s design travels: background, colours and the QR look. Each event keeps its own texts, steps and survey look.',
  'applyPoster.applied': 'Poster design applied to {n} live links, across {e} events.',
  'applyPoster.noneApplied': 'None of the events you picked has a survey deployed yet, so nothing changed.',
  'applyPoster.failed': 'The poster design could not be applied everywhere',
  'applyPoster.partial': 'Poster design applied to {ok} of {total} events',
  'designJourney.stepFrame': 'Step frame',
  'designJourney.layoutFit': 'Height',
  'designJourney.layoutAlign': 'Alignment',
  'designJourney.navigation': 'Navigation',
  'designJourney.navLayout': 'Buttons',
  // The button under the prize QR — where it sends whoever just played.
  // Event weather — forecast ahead, our own record for days already run.
  // Quick jumps at the top of the Plan tab — it got long enough to hide things.
  'plan.anchors.label': 'Jump to',
  'plan.anchors.stations': 'Stations',
  'plan.anchors.weather': 'Weather',
  'plan.anchors.goals': 'Goals',
  'plan.anchors.staff': 'Staff',
  // The event's end is chosen as a DURATION, not as a second date.
  'eventForm.duration': 'Duration',
  'eventForm.hours': '{n} hours',
  'eventForm.endsAt': 'Ends {fecha}',
  'eventForm.durationOther': 'Current: {h} h',
  'weather.title': 'Weather',
  'weather.recorded': 'Recorded',
  'weather.noLocation':
    'We could not place this event on the map. Add a venue or a city to the address and the forecast shows up.',
  'weather.outOfRange':
    'No weather to show: the forecast reaches 15 days ahead, and this event either sits beyond that or already ran before we started recording it.',
  'weather.footnote':
    'Temperatures in °F, wind in mph. Days already run are kept as a record and no longer change; the rest is a forecast and will.',
  'weather.code.clear': 'Clear',
  'weather.code.partly': 'Partly cloudy',
  'weather.code.cloudy': 'Cloudy',
  'weather.code.fog': 'Fog',
  'weather.code.drizzle': 'Drizzle',
  'weather.code.rain': 'Rain',
  'weather.code.showers': 'Showers',
  'weather.code.snow': 'Snow',
  'weather.code.storm': 'Storm',
  'weather.code.unknown': '—',
  'designJourney.prizeLink': 'After the game',
  'designJourney.prizeLinkUrl': 'Button link',
  'designJourney.defaultLocale': 'Starts in',
  'designJourney.defaultLocaleHint': 'The language the survey opens in at this event; visitors can still switch.',
  'designJourney.prizeLinkStyle': 'Show it as',
  'designJourney.prizeLinkStyle.link': 'Link',
  'designJourney.prizeLinkStyle.button': 'Button',
  'designJourney.prizeLinkStyle.iconButton': 'Button with icon',
  'designJourney.welcome': 'Welcome screen',
  'designJourney.welcomeImage': 'Image or logo',
  'designJourney.welcomeRole': 'Placement',
  'designJourney.welcomeRole.hero': 'Header image',
  'designJourney.welcomeRole.logo': 'Centered logo',
  'designJourney.welcomeRole.background': 'Background',
  'designJourney.welcomeHint': 'Only for this event; the survey keeps its own image.',
  'designJourney.presetModified': 'modified since',
  'designJourney.prizeLinkPlaceholder': 'https://your-brand.com/offer',
  'designJourney.prizeLinkLabel': 'Button text',
  'designJourney.prizeLinkLabelPlaceholder': 'Learn more',
  'designJourney.prizeLinkHint':
    'Shown under the prize QR, once they have played. Leave it empty and no button appears; touch nothing and the survey\u2019s own link is used.',
  'designJourney.gameLockedHint': 'Not in your plan yet — open Billing to see how to add it.',
  'designJourney.progress': 'Progress',
  // How the runtime tells the respondent where they are in the survey.
  'designJourney.paginado.numeric': 'Step number (1 of 7)',
  'designJourney.paginado.dots': 'Dots',
  'designJourney.paginado.bar': 'Progress bar',
  'designJourney.paginado.none': 'Nothing',
  'designJourney.localesLast': 'A survey needs at least one language.',
  // ── Projects ───────────────────────────────────────────────────────
  'project.detail.kpi.events': 'Events',
  'project.detail.kpi.stations': 'Stations',
  'project.detail.kpi.responses': 'Interactions',
  'project.detail.events': 'Events',
  'project.detail.noEvents': 'No events yet in this project.',
  'project.detail.newEvent': 'New event',
  'project.detail.editProject': 'Edit project',
  'project.detail.deleteProject': 'Delete project',
  'project.detail.confirmDelete': 'Delete this project?',
  'project.detail.deletedTitle': 'Project deleted',
  'project.detail.savedTitle': 'Project saved',
  'project.detail.notFound': 'Project not found.',
  'project.detail.backToBrand': 'Back to brand',

  // ── Contacts ───────────────────────────────────────────────────────
  'contacts.title': 'Contacts',
  'contacts.subtitle': 'Journey interactions captured at your stations.',
  'contacts.empty.title': 'No interactions yet',
  'contacts.empty.body': 'Run a journey at a station and interactions will appear here.',
  'contacts.searchPlaceholder': 'Search contacts…',
  'contacts.column.name': 'Name',
  'contacts.column.email': 'Email',
  'contacts.column.phone': 'Phone',
  'contacts.column.event': 'Event',
  'contacts.column.date': 'Date',
  'contacts.column.source': 'Source',
  'contacts.exportFilename': 'contacts',

  // ── Insights ───────────────────────────────────────────────────────
  'insights.title': 'Insights',
  'insights.subtitle': 'Distribution performance across brands, events and inventory.',
  'insights.kpi.totalResponses': 'Total interactions',
  'insights.kpi.uniqueContacts': 'Unique contacts',
  'insights.kpi.activeStations': 'Active stations',
  'insights.kpi.completionRate': 'Completion rate',
  'insights.responsesByDay': 'Interactions by day',
  'insights.responsesByEvent': 'Interactions by event',
  'insights.responsesByStation': 'Interactions by station',
  'insights.topStock': 'Top merch',
  'insights.empty.title': 'Not enough data yet',
  'insights.empty.body': 'Once interactions start coming in, your insights will appear here.',
  'insights.notCaptured.title': 'Nothing was captured.',
  'insights.notCaptured.body': 'The {n} event(s) in this selection have no recorded interactions at all. This is not a filter excluding data: no play, survey or scan was written for them. Check the station links and the deployment status before reading any chart.',

  // ── Settings ───────────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.subtitle': 'Preferences and settings for your account.',
  'settings.section.account': 'Account',
  'settings.section.workspace': 'Account',
  'settings.section.notifications': 'Notifications',
  'settings.section.danger': 'Danger zone',
  'settings.workspace.name': 'Account name',
  'settings.workspace.timezone': 'Timezone',
  'settings.workspace.language': 'Default language',
  'settings.notifications.email': 'Email notifications',
  'settings.notifications.product': 'Product updates',
  'settings.danger.deleteWorkspace': 'Delete account',
  'settings.danger.deleteWorkspaceHint': 'This action cannot be undone.',
  'settings.savedTitle': 'Settings saved',

  // ── Access manager ─────────────────────────────────────────────────
  'access.title': 'Access manager',
  'access.subtitle': 'Invite teammates and control who can do what.',
  'access.tabs.members': 'Members',
  'access.tabs.roles': 'Roles',
  'access.tabs.grants': 'Grants',
  'access.members.invite': 'Invite member',
  'access.members.empty.title': 'No members yet',
  'access.members.empty.body': 'Invite teammates to collaborate on your activations.',
  'access.members.column.name': 'Name',
  'access.members.column.email': 'Email',
  'access.members.column.role': 'Role',
  'access.members.column.status': 'Status',
  'access.members.dialog.inviteTitle': 'Invite member',
  'access.members.dialog.editTitle': 'Edit member',
  'access.members.dialog.email': 'Email',
  'access.members.dialog.role': 'Role',
  'access.members.dialog.firstName': 'First name',
  'access.members.dialog.lastName': 'Last name',
  'access.members.savedTitle': 'Member saved',
  'access.members.removedTitle': 'Member removed',
  'access.members.confirmRemove': 'Remove this member?',
  'access.roles.new': 'New role',
  'access.roles.empty.title': 'No roles yet',
  'access.roles.empty.body': 'Create a role to bundle permissions for teammates.',
  'access.roles.column.name': 'Name',
  'access.roles.column.members': 'Members',
  'access.roles.column.permissions': 'Permissions',
  'access.roles.dialog.newTitle': 'New role',
  'access.roles.dialog.editTitle': 'Edit role',
  'access.roles.dialog.name': 'Role name',
  'access.roles.dialog.description': 'Description',
  'access.roles.dialog.permissions': 'Permissions',
  'access.roles.savedTitle': 'Role saved',
  'access.roles.deletedTitle': 'Role deleted',
  'access.roles.confirmDelete': 'Delete this role?',
  'access.grants.new': 'New grant',
  'access.grants.empty.title': 'No grants yet',
  'access.grants.empty.body': 'Grant a teammate access to a specific brand, project, or event.',
  'access.grants.column.member': 'Member',
  'access.grants.column.scope': 'Scope',
  'access.grants.column.role': 'Role',
  'access.grants.dialog.newTitle': 'New grant',
  'access.grants.dialog.editTitle': 'Edit grant',
  'access.grants.dialog.member': 'Member',
  'access.grants.dialog.scope': 'Scope',
  'access.grants.dialog.role': 'Role',
  'access.grants.savedTitle': 'Grant saved',
  'access.grants.deletedTitle': 'Grant removed',
  'access.grants.confirmDelete': 'Remove this grant?',
  'access.permissions.available': 'Available',
  'access.permissions.selected': 'Selected',
  'access.permissions.searchPlaceholder': 'Search permissions…',

  // ── Brand identity ─────────────────────────────────────────────────
  'brandIdentity.title': 'Brand identity',
  'brandIdentity.subtitle': 'Logo, colors, and voice that define your brand.',
  'brandIdentity.logo': 'Logo',
  'brandIdentity.primaryColor': 'Primary color',
  'brandIdentity.accentColor': 'Accent color',
  'brandIdentity.voice': 'Brand voice',
  'brandIdentity.voicePlaceholder': 'A few adjectives that describe how your brand speaks.',
  'brandIdentity.savedTitle': 'Brand identity saved',

  // ── Brand studio (new page) ────────────────────────────────────────
  'brandStudio.new.title': 'Create a new brand',
  'brandStudio.new.subtitle': 'Set up the essentials. You can refine everything later.',
  'brandStudio.new.brandName': 'Brand name',
  'brandStudio.new.brandNamePlaceholder': 'e.g. Atledi',
  'brandStudio.new.logo': 'Logo',
  'brandStudio.new.primaryColor': 'Primary color',
  'brandStudio.new.accentColor': 'Accent color',
  'brandStudio.new.create': 'Create brand',
  'brandStudio.new.createdTitle': 'Brand created',

  // ── Agency ─────────────────────────────────────────────────────────
  'agency.title': 'Agency profile',
  'agency.subtitle': 'How your agency appears to brands and partners.',
  'agency.section.basics': 'Basics',
  'agency.section.team': 'Team',
  'agency.section.brands': 'Brands',
  'agency.name': 'Agency name',
  'agency.tagline': 'Tagline',
  'agency.about': 'About',
  'agency.aboutPlaceholder': 'A short description of your agency.',
  'agency.logo': 'Logo',
  'agency.website': 'Website',
  'agency.savedTitle': 'Agency saved',
  'agency.team.empty': 'Invite teammates from the Access manager.',
  'agency.brands.empty': 'No brands linked to this agency yet.',
  'agency.home.title': 'Agency dashboard',
  'agency.home.subtitle': 'Track activity across all the brands you manage.',
  'agency.home.activeBrands': 'Active brands',
  'agency.home.liveEvents': 'Live events',
  'agency.home.totalResponses': 'Total interactions',
  'agency.home.teammates': 'Teammates',

  // ── Billing ────────────────────────────────────────────────────────
  'billing.title': 'Billing',
  'billing.subtitle': 'Plans, usage and invoices.',
  'billing.currentPlan': 'Current plan',
  'billing.changePlan': 'Change plan',
  'billing.upgrade': 'Upgrade',
  'billing.downgrade': 'Downgrade',
  'billing.cancelPlan': 'Cancel plan',
  'billing.invoices': 'Invoices',
  'billing.noInvoices': 'No invoices yet.',
  'billing.usageTitle': 'Usage',
  'billing.usage.responses': 'Interactions',
  'billing.usage.stations': 'Stations',
  'billing.usage.events': 'Events',
  'billing.usage.teammates': 'Teammates',
  'billing.usage.usedOf': 'used of',
  'billing.pricing.title': 'Plans',
  'billing.pricing.subtitle': 'Pick the plan that fits your activations.',
  'billing.pricing.month': '/ month',
  'billing.pricing.year': '/ year',
  'billing.pricing.choosePlan': 'Choose plan',
  'billing.pricing.currentPlan': 'Current plan',
  'billing.pricing.contactSales': 'Contact sales',
  'billing.upgradeDialog.title': 'Upgrade plan',
  'billing.upgradeDialog.confirm': 'Confirm upgrade',
  'billing.upgradeDialog.cancel': 'Cancel',
  'billing.checkout.successTitle': 'Payment successful',
  'billing.checkout.successBody': 'Your plan has been updated. Welcome aboard!',
  'billing.checkout.failedTitle': 'Payment failed',
  'billing.checkout.failedBody': 'Something went wrong while processing your payment.',
  'billing.checkout.returnToBilling': 'Return to billing',
  'billing.mock.title': 'Mock checkout',
  'billing.mock.subtitle': 'This is a sandboxed flow — no real payment will be charged.',
  'billing.mock.pay': 'Pay now',
  'billing.mock.cancel': 'Cancel',

  // ── Public survey ──────────────────────────────────────────────────
  'publicSurvey.continue': 'Continue',
  'publicSurvey.back': 'Back',
  'publicSurvey.submit': 'Submit',
  'publicSurvey.finish': 'Finish',
  'publicSurvey.thanks.title': 'Thanks for your interaction!',
  'publicSurvey.thanks.body': 'Your answers have been recorded.',
  'publicSurvey.thanks.replay': 'Take another spin',
  'publicSurvey.notFound.title': 'Journey not found',
  'publicSurvey.notDeployed': 'This station has no live survey right now. If you run this event, deploy it from the event\u2019s Survey tab.',
  'publicSurvey.notFound.body': 'Double-check the link or QR code and try again.',
  'publicSurvey.required': 'This field is required.',
  'publicSurvey.invalidEmail': 'Please enter a valid email address.',
  'publicSurvey.invalidPhone': 'Please enter a valid phone number.',
  'publicSurvey.wheel.spin': 'Spin the wheel',
  'publicSurvey.wheel.spinning': 'Spinning…',
  'publicSurvey.wheel.youWon': 'You won',
  'publicSurvey.wheel.tryAgain': 'Try again',
  'publicSurvey.wheel.claim': 'Claim your prize',
  'publicSurvey.loading': 'Loading journey…',

  // ── Sidebar ────────────────────────────────────────────────────────
  'sidebar.home': 'Home',
  'sidebar.myXotskies': 'My Merch',
  'sidebar.brandInventory': 'Brand inventory',
  // @deprecated sidebar.calculator y calculator.*: la página usa merchPlanner.* desde T-2026-10-21-mp-s1-motor-planner; se borran en una limpieza aparte.
  'sidebar.calculator': 'Calculator',
  'calculator.title': 'Calculator',
  'calculator.subtitle': 'Never run out, never over-order: from expected attendance to the units to order per prize, with the working in view. Save the result into an event and Plan, Track and the report measure against it.',
  'calculator.inputs': 'Inputs',
  'calculator.attendance': 'Expected attendance per day',
  'calculator.attendanceHint': 'People at the venue, not players.',
  'calculator.captureRate': 'Capture rate',
  // @deprecated — sustituida por calculator.captureRateHintStart (T-2026-10-23-xotski-calculadora-arreglos)
  'calculator.captureRateHint': 'Share of attendees who play. Your past events sit around 30–40%.',
  'calculator.days': 'Event days',
  'calculator.buffer': 'Buffer',
  'calculator.bufferHint': 'Extra on top of the expected units.',
  'calculator.prizes': 'Prizes in the game',
  'calculator.noStock': 'The brand has no merch yet. Add it in',
  'calculator.rarity': 'rarity {n}',
  'calculator.pendingTitle': 'Pending',
  'calculator.pendingBody': 'Missing: {needs}. The result is not a guess: fill these in and it appears.',
  'calculator.need.attendance': 'expected attendance',
  'calculator.need.captureRate': 'a capture rate between 1 and 100%',
  'calculator.need.days': 'at least one day',
  'calculator.need.buffer': 'a buffer of 0% or more',
  'calculator.need.items': 'at least one prize in the game',
  'calculator.results': 'Order per prize',
  'calculator.col.prize': 'Prize',
  'calculator.col.weight': 'Weight',
  'calculator.col.probability': 'Chance',
  'calculator.col.expected': 'Expected',
  'calculator.col.order': 'Order',
  'calculator.col.threshold': 'Restock at',
  'calculator.total': 'Total · {plays} plays',
  'calculator.working': 'The working',
  'calculator.working.plays': 'plays = {a} attendees × {c} capture × {d} day(s) = {plays}',
  'calculator.working.weight': 'chance per prize = weight ÷ {sum}, with weight = (6 − rarity)², the same curve the game uses',
  'calculator.working.order': 'order = ceil(plays × chance × (1 + {b} buffer))',
  // @deprecated — sustituida por calculator.working.restock (T-2026-10-23-xotski-calculadora-arreglos)
  'calculator.working.threshold': 'restock at = ceil(order ÷ {d} day(s)): one day of consumption',
  'calculator.saveTitle': 'Save into an event',
  // @deprecated — sustituida por calculator.saveBodyEvent (T-2026-10-23-xotski-calculadora-arreglos)
  'calculator.saveBody': 'Writes the order as the expected units of each prize on that event, split across its stations. Prizes not allocated at the event are skipped: allocate them in Plan first.',
  'calculator.project': 'Project',
  'calculator.event': 'Event',
  'calculator.pickProject': 'Pick a project…',
  'calculator.pickEvent': 'Pick an event…',
  'calculator.saveCta': 'Save expected into the event',
  'calculator.savedToast': 'Expected saved on {n} allocation(s)',
  'calculator.savedSkipped': '{n} prize(s) skipped: not allocated at this event.',
  'calculator.saveFailed': 'The expected could not be saved',
  'calculator.captureRateHintStart': 'Share of attendees who play. 35% is only a starting point, not your brand\'s data: adjust it to the event.',
  'calculator.hours': 'Hours open per day',
  'calculator.hoursHint': 'Fills in from the event when you pick one. Only used for the restock guide.',
  'calculator.eventSection': 'Event (optional)',
  'calculator.eventSectionHint': 'Pick one to plan only the merch that plays there, with the rarity set at each station.',
  'calculator.leftOut.title': 'Left out of the plan',
  'calculator.leftOut.notAllocated': 'not allocated at this event',
  'calculator.leftOut.hidden': 'hidden from the games at every station',
  'calculator.leftOut.removed': 'removed from the brand; the game can still hand it out while it stays allocated',
  'calculator.leftOut.allocate': 'Allocate merch in Plan',
  'calculator.working.restock': 'restock at = ceil(order ÷ {d} day(s) ÷ {h} h ÷ stations with that merch): about one hour of handouts at one station. A guide only: it is not saved.',
  'calculator.working.restockPending': 'restock at: add the hours open per day to see it.',
  'calculator.saveBodyEvent': 'Writes the order as the expected units of each merch in this plan on the chosen event, split across its stations.',
  'calculator.saveNeedsEvent': 'Pick an event above to save.',
  'calculator.listTooLong': 'This event or brand has more than 500 allocations or merch, so the calculator can\'t see them all: it doesn\'t plan or save this event.',
  'calculator.allocationsError': 'We couldn\'t load this event\'s allocations, so the calculator can\'t tell what plays here. It doesn\'t plan or save this event: reload the page or try again in a moment.',
  'sidebar.merchPlanner': 'Merch planner',
  'merchPlanner.title': 'Merch planner',
  'merchPlanner.subtitle': 'How much merch to order for an event, with the working in view: guests, who plays, who comes to collect, and a buffer. The odds are the game\'s own.',
  'merchPlanner.planFor': 'Event (optional)',
  'merchPlanner.planForHint': 'Pick one to plan only the merch that plays there, station by station, with the rarity set at each one.',
  'merchPlanner.project': 'Project',
  'merchPlanner.pickProject': 'Pick a project…',
  'merchPlanner.event': 'Event',
  'merchPlanner.noEvent': 'No event yet: plan with all the brand\'s merch',
  'merchPlanner.eventNotFound': 'That event is not available. Pick one from your projects.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.inputs': 'Inputs',
  'merchPlanner.events': 'Events',
  'merchPlanner.guests': 'Guests per event',
  'merchPlanner.guestsHint': 'People at the event, not players.',
  'merchPlanner.shareWhoPlay': 'Share who play',
  'merchPlanner.shareWhoPlayHint': 'Share of guests who play. 40% is a starting point, not your data: Xotski does not record attendance yet.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.claimRate': 'Claim rate',
  'merchPlanner.claimRateHint': 'Winners who come to collect their prize. The event report calls it the redemption rate.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.buffer': 'Buffer',
  'merchPlanner.bufferHint': 'Extra on top of what you expect to hand out.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.hours': 'Hours open',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.hoursFromEvent': 'From the event: {h} h. Type to change it for this event.',
  'merchPlanner.hoursMultiDay': 'This event spans {h} hours over several days: type the hours it is open.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.stations': 'Stations per event',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.stationsFromEvent': '{n} station(s) with merch in the game, from the event.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.minutesPerHandout': 'Minutes per hand-out',
  'merchPlanner.minutesPerHandoutHint': 'Time at the station to hand one prize over.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.draftNote': 'Your inputs are kept on this device until you sign out.',
  'merchPlanner.prizes': 'Prizes in the game',
  'merchPlanner.noMerch': 'The brand has no merch yet. Add it in',
  'merchPlanner.rarity': 'rarity {n}',
  'merchPlanner.rarityRange': 'rarity {min}–{max}',
  'merchPlanner.cooldown': 'one every {n} min',
  'merchPlanner.duplicateRows': '{n} rows of this prize at {station}: the game uses only the first.',
  'merchPlanner.stationNoMerch': '{station} has no merch in the game: the plan splits its guests across the other stations.',
  'merchPlanner.notInGame.title': 'Left out of the plan',
  'merchPlanner.notInGame.body': 'They take no share of the plan. Allocate them to the event to count them.',
  'merchPlanner.notInGame.unallocated': 'not allocated at this event',
  'merchPlanner.notInGame.hidden': 'hidden from the games at every station',
  'merchPlanner.notInGame.disabled': 'removed from the brand',
  'merchPlanner.notInGame.disabledStillPlays': 'While it stays allocated here, the game can still hand it out. Remove its allocation in Allocate prizes.',
  'merchPlanner.loadError': 'We couldn\'t load this event\'s merch, stations or allocations, so the planner can\'t tell what plays here. It doesn\'t plan or save this: reload the page or try again in a moment.',
  'merchPlanner.listTooLong': 'There are more than 500 allocations, stations or merch items here, so the planner cannot see them all. It does not plan or save this.',
  'merchPlanner.pendingTitle': 'Pending',
  'merchPlanner.pendingBody': 'Missing: {needs}. The result is not a guess: fill these in and it appears.',
  'merchPlanner.need.events': 'at least one event',
  'merchPlanner.need.guests': 'the guests per event',
  'merchPlanner.need.shareWhoPlay': 'a share who play between 1 and 100%',
  'merchPlanner.need.claimRate': 'a claim rate between 1 and 100%',
  'merchPlanner.need.buffer': 'a buffer of 0% or more',
  'merchPlanner.need.stations': 'at least one station',
  'merchPlanner.need.items': 'at least one prize in the game',
  'merchPlanner.need.hours': 'the hours open',
  'merchPlanner.need.minutesPerHandout': 'minutes per hand-out, more than 0 and up to 60',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.results': 'What to order',
  'merchPlanner.col.prize': 'Prize',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.col.weight': 'Weight',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.col.chance': 'Chance',
  'merchPlanner.col.planned': 'Planned',
  'merchPlanner.col.order': 'To order',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.col.perEvent': '≈ Per event',
  'merchPlanner.col.restock': 'Restock at (per station)',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.total': 'Total · {players} players · {claims} claims',
  'merchPlanner.restockGuide': 'Restock at = about one hour of hand-outs left at a station. A guide only: it is not saved.',
  'merchPlanner.restockPending': 'Restock at needs the hours open.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.capacity.ok': '{points} station(s) hand out up to {capacity} an hour; your busiest hour needs about {peak}. Covered.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.capacity.short': '{points} station(s) hand out up to {capacity} an hour; your busiest hour needs about {peak}: add {extra} more.',
  'merchPlanner.capacity.pending': 'Capacity: missing {needs}.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.working': 'The working',
  'merchPlanner.working.players': 'players = {events} event(s) × {guests} guests × {share} who play = {players}',
  'merchPlanner.working.claims': 'claims = {players} players × {claim} claim rate = {claims}',
  'merchPlanner.working.order': 'to order = ⌈{claims} claims × (1 + {buffer} buffer)⌉ = {order}',
  'merchPlanner.working.chance': 'chance per prize = its weight over the weights at each station, with weight = (6 − rarity)², the same curve the game uses; guests split evenly across stations',
  'merchPlanner.working.split': 'planned and to order are split per prize by chance, so the rows add up to the totals',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.saveTitle': 'Save into the event',
  'merchPlanner.saveBody': 'Writes the units to order of each prize, buffer included, as the expected units on this event, split across the stations that hold it. Track and the event report measure delivered prizes against them. Merch outside the plan is set to 0.',
  'merchPlanner.saveNeedsEvent': 'Pick an event to save the plan into it.',
  'merchPlanner.savedNow': 'Saved on the event now: {n} expected unit(s).',
  'merchPlanner.saveCta': 'Save the order into the event',
  'merchPlanner.savedToast': 'Expected units saved on {n} allocation(s)',
  'merchPlanner.savedSkipped': '{n} prize(s) skipped: not allocated at this event.',
  'merchPlanner.saveFailed': 'The expected units could not be saved',
  'merchPlanner.entry.planMerch': 'Plan merch',
  'merchPlanner.entry.saveFirst': 'Save the event first',
  'merchPlanner.entry.fromDrawer': 'Plan this event',
  'merchPlanner.entry.applyFirst': 'Apply or cancel your changes first',
  'merchPlanner.crowdTitle': 'Your crowd',
  'merchPlanner.guestsInAll': '{n} guests in all',
  'merchPlanner.guestsFewer': 'Fewer guests',
  'merchPlanner.guestsMore': 'More guests',
  'merchPlanner.shareWords.few': 'A few curious guests stop by.',
  'merchPlanner.shareWords.typical': 'About {n} in 10 guests play. Typical for a booth with a game.',
  'merchPlanner.shareWords.most': 'Most of the crowd plays. Think a headline activation.',
  'merchPlanner.claimLabel': 'Winners who pick up their prize',
  'merchPlanner.bufferLabel': 'Safety buffer',
  'merchPlanner.buffer.tight': 'Tight',
  'merchPlanner.buffer.lean': 'Lean',
  'merchPlanner.buffer.safe': 'Safe',
  'merchPlanner.buffer.generous': 'Generous',
  'merchPlanner.dayTitle': 'The day',
  'merchPlanner.eventsLess': 'One event less',
  'merchPlanner.eventsMore': 'One event more',
  'merchPlanner.stationsLabel': 'Stations or field staff (BAs)',
  'merchPlanner.stationsBaHint': 'Each BA with their own QR code is one more handout point.',
  'merchPlanner.stationsLess': 'One station or BA less',
  'merchPlanner.stationsMore': 'One station or BA more',
  // @deprecated (S1b r2): sin uso, se conserva por compatibilidad
  'merchPlanner.stationsLocked': 'From the event. Add or remove stations or BAs in the project plan.',
  'merchPlanner.hoursOpen': 'Hours open per day',
  'merchPlanner.hoursLess': 'One hour less',
  'merchPlanner.hoursMore': 'One hour more',
  'merchPlanner.hoursEventChip': 'Event · {h} h',
  'merchPlanner.timePerHandout': 'Time per handout',
  'merchPlanner.clock.minuteUp': 'One minute more',
  'merchPlanner.clock.minuteDown': 'One minute less',
  'merchPlanner.clock.secondUp': 'Fifteen seconds more',
  'merchPlanner.clock.secondDown': 'Fifteen seconds less',
  'merchPlanner.clock.caption': 'MIN:SEC',
  'merchPlanner.clock.value': '{m} minutes {s} seconds per handout',
  'merchPlanner.clock.hint': 'One guest plays, wins and walks away with the prize. That is {n} handouts an hour per station or BA.',
  'merchPlanner.savedOnDevice': 'Saved on this device',
  'merchPlanner.result.order': 'Order',
  'merchPlanner.result.piecesPerEvent': 'pieces of merch, about {n} per event',
  'merchPlanner.result.piecesOne': 'pieces of merch for this event',
  'merchPlanner.result.players': 'Players',
  'merchPlanner.result.claims': 'Prizes picked up',
  'merchPlanner.result.buffer': 'Buffer',
  'merchPlanner.split.title': 'How the order splits',
  'merchPlanner.split.hint': 'Rarer merch wins less often, so you need less of it.',
  'merchPlanner.col.rarity': 'Rarity',
  'merchPlanner.rarityWord.1': 'Very common',
  'merchPlanner.rarityWord.2': 'Common',
  'merchPlanner.rarityWord.3': 'Medium',
  'merchPlanner.rarityWord.4': 'Rare',
  'merchPlanner.rarityWord.5': 'Very rare',
  'merchPlanner.restockWhen': 'when {n} left',
  'merchPlanner.workingToggle': 'How we got {n}',
  'merchPlanner.capacity.avgOk': '{points} station(s) or BA(s) hand out up to {capacity} an hour; an average hour needs about {peak}. Covered on average.',
  'merchPlanner.capacity.avgShort': '{points} station(s) or BA(s) hand out up to {capacity} an hour; an average hour needs about {peak}: add {extra} more.',
  'merchPlanner.sim.title': 'Rush-hour simulator',
  'merchPlanner.sim.subtitle': 'One event day, played fast: guests arrive, line up, win and leave.',
  'merchPlanner.sim.crowdLabel': 'How the crowd arrives',
  'merchPlanner.sim.steady': 'Steady crowd',
  'merchPlanner.sim.rush': 'Rush mid-day',
  'merchPlanner.sim.play': 'Play the day',
  'merchPlanner.sim.resume': 'Resume',
  'merchPlanner.sim.again': 'Play again',
  'merchPlanner.sim.pause': 'Pause',
  'merchPlanner.sim.reset': 'Reset',
  'merchPlanner.sim.clock': 'Event clock',
  'merchPlanner.sim.doorsOpen': 'Doors open',
  'merchPlanner.sim.close': 'Close',
  'merchPlanner.sim.progress': '{n}% of the day',
  'merchPlanner.sim.station': 'Station or BA {n}',
  'merchPlanner.sim.served': 'served {n}',
  'merchPlanner.sim.noLine': 'no line',
  'merchPlanner.sim.noWait': 'no wait',
  'merchPlanner.sim.wait': '~{n} min',
  'merchPlanner.sim.waitCaption': 'wait in line',
  'merchPlanner.sim.moreStations': '+{n} more stations or BAs, {q} people waiting',
  'merchPlanner.sim.played': 'Played so far',
  'merchPlanner.sim.merchLeft': 'Merch left for this event',
  'merchPlanner.sim.longestLine': 'Longest line today',
  'merchPlanner.sim.people': '{n} people',
  'merchPlanner.sim.okOne': 'One station or BA keeps up, even at the busiest moment.',
  'merchPlanner.sim.okMany': 'Your {n} stations or BAs keep up, even at the busiest moment.',
  'merchPlanner.sim.short': 'A line builds at the busiest moment: about {peak} players an hour against {capacity} you can serve.',
  'merchPlanner.sim.addOne': 'Add 1 station or BA',
  'merchPlanner.sim.addStations': 'Add {n} stations or BAs',
  // @deprecated (S1b r2): sin uso, se conserva por compatibilidad
  'merchPlanner.sim.addInPlan': 'Add stations or BAs in the project plan',
  'merchPlanner.sim.pending': 'The simulator needs {needs}.',
  'merchPlanner.sim.dayOver': 'Day over: {played} played, longest line {line}.',
  'merchPlanner.sim.reducedMotion': 'Motion is reduced on this device, so Play shows the end of the day.',
  'merchPlanner.hoursChip': '{n}h',
  'merchPlanner.col.merch': 'Merch',
  'merchPlanner.stationsLockedBa': 'From the event. To add BAs, add a station for each one in the project; each station has its own QR code.',
  'merchPlanner.sim.addInPlanBa': 'To add BAs, add a station for each one in the project; each station has its own QR code.',
  'sidebar.brandStudio': 'Brand studio',
  'sidebar.projects': 'Projects',
  'sidebar.insights': 'Insights',
  'sidebar.contacts': 'Contacts',
  'sidebar.redeem': 'Redeem',
  'sidebar.profile': 'Profile',
  'sidebar.accessManager': 'Access Manager',
  'sidebar.settings': 'Settings',
  'sidebar.admin': 'Admin',
  'sidebar.collapse': 'Collapse sidebar',
  'sidebar.expand': 'Expand sidebar',
  'sidebar.profileMenu.workspace': 'Account',
  'sidebar.profileMenu.workspaceSublabel': 'Plan · seats · brands · billing',
  'sidebar.profileMenu.plan': 'Plan',
  'sidebar.profileMenu.planSublabel': 'Seats · brands · billing',
  'sidebar.profileMenu.language': 'Language',
  'sidebar.profileMenu.languagePickerSoon': 'Language picker coming soon',
  'sidebar.profileMenu.openMenu': 'open menu',
  'sidebar.favorites': 'Favorites',
  'sidebar.workspace': 'Account',
  'sidebar.notifications': 'Notifications',
  'sidebar.billing': 'Billing',
  'sidebar.followup': 'Follow-up',
  'sidebar.integrations': 'Integrations',
  'sidebar.surveyBuilder': 'Design Journey',

  // ── Brand Inventory (PRD §9, two-tier inventory) ───────────────
  'brandInventory.title': 'Brand inventory',
  'brandInventory.subtitle':
    'Brand-level pool of items. Allocate into projects, return when an event closes, or write off.',
  'brandInventory.noun': 'items',
  'brandInventory.filterByState': 'Filter by state',
  'brandInventory.allStates': 'All',
  'brandInventory.empty': 'No items match these filters.',
  'brandInventory.emptyHint': 'Capture an item from the field or create one from the admin.',
  'brandInventory.emptyNotEnabled': 'Brand Inventory is not enabled',
  'brandInventory.emptyNotEnabledHint':
    'Enable it in Settings › Brand › Inventory to start tracking brand-level items.',
  'brandInventory.estimated': 'estimated',
  'brandInventory.col.name': 'Name',
  'brandInventory.col.quantity': 'Qty',
  'brandInventory.col.state': 'State',
  'brandInventory.col.rating': 'Rarity',
  'brandInventory.col.tags': 'Tags',
  'brandInventory.state.unassigned': 'In warehouse',
  'brandInventory.state.allocated': 'Allocated (in play)',
  'brandInventory.state.distributed': 'Delivered',
  'brandInventory.state.claimed': 'Claimed',
  'brandInventory.state.returned': 'Returned at close',
  'brandInventory.basis': 'Counts from the prize ledger of the brand’s {n} event(s) and its live allocations. Same facts as each event report.',
  'brandInventory.goToMerch': 'Open My Merch',
  'brandInventory.emptyNoMerch': '{brand} has no merch yet',
  'brandInventory.emptyNoMerchHint': 'Add items in My Merch; they show up here as soon as they exist, with what happened to every unit that went out.',
  'brandInventory.emptyFilter': 'No item is in state «{state}»',
  'brandInventory.emptyFilterHint': 'The brand has {n} item(s); none has units in this state. Pick another chip or «All».',
  'brandInventory.rarity': 'Rarity {n}/5',
  'brandInventory.col.total': 'Total',
  'brandInventory.state.pending': 'Pending pickup',
  'brandInventory.state.lapsed': 'Expired / voided',
  'brandInventory.state.unaccounted': 'Unaccounted',
  'brandInventory.state.written_off': 'Written off',

  // ── Entity Home (PRD §7.1) ─────────────────────────────────────
  'entityHome.atledi.label': 'Atledi',
  'entityHome.atledi.youFallback': 'team',
  'entityHome.atledi.greeting.morning':
    'Good morning, {name}. Three days into a new week — what activation is next on deck?',
  'entityHome.atledi.greeting.afternoon':
    'Good afternoon, {name}. Mid-day is when capture rate climbs. Want me to check your live stations?',
  'entityHome.atledi.greeting.evening':
    'Good evening, {name}. Tomorrow’s prep window starts now. Ready to brief the next event?',
  // Brand home (simplified Entity Home)
  'brandHome.cooking': 'What’s cooking today, {name}?',
  'brandHome.addXotskies': 'Add Merch',
  'brandHome.addXotskiesHint': 'Prizes and merch for your inventory',
  'brandHome.addEvents': 'Add events',
  'brandHome.addEventsHint': 'Schedule an activation',
  'brandHome.addProject': 'Add a Project',
  'brandHome.addProjectHint': 'Group events under a campaign',
  'brandHome.jumpBack': 'Jump back into your projects',
  'brandHome.askAtledi': 'Ask Atledi for something else…',
  'brandHome.status.planning': 'Planning',
  'brandHome.status.wrapped': 'Wrapped',
  // Event create form (right panel)
  'eventForm.project': 'Project',
  'eventForm.noProjects': 'Create a project first — events live inside a project.',
  'eventForm.name': 'Event name',
  'eventForm.namePlaceholder': 'e.g. Opening Night',
  'eventForm.address': 'Address',
  'eventForm.addressPlaceholder': 'Venue or location',
  'eventForm.starts': 'Starts',
  'eventForm.ends': 'Ends',
  'eventForm.create': 'Create event',
  // ── Advanced create (event / project scaffolding) ────────────────────
  'scaffold.advanced': 'Advanced — stations and prizes',
  'scaffold.events': 'Events',
  'scaffold.eventsHint': 'Spread evenly across the project date range.',
  'scaffold.stations': 'Stations per event',
  'scaffold.stationsHint': 'Each one gets its own QR and its share of the prizes.',
  'scaffold.prizes': 'Prizes to hand out',
  'scaffold.noPrizes': 'This brand has no prizes yet. You can add them to the event later.',
  'scaffold.left': '{n} left',
  'scaffold.perEvent': 'per event',
  'scaffold.total': 'in total',
  'scaffold.oneStation': 'all {n} at the single station',
  'scaffold.split': '{n} at each of the {s} stations',
  'scaffold.splitUneven': '{hi} at {hiN}, {lo} at {loN}',
  'scaffold.fewerThanStations': 'only {n} for {s} stations — some get none',
  'scaffold.overStock': 'That is {total} units and only {left} remain in the brand.',
  'scaffold.summary': 'Creates {events} event(s) and {stations} station(s).',
  'scaffold.datesNote': 'The events are spread across the project date range.',
  'scaffold.built': 'Built {events} event(s) with {stations} station(s)',
  'scaffold.projectOnly': 'Project created, but its events were not',
  // ── Create project form ──────────────────────────────────────────────
  'projectForm.name': 'Project name',
  'projectForm.namePlaceholder': 'e.g. Spring Tour 2026',
  'projectForm.codeRef': 'Code reference',
  'projectForm.codeRefPlaceholder': 'e.g. SPRING26',
  'projectForm.starts': 'Start date',
  'projectForm.ends': 'End date',
  'projectForm.optional': 'Optional.',
  'projectForm.rangeNote': 'The project date range governs the valid dates of its events. You can set or change it later from the project page.',
  'projectForm.create': 'Create project',
  'projectForm.creating': 'Creating…',
  'projectForm.created': 'Project "{name}" created',
  'projectForm.createFailed': 'Could not create project',
  'projectForm.badRange': 'End date must be on or after the start date',
  // ── Calendar ─────────────────────────────────────────────────────────
  'sidebar.calendar': 'Calendar',
  'calendar.prev': 'Previous month',
  'calendar.next': 'Next month',
  'calendar.today': 'Today',
  'calendar.countThisMonth': '{n} events this month',
  'calendar.countThisMonthOne': '1 event this month',
  'calendar.empty': 'This brand has no events yet. Use the + on any day to schedule the first one.',
  'calendar.noBrand': 'Pick a brand to see its calendar.',
  'calendar.more': '+{n} more',
  'calendar.newOnDay': 'New event · {day}',
  'calendar.newOnDayShort': 'New event on this day',
  'calendar.phase.live': 'Live',
  'calendar.phase.upcoming': 'Upcoming',
  'calendar.phase.edit': 'Being set up',
  'calendar.phase.done': 'Finished',
  'calendar.view.month': 'Month',
  'calendar.view.week': 'Week',
  'calendar.countThisWeek': '{n} events this week',
  'calendar.countThisWeekOne': '1 event this week',
  'calendar.weekEmpty': 'Nothing on this week.',
  'calendar.summary.title': 'Event',
  'calendar.summary.open': 'Open the event',
  'calendar.summary.unnamedPrize': 'Unnamed prize',
  'calendar.summary.gone': 'That event is no longer available.',
  'calendar.summary.noDates': 'No dates yet',
  'calendar.summary.stations': '{n} stations',
  'calendar.summary.prizes': 'Prizes',
  'calendar.summary.left': '{left} of {total} left',
  // ── Address field (Google Places + map picker) ───────────────────────
  'address.placeholder': 'Search for a venue or street…',
  'address.hint': 'Pick a suggestion or drop a pin on the map so the venue can be located, not just read.',
  'address.pinned': 'Pinned at {lat}, {lng}',
  'address.noKey': 'Free text. Suggestions and the map need VITE_GOOGLE_MAPS_API_KEY configured.',
  'address.openMap': 'Pick on the map',
  'address.mapTitle': 'Where is it?',
  'address.mapHint': 'Drag the pin or tap anywhere on the map. The address is worked out from the pin.',
  'address.resolving': 'Looking up the address…',
  'address.nothingYet': 'No point chosen yet.',
  'address.useThis': 'Use this location',
  'scaffold.venue': 'Venue for every event',
  // ── Delete a brand (workspace switcher) ──────────────────────────────
  'brandDelete.action': 'Delete {name}',
  'brandDelete.title': 'Delete “{name}”?',
  'brandDelete.counting': 'Checking what this brand holds…',
  'brandDelete.body': 'It holds {projects} project(s) and {events} event(s). They all stop being available with it.',
  'brandDelete.typeName': 'Type “{name}” to confirm',
  'brandDelete.recoverable': 'Nothing is destroyed: the brand stops appearing in the lists and its data stays in the database, so an administrator can bring it back.',
  'brandDelete.confirm': 'Delete brand',
  'brandDelete.deleting': 'Deleting…',
  'brandDelete.done': 'Brand “{name}” deleted',
  'brandDelete.failed': 'Could not delete the brand',
  // ── Background studio (same engine as Xurvey) ────────────────────────
  'designJourney.designBackground': 'Design the background',
  'bgStudio.title': 'Design the background',
  'bgStudio.hue': 'Hue',
  'bgStudio.harmony': 'Harmony',
  'bgStudio.gradient': 'Gradient',
  'bgStudio.angle': 'Angle',
  'bgStudio.pattern': 'Pattern',
  'bgStudio.noPattern': 'No pattern',
  'bgStudio.patternSize': 'Pattern size',
  'bgStudio.patternOpacity': 'Pattern opacity',
  'bgStudio.blend': 'Blend',
  'bgStudio.vignette': 'Vignette',
  'bgStudio.glow': 'Glow',
  'bgStudio.grain': 'Grain',
  'bgStudio.another': 'Another variation',
  'bgStudio.applyPalette': 'Apply the colours too',
  'bgStudio.use': 'Use this background',
  'bgStudio.applying': 'Applying…',
  'bgStudio.savedAs': 'Saved as an image · {format} 1080×1920 · ~{kb} KB',
  'bgStudio.lightened': 'lightened so the button shows',
  'bgStudio.textContrast': 'Text contrast',
  'bgStudio.buttonContrast': 'Button contrast',
  'designJourney.projectScope': 'What you design here applies to {deployments}, across {events} of this project. Deploying a survey to an event is done from that event.',
  'designJourney.oneDeployment': '1 live deployment',
  'designJourney.nDeployments': '{n} live deployments',
  'designJourney.oneEvent': '1 event',
  'designJourney.nEvents': '{n} events',
  'eventForm.outOfRange': 'Event dates must fall within the project date range',
  'eventForm.badRange': 'End date must be after the start date',
  'entityHome.kpi.moments': 'Moments captured',
  'entityHome.kpi.throughput': 'Throughput',
  'entityHome.kpi.inventoryHealth': 'Inventory health',
  'entityHome.kpi.contacts': 'Contacts captured',
  'entityHome.kpi.comingSoon': 'Live insights coming soon',
  'entityHome.kpi.aboveThreshold': 'Items that still have units',
  'entityHome.kpi.totalDeduped': 'Total deduped contacts',
  'entityHome.nextEvent.label': 'My next event',
  'entityHome.nextEvent.openCta': 'Open event',
  'entityHome.nextEvent.noneTitle': 'No upcoming event scheduled',
  'entityHome.nextEvent.noneHint':
    'Create a project to schedule your first activation.',

  // ── Project Environment (PRD §7.2) ─────────────────────────────
  'project.atledi.label': 'Atledi',
  'project.atledi.emptyProject':
    "{name} doesn't have events yet. Want me to draft the first one from your brand brief?",
  'project.atledi.liveActivity':
    '{name} is live right now across {live} event(s). Watch capture rate against expected throughput.',
  'project.atledi.scheduled':
    '{name} has {total} event(s) on the calendar. Lock the brief 6 days out to hit the season window.',
  'project.panels.nextEvent.label': 'Next event',
  'project.panels.nextEvent.tbd': 'Date TBD',
  'project.panels.nextEvent.empty': 'No upcoming event scheduled.',
  'project.panels.nextEvent.open': 'Open event',
  'project.panels.myXotskies.label': 'My Merch',
  'project.panels.myXotskies.itemsTotal': 'items total',
  'project.panels.myXotskies.lowStock': 'low',
  'project.panels.myXotskies.open': 'Open My Merch',
  'project.panels.reporters.label': 'Reporters',
  'project.panels.reporters.comingSoon':
    'Reporter View ships in Phase 2 — magic-link onboarding for field staff.',
  'project.capacity.label': 'Capacity check',
  'project.capacity.green': 'On track',
  'project.capacity.yellow': 'Tight',
  'project.capacity.red': 'Under-provisioned',
  'project.capacity.pendingShort': 'Pending',
  'project.capacity.pending':
    "We don't have a historical capture rate for this project yet. Run a manual check or wait for Atledi to learn after the first event.",
  'project.capacity.summary.green':
    'Inventory {inventory} ≥ expected {expected} × 1.15 buffer. You are well-provisioned.',
  'project.capacity.summary.yellow':
    'Inventory {inventory} is between expected {expected} and the 15% buffer. Consider topping up.',
  'project.capacity.summary.red':
    'Inventory {inventory} is below expected captures {expected}. Provision more or add a BA.',

  // ── Caso 15 v2 — Top-of-shell plan alert banner ────────────────
  'plan.banner.viewPlans': 'View plans',
  'plan.banner.upgradeNow': 'Upgrade now',
  'plan.banner.expiringSoon': 'Your plan is about to expire.',
  'plan.banner.limitNear':
    "You're approaching one of your plan limits.",
  'plan.banner.limitCritical':
    "You're about to run out — upgrade now to keep your event running.",
  'plan.banner.expired':
    'Your plan has expired — your account is now on Free.',
  'plan.banner.day': 'day',
  'plan.banner.days': 'days',
  'sidebar.copyright.product': 'A product of',

  // ── Mobile bottom navigation ─────────────────────────────────────
  'mobileNav.label': 'Primary navigation',
  'mobileNav.more': 'More',
  'mobileNav.close': 'Close menu',

  // ── Help Center (chrome only — entries live in helpContent.ts) ───
  'help.title': 'Help Center',
  'help.subtitle': 'Browse what every screen does, the data it shows, and what it is for. Use search or jump to a category.',
  'help.searchPlaceholder': 'Search topics, keywords…',
  'help.whatItDoes': 'What it does',
  'help.whatData': 'What data it shows',
  'help.whatFor': 'What it is for',
  'help.related': 'Related',
  'help.goToPage': 'Go to page',
  'help.noResults': 'No topics match your search.',
  'help.resultsCount': '{count} results',
  'help.hintAria': 'Open help for this page',

  // ── Caso 13 — Outbound CRM integrations ──────────────────────────
  'integrations.title': 'Integrations',
  'integrations.subtitle':
    'Send each new contact to your CRM or any webhook in real time.',
  'integrations.newWebhook': 'New webhook',
  'integrations.empty':
    'No integrations yet. Add a webhook to start syncing contacts.',
  'integrations.row.enabled': 'Enabled',
  'integrations.row.disabled': 'Disabled',

  'integrations.stats.title': 'Sync status',
  'integrations.stats.subtitle':
    'Counts across the outbound queue for this account.',
  'integrations.state.PENDING': 'Pending',
  'integrations.state.SENT': 'Sent',
  'integrations.state.FAILED': 'Failed',
  'integrations.state.SKIPPED': 'Skipped',
  'integrations.dispatch.run': 'Run dispatch now',
  'integrations.dispatch.running': 'Dispatching…',
  'integrations.dispatch.lastRun': 'Last run',

  'integrations.oauth.title': 'HubSpot · Mailchimp',
  'integrations.oauth.subtitle':
    'OAuth-based connectors will be enabled once the Onxite apps are registered with each provider.',
  'integrations.oauth.comingSoon': 'Coming soon',

  'integrations.list.title': 'Configured integrations',
  'integrations.list.subtitle':
    'Each row fires a signed POST after every new contact captured.',

  'integrations.editor.newTitle': 'New webhook',
  'integrations.editor.editTitle': 'Edit webhook',
  'integrations.editor.subtitle':
    'Onxite signs every payload with HMAC-SHA256. Verify the X-Onxite-Signature header on your endpoint.',
  'integrations.editor.name': 'Name',
  'integrations.editor.namePlaceholder': 'e.g. Sales CRM webhook',
  'integrations.editor.url': 'Webhook URL',
  'integrations.editor.secret': 'Shared secret',
  'integrations.editor.secretPlaceholder': 'Optional — used to sign payloads',
  'integrations.editor.secretHelp':
    'When set, every request adds X-Onxite-Signature: sha256=<hex>.',
  'integrations.editor.consentOnly':
    'Only send contacts with marketing consent',
  'integrations.editor.enabled': 'Enabled',
  // Caso 13 v2 — multi-provider editor.
  'integrations.new.webhook': 'New webhook',
  'integrations.new.hubspot': 'New HubSpot',
  'integrations.new.mailchimp': 'New Mailchimp',
  'integrations.editor.webhookHelp':
    'Onxite signs every payload with HMAC-SHA256. Verify the X-Onxite-Signature header on your endpoint.',
  'integrations.editor.hubspotHelp':
    'Create a Private App in HubSpot with crm.objects.contacts.write scope and paste its access token below.',
  'integrations.editor.mailchimpHelp':
    'Paste a Mailchimp API key and the target Audience ID. Contacts are upserted as audience members.',
  'integrations.editor.secretSaved': '•••••••• (saved — leave blank to keep)',
  'integrations.editor.hubspotAccessToken': 'Private App access token',
  'integrations.editor.hubspotAccessTokenHelp':
    'HubSpot → Settings → Integrations → Private Apps. Stored encrypted.',
  'integrations.editor.mailchimpApiKey': 'API key',
  'integrations.editor.mailchimpApiKeyHelp':
    'Mailchimp → Account → Extras → API keys. The datacenter suffix (-us21) is required. Stored encrypted.',
  'integrations.editor.mailchimpAudienceId': 'Audience ID',
  'integrations.editor.mailchimpAudienceIdHelp':
    'Mailchimp → Audience → Settings → Audience name and defaults.',

  // ── Caso 08 — Embeddable survey builder (host page) ───────────────
  // Caso 08 — workspace: list / import / export / clone / template.
  // ── Stock bulk import ──────────────────────────────────────────────
  'stock.list.importButton': 'Import',
  'stock.import.title': 'Bulk import merch',
  'stock.import.subtitle': 'Upload a ZIP with stock.xlsx and an images/ folder.',
  'stock.import.dropzone': 'Drop a ZIP file here or click to select',
  'stock.import.dropzoneHint': 'Accepts .zip (max 50 MB)',
  'stock.import.downloadTemplate': 'Download template',
  'stock.import.parsing': 'Reading ZIP…',
  'stock.import.parseFailed': 'Could not read this ZIP',
  'stock.import.preview': 'Preview',
  'stock.import.previewCount': '{count} rows · {errors} errors',
  'stock.import.col.image': 'Image',
  'stock.import.col.name': 'Name',
  'stock.import.col.classification': 'Class',
  'stock.import.col.rating': 'Rating',
  'stock.import.col.qty': 'Qty',
  'stock.import.col.status': 'Status',
  'stock.import.status.new': 'New',
  'stock.import.status.duplicate': 'Already exists',
  'stock.import.status.error': 'Error',
  'stock.import.action.replace': 'Replace existing',
  'stock.import.action.createNew': 'Add as new',
  'stock.import.execute': 'Import {count} items',
  'stock.import.cancel': 'Cancel',
  'stock.import.progress.upload': 'Uploading images',
  'stock.import.progress.create': 'Creating merch',
  'stock.import.progress.replace': 'Updating existing',
  'stock.import.summarySuccess': '{created} created · {updated} updated',
  'stock.import.summaryMixed': '{created} created · {updated} updated · {failed} failed',
  'stock.import.summaryAllFailed': 'Import failed for all {count} rows',
  'stock.import.done': 'Done',
  'stock.import.error.noXlsx': 'No .xlsx file found in the ZIP',
  'stock.import.error.multipleXlsx': 'More than one .xlsx file found — keep only stock.xlsx',
  'stock.import.error.missingNameCol': 'Missing required column: Name',
  'stock.import.error.emptySheet': 'The xlsx has no data rows',
  'stock.import.error.nameRequired': 'Name is required',
  'stock.import.error.nameTooShort': 'Name must be at least 2 characters',
  'stock.import.error.ratingRange': 'Rating must be between 1 and 5',
  'stock.import.error.classificationInvalid': 'Classification must be SEE, ENGAGE, TRY or ACT',
  'stock.import.error.imageNotFound': 'Image "{file}" not found in images/ folder',
  'stock.import.error.logoNotFound': 'Logo "{file}" not found in images/ folder',
  'stock.import.error.qtyNotInt': 'Initial qty must be a whole number',
  'stock.import.error.zipTooLarge': 'ZIP file is larger than 50 MB',
  'stock.import.error.notAZip': 'File must be a .zip',

  // ── Favorites ──────────────────────────────────────────────────────
  'favorites.added': 'Added to favorites',
  'favorites.removed': 'Removed from favorites',
  'favorites.addTitle': 'Add to favorites',
  'favorites.removeTitle': 'Remove from favorites',

  // ── Notifications ──────────────────────────────────────────────────
  'notifications.planAlert.title': 'Your plan is almost full',
  'notifications.planAlert.body': 'Your account has nearly used up one of its plan limits. Upgrade before your next live event to avoid interruptions.',
  'notifications.banner.markRead': 'Mark as read',
  'notifications.banner.moreCount': 'more',
  'notifications.banner.systemSender': 'System',
  'notifications.bell.title': 'Notifications',
  'notifications.bell.empty': 'No notifications yet.',
  'notifications.bell.allCaughtUp': 'All caught up',
  'notifications.bell.unreadCount': '{{count}} unread',
  'notifications.compose.pageTitle': 'Send a notification',
  'notifications.compose.pageSubtitle': 'Write a note and pick the teammates who should see it. They will also get a push notification.',
  'notifications.compose.messageTitle': 'Message',
  'notifications.compose.messageDescription': 'Title shown in the push and as the banner headline.',
  'notifications.compose.titleLabel': 'Title',
  'notifications.compose.titlePlaceholder': 'A short headline…',
  'notifications.compose.bodyLabel': 'Body',
  'notifications.compose.bodyPlaceholder': 'Add detail. Emoji and line breaks work 🎉',
  'notifications.compose.emailCopy': 'Also send by email',
  'notifications.compose.send': 'Send ({{count}})',
  'notifications.compose.recipientsTitle': 'Recipients',
  'notifications.compose.recipientsDescription': 'Teammates within your company tree.',
  'notifications.compose.searchPlaceholder': 'Search by name or email…',
  'notifications.compose.available': 'available',
  'notifications.compose.noCandidates': 'No teammates available to notify.',
  'notifications.compose.titleRequired': 'Title is required',
  'notifications.compose.bodyRequired': 'Body is required',
  'notifications.compose.recipientsRequired': 'Pick at least one recipient',
  'notifications.toast.sent': 'Notification sent to {{count}} teammate(s)',
  'notifications.toast.sendFailed': 'Failed to send notification',
  'notifications.push.disabledTitle': 'Push notifications disabled',
  'notifications.push.insecureContextBody':
    "This dev URL isn't HTTPS or localhost, so the browser blocks the service worker that delivers push. Open the app over HTTPS or via http://localhost to enable it.",
  'notifications.push.permissionDeniedBody':
    'You blocked notifications for this site. Re-enable them from the browser settings (lock icon → Notifications) and reload.',

  // ── Auth · Login ───────────────────────────────────────────────────
  'auth.login.welcomeBack': 'Welcome back',
  'auth.login.subtitle': 'Sign in to manage your campaigns. New here? Create your account below.',
  'auth.login.noAccount': 'No account uses this email yet.',
  'auth.login.createWithEmail': 'Create your account with this email →',
  'auth.login.modeOtp': 'Email code',
  'auth.login.modePassword': 'Password',
  'auth.login.emailLabel': 'Email address',
  'auth.login.email': 'Email',
  'auth.login.password': 'Password',
  'auth.login.continueWithEmail': 'Continue with email',
  'auth.login.signIn': 'Sign in',
  'auth.login.forgotPassword': 'Forgot password?',
  'auth.forgot.title': 'Recover your password',
  'auth.forgot.subtitle': "Enter your email and we'll send a 6-digit code to your recovery email.",
  'auth.forgot.submit': 'Send recovery code',
  'auth.forgot.sentTitle': 'Code sent',
  'auth.forgot.sentBody': 'Check your inbox for the 6-digit code.',
  'auth.forgot.failed': 'Could not send recovery code',
  'auth.forgot.backToLogin': 'Back to sign in',
  'auth.reset.title': 'Set a new password',
  'auth.reset.subtitleFor': 'Enter the 6-digit code sent to {email} and your new password.',
  'auth.reset.subtitleNoEmail': 'Missing email. Request a new recovery code.',
  'auth.reset.requestNew': 'Request a new code',
  'auth.reset.otpLabel': 'Recovery code',
  'auth.reset.newPasswordLabel': 'New password',
  'auth.reset.confirmPasswordLabel': 'Confirm new password',
  'auth.reset.submit': 'Update password',
  'auth.reset.successTitle': 'Password updated',
  'auth.reset.successBody': 'You can now sign in with your new password.',
  'auth.reset.failed': 'Could not reset password',
  'auth.reset.errOtpLength': 'Code must be 6 digits.',
  'auth.reset.errPasswordShort': 'Password must be at least 8 characters.',
  'auth.reset.errMismatch': 'Passwords do not match.',
  'auth.login.demoTipLabel': 'Demo tip:',
  'auth.login.demoTipBody': 'any email works. The OTP is always',
  'auth.login.curious': 'Just curious?',
  'auth.login.seePlans': 'See plans',
  'auth.login.newHere': 'New here?',
  'auth.login.pendingPlan': 'Sign in and you go straight to the {plan} payment.',
  'auth.login.createAccount': 'Create your account',
  'auth.login.heroTitle': 'Inventory meets engagement',
  'auth.login.heroBody': 'Manage every event, station and prize spin in real time. Branded journeys, instant insights, and games your guests will remember.',
  'auth.toast.devHint': 'Dev hint',
  'auth.toast.otpFailed': 'Could not send OTP',
  'auth.toast.invalidCredentials': 'Invalid credentials',
  'auth.toast.alreadySignedIn': 'Account already in use',
  'auth.toast.alreadySignedInBody':
    'You are already signed in from another device or browser. We notified that session of this attempt.',
  'auth.footer.copyrightPrefix': '© {year} Xotski by ',
  'auth.footer.copyrightSuffix': '. All rights reserved.',

  // ── Auth · OTP ─────────────────────────────────────────────────────
  'otp.checkInbox': 'Check your inbox',
  'otp.subtitlePrefix': 'We sent a 6-digit code to',
  'otp.verifyCode': 'Verify code',
  'otp.didntReceive': "Didn't receive it?",
  'otp.resendIn': 'Resend in {seconds}s',
  'otp.resendNow': 'Resend now',
  'otp.toast.welcome': 'Welcome!',
  'otp.toast.signedIn': 'Signed in',
  'otp.toast.invalidExpired': 'Invalid or expired code',
  'otp.toast.newCodeSent': 'New code sent',
  'otp.toast.newCodeBody': 'It is still 123456.',

  // ── Auth · Register (3-step wizard) ────────────────────────────────
  // ── Registro v2 (alta rediseñada, sept 2026) ──
  'register.step.create': 'Create account',
  'register.step.org': 'Your organization',
  'register.step.ready': 'Get ready',
  'register.acct.title': 'Create your account',
  'register.acct.subtitle': 'Start running activations in minutes.',
  'register.acct.fullName': 'Full name',
  'register.acct.fullNamePlaceholder': 'e.g. Marco Reyes',
  'register.acct.email': 'Work email',
  'register.acct.password': 'Password',
  'register.acct.passwordPlaceholder': 'At least 8 characters',
  'register.acct.consentPre': 'I agree to the ',
  'register.acct.consentTerms': 'Terms',
  'register.acct.consentMid': ' and ',
  'register.acct.consentPrivacy': 'Privacy Policy',
  'register.acct.consentPost':
    ", and consent to Xotski processing activation data on my organization's behalf.",
  'register.acct.create': 'Create account',
  'register.org.kicker': 'Step 2 of 3 — your organization',
  'register.org.title': 'Set up your organization',
  'register.org.body':
    'The organization you create owns the account — members join under it. Brands sit beneath it and own the warehouse, the projects, and the events and stations inside them.',
  'register.org.kindLabel': "I'm setting this up as a…",
  'register.org.kind.brand': 'Brand',
  'register.org.kind.brandHint': 'Activations for our own products',
  'register.org.kind.agency': 'Agency',
  'register.org.kind.agencyHint': 'Activations for client brands',
  'register.org.kind.other': 'Other',
  'register.org.kind.otherHint': 'Venue, distributor or partner',
  'register.org.orgName.brand': 'Parent company',
  'register.org.orgName.agency': 'Agency name',
  'register.org.orgName.other': 'Organization name',
  'register.org.orgPlaceholder.brand': 'e.g. Joyly Beverages Inc.',
  'register.org.orgPlaceholder.agency': 'e.g. Matraka',
  'register.org.orgPlaceholder.other': 'e.g. Lone Star Venues',
  'register.org.industry.brand': 'Industry',
  'register.org.industry.agency': 'Primary industry you serve',
  'register.org.industry.other': 'Industry',
  'register.org.industryPlaceholder': 'Select an industry…',
  'register.org.companySize': 'Company size',
  'register.org.companySizePlaceholder': 'Select…',
  'register.org.region': 'Primary region',
  'register.org.regionPlaceholder': 'e.g. Los Angeles, CA',
  'register.org.brand.brand': 'Brand(s) on this account',
  'register.org.brand.agency': 'Brands you represent',
  'register.org.brand.other': 'Brand(s) on this account',
  'register.org.brandPlaceholder.brand': 'Add a brand…',
  'register.org.brandPlaceholder.agency': 'Add a client brand…',
  'register.org.brandPlaceholder.other': 'Add a brand…',
  'register.org.brandHint.brand':
    "Add the brand you're opening this account for. You can add more later.",
  'register.org.brandHint.agency':
    'Add the client brand you run activations for. You can add more later.',
  'register.org.brandHint.other':
    "Add the brand you're opening this account for. You can add more later.",
  'register.org.continue': 'Continue',
  'register.industry.bevNonAlc': 'Beverage (non-alcoholic)',
  'register.industry.beerWineSpirits': 'Beer, wine & spirits',
  'register.industry.foodCpg': 'Food & CPG',
  'register.industry.snacks': 'Snacks & confectionery',
  'register.industry.beauty': 'Beauty & personal care',
  'register.industry.health': 'Health & wellness',
  'register.industry.tech': 'Tech & electronics',
  'register.industry.automotive': 'Automotive',
  'register.industry.retail': 'Retail & apparel',
  'register.industry.hospitality': 'Hospitality & QSR',
  'register.industry.cannabis': 'Cannabis & CBD',
  'register.industry.other': 'Other',
  'validation.fullNameMin': 'Enter your full name',
  'validation.consentRequired': 'You must accept the terms to continue',
  'validation.orgNameMin': 'The name must be at least 2 characters',
  'validation.brandNameMin': 'The brand name must be at least 2 characters',
  'validation.industrySelect': 'Please select an industry',
  'validation.companySizeSelect': 'Please select a company size',
  'validation.emailInvalid': 'Please enter a valid email address',
  'validation.passwordMin6': 'Password must be at least 6 characters',
  'validation.passwordMin8': 'Password must be at least 8 characters',
  // HTML constraint messages (required, type, min/max…) that the browser used
  // to show in the computer's language. Used by `nativeValidation.ts`.
  'validation.required': 'This field is required',
  'validation.urlInvalid': 'Please enter a valid web address',
  'validation.invalid': 'Please check this value',
  'validation.number': 'Please enter a number',
  'validation.pattern': 'Please use the requested format',
  'validation.maxLength': 'Please use at most {n} characters',
  'validation.minLength': 'Please use at least {n} characters',
  'validation.min': 'Please enter {min} or more',
  'validation.max': 'Please enter {max} or less',
  'validation.wholeNumber': 'Please enter a whole number',
  'validation.pickOne': 'Please pick one',
  'register.step.company': 'Brand',
  'register.step.account': 'Account',
  'register.step.verify': 'Verify',
  'register.orWithSocial': 'Sign up with',
  'register.orWithSocialHint': 'We skip the email code if you sign up with a social provider.',
  'register.orWithEmail': 'Or with email',
  'register.company.title': 'Create your account',
  'register.company.subtitle': "Tell us your company name — we'll set up a Free plan you can grow from.",
  'register.company.companyName': 'Brand name',
  'register.company.companyNamePlaceholder': 'Acme Inc.',
  'register.company.continue': 'Continue',
  'register.account.title': 'Your account',
  'register.account.subtitlePrefix': "You'll be the owner of",
  'register.account.firstname': 'First name',
  'register.account.lastname': 'Last name',
  'register.account.email': 'Work email',
  'register.account.password': 'Password',
  'register.account.passwordHint': 'At least 8 characters.',
  'register.account.confirmPassword': 'Confirm password',
  'register.account.continue': 'Send verification code',
  'register.verify.title': 'Verify your email',
  'register.verify.subtitlePrefix': 'We sent a 6-digit code to',
  'register.verify.verify': 'Create my account',
  'register.verify.didntReceive': "Didn't receive it?",
  'register.verify.resendIn': 'Resend in {seconds}s',
  'register.verify.resendNow': 'Resend now',
  'register.haveAccount': 'Already have an account?',
  'register.pendingPlan': 'Once your email is verified you go straight to the {plan} payment.',
  'register.signIn': 'Sign in',
  'register.hero.title': 'Launch in minutes',
  'register.hero.body': 'Free plan: 1 brand, 2 journeys, 2 stations, 5 merch items. Upgrade when you are ready.',
  'register.toast.devHint': 'Dev hint',
  'register.toast.welcome': 'Welcome, {name}!',
  'register.toast.welcomeBody': 'Your account is ready.',
  'register.toast.initiateFailed': 'Could not start registration',
  'register.toast.emailTaken': 'That email is already registered',
  'register.toast.emailTakenBody': 'Try signing in instead.',
  'register.toast.invalidOtp': 'Invalid or expired code',

  // ── Auth · Pending access ──────────────────────────────────────────
  'pendingAccess.title': 'Waiting for access',
  'pendingAccess.body': "Your account is created but an admin still needs to grant you a role. We'll keep this page warm — sign back in once they're done.",
  'pendingAccess.signedInAs': 'Signed in as',

  // ── Onboarding shell ───────────────────────────────────────────────
  'onboarding.step.account': 'Account',
  'onboarding.step.brandProfile': 'Brand profile',
  'onboarding.step.brandKit': 'Brand kit',

  // ── Edit profile dialog ────────────────────────────────────────────
  'editProfile.eyebrow': 'Edit profile',
  'editProfile.title': 'Update your details',
  'editProfile.subtitle': "Visible across your account. Email and role can't change here.",
  'editProfile.section.visuals': 'Visuals',
  'editProfile.section.identity': 'Identity',
  'editProfile.section.professional': 'Professional',
  'editProfile.section.tags': 'Tags',
  'editProfile.field.avatar': 'Avatar',
  'editProfile.field.cover': 'Cover image',
  'editProfile.field.firstName': 'First name',
  'editProfile.field.lastName': 'Last name',
  'editProfile.field.username': 'Handle (@username)',
  'editProfile.field.phone': 'Phone',
  'editProfile.field.bio': 'Bio',
  'editProfile.field.bioPlaceholder': 'A short sentence on what you build.',
  'editProfile.field.title': 'Title',
  'editProfile.field.titlePlaceholder': 'Brand Manager',
  'editProfile.field.location': 'Location',
  'editProfile.field.locationPlaceholder': 'San Antonio, TX',
  'editProfile.field.studiedAt': 'Studied at',
  'editProfile.tag.placeholder': 'Add and press Enter…',
  'editProfile.tag.remove': 'Remove {tag}',
  'editProfile.save': 'Save changes',

  // ── Change password dialog ─────────────────────────────────────────
  'changePassword.eyebrow': 'Security',
  'changePassword.title': 'Change password',
  'changePassword.subtitle': "Pick at least 8 characters. You'll stay signed in.",
  'changePassword.field.current': 'Current password',
  'changePassword.field.new': 'New password',
  'changePassword.field.confirm': 'Confirm new password',
  'changePassword.show': 'Show password',
  'changePassword.hide': 'Hide password',
  'changePassword.hint.lengthOk': 'Length looks good.',
  'changePassword.hint.lengthShort': '{count}/8 characters — add {missing} more.',
  'changePassword.hint.match': 'Passwords match.',
  'changePassword.hint.noMatch': 'Passwords do not match yet.',
  'changePassword.error.length': 'New password must be at least 8 characters.',
  'changePassword.error.mismatch': 'New password and confirmation do not match.',
  'changePassword.submit': 'Update password',
  'changePassword.toast.updated': 'Password updated',

  // ── Pricing page ───────────────────────────────────────────────────
  'pricing.back': 'Back',
  'pricing.heroTitle': 'Plans built for every brand',
  'pricing.heroBody': 'Same SKU, two lenses: brands or merch. Pick the tier that fits your roadmap.',
  'pricing.checkoutFailed': 'Checkout failed',
  'billingPage.alacarte.seatPlanner': '+1 Planner seat',
  'billingPage.alacarte.seatPlannerHint': 'The Plan/Test/Track workbench. One person, one seat.',
  'billingPage.alacarte.seatEventManager': '+1 Event Manager seat',
  'billingPage.alacarte.seatEventManagerHint': 'Inventory management and running events.',
  'billingPage.alacarte.seatEditor': '+1 Editor seat',
  'billingPage.alacarte.seatEditorHint': 'Two included on XK1, three on XK2, ten on Enterprise. One person, one seat.',
  'billingPage.alacarte.coterm': 'Everything annual renews together on {date} — mid-term additions are prorated to that date.',
  'billingPage.alacarte.title': 'À la carte',
  'billingPage.alacarte.desc': 'Grow this year without changing plans.',
  'billingPage.alacarte.event': '+1 event',
  'billingPage.alacarte.eventHint': 'Adds one event to your annual allotment. Valid for a year.',
  'billingPage.alacarte.unlimited': 'Unlimited events',
  'billingPage.alacarte.unlimitedHint': 'Your events stop counting for the rest of the term.',
  'billingPage.alacarte.unlimitedNeedsXk2': 'XK2 add-on — upgrade first, then unlock unlimited.',
  'billingPage.alacarte.buyFor': 'Add — {price}',
  'billingPage.alacarte.goXk2': 'See XK2',
  'billingPage.addonFailed': 'Could not start the purchase',
  'pricing.startFree': 'Start free',
  'pricing.alreadyFree': 'You already have Freemium — every account starts there.',
  'pricing.startingAt': 'From {price}',
  'pricing.talkToUs': 'Talk to us',
  'pricing.talkToUsBody': 'We will reach out to design a custom plan for you.',
  'pricing.planUnavailable': 'Plan unavailable',
  'pricing.missingTier': 'Missing tier "{slug}"',

  // ── Price card ─────────────────────────────────────────────────────
  'priceCard.tier.pilot': 'Pilot',
  'priceCard.tier.scale': 'Scale ★',
  'priceCard.tier.program': 'Program',
  'priceCard.tier.enterprise': 'Enterprise',
  'priceCard.period.year': '/year',
  'priceCard.period.month': '/month',
  'priceCard.mostPopular': '★ Most popular',
  'priceCard.planAria': '{tier} plan',
  'priceCard.selected': 'Selected',
  'priceCard.talkToUs': 'Talk to us',
  'priceCard.select': 'Select {tier}',
  'priceCard.line.inBrands': 'In brands',
  'priceCard.line.inCapacity': 'In capacity',
  'priceCard.line.inEvents': 'In events',
  'priceCard.line.perUnit': 'Per unit',
  'priceCard.brandsOne': '{count} brand',
  'priceCard.brandsMany': '{count} brands',
  'priceCard.xotskiesValue': '{count} merch items',
  'priceCard.editorSeatOne': '{count} editor seat (transferable)',
  'priceCard.editorSeatMany': '{count} editor seats (transferable)',
  'priceCard.editorSeatsUnlimited': '{count} editor seats (transferable)',
  'priceCard.unlimitedReporters': 'Unlimited reporters (free)',
  'priceCard.tier1Games': 'Tier 1 branded games',
  'priceCard.tier2Games': 'Tier 2 game library included',
  'priceCard.realtime': 'Real-time dashboard + Smart inventory',
  'priceCard.enterpriseExtras': 'SSO + Audit log + API + Dedicated CSM',
  'priceCard.feature.notifications': 'Push notifications',
  'priceCard.feature.activityLog': 'Activity log',
  'priceCard.feature.stockImport': 'Merch CSV import',
  'priceCard.feature.aiAssistant': 'AI assistant',
  'priceCard.feature.userManagement': 'User management',

  // ── Upgrade dialog ─────────────────────────────────────────────────
  'upgradeDialog.title': "You've hit your {resource} limit",
  'upgradeDialog.body': 'Your {plan} plan includes {limit} {resource}. Upgrade to keep building.',
  'upgradeDialog.maybeLater': 'Maybe later',
  'upgradeDialog.seePlans': 'See plans',
  'upgradeDialog.resource.projects': 'projects',
  'upgradeDialog.resource.groups': 'event groups',
  'upgradeDialog.resource.events': 'events',
  'upgradeDialog.resource.stations': 'stations',
  'upgradeDialog.resource.stocks': 'merch items',
  'upgradeDialog.resource.brands': 'brands',
  'upgradeDialog.resource.users': 'users',
  'upgradeDialog.resource.roles': 'roles',
  'upgradeDialog.resource.surveys': 'journeys',

  // ── Billing page ───────────────────────────────────────────────────
  'billingPage.title': 'Billing',
  'billingPage.subtitle': 'Track usage, manage your plan and download invoices.',
  'billingPage.currentBadge': 'Current plan',
  'billingPage.currentTitle': 'Current plan',
  'billingPage.currentDesc': 'Plan details endpoint not yet exposed by backend.',
  'billingPage.changePlan': 'Change plan',
  'billingPage.currentRenews': 'Renews {date}',
  'billingPage.currentFree': 'Free plan · no renewal date. Upgrade whenever you need more events.',
  'billingPage.currentAnnual': 'Annual plan',
  'billingPage.currentPerYear': '{price} a year',
  'billingSuccess.confirming': 'Confirming your payment…',
  'billingSuccess.readyTitle': 'Payment received',
  'billingSuccess.readyBody': 'Your new plan is active. Taking you to Billing…',
  'billingSuccess.timeoutTitle': 'Still processing',
  'billingSuccess.timeoutBody': 'Stripe is taking longer than usual to confirm. Your invoice will appear in Billing shortly.',
  'billingSuccess.goToBilling': 'Go to Billing',
  'billingPage.usageTitle': 'Usage',
  'billingPage.usageDesc': "How much of your plan you've consumed.",
  'billingPage.invoicesTitle': 'Invoices',
  'billingPage.invoicesDesc': 'Your plan purchase history.',
  'billingPage.noInvoices': 'No invoices yet.',
  'billingPage.col.date': 'Date',
  'billingPage.col.plan': 'Plan',
  'billingPage.col.amount': 'Amount',
  'billingPage.col.status': 'Status',
  'billingPage.invoice.paid': 'Paid',
  'billingPage.invoice.pending': 'Pending',
  'billingPage.invoice.status.active': 'Active',
  'billingPage.invoice.status.expired': 'Expired',
  'billingPage.invoice.status.refunded': 'Refunded',
  'billingPage.invoice.status.exhausted': 'Exhausted',
  'billingPage.resource.brands': 'Brands',
  'billingPage.resource.users': 'Users',
  'billingPage.resource.roles': 'Roles',
  'billingPage.resource.surveys': 'Journeys',
  'billingPage.resource.responsesPerSurvey': 'Interactions / journey',
  'billingPage.resource.projects': 'Projects',
  'billingPage.resource.eventGroups': 'Event groups',
  'billingPage.resource.events': 'Events',
  'billingPage.resource.stations': 'Stations',
  'billingPage.resource.stocks': 'Xotskies',
  'billingPage.featuresTitle': 'Plan features',
  'billingPage.featuresDesc': "What's switched on for your account right now.",
  'billingPage.feature.notifications': 'Push notifications',
  'billingPage.feature.activeLog': 'Activity log',
  'billingPage.feature.aiAssistant': 'AI assistant',
  'billingPage.feature.stockImport': 'Merch CSV import',
  'billingPage.feature.userManagement': 'User management',
  'billingPage.gamesTitle': 'Allowed games',
  'billingPage.gamesDesc': 'Game mechanics included in your plan.',
  'billingPage.gamesEmpty': 'No game mechanics enabled on this plan.',

  // ── Checkout result ────────────────────────────────────────────────
  'checkoutResult.successTitle': "You're all set",
  'checkoutResult.successBody': 'Your plan upgrade is live and quotas have been refreshed.',
  'checkoutResult.seeBilling': 'See billing',
  'checkoutResult.backToDashboard': 'Back to dashboard',
  'checkoutResult.cancelTitle': 'Checkout cancelled',
  'checkoutResult.cancelBody': "No charges were made. You can pick another plan whenever you're ready.",
  'checkoutResult.seePlans': 'See plans',

  // ── Mock checkout page ─────────────────────────────────────────────
  'mockCheckout.paymentConfirmed': 'Payment confirmed',
  'mockCheckout.confirmationFailed': 'Confirmation failed',
  'mockCheckout.invalidSession': 'Invalid checkout session',
  'mockCheckout.backToPlans': 'Back to plans',
  'mockCheckout.badge': 'Mock checkout',
  'mockCheckout.planTitle': '{plan} plan',
  'mockCheckout.planDesc': "You'll be charged today and on the same day every month. Cancel anytime.",
  'mockCheckout.subtotal': 'Subtotal',
  'mockCheckout.tax': 'Tax',
  'mockCheckout.taxIncluded': 'Included',
  'mockCheckout.totalDue': 'Total due today',
  'mockCheckout.pay': 'Pay {amount}',
  'mockCheckout.cancelGoBack': 'Cancel and go back',
  'mockCheckout.mockEnv': 'Mock environment — no real money moves.',
  'mockCheckout.whatsIn': "What's in {plan}?",

  // ── Settings page (v2 list-style page) ─────────────────────────────
  'settings.comingSoon': 'Coming soon',
  'settings.section.accountDesc': 'Brand info, timezone, language',
  'settings.section.access': 'Access Manager',
  'settings.section.accessDesc': 'Invite members, assign roles, share access',
  'settings.section.brand': 'Brand',
  'tags.settingsSection': 'Tags',
  'tags.settingsSectionDesc': 'Tidy the tag vocabulary: rename, recolour and retire.',
  'settings.section.brandDesc': 'Brand profiles (identity), Brand Inventory opt-in and public page',
  'settings.section.workspaceType': 'Account type',
  'settings.section.workspaceDesc': 'Brand or agency, and what each one gets',
  'settings.workspace.title': 'Account type',
  'settings.workspace.subtitle': 'What kind of account this is. Chosen at sign-up and read by every Onxite product.',
  'settings.workspace.accountType': 'Account type',
  'settings.workspace.currentFor': 'For {name}.',
  'settings.workspace.current': 'Current',
  'settings.workspace.brand': 'Brand',
  'settings.workspace.brandBody': 'Runs activations for its own products. Opens straight into its brand, no switcher in the way.',
  'settings.workspace.agency': 'Agency',
  'settings.workspace.agencyBody': 'Runs activations for client brands. Holds many brands and gets the brand switcher in the top bar.',
  'settings.workspace.notRoot': 'Only the main account can change its account type.',
  'settings.workspace.keepsData': 'Changing the type keeps every project, event, brand and contact. An agency with more than one brand has to keep a single brand before it can become a brand account.',
  'settings.workspace.confirmTitle': 'Switch this account to {type}?',
  'settings.workspace.confirmToAgency': 'The account can hold several brands from now on and the brand switcher appears in the top bar. Nothing is deleted.',
  'settings.workspace.confirmToBrand': 'The account opens straight into its one brand and the switcher goes away. Nothing is deleted.',
  'settings.workspace.confirm': 'Switch',
  'settings.workspace.savedToast': 'Account type updated',
  'settings.workspace.errorToast': 'The account type could not be changed',
  'settings.brand.title': 'Brand settings',
  'settings.brand.subtitle': 'Brand profiles and per-brand settings. Affects the brand experience across Xotski.',
  'settings.brand.profilesTitle': 'Brand profiles',
  'settings.brand.profilesDescription': 'Every brand in your account with its identity: logo, palette and font. Edit any of them in Brand Studio; live surveys, games and posters paint themselves with it.',
  'settings.brand.profileEdit': 'Edit identity',
  'settings.brand.profileOpen': 'Open brand',
  'settings.brand.profileNoKit': 'No identity yet — set it up in Brand Studio.',
  'settings.brand.profileRoot': 'Account',
  'settings.brand.profileFont': 'Font',
  'settings.brand.profileNoIndustry': 'No industry',
  'settings.brand.swatchesLabel': 'Palette of {brand}',
  'settings.brand.swatchPrimary': 'Primary',
  'settings.brand.swatchSecondary': 'Secondary',
  'settings.brand.swatchAccent': 'Accent',
  'settings.brand.swatchBackground': 'Background',
  'settings.brand.swatchText': 'Text',
  'settings.brand.inventoryTitle': 'Brand Inventory',
  'settings.brand.inventoryDescription':
    'Two-tier inventory model (PRD §9): items live in a brand-level pool and pull into project warehouses. Off until you flip it on per brand.',
  'settings.brand.lockedBadge': 'Plan locked',
  'settings.brand.noBrands': 'No brands visible to your account yet.',
  'settings.brand.savedToast': 'Saved',
  'settings.brand.publicPageTitle': 'Public brand page',
  'settings.brand.publicPageDescription': 'A stable URL (/b/slug) you can print on QR codes today. It stays closed to the brand team and its agency until you open it; everyone else sees «not available yet» and a sign-in path. It paints with the Brand Studio kit.',
  'settings.brand.publicPageNoSlug': 'No public page yet: give it a slug.',
  'settings.brand.publicPageSlug': 'Public page slug for {brand}',
  'settings.brand.publicPageSlugPlaceholder': 'my-brand',
  'settings.brand.publicPageOpen': 'Open the public page of {brand}',
  'settings.brand.publicPageIsOpen': 'Open to everyone',
  'settings.brand.publicPageIsGated': 'Team only',
  'publicBrand.notFoundTitle': 'This page does not exist',
  'publicBrand.notFoundBody': 'Check the link or the QR code you followed.',
  'publicBrand.gatedTitle': 'Not available yet',
  'publicBrand.gatedBody': 'This brand page is being prepared. If you are part of the team, sign in to preview it.',
  'publicBrand.gatedSignedIn': 'Your account is not part of this brand’s team.',
  'publicBrand.signIn': 'Sign in',
  'publicBrand.openBody': 'Scan, play and claim your prize at the stand.',
  'publicBrand.cta': 'Find us at the event',
  'publicBrand.poweredBy': 'Powered by Xotski',
  'settings.brand.errorToast': 'Could not save change',
  'settings.section.billing': 'Billing',
  'settings.section.billingDesc': 'Plan, usage and invoices',
  'settings.section.followup': 'Follow-up emails',
  'settings.section.followupDesc': 'Templates, coupons and post-event nurture',
  'settings.section.integrations': 'CRM integrations',
  'settings.section.integrationsDesc': 'Connect HubSpot, Mailchimp or a webhook',
  'settings.section.crm': 'CRM integrations',
  'settings.section.crmDesc': 'Connect HubSpot, Salesforce or a webhook',
  'settings.section.api': 'API keys',
  'settings.section.apiDesc': 'Generate keys for programmatic access',
  'settings.section.notificationsDesc': 'Email alerts for inventory and events',
  'settings.section.activeLog': 'Active Log',
  'settings.section.activeLogDesc': 'Audit trail of every change in your account',

  // ── Insights page (v2 distribution overview) ───────────────────────
  'insights.resetFilters': 'Reset filters',
  'insights.filter.brand': 'Brand',
  'insights.filter.event': 'Event',
  'insights.filter.project': 'Project',
  // Deprecado: el filtro de clasificación de Insights se retiró en favor del
  // de proyecto. La clave se queda hasta la siguiente limpieza de i18n.
  'insights.filter.classification': 'Classification',
  'insights.filter.from': 'From',
  'insights.filter.to': 'To',
  'insights.filter.allBrands': 'All brands ({count})',
  'insights.filter.allEvents': 'All events ({count})',
  'insights.filter.allProjects': 'All projects ({count})',
  'insights.filter.all': 'All',
  'insights.filter.unclassified': 'Unclassified',
  'insights.kpi.distributed': 'Distributed',
  'insights.kpi.distributedHint': '{rate}% of allocated',
  'insights.kpi.remaining': 'Remaining on stations',
  'insights.kpi.remainingHint': '{total} allocated total',
  'insights.kpi.value': 'Distributed value',
  'insights.kpi.valueHint': 'Based on unit cost',
  'insights.kpi.activeEvents': 'Active events',
  'insights.kpi.activeEventsHintOne': '{brands} brand · {events} event in scope',
  'insights.kpi.activeEventsHintMany': '{brands} brands · {events} events in scope',
  'insights.restockAlertOne': '{count} station at or below restock threshold.',
  'insights.restockAlertMany': '{count} stations at or below restock threshold.',
  'insights.restockRefill': 'Refill before the next event window.',
  'insights.chart.byBrand': 'Distribution by brand',
  'insights.chart.topEvents': 'Top events by units distributed',
  'insights.chart.topStocks': 'Top 10 merch items (units distributed)',
  'insights.chart.byClassification': 'Distributed units by classification',
  'insights.chart.byRarity': 'Distributed units by rarity',
  'insights.chart.usedAvailable': 'Merch used vs available (top 10)',
  'insights.chart.used': 'Used',
  'insights.chart.available': 'Available',
  'insights.chart.empty': 'No data for the current filters',
  'insights.rarity.common': 'Common',
  'insights.rarity.uncommon': 'Uncommon',
  'insights.rarity.rare': 'Rare',
  'insights.rarity.epic': 'Epic',
  'insights.rarity.legendary': 'Legendary',
  'insights.rarity.tier': 'Tier {n}',
  'insights.unclassifiedLabel': 'Unclassified',

  // ── Contacts page (v2 list) ────────────────────────────────────────
  'contacts.responsesCount': '{count} interactions',
  'contacts.col.when': 'When',
  'contacts.col.survey': 'Journey',
  'contacts.col.station': 'Station',
  'contacts.col.source': 'Source',
  'contacts.col.prize': 'Prize',
  'contacts.noun': 'interactions',
  // ── Caso 01 — Leads CRM (real /co-contacts integration) ────────────
  'contacts.export': 'Export CSV',
  'contacts.applySearch': 'Search',
  'contacts.filter.consentOnly': 'Only with consent',
  'contacts.totalCount': '{count} contacts',
  'contacts.col.name': 'Name',
  'contacts.col.contact': 'Contact',
  'contacts.col.interactions': 'Visits',
  'contacts.col.prizes': 'Prizes',
  'contacts.col.lastSeen': 'Last seen',
  'contacts.col.consent': 'Consent',
  'contacts.anonymousName': 'Anonymous participant',
  'contacts.anonymousSnippet': 'No identity captured',
  'contacts.consent.yes': 'Opted in',
  'contacts.consent.no': 'Not opted in',
  'contacts.consent.sms': 'SMS',
  'contacts.detail.title': 'Contact detail',
  'contacts.detail.back': 'Back to contacts',
  'contacts.detail.invalidId': 'Invalid contact id.',
  'contacts.detail.delete': 'Delete (GDPR)',
  'contacts.detail.deleteConfirm':
    'Permanently scrub PII for {name}? This cannot be undone.',
  'contacts.detail.engagement.title': 'Engagement',
  'contacts.detail.engagement.interactions': 'Visits',
  'contacts.detail.engagement.prizes': 'Prizes won',
  'contacts.detail.engagement.firstSeen': 'First seen',
  'contacts.detail.engagement.lastSeen': 'Last seen',
  'contacts.detail.demographics.title': 'Demographics',
  'contacts.detail.demographics.age': 'Age',
  'contacts.detail.demographics.gender': 'Gender',
  'contacts.detail.demographics.city': 'City',
  'contacts.detail.demographics.state': 'State',
  'contacts.detail.demographics.zip': 'ZIP',
  'contacts.detail.demographics.language': 'Language',
  'contacts.detail.controls.title': 'Controls',
  'contacts.detail.controls.revokeConsent': 'Revoke marketing consent',
  'contacts.detail.controls.grantConsent': 'Grant marketing consent',
  'contacts.detail.controls.tags': 'Tags',
  'contacts.detail.controls.add': 'Add',
  'contacts.detail.controls.addTagPlaceholder': 'Add a tag…',
  'contacts.detail.controls.removeTag': 'Remove tag',
  'contacts.detail.timeline.title': 'Interactions ({count})',
  'contacts.detail.timeline.empty': 'No interactions captured yet.',
  'contacts.detail.timeline.atEvent': 'Event #{id}',
  'contacts.detail.timeline.unknownEvent': 'Unknown event',
  'contacts.detail.timeline.station': 'Station #{id}',
  'contacts.detail.timeline.prize': 'Prize #{id}',

  // ── Caso 02 — Event results report ─────────────────────────────────
  'eventReport.invalidId': 'Invalid event id.',
  'eventReport.backToEvent': 'Back to event',
  'eventReport.print': 'Print / Save PDF',
  'eventReport.printedOn': 'Report generated {date}',
  'eventReport.createShare': 'Create share link',
  'eventReport.shareCopied': 'Link copied to clipboard',
  'eventReport.publicFooter':
    'Aggregated results — no individual participant data is shared.',
  'eventReport.publicInvalid': 'Invalid share link.',
  'eventReport.publicNotFound.title': 'This report is not available',
  'eventReport.publicNotFound.body':
    'The link may have expired or been revoked. Ask the host for a fresh one.',
  'eventReport.reach.interactions': 'Interactions',
  'eventReport.reach.last24h': 'Last 24 h',
  'eventReport.reach.distinctContacts': 'Unique contacts',
  'eventReport.reach.leads': 'Leads captured',
  'eventReport.prizes.title': 'Prizes',
  'eventReport.prizes.allocated': 'Allocated',
  'eventReport.prizes.distributed': 'Distributed',
  'eventReport.prizes.remaining': 'Remaining',
  'eventReport.prizes.value': 'Distributed value',
  'eventReport.costs.title': 'Cost per outcome',
  'eventReport.costs.perInteraction': 'Per interaction',
  'eventReport.costs.perLead': 'Per lead',
  'eventReport.topAnswers.title': 'Top answers',
  'eventReport.demographics.title': 'Demographics',
  'surveyDemographics.title': 'Who we reached',
  'surveyDemographics.coverage': '{n} completed responses \u00b7 {links} deployments',
  'surveyDemographics.noLinks': 'No survey deployed on these stations yet.',
  'surveyDemographics.empty': 'No completed responses yet. The charts appear as soon as guests finish the journey.',
  'surveyDemographics.noChoice': 'These journeys ask no single-choice questions, so there is nothing to chart. Add age, gender, background or a yes/no question to a step.',
  'surveyDemographics.persona': 'The composite visitor',
  'surveyDemographics.personaNote': 'The most frequent answer to each demographic question, side by side.',
  'surveyDemographics.per100': '{label} \u00b7 100 people',
  'surveyDemographics.largest': 'Largest bracket: {label}, {pct}% of answers.',
  'surveyDemographics.lean': '{label} lean',
  'surveyDemographics.even': 'Even split',
  'surveyDemographics.sayYes': 'say yes',
  'surveyDemographics.answered': '{n} answered',
  'surveyDemographics.multiple': 'several answers allowed',
  'surveyDemographics.other': 'Other',
  'surveyDemographics.fromSurvey': 'from \u201c{name}\u201d',

  // ── Caso 03 — Prize redemption (operator screen + stats) ───────────
  'redemption.title': 'Redeem a prize',
  'redemption.subtitle': 'Type or scan the consumer\'s code, then confirm delivery.',
  'redemption.codeLabel': 'Redemption code',
  'redemption.lookUp': 'Look up',
  'redemption.scan': 'Scan',
  // Caso 11 v2 — camera-based QR scanner overlay.
  'operator.scanner.title': 'Scan QR code',
  'operator.scanner.hint': 'Point the camera at the consumer QR.',
  'operator.scanner.unsupported':
    "This browser doesn't support camera-based scanning. Type the code manually instead.",
  'operator.scanner.cameraError':
    'Could not access the camera. Check the permission and try again.',
  'redemption.preview.title': 'Prize ready to deliver',
  'redemption.preview.code': 'Code',
  'redemption.preview.stockId': 'Prize',
  'redemption.preview.station': 'Station',
  'redemption.preview.won': 'Won at',
  'redemption.preview.expires': 'Expires',
  'redemption.preview.confirm': 'Confirm delivery',
  'redemption.preview.confirming': 'Confirming…',
  'redemption.preview.state.delivered': 'Already redeemed.',
  'redemption.preview.state.expired': 'This redemption has expired.',
  'redemption.preview.state.voided': 'This redemption has been voided.',
  'redemption.success.title': 'Delivered!',
  'redemption.success.body': 'Code {code} confirmed.',
  'redemption.error.generic': 'Could not redeem. Try again.',
  'redemption.error.tooShort': 'Codes are 6+ characters.',
  'redemption.stats.title': 'Prize redemption',
  'redemption.stats.won': 'Won',
  'redemption.stats.delivered': 'Delivered',
  'redemption.stats.expired': 'Expired',
  'redemption.stats.rate': 'Rate',

  // ── Caso 04 — Post-event follow-up settings ────────────────────────
  'followup.title': 'Follow-up email',
  'followup.subtitle':
    'Configure the email sent to opted-in participants after their event.',
  'followup.enabled': 'Active',
  'followup.disabled': 'Off',
  'followup.newTemplate': 'New template',
  'followup.confirmDelete': 'Delete this template?',
  'followup.templates.title': 'Templates',
  'followup.templates.description':
    'The most specific match wins per dispatch: event-scoped first, then brand-wide.',
  'followup.templates.empty':
    'No templates yet. Create one to start nurturing.',
  'followup.editor.newTitle': 'New template',
  'followup.editor.editTitle': 'Edit template',
  'followup.editor.name': 'Name',
  'followup.editor.nameRequired': 'Name is required — Save unlocks when you fill this in.',
  'followup.editor.scope': 'Apply to',
  'followup.editor.scopeHint': 'Event-specific templates win over brand-wide for that event.',
  'followup.scope.brandWide': 'All events (brand-wide)',
  'followup.scope.event': 'Event',
  'followup.toast.created': 'Template created',
  'followup.toast.updated': 'Template updated',
  'followup.toast.deleted': 'Template deleted',
  'followup.toast.saveError': 'Could not save template',
  'followup.toast.deleteError': 'Could not delete template',
  'followup.editor.subject': 'Subject',
  'followup.editor.body': 'Body (HTML)',
  'followup.editor.variables':
    'Variables: {{firstName}} {{lastName}} {{prizeName}} {{couponCode}} {{eventName}} {{unsubscribeUrl}}',
  'followup.editor.delay': 'Send delay (hours)',
  'followup.defaults.subject': 'Thanks for stopping by!',
  'followup.defaults.bodyHtml':
    '<p>Hi {{firstName}},</p><p>Thanks for visiting us at {{eventName}}. Use code <strong>{{couponCode}}</strong> next time you shop.</p><p><a href="{{unsubscribeUrl}}">Unsubscribe</a></p>',

  // ── Caso 14 — First-event onboarding checklist ────────────────────
  'firstSteps.title': 'Get to your first activation',
  'firstSteps.step.brand.title': 'Add your brand',
  'firstSteps.step.brand.description': 'Logo + colors so every screen carries your identity.',
  'firstSteps.step.brand.cta': 'Add brand',
  'firstSteps.step.event.title': 'Create your first event',
  'firstSteps.step.event.description': 'Dates, location, and which brand it belongs to.',
  'firstSteps.step.event.cta': 'Create event',
  'firstSteps.step.station.title': 'Add a station',
  'firstSteps.step.station.description': 'A booth or touchpoint at the event. Generates the QR.',
  'firstSteps.step.identity.title': 'Make it yours',
  'firstSteps.step.identity.description':
    'Add your logo and colours so the survey your guests see looks like your brand. Optional — it works without this.',
  'firstSteps.step.identity.cta': 'Personalise',
  'firstSteps.step.station.cta': 'Add station',
  'common.dismiss': 'Dismiss',

  // ── Caso 14 v1 — Modal guided wizard ───────────────────────────
  'firstEventWizard.title': "Let's launch your first Xotski",
  'firstEventWizard.subtitle':
    "Three steps and you're live: brand, event, station.",
  'firstEventWizard.progress': 'Setup progress',
  'firstEventWizard.dontShowAgain': "Don't show this again",
  'firstEventWizard.step.brand.title': 'Create your brand',
  'firstEventWizard.step.brand.description':
    'Logo, colors and tone — the look every journey inherits.',
  'firstEventWizard.step.brand.cta': 'Open brand studio',
  'firstEventWizard.step.event.title': 'Create your first event',
  'firstEventWizard.step.event.description':
    'A live activation: when and where it runs.',
  'firstEventWizard.step.event.cta': 'Create event',
  'firstEventWizard.step.station.title': 'Add a station and print the QR',
  'firstEventWizard.step.station.description':
    'A booth, kiosk, or touchpoint where guests scan to play.',
  'firstEventWizard.step.station.cta': 'Add station',
  // Caso 14 v2 — inline brand creation.
  'firstEventWizard.brand.nameLabel': 'Brand name',
  'firstEventWizard.brand.namePlaceholder': 'e.g. Atledi',
  'firstEventWizard.brand.createSubmit': 'Create brand',
  'firstEventWizard.brand.openStudio': 'Open the full Brand Studio →',
  'firstEventWizard.brand.created': 'Brand created.',
  'firstEventWizard.brand.createFailed': "Couldn't create the brand.",

  // ── Project detail page ────────────────────────────────────────────
  'project.status.planning': 'Planning',
  'project.status.live': 'Live',
  'project.status.wrapped': 'Wrapped',
  'project.backToBrand': 'Back to brand',
  'project.editProject': 'Edit project',
  'project.eventsCardTitle': 'Events',
  'project.newEvent': 'New event',
  'project.viewTrends': 'View trends',

  // ── Caso 12 parte 2 — Event-to-event trends ─────────────────────
  'trends.title': 'Event trends',
  'trends.subtitle':
    'Compare how each activation performed side-by-side.',
  'trends.empty': 'Not enough events to compare yet.',
  'trends.chart.title': 'Metrics per event',
  'trends.chart.subtitle':
    'Bars are grouped by event so you can spot week-over-week patterns at a glance.',
  'trends.table.title': 'Detail table',
  'trends.table.event': 'Event',
  'trends.table.date': 'Date',
  'trends.table.status': 'Status',
  'trends.metric.responses': 'Interactions',
  'trends.metric.leads': 'Leads',
  'trends.metric.prizes': 'Prizes delivered',
  'trends.metric.prizesDistributed': 'Prizes distributed',
  'project.field.eventName': 'Event name',
  'project.field.eventNamePlaceholder': 'Brooklyn Block Party',
  'project.field.address': 'Address',
  'project.field.addressPlaceholder': '123 Wythe Ave, Brooklyn NY',
  'project.field.starts': 'Starts',
  'project.field.ends': 'Ends',
  'project.field.sections': 'Stations',
  'project.field.codeRef': 'Code ref',
  'project.field.codeRefPlaceholder': 'LOLL26',
  'project.createEvent': 'Create event',
  'project.eventsEmpty': 'No events yet. Add one to start placing stations.',
  'project.noAddress': 'No address',
  'project.tbd': 'TBD',
  'project.noun.events': 'events',
  'project.notFound': 'Project not found',
  'project.eventCreatedToast': 'Event created',
  'project.eventCreateFailedToast': 'Create failed',
  'project.updatedToast': 'Project "{name}" updated',
  'project.updateFailedToast': 'Could not update project',
  'project.deletedToast': 'Project "{name}" deleted',
  'project.deleteFailedToast': 'Could not delete project',
  'project.edit.title': 'Edit project',
  'project.edit.description': 'Update the project name, code, status and dates. Events stay attached.',
  'project.edit.field.name': 'Project name',
  'project.edit.field.codeRef': 'Code reference',
  'project.edit.field.codeRefPlaceholder': 'JST26',
  'project.edit.field.status': 'Status',
  'project.edit.note': 'project dates are derived from the events below. Edit individual events to change the schedule.',
  'project.edit.noteLabel': 'Note:',
  'project.edit.field.startDate': 'Project starts',
  'project.edit.field.endDate': 'Project ends',
  'project.edit.rangeNoteLabel': 'Date range:',
  'project.edit.rangeNote': 'this window governs the dates allowed on every event in the project. Leave empty to derive it from the events.',
  'project.edit.rangeInvalid': 'Project end must be after the start.',
  // Project workspace tabs
  'project.tab.plan': 'Plan',
  'project.tab.track': 'Track',
  'project.tab.measure': 'Report',
  // The event status badge — the column holds Active/Disabled/Finished and was
  // painted raw, so it stayed English in the Spanish app.
  'events.status.active': 'Active',
  'events.status.disabled': 'Disabled',
  'events.status.finished': 'Finished',
  // PLAN tab — Planner
  'planner.xotskiCarousel.title': 'Merch',
  'planner.xotskiCarousel.empty': 'No merch in this brand yet.',
  'planner.xotskiCarousel.availableTip': '{n} available in the brand pool',
  'planner.xotskiCarousel.outOfStock': 'Out of stock — restock to allocate it',
  'planner.xotskiCarousel.out': 'Out of stock',
  'planner.stationSource.label': '+ Station',
  'planner.reporters.title': 'Reporters',
  'planner.reporters.emailPlaceholder': 'email@domain.com',
  'planner.reporters.namePlaceholder': 'Name (optional)',
  'planner.reporters.inviteToSelected': 'Invite to selected event',
  'planner.reporters.selectEventFirst': 'Select an event to invite',
  'planner.reporters.empty': 'No reporters yet. Invite one above or drag onto an event.',
  'planner.reporters.dragHint': 'Drag onto an event to (re)invite',
  'planner.reporters.invitedToast': 'Reporter invited',
  'planner.reporters.inviteFailedToast': 'Could not invite reporter',
  'planner.reporters.statusConfirmed': 'Confirmed',
  'planner.reporters.statusPending': 'Pending',
  'planner.reporters.statusRevoked': 'Revoked',
  'planner.reporters.statusExpired': 'Expired',
  'planner.reporters.remove': 'Remove reporter',
  'planner.reporters.removed': 'Reporter removed',
  'planner.reporters.removeFailed': 'Could not remove reporter',
  'planner.alloc.available': '{n} left',
  'planner.alloc.increase': 'Increase quantity',
  'planner.alloc.decrease': 'Decrease quantity',
  'planner.alloc.atCeiling': 'No more units available in the brand',
  'planner.alloc.remove': 'Remove from event',
  'planner.alloc.removed': 'Merch removed from event',
  'planner.alloc.removeFailed': 'Could not remove merch',
  'planner.alloc.qtyFailed': 'Could not update quantity',
  'planner.alloc.qtyInput': 'Quantity — type a number or use the buttons',
  'planner.alloc.availableOf': '{available} of {quantity} left',
  'planner.alloc.soldOut': 'Sold out',
  'planner.alloc.hidden': 'Hidden from games',
  'planner.alloc.drawer.open': 'Allocate prizes',
  'planner.alloc.drawer.title': 'Allocate prizes · {event}',
  'planner.alloc.drawer.hint': 'Units per prize and station, committed out of the brand pool. Increases are capped by what the brand has left; 0 removes the allocation. A cell with several rows for the same prize is edited on the station.',
  'planner.alloc.drawer.prize': 'Prize',
  'planner.alloc.drawer.left': '{n} left in the brand',
  'planner.alloc.drawer.over': 'Over by {n}',
  'planner.alloc.drawer.multi': '{n} rows · edit on the station',
  'planner.alloc.drawer.noStations': 'This event has no stations yet. Add one on the board first.',
  'planner.alloc.drawer.noStock': 'The brand has no merch yet. Add it in',
  'planner.alloc.drawer.changes': '{n} change(s)',
  'planner.alloc.drawer.apply': 'Apply',
  'planner.alloc.drawer.applied': '{n} allocation(s) updated',
  'planner.alloc.drawer.failed': 'Some allocations could not be saved',
  'planner.alloc.cooling': 'Cooling down',
  'planner.alloc.settings': 'Prize settings',
  'planner.alloc.availableInput': 'Units left',
  'planner.alloc.mode.label': 'How it comes up in the games',
  'planner.alloc.mode.weight': 'By weight',
  'planner.alloc.mode.cooldown': 'Every N minutes',
  'planner.alloc.mode.weightHint': 'Rarity 1 = most common (weight 25) · 5 = rarest (weight 1)',
  'planner.alloc.mode.cooldownHint': 'It cannot come up again until this many minutes have passed since it was last won.',
  'planner.alloc.mode.cooldownMinutes': 'Minutes between wins',
  'planner.alloc.hideFromGames': 'Take out of the games',
  'planner.alloc.showInGames': 'Put back in the games',
  'planner.alloc.saveFailed': 'Could not save the prize settings',
  'planner.station.refresh': 'Refresh availability',
  'planner.station.refreshed': 'Availability refreshed',
  'planner.station.actions': 'Station actions',
  'planner.events.actions': 'Event actions',
  'planner.properties.open': 'Properties',
  // Per-station inline allocation editor
  'planner.station.expand': 'Expand station',
  'planner.station.collapse': 'Collapse station',
  'planner.station.sliceCount': '{count} slices',
  'planner.station.empty': 'No merch here yet. Drop one on the event to fill every station.',
  'planner.station.drag': 'Drag to reorder',
  'planner.station.duplicate': 'Duplicate as another slice',
  'planner.station.duplicated': 'Slice duplicated',
  'planner.station.duplicateFailed': 'Could not duplicate slice',
  'planner.station.reorderFailed': 'Could not save the new order',
  'planner.station.preview': 'Preview the game',
  'planner.station.previewTitle': 'Preview · {name}',
  'planner.station.previewNoSurvey': "No guest journey yet — design one on the project's Design Journey tab and it applies here.",
  'planner.alloc.groupedHint': '{n} allocations · {total} units total. Edit per-station quantities in the station detail.',
  'planner.alloc.removeGroupTitle': 'Remove “{name}” from this event?',
  'planner.alloc.removeGroupBody': 'This removes all {n} allocations of “{name}” across this event’s stations ({total} units total) and takes it off their games. The units return to the brand pool; past wins are kept. To remove it from a single station instead, use that station’s detail.',
  'planner.properties.title': 'Event properties',
  'planner.properties.noSelection': 'Select an event to edit its properties.',
  'planner.properties.savedToast': 'Event saved',
  'planner.properties.saveFailedToast': 'Could not save event',
  'planner.properties.endAfterStart': 'End must be after start.',
  'planner.properties.outOfRange': 'Dates must fall within the project range.',
  'planner.properties.save': 'Save',
  'planner.properties.sectionsReadonly': 'Read-only — equals the number of stations on this event',
  'planner.properties.sectionsHint': 'read-only',
  'planner.properties.surveyTemplate': 'Journey starting point',
  'planner.properties.pickTemplate': 'Pick a starting point…',
  'planner.properties.applyingTemplate': 'Applying…',
  'planner.events.title': 'Events in planning',
  'planner.events.empty': 'No events to plan. Use “New events” to create some.',
  'planner.events.collapse': 'Collapse',
  'planner.events.expand': 'Expand',
  'planner.events.openConfig': 'Open config',
  'planner.events.merch': 'Merch',
  'planner.events.reporters': 'Reporters',
  'planner.events.dropXotskiHint': 'Drag a merch item here to allocate it.',
  'planner.events.xotskiRollupEmpty': 'No merch yet — drop one on a station below to add it.',
  'planner.events.dropReporterHint': 'Drag a reporter here to invite them.',
  'planner.events.stationCount': '{count} station(s)',
  'planner.events.dropStationHint': 'Drag the “+ Station” chip here to add one.',
  'planner.events.openStationConfig': 'Open station configuration',
  'planner.events.removeStation': 'Remove station',
  'planner.events.lastStation': 'An event must keep at least one station',
  'planner.events.stationRemoved': 'Station removed',
  'planner.events.stationRemoveFailed': 'Could not remove station',
  'planner.phase.live': 'Live',
  'planner.phase.upcoming': 'Upcoming',
  'planner.phase.edit': 'In edit',
  'planner.phase.done': 'Completed',
  'eventForm.stationNote': 'The event is born with its first station, ready to receive answers. That station counts towards your plan.',
  'planner.spreadAll.cta': 'Spread my prizes here',
  'planner.spreadAll.done': 'Prizes spread across this event ({n} of them).',
  'planner.spreadAll.nothing': 'Nothing to spread: no free merch, or every station already has these prizes.',
  'planner.spreadAll.noStock': 'You have no prizes yet: add one under My Merch.',
  'planner.newEvent': 'New event',
  'planner.batch.open': 'Several at once',
  'planner.batch.title': 'Create multiple events',
  'planner.batch.description': 'Pick how many events to create. Names and dates are filled in automatically and each event gets one station.',
  'planner.batch.countLabel': 'How many events?',
  'planner.batch.noRange': 'no project range set',
  'planner.batch.previewNames': 'Names: {first} … {last}',
  'planner.batch.previewRange': 'Dates spread across {range}',
  'planner.batch.previewStation': 'Each event starts with one station.',
  'planner.batch.cancel': 'Cancel',
  'planner.batch.create': 'Create events',
  'planner.batch.createdToast': '{count} event(s) created',
  'planner.batch.failedToast': 'Could not create events',
  'planner.drop.xotskiDone': '{name} allocated to all stations',
  'planner.drop.xotskiToStation': '{name} allocated to this station',
  'planner.drop.xotskiUseStation': 'Drop the merch item onto a station to allocate it.',
  'planner.drop.xotskiOnStationExists': '{name} is already on this station — use the +/- or duplicate it',
  'planner.drop.reporterDone': '{name} invited',
  'planner.drop.stationDone': 'Station added',
  'planner.drop.failed': 'Drop failed',
  'planner.drop.noStations': 'This event has no stations yet.',
  'planner.drop.allStationsFailed': 'Could not allocate to any station.',
  'planner.drop.partial': 'Allocated, but {failed} of {total} stations failed.',
  'planner.drop.here': 'Drop here',
  'planner.drop.xotskiExists': '{name} is already in this event — use +/- to change quantity',
  'planner.drop.reporterExists': '{name} is already invited to this event',
  'sharedQr.pick.title': 'Where are you?',
  'sharedQr.pick.body': 'Pick your station to start.',
  'sharedQr.loading.title': 'Loading…',
  'sharedQr.loading.body': 'Fetching the stations for this QR.',
  'sharedQr.invalid.title': 'Invalid QR',
  'sharedQr.invalid.body': 'This shared QR is not valid or has been removed.',
  'sharedQr.empty.title': 'No stations',
  'sharedQr.empty.body': 'This QR has no stations assigned yet.',
  'sharedQr.manager.title': 'Shared QR',
  'sharedQr.manager.subtitle': 'One QR for several stations — the consumer picks their station first.',
  'sharedQr.manager.create': 'Create shared QR',
  'sharedQr.manager.empty': 'No shared QRs yet for this event.',
  'sharedQr.manager.copy': 'Copy link',
  'sharedQr.manager.copied': 'Link copied',
  'sharedQr.manager.delete': 'Delete',
  'sharedQr.manager.removed': 'Shared QR deleted',
  'sharedQr.manager.removeFailed': 'Could not delete the shared QR',
  'sharedQr.manager.dialogTitle': 'Create a shared QR',
  'sharedQr.manager.dialogBody': 'Pick 2+ stations to share one QR. Each keeps its own merch.',
  'sharedQr.manager.nameLabel': 'Name',
  'sharedQr.manager.defaultName': 'Shared QR',
  'sharedQr.manager.pickStations': 'Stations',
  'sharedQr.manager.needTwo': 'This event needs at least 2 stations to share a QR.',
  'sharedQr.manager.cancel': 'Cancel',
  'sharedQr.manager.createBtn': 'Create',
  'sharedQr.manager.createdToast': 'Shared QR created',
  'sharedQr.manager.createFailed': 'Could not create the shared QR',
  // TRACK tab
  'track.empty': 'No events in this project yet.',
  'track.noDate': 'No date set',
  'track.edit': 'Edit',
  'track.view': 'View',
  'track.group.live': 'Live now',
  'track.group.upcoming': 'Upcoming',
  'track.group.edit': 'In edit',
  'track.group.done': 'Completed',
  // MEASURE tab
  'measure.range': 'Date range',
  'measure.events': 'Events',
  'measure.live': 'Live now',
  'measure.stockChartTitle': 'Merch across the project',
  'measure.noStockData': 'No merch has been handed to a station yet.',
  'measure.quantity': 'Allocated',
  'measure.available': 'Available',
  'project.edit.confirmTitle': 'Delete this project?',
  'project.edit.confirmBody': 'All of its events, stations and merch allocations will be removed permanently.',
  'project.edit.deleting': 'Deleting…',
  'project.edit.confirmDelete': 'Confirm delete',
  'project.edit.deleteProject': 'Delete project',
  'project.edit.cancel': 'Cancel',
  'project.edit.saving': 'Saving…',
  'project.edit.saveChanges': 'Save changes',

  // ── Access Manager ─────────────────────────────────────────────────
  'accessManager.title': 'Access Manager',
  'accessManager.scope': 'scope: {app}',
  'accessManager.subtitle': 'Manage members, roles, and external access grants for your account and child agencies. Listings and new entries are scoped to the {app} app — switch app to manage other modules.',
  'accessManager.tab.members': 'Members',
  'accessManager.tab.roles': 'Roles',
  'accessManager.tab.grants': 'External Sharing',
  'accessManager.currentApp': 'current',
  'accessManager.noAccess.title': 'No access',
  'accessManager.noAccess.body': 'You don’t have permission to view members, roles, or external sharing. Ask your administrator to grant you at least one.',

  // ── Members tab ────────────────────────────────────────────────────
  'members.title': 'Members',
  'memberForm.titleAdd': 'Add member',
  'memberForm.titleEdit': 'Edit member',
  'memberForm.subtitle': 'New members are created in {company}. The role you pick decides which company subtree they can actually see.',
  'memberForm.firstName': 'First name',
  'memberForm.lastName': 'Last name',
  'memberForm.email': 'Email',
  'memberForm.phone': 'Phone (optional)',
  'memberForm.password': 'Initial password (optional — defaults to system invitation flow)',
  'memberForm.role': 'Role',
  'memberForm.noRole': 'No role',
  'memberForm.roleHint': "The role's company scope is what restricts this user's visibility on login.",
  'memberForm.submitCreate': 'Create member',
  'memberForm.submitSave': 'Save',
  'memberForm.errRequired': 'Email and name are required',
  'memberForm.errNoWorkspace': 'Missing account context — sign in again',
  'memberForm.created': 'Member created',
  'memberForm.existed': 'That person already had an account — app access ensured',
  'memberForm.updated': 'Member updated',
  'members.addMember': 'Add member',
  'members.empty.title': 'No members yet',
  'members.empty.body': 'Add the first team member to your account.',
  'members.confirmDelete': 'Soft-delete {email}?',
  'members.deletedToast': 'Member removed',
  'members.col.name': 'Name',
  'members.col.email': 'Email',
  'members.col.company': 'Brand',
  'members.col.role': 'Role',
  'members.col.actions': 'Actions',
  'members.youBadge': 'you',
  'members.openRoleTitle': 'Open role "{role}" in the Roles tab',
  'members.cantDeleteSelf': "You can't delete your own account",
  'members.setPassword.action': 'Set password',
  'members.setPassword.title': 'Set password',
  'members.setPassword.description': "Set a new password for {name}. They'll use it on their next sign-in.",
  'members.setPassword.newPassword': 'New password',
  'members.setPassword.confirm': 'Confirm password',
  'members.setPassword.submit': 'Set password',
  'members.setPassword.show': 'Show password',
  'members.setPassword.hide': 'Hide password',
  'members.setPassword.minLength': 'Password must be at least {n} characters.',
  'members.setPassword.mismatch': "Passwords don't match.",
  'members.setPassword.successToast': 'Password set for {name}',
  'members.setPassword.errorToast': 'Could not set password',

  // ── Roles tab ──────────────────────────────────────────────────────
  'roles.title': 'Roles',
  'roles.createRole': 'Create role',
  'roles.empty.title': 'No roles yet',
  'roles.empty.body': 'Create roles to assign reusable permission bundles to your members.',
  'roles.confirmDelete': 'Delete role {name}?',
  'roles.deletedToast': 'Role deleted',
  'roles.templateBadge': 'template',
  'roles.permsCount': '{count} permission(s)',
  'roles.cloneTitle': 'Create a new role with these same permissions',
  'roles.clone': 'Clone',
  'roles.readOnlyTitle': 'System templates are read-only. Clone to customize.',
  'roles.readOnlyLabel': 'Read-only template',

  // ── Grants tab ─────────────────────────────────────────────────────
  'grants.title': 'External Sharing',
  'grants.comingSoonNotice': 'External Sharing is coming soon. This feature is not yet available — stay tuned!',
  'grants.shareAccess': 'Share access',
  'grants.confirmRevoke': 'Revoke access shared with {email}?',
  'grants.revokedToast': 'Grant revoked',
  'grants.tab.outgoing': 'Granted by me',
  'grants.tab.incoming': 'Granted to me',
  'grants.empty.outgoingTitle': 'No active grants',
  'grants.empty.incomingTitle': 'No incoming grants',
  'grants.empty.outgoingBody': 'Share access to one of your companies with an external user.',
  'grants.empty.incomingBody': 'External parties have not shared access with you yet.',
  'grants.user': 'user #{id}',
  'grants.expires': 'expires {date}',
  'grants.permsCount': '{count} permission(s)',
  'grants.grantedBy': 'granted by {email}',
  'grants.revoke': 'Revoke',

  // ── Brand Identity page (additional descriptive variants) ──────────
  'brandIdentity.subtitleLong': "Brand-level defaults for logo, colors, font and campaign name. Per-event customizations (data collection, age gate, game, prizes) live on each event's Design Journey.",
  'brandIdentity.saveChanges': 'Save changes',
  'brandIdentity.savedToast': 'Brand identity saved',
  'brandIdentity.saveFailedToast': 'Save failed',

  // ── Brand Studio (new) ─────────────────────────────────────────────
  'brandStudio.title': 'Brand Studio',
  'brandStudio.editing': 'Editing',
  'brandStudio.newBrandInherit': 'New brand · inherited from',
  'brandStudio.back': 'Back',
  'brandStudio.deleting': 'Deleting…',
  'brandStudio.confirmDelete': 'Confirm delete',
  'brandStudio.deleteBrand': 'Delete brand',
  'brandStudio.cancel': 'Cancel',
  'brandStudio.saving': 'Saving…',
  'brandStudio.saveChanges': 'Save changes',
  'brandStudio.savePublish': 'Save & Publish',
  'brandStudio.confirmDeleteTitle': 'Delete this brand?',
  'brandStudio.confirmDeleteBody': 'All of its projects, events, stations, merch and journeys will be removed permanently. Click',
  'brandStudio.confirmDeleteEmph': 'Confirm delete',
  'brandStudio.confirmDeleteEnd': 'again to proceed.',
  'brandStudio.brandCreated': 'Brand "{name}" created',
  'brandStudio.brandUpdated': 'Brand "{name}" updated',
  'brandStudio.brandDeleted': 'Brand "{name}" deleted',
  'brandStudio.createFailed': 'Could not create brand',
  'brandStudio.updateFailed': 'Could not update brand',
  'brandStudio.kitSaveFailed': 'Brand saved but the kit (colors / experience defaults) could not be persisted',
  'brandStudio.deleteFailed': 'Could not delete brand',
  'brandStudio.error.nameRequired': 'Brand name is required',
  'brandStudio.error.primaryHex': 'Primary color must be a hex value (#RGB or #RRGGBB)',
  'brandStudio.error.secondaryHex': 'Secondary color must be a hex value',
  'brandStudio.error.textHex': 'Text color must be a hex value',
  'brandStudio.fixOne': 'Fix {count} issue before saving:',
  'brandStudio.fixMany': 'Fix {count} issues before saving:',
  'brandStudio.tab.identity': 'Identity',
  'brandStudio.tab.experience': 'Experience',
  'brandStudio.tab.lookFeel': 'Look & Feel',
  'brandStudio.tab.game': 'Game',
  'brandStudio.tab.prizes': 'Prizes',
  'brandStudio.tab.output': 'Output',
  'brandStudio.tab.notSaved': 'Not saved yet',
  'brandStudio.tab.notSavedHint': 'This tab has no place to persist yet. Set these per event in Design Journey.',
  'brandStudio.lookFeel.partialNote': 'Only the celebration is saved with the brand today. Button style, consent wording, game CTA and the post-claim redirect are set per event in Design Journey.',
  'brandStudio.livePreview': 'Live preview',
  'brandStudio.previewFooter': 'The preview updates as you change settings — journey welcome, game CTA, and prize reveal screens all share these styles.',
  // Caso 05 v1 — Game tab celebration simulation
  'brandStudio.game.celebration': 'Celebration animation',
  'brandStudio.game.test': 'Test celebration',
  'brandStudio.game.themeHint.confetti': 'Bursting paper rain — feels celebratory.',
  'brandStudio.game.themeHint.fireworks': 'Bigger payoff — radial explosions.',
  'brandStudio.game.themeHint.sparks': 'Subtle shimmer — minimal & elegant.',
  // Identity tab
  'brandStudio.identity.hint': 'Upload your logo first — in the future Atledi will auto-suggest brand colors from it. For now, set your 5-color palette below.',
  'brandStudio.identity.brandName': 'Brand name',
  'brandStudio.identity.brandNameHint': 'What this brand will appear as in Xotski.',
  'brandStudio.identity.brandNamePlaceholder': 'e.g. Joyly',
  'brandStudio.identity.industry': 'Industry',
  'brandStudio.identity.industryHint': 'Optional. Helps Atledi tailor templates.',
  'brandStudio.identity.industryPlaceholder': 'Food & Beverage',
  'brandStudio.identity.campaignName': 'Campaign name',
  'brandStudio.identity.campaignNameHint': 'Used in QR URLs, station labels and reports.',
  'brandStudio.identity.campaignNamePlaceholder': 'e.g. Joyly Spring Tour 2026',
  'brandStudio.identity.brandLogo': 'Brand logo',
  'brandStudio.identity.brandLogoHint': 'Inherited from the company until overridden.',
  'brandStudio.identity.noLogo': 'No logo',
  'brandStudio.identity.qrThumb': 'QR thumbnail',
  'brandStudio.identity.qrThumbHint': 'Square image inside QR codes. Falls back to Brand logo.',
  'brandStudio.identity.noThumb': 'No thumb',
  'brandStudio.identity.backgroundImage': 'Background image',
  'brandStudio.identity.backgroundImageHint': 'Full-bleed photo behind the journey. Empty = solid background color.',
  'brandStudio.identity.noImage': 'No image',
  'brandStudio.identity.palette': '5-color palette',
  'brandStudio.identity.color.primary': 'Primary',
  'brandStudio.identity.color.primaryDesc': 'Headers, QR background, main surfaces',
  'brandStudio.identity.color.secondary': 'Secondary',
  'brandStudio.identity.color.secondaryDesc': 'Supporting surfaces, borders, dividers',
  'brandStudio.identity.color.accent': 'Accent',
  'brandStudio.identity.color.accentDesc': 'Buttons, active states, QR foreground',
  'brandStudio.identity.color.background': 'Background',
  'brandStudio.identity.color.backgroundDesc': 'Card and container backgrounds',
  'brandStudio.identity.color.text': 'Text',
  'brandStudio.identity.color.textDesc': 'Primary text on brand surfaces',
  'brandStudio.identity.brandFont': 'Brand font',
  // Experience tab
  'brandStudio.experience.hint': 'Bilingual journeys see up to 34% higher completion at multicultural events.',
  'brandStudio.experience.surveyLanguage': 'Journey language',
  'brandStudio.experience.english': 'English',
  'brandStudio.experience.spanish': 'Español',
  'brandStudio.experience.bilingualHint': 'Bilingual mode — users pick their language at start',
  'brandStudio.experience.contactInfo': 'Contact info',
  'brandStudio.experience.collectContact': 'Collect contact data from attendees',
  'brandStudio.experience.fullName': 'Full name',
  'brandStudio.experience.email': 'Email',
  'brandStudio.experience.phone': 'Phone',
  'brandStudio.experience.ageGate': 'Age gate',
  'brandStudio.experience.disabled': 'Disabled',
  'brandStudio.experience.demographics': 'Demographics',
  'brandStudio.experience.ageRange': 'Age range',
  'brandStudio.experience.gender': 'Gender',
  'brandStudio.experience.ethnicity': 'Ethnicity',
  'brandStudio.experience.nationality': 'Nationality',
  'brandStudio.experience.pronouns': 'Pronouns',
  'brandStudio.experience.customQuestions': 'Custom questions',
  'brandStudio.experience.customQuestionsLabel': '{count} custom question(s) added',
  'brandStudio.experience.customQuestionsAccessOn': 'Plan allows custom questions. Add them in Phase 4 question editor.',
  'brandStudio.experience.customQuestionsAccessOff': 'Upgrade to add custom questions to the consumer flow.',
  // Look & Feel tab
  'brandStudio.look.hint': 'Consistent button styles and celebration themes build brand recognition.',
  'brandStudio.look.buttonStyle': 'Button style',
  'brandStudio.look.next': 'Next',
  'brandStudio.look.celebration': 'Celebration theme',
  'brandStudio.look.noAnimation': 'No animation',
  'brandStudio.look.consent': 'Consent wording',
  'brandStudio.look.consentHint': 'Shown before data collection begins. Must comply with your privacy policy.',
  'brandStudio.look.gameCTAEn': 'Game CTA (EN)',
  'brandStudio.look.gameCTAEnPlaceholder': 'Spin to win!',
  'brandStudio.look.gameCTAEs': 'Game CTA (ES)',
  'brandStudio.look.gameCTAEsPlaceholder': '¡Gira para ganar!',
  // Game tab
  'brandStudio.game.hint': 'Each game showcases your brand differently. The Prize Wheel ships with all plans; premium games unlock with upgrades.',
  'brandStudio.game.chooseYourGame': 'Choose your game',
  'brandStudio.game.brandIntegration': 'Brand integration',
  'brandStudio.brandIntegration': 'Brand integration',
  'brandStudio.game.wheel.name': 'Prize Wheel',
  'brandStudio.game.wheel.desc': 'Spin to win — the classic. Logo in the hub, brand colors on segments.',
  'brandStudio.game.wheel.s1': 'Logo appears in the wheel hub',
  'brandStudio.game.wheel.s2': 'Brand color on wheel segments',
  'brandStudio.game.wheel.s3': 'Prize photo fills the reveal screen',
  'brandStudio.game.scratch.name': 'Scratch Card',
  'brandStudio.game.scratch.desc': 'Scratch to reveal — tactile and satisfying.',
  'brandStudio.game.scratch.s1': 'Logo watermark under scratch surface',
  'brandStudio.game.scratch.s2': 'Brand color gradient on card',
  'brandStudio.game.scratch.s3': 'Prize photo revealed underneath',
  'brandStudio.game.pirinola.name': 'Pirinola',
  'brandStudio.game.pirinola.desc': 'Spin the top — a cultural classic with a digital twist.',
  'brandStudio.game.pirinola.s1': 'Brand colors on pirinola faces',
  'brandStudio.game.pirinola.s2': 'Logo on the top face',
  'brandStudio.game.pirinola.s3': 'Prize name on the winning face',
  'brandStudio.game.random.name': 'Random Draw',
  'brandStudio.game.random.desc': 'Lottery-style reveal — suspense and surprise.',
  'brandStudio.game.random.s1': 'Brand-colored draw animation',
  'brandStudio.game.random.s2': 'Logo in the header',
  'brandStudio.game.random.s3': 'Prize card with product photo',
  'brandStudio.game.xinkit.name': 'Xinkit',
  'brandStudio.game.xinkit.desc': 'Drop the ball — physics-based fun with branded pegs.',
  'brandStudio.game.xinkit.s1': 'Brand color on pegs and board',
  'brandStudio.game.xinkit.s2': 'Logo on the ball',
  'brandStudio.game.xinkit.s3': 'Prize slots at the bottom',
  'brandStudio.game.soccer.name': 'Soccer Ball',
  'brandStudio.game.soccer.desc': 'Tap to kick a ball into the goal — slots inside reveal the prize.',
  'brandStudio.game.soccer.s1': 'Brand color on the ball and goal frame',
  'brandStudio.game.soccer.s2': 'Logo on prize slots',
  'brandStudio.game.soccer.s3': 'Best for sports brands and beverage activations',
  // PRD §18 — Lotería catalog entry
  'brandStudio.game.loteria.name': 'Lotería',
  'brandStudio.game.loteria.desc':
    'Mexican folk-art board game. 4×3 of cards. Match a row to win. Highest cultural resonance for Latino events.',
  'brandStudio.game.loteria.s1': 'Brand colors on row-win highlight + Jackpot banner',
  'brandStudio.game.loteria.s2': 'Auto-marked board · longest dwell of all games',
  'brandStudio.game.loteria.s3': 'Festival anchor; best for Hispanic-market activations',
  'brandStudio.game.slots.name': 'Slot Machine',
  'brandStudio.game.slots.desc':
    'Three reels of prize icons. Tap to spin; line up the middle row to win.',
  'brandStudio.game.slots.s1': 'Brand colors on the cabinet and reel frame',
  'brandStudio.game.slots.s2': 'Prize icons on every reel',
  'brandStudio.game.slots.s3': 'High-energy mechanic; great for arcade-style booths',
  // Game picker UI copy
  'brandStudio.gamePicker.selectedGame': 'Selected game',
  'brandStudio.gamePicker.tierRequired': 'Tier required',
  'publicSurvey.soccer.kick': 'Kick!',
  'publicSurvey.soccer.kicking': 'Kicking…',
  'publicSurvey.soccer.goal': 'GOAL!',
  'publicSurvey.soccer.fieldAlt': 'Soccer field with goal',
  // PRD §18 — Lotería (loteria game UI)
  'publicSurvey.loteria.label': 'Lotería',
  'publicSurvey.loteria.cardCalled': 'Card called',
  'publicSurvey.loteria.getReady': 'Get ready…',
  'publicSurvey.loteria.start': '¡Lotería!',
  'publicSurvey.loteria.hint': 'Cards match automatically · Complete a row to win',
  // PRD §18 — Unified game UX (shared action/state labels; no emojis; EN+ES)
  'publicSurvey.games.youWon': 'You won',
  'publicSurvey.games.tapToPlay': 'Tap to play',
  'publicSurvey.games.sound': 'Sound',
  'publicSurvey.games.soundOn': 'Sound on',
  'publicSurvey.games.soundOff': 'Sound off',
  'publicSurvey.games.wheel.action': 'Spin the wheel',
  'publicSurvey.games.wheel.busy': 'Spinning…',
  'publicSurvey.games.wheel.hub': 'SPIN',
  'publicSurvey.games.scratch.action': 'Scratch to reveal',
  'publicSurvey.games.scratch.hint': 'Scratch with your finger',
  'publicSurvey.games.scratch.revealed': 'Revealed!',
  'publicSurvey.games.slots.action': 'Pull',
  'publicSurvey.games.slots.busy': 'Spinning…',
  'publicSurvey.games.xinkit.action': 'Drop the ball',
  'publicSurvey.games.xinkit.busy': 'Dropping…',
  'publicSurvey.games.xinkit.tagline': 'Drop · Bounce · Win',
  'publicSurvey.games.pirinola.action': 'Spin the top',
  'publicSurvey.games.pirinola.busy': 'Spinning…',
  'publicSurvey.games.randomDraw.action': 'Draw a card',
  'publicSurvey.games.randomDraw.busy': 'Shuffling…',
  'publicSurvey.games.randomDraw.done': 'Revealed!',
  'publicSurvey.games.loteria.waiting': 'Waiting…',
  'brandStudio.game.wheelTaglineEn': 'Game tagline (EN)',
  'brandStudio.game.wheelTaglineEs': 'Game tagline (ES)',
  'brandStudio.game.wheelTaglineEnPlaceholder': 'Spin to win!',
  'brandStudio.game.wheelTaglineEsPlaceholder': '¡Gira para ganar!',
  'brandStudio.game.heroImage': 'Game hero image',
  'brandStudio.game.heroImageHint': 'Optional. Product shot or event banner shown behind the game.',
  'brandStudio.game.noHero': 'No hero',
  'brandStudio.game.tier.free': 'Free',
  'brandStudio.game.tier.premium': 'Premium',
  'brandStudio.game.tier.pro': 'Pro',
  // Prizes tab
  'brandStudio.prizes.hint': '3–5 prizes with varied rarity perform best. Rare (1–2 stars) builds excitement; common (4–5 stars) keeps everyone winning.',
  'brandStudio.prizes.inventory': 'Prize inventory',
  'brandStudio.prizes.selected': '{count} selected',
  'brandStudio.prizes.units': '{count} units',
  'brandStudio.prizes.revealPreview': 'Prize reveal preview',
  'brandStudio.prizes.youWon': 'You Won!',
  'brandStudio.prizes.fallback': 'Prize',
  'brandStudio.prizes.claim': 'Show this screen to event staff to claim your prize',
  'brandStudio.prizes.rare': 'Rare',
  'brandStudio.prizes.medium': 'Medium',
  'brandStudio.prizes.common': 'Common',
  'brandStudio.prizes.inv.tshirt': 'Branded T-Shirt',
  'brandStudio.prizes.inv.hat': 'Snapback Hat',
  'brandStudio.prizes.inv.sticker': 'Sticker Pack',
  'brandStudio.prizes.inv.tote': 'Canvas Tote Bag',
  'brandStudio.prizes.inv.bottle': 'Water Bottle',
  'brandStudio.prizes.inv.hoodie': 'Premium Hoodie',
  // Output tab
  'brandStudio.output.hint': 'Each station gets its own unique QR — brand colors and logo configured here apply automatically.',
  'brandStudio.output.universalQr': 'Universal QR code',
  'brandStudio.output.embedLogo': 'QR includes your embedded logo',
  'brandStudio.output.uploadLogo': 'Upload a logo to embed it in the QR',
  'brandStudio.output.shareableLink': 'Shareable link',
  'brandStudio.output.uniquePerStation': 'Each station gets its own unique link & QR',
  'brandStudio.output.uniquePerStationBody': 'Public links are generated per station — not per brand. Go to {path} to download individual QR codes. The brand settings configured here apply automatically.',
  'brandStudio.output.path': 'Projects → Event → Station',
  'brandStudio.output.downloadPng': 'Download PNG',
  'brandStudio.output.downloadPngHint': 'Available after saving + wiring to a station',
  'brandStudio.output.printDisplay': 'Print display',
  'brandStudio.output.upload': 'Upload',
  'brandStudio.output.clear': 'Clear',
  // Preview
  'brandStudio.preview.yourBrand': 'Your brand',
  'brandStudio.preview.brandWorkspace': 'Brand',
  'brandStudio.preview.fallbackCampaign': '{brand} sampling',
  'brandStudio.preview.continue': 'Continue',
  'brandStudio.preview.spinToWin': 'Spin to win',

  // ── Public-survey end-user surface ─────────────────────────────────
  'publicSurvey.finishPage.title': 'Thanks for playing!',
  'publicSurvey.finishPage.body': "Come back anytime — there's always more to unlock.",
  'publicSurvey.missingSurveyOrStation': 'Missing journey or station.',
  // Caso 15 — Plan-cap / lock terminal screens (consumed by SurveyLockedScreen).
  'publicSurvey.locked.limitExhausted':
    'This experience has reached its limit for now. Thanks for stopping by!',
  'publicSurvey.locked.planExpired':
    'This experience is no longer available. Please contact the host for more info.',
  'publicSurvey.locked.manualDisable':
    'This experience is temporarily disabled by its host.',
  'publicSurvey.ageGate.eyebrow': 'Age verification',
  'publicSurvey.ageGate.question': 'Are you {age} or older?',
  'publicSurvey.ageGate.body': 'You need to confirm to continue.',
  'publicSurvey.ageGate.yes': 'Yes, I am',
  'publicSurvey.ageGate.no': 'No',
  'publicSurvey.continueArrow': 'Continue →',
  'publicSurvey.prizeReveal.claimHint': 'Show this screen at the bar / station to claim your prize.',
  // PRD §11 — deliveryMode='off' hint (BA hands prize directly).
  'publicSurvey.prizeReveal.directDelivery': 'Your prize will be handed to you by the staff. No code needed.',
  'publicSurvey.photoFilm.eyebrow': 'Notice',
  'publicSurvey.photoFilm.title': 'Photography & filming',
  'publicSurvey.photoFilm.body': 'By participating in this event you acknowledge you may be photographed or filmed for marketing use.',
  'publicSurvey.photoFilm.bodyWithBrand': 'By participating in {brand} you acknowledge you may be photographed or filmed for marketing use.',
  'publicSurvey.photoFilm.notACheckbox': 'This is a notice, not a consent — no checkbox required.',
  'publicSurvey.photoFilm.acknowledge': 'I understand',
  'publicSurvey.prizeReveal.continueExternal': 'Continue to brand site →',
  'publicSurvey.prizeReveal.brandedThankYou': 'Thanks for playing — keep this screen ready when the brand ambassador comes to deliver your prize.',
  'insights.tabs.recap': 'Atledi Recap',
  'insights.tabs.throughput': 'Throughput',
  'insights.tabs.distribution': 'Distribution',
  'insights.tabs.demographics': 'Demographics',
  'insights.tabs.leads': 'Leads',
  'insights.leads.eyebrow': 'Locked CSV export',
  'insights.leads.title': 'Lead capture & follow-up',
  'insights.leads.body': 'Lead identities — names, contact, demographics and marketing consent — live in the Contacts surface alongside the locked 22-column CSV export (PRD §10.1).',
  'insights.leads.cta': 'Open Contacts',
  'insights.exportPdf': 'Export PDF',
  'insights.pdfPaid': 'PDF · Paid',
  'insights.recap.eyebrow': 'Atledi Recap',
  'insights.recap.narrative': 'Narrative',
  'insights.recap.narrativePlaceholder':
    'Live narrative ships with the Atledi/Sonnet endpoint in Phase 4. Each event will get a 2–3 sentence story of what just happened, in the brand voice configured in Brand Studio.',
  'insights.recap.kpis': 'KPI summary',
  'insights.recap.recommendation': 'Recommendation',
  'insights.recap.recommendationPlaceholder':
    'One concrete next action with confidence level appears here once the recap endpoint is wired.',
  'insights.recap.confidence.low': 'Low',
  'insights.recap.confidence.medium': 'Medium',
  'insights.recap.confidence.high': 'High',
  'insights.kpi.moments': 'Moments',
  'insights.kpi.throughput': 'Throughput',
  'insights.kpi.inventory': 'Inventory',
  'legal.eyebrow': 'Legal · One-time acknowledgement',
  'legal.brandObligations': 'Brand obligations',
  'legal.xotskiObligations': 'Xotski obligations',
  'legal.xotskiObligationsBody':
    'Xotski processes personal data on your documented instructions only, keeps it inside the regions you select, and notifies you of any breach within 24 hours of detection.',
  'legal.signerName': 'Signer name',
  'legal.signerTitle': 'Signer title',
  'legal.later': 'Remind me later',
  'legal.submitting': 'Saving…',
  'legal.accept': 'I accept',
  'reporterAdmin.eyebrow': 'Reporters',
  'reporterAdmin.title': 'Field reporters for this event',
  'reporterAdmin.invite': 'Invite reporter',
  'reporterAdmin.freeUnlimited': 'Free · Unlimited',
  'reporterAdmin.paidOnly': 'Paid',
  'reporterAdmin.paidHint': 'Upgrade to invite reporters with 24h magic links — free unlimited reporters in every paid tier.',
  'reporterAdmin.loading': 'Loading reporters…',
  'reporterAdmin.empty': 'No reporters yet. Send a magic link to your first field staff.',
  'reporterAdmin.expiresAt': 'expires {date}',
  'reporterAdmin.state.active': 'Active',
  'reporterAdmin.state.revoked': 'Revoked',
  'reporterAdmin.state.expired': 'Expired',
  'reporterAdmin.reissueHint': 'Reissue link (copies new URL)',
  'reporterAdmin.revokeHint': 'Revoke active link',
  'reporterAdmin.revoked': 'Reporter link revoked',
  'reporterAdmin.reissuedCopied': 'New link copied to clipboard',
  'reporterAdmin.invitedCopied': 'Magic URL copied to clipboard',
  'reporterAdmin.capReached': 'Reviewer limit reached',
  'reporterAdmin.capReachedBody': 'Your plan covers a set number of free reviewers. Upgrade to invite more — XK2 and up have unlimited reviewers.',
  'reporterAdmin.inviteError': 'Could not send invitation',
  'reporterAdmin.copied': 'Copied',
  'reporterAdmin.copy': 'Copy URL',
  'reporterAdmin.modal.eyebrow': 'PRD §12 · Reporter View',
  'reporterAdmin.modal.title': 'Invite a field reporter',
  'reporterAdmin.modal.body': 'They receive a 24h magic link to a 4-tab field surface — no account, no password.',
  'reporterAdmin.modal.email': 'Email',
  'reporterAdmin.modal.nameOptional': 'Name (optional)',
  'reporterAdmin.modal.namePlaceholder': 'Brand ambassador name',
  'reporterAdmin.modal.send': 'Send invite',
  'reporterAdmin.modal.sending': 'Sending…',
  'reporterAdmin.modal.successTo': 'Magic link sent to {email}',
  'reporterAdmin.modal.magicUrl': 'Magic URL',
  'reporterAdmin.modal.expiresHint': 'Link expires {date}',
  'reporter.tabs.today': 'Today',
  'reporter.tabs.inventory': 'Inventory',
  'reporter.tabs.notes': 'Notes',
  'reporter.tabs.claims': 'Claims',
  'reporter.header.eyebrow': 'Reporter View',
  'reporter.header.expiresAt': 'Link expires {date}',
  'reporter.verifying.title': 'Verifying your link…',
  'reporter.verifying.body': 'One moment while we open your event.',
  'reporter.invalidLink.title': 'This link is invalid',
  'reporter.invalidLink.body': 'Ask the brand to send you a fresh link.',
  'reporter.expired.title': 'Link expired',
  'reporter.expired.body': 'Reporter links are valid for 24 hours. Ask the brand for a new one.',
  'reporter.terms.eyebrow': 'Reporter terms',
  'reporter.terms.title': 'Before you start at {event}',
  'reporter.terms.body': 'You are signing in as a brand reporter. Quick acknowledgements before you can log notes:',
  'reporter.terms.point1': 'Notes and photos you log are shared with the organizing brand only.',
  'reporter.terms.point2': 'You will not share the magic link or screenshots that expose claim codes.',
  'reporter.terms.point3': 'You may stop reporting at any time by closing this tab.',
  'reporter.terms.checkbox': 'I understand and want to start reporting.',
  'reporter.terms.accept': 'Start reporting',
  'reporter.terms.submitting': 'Saving…',
  'reporter.today.scheduleTitle': 'Schedule',
  'reporter.today.scheduleBody': 'Stations + start/end times for {event}. Live view ships with the offline queue in Phase 3.',
  'reporter.today.atlediEyebrow': 'Atledi briefing',
  'reporter.today.atlediTitle': 'Your focus right now',
  'reporter.today.atlediBody': 'Capacity tip + first-hour focus from the Atledi assistant. Coming in Phase 4.',
  'reporter.today.atlediBriefing.morning': 'Good morning at {event}. Open posters by every entrance and warm the QR scanners — the first hour drives 30–40% of the day’s captures.',
  'reporter.today.atlediBriefing.afternoon': 'Afternoon push at {event}. Refresh any picked-clean stations, walk the bar line, and re-engage groups that haven’t scanned yet.',
  'reporter.today.atlediBriefing.evening': 'Evening flow at {event}. Watch claim-code timers and flag any near-expiry before the bar runs out of inventory.',
  'reporter.today.atlediBriefing.night': 'Late shift at {event}. Tally what’s left, capture closing photos, and queue notes for the manager review tomorrow.',
  'reporter.inventory.title': 'Brand inventory',
  'reporter.inventory.body': 'Items allocated to {event} with quick adjusters for damaged or written-off units.',
  'reporter.notes.title': 'Field notes',
  'reporter.notes.body': 'Photo + text notes for the brand manager. Tap "+" on the bottom right to add one.',
  'reporter.notes.offlineTitle': 'Offline-first',
  'reporter.notes.offlineBody': 'Notes typed without signal queue in IndexedDB and sync once the connection comes back.',
  'reporter.claims.title': 'Claim verification',
  'reporter.claims.body': 'Scan a guest QR or type a claim code to mark a prize delivered for {event}.',
  'brandStudio.look.atlediTone': 'Atledi voice',
  'brandStudio.look.atlediToneHint': 'Shapes the recap + welcome narrative Atledi writes for this brand.',
  'brandStudio.look.atlediTone.playful': 'Playful',
  'brandStudio.look.atlediTone.professional': 'Professional',
  'brandStudio.look.atlediTone.warm': 'Warm',
  'brandStudio.look.atlediTone.bold': 'Bold',
  'brandStudio.look.atlediTone.custom': 'Custom',
  'brandStudio.look.atlediToneCustomPlaceholder': 'Describe the voice — e.g. "concise, data-first, no exclamation marks".',
  'brandStudio.look.consentVariant': 'Consent variant',
  'brandStudio.look.consentVariantHint': 'Anonymous (A) skips PII and marketing checkboxes. PII (B) collects contact info and shows the unbundled marketing opt-in.',
  'brandStudio.look.consentVariantA': 'A · Anonymous',
  'brandStudio.look.consentVariantADesc': 'No PII, no marketing. Just T&C acceptance.',
  'brandStudio.look.consentVariantB': 'B · PII + Marketing opt-in',
  'brandStudio.look.consentVariantBDesc': 'Collects contact + separate optional marketing checkbox.',
  'publicSurvey.claim.preparing': 'Generating your claim code…',
  'publicSurvey.claim.codeLabel': 'Claim code',
  'publicSurvey.claim.showToBa':
    'Show this code to the brand ambassador before the timer runs out.',
  'publicSurvey.claim.expiredAskBa':
    'The timer expired. Flag a brand ambassador and they can extend it.',
  'publicSurvey.wheel.emptyTitle': 'No prizes left',
  'publicSurvey.wheel.emptyBody': 'All prizes have been claimed for this station.',

  // ── Active Log (audit trail) ───────────────────────────────────────
  'activeLog.title': 'Active log',
  'activeLog.subtitle': 'Every mutation across your account — who, when, where.',
  'sidebar.activeLog': 'Active log',
  'activeLog.noAccessTitle': 'No audit access',
  'activeLog.noAccessBody': "Your role doesn't grant access to the audit log.",
  'activeLog.empty.title': 'No activity yet',
  'activeLog.empty.body': 'Once people start working, every meaningful action shows up here.',
  'activeLog.filter.search': 'Search',
  'activeLog.filter.searchPlaceholder': 'Description, URL, user…',
  'activeLog.filter.action': 'Action',
  'activeLog.filter.actionAll': 'All actions',
  'activeLog.filter.entity': 'Entity',
  'activeLog.filter.userId': 'User ID',
  'activeLog.filter.eventId': 'Event ID',
  'activeLog.filter.from': 'From',
  'activeLog.filter.to': 'To',
  'activeLog.filter.apply': 'Apply',
  'activeLog.filter.reset': 'Reset',
  'activeLog.table.when': 'When',
  'activeLog.table.user': 'User',
  'activeLog.table.action': 'Action',
  'activeLog.table.entity': 'Entity',
  'activeLog.table.description': 'Description',
  'activeLog.pager.summary': 'Showing {from}–{to} of {total}',
  'activeLog.pager.prev': 'Previous page',
  'activeLog.pager.next': 'Next page',
  'activeLog.detail.id': 'ID',
  'activeLog.detail.companyId': 'Brand',
  'activeLog.detail.user': 'User',
  'activeLog.detail.email': 'Email',
  'activeLog.detail.method': 'HTTP method',
  'activeLog.detail.appType': 'App',
  'activeLog.detail.eventId': 'Event ID',
  'activeLog.detail.ip': 'IP address',
  'activeLog.detail.url': 'URL',
  'activeLog.detail.userAgent': 'User agent',
  'activeLog.detail.description': 'Description',
  'activeLog.detail.oldData': 'Old data',
  'activeLog.detail.newData': 'New data',

  // ── Paywall (premium feature gate) ─────────────────────────────────
  'paywall.badge.premium': 'Premium',
  'paywall.badge.locked': 'Locked',
  'paywall.cta.viewPlans': 'View plans',
  'paywall.activeLog.title': 'Active Log is a premium service',
  'paywall.activeLog.description':
    "Audit every action your team takes: who created, edited or deleted what, from where and when. Activate this service in your plan to start seeing the full history.",
  'paywall.activeLog.perk1': 'Granular history of every change (before/after)',
  'paywall.activeLog.perk2': 'Filter by user, entity, date and IP',
  'paywall.activeLog.perk3': 'Export and extended retention',
  'paywall.notifications.title': 'Notifications is a premium service',
  'paywall.notifications.description':
    'Send real-time messages (push, in-app and email) to your team and station operators. Activate this service in your plan to start communicating.',
  'paywall.notifications.perk1': 'Instant push to operator devices',
  'paywall.notifications.perk2': 'Optional email to the same recipient',
  'paywall.notifications.perk3': 'Segmentation by users or groups',
  'paywall.aiAssistant.title': 'AI Assistant is a premium service',
  'paywall.aiAssistant.description':
    'Ask questions about your projects, events, journeys and interactions in natural language. The assistant streams answers grounded in your live data.',
  'paywall.aiAssistant.perk1': 'Instant Q&A about your events, stations and interactions',
  'paywall.aiAssistant.perk2': 'Surfacing insights without exporting to a spreadsheet',
  'paywall.aiAssistant.perk3': 'Always grounded in the data your plan unlocks',
  // ── Etiquetas ──────────────────────────────────────────────────────
  'tags.picker.add': 'Tag',
  'tags.picker.more': 'Add',
  'tags.picker.placeholder': 'Search or create a tag…',
  'tags.picker.create': 'Create “{name}”',
  'tags.picker.emptyVocab': 'No tags yet. Type a name to create the first one.',
  'tags.picker.noMatches': 'Nothing matches. Type a different name to create it.',
  'tags.filter.label': 'Tags',
  'tags.filter.clear': 'Clear',
  'tags.filter.andHint':
    'Showing only what carries every tag you picked — each one narrows the list.',
  'tags.filter.oneResult': '1 result',
  'tags.filter.nResults': '{n} results',
  'tags.saveFailed': 'Could not save the tags',
  'tags.type.project': 'Projects',
  'tags.type.event': 'Events',
  'tags.type.station': 'Stations',
  'tags.manager.title': 'Tags',
  'tags.manager.subtitle':
    'The tag vocabulary for this brand. Tagging itself happens on each list — here you tidy up: rename, recolour and retire what no longer serves.',
  'tags.manager.noBrand': 'Pick a brand first',
  'tags.manager.noBrandBody': 'Tags belong to a brand, so there is nothing to show until you choose one.',
  'tags.manager.empty': 'No tags yet',
  'tags.manager.emptyBody': 'Create the first one while tagging a {type} — they appear here as you go.',
  'tags.manager.count': '{n} tags',
  'tags.manager.unused': 'not used',
  'tags.manager.usedOne': 'on 1',
  'tags.manager.usedMany': 'on {n}',
  'tags.manager.rename': 'Rename {name}',
  'tags.manager.remove': 'Retire {name}',
  'tags.manager.removeTitle': 'Retire “{name}”?',
  'tags.manager.removeBodyOne':
    'It will be removed from 1 {type} as well as from the vocabulary. Nothing else about it changes.',
  'tags.manager.removeBodyMany':
    'It will be removed from {n} {type} as well as from the vocabulary. Nothing else about them changes.',
  'tags.manager.removeUnused': 'Nothing carries it, so this only removes it from the vocabulary.',
  'tags.manager.removeConfirm': 'Retire',
  'tags.manager.removed': '“{name}” retired',
  // ── Mis dispositivos ───────────────────────────────────────────────
  'sessions.title': 'My devices',
  'sessions.settingsDesc': 'See where your account is signed in, and sign out anything you do not recognise.',
  'sessions.subtitle':
    'Your account can be signed in on {n} devices at once — laptop, phone, the tablet at the event. Sign in on a fourth and the least recently used one is closed, so nobody is ever locked out mid-event.',
  'sessions.count': '{n} of {max} devices',
  'sessions.thisDevice': 'This device',
  'sessions.unknownDevice': 'Unrecognised device',
  'sessions.lastSeen': 'last used {when}',
  'sessions.now': 'just now',
  'sessions.minutesAgo': '{n} min ago',
  'sessions.hoursAgo': '{n} h ago',
  'sessions.close': 'Sign out',
  'sessions.closed': 'Device signed out',
  'sessions.closeFailed': 'Could not sign that device out',
  'sessions.sharedHint':
    'This account has been used on {n} devices today. If that is your field team, they do not need your seat — invite them as reporters and they get their own link, without a password to pass around.',
  'sessions.atLimit':
    'You are at {n} devices. Signing in somewhere else will close the one you have not used in longest.',

  // ── Dashboards (Insights · informe y Track de evento · informe de proyecto) ──────────────────────────────────────────────
  'dash.pending': 'Pending',
  'dash.measured': 'measured',
  'dash.needs': 'needs {what}',
  'dash.basis': 'n = {n} · ±{moe} pts',
  'compare.pickDefaultGroups': 'The first {n}',
  'compare.pickEvents': 'Events to compare (up to {max})',
  'compare.pickGroups': 'Groups to compare (up to {max})',
  'compare.pickDefault': 'The {n} most recent',
  'compare.searchGroups': 'Search groups',
  'compare.groupsPicked': '{n} groups',
  'project.tab.compare': 'Compare',
  'insights.tabs.compare': 'Compare',
  'compare.needTwo': 'Pick at least two groups with data to compare them.',
  'compare.baseline': 'Reference',
  'compare.ref': 'reference',
  'compare.eventsN': '{n} event(s)',
  'compare.capped': 'Only the first {n} groups are compared; narrow the scope or the grouping.',
  'compare.chartNoteProportion': 'Bars carry their 95% margin of error. Differences are tested against the reference with a two-proportion z-test.',
  'compare.chartNoteNoTest': 'Not a proportion: compared as a plain difference, without a significance test.',
  'compare.col.metric': 'Metric',
  'compare.col.context': 'Context',
  'compare.context': '{events} event(s) · {interactions} interactions · {contacts} contacts · {leads} leads · {delivered} delivered',
  'compare.ns': 'n.s.',
  'compare.footnote': 'Every percentage carries its own base (n) and 95% margin. Badges show the difference vs the reference: p < 0.05 means the gap is unlikely to be noise; n.s. means it could be. A tilde (~) marks a base under 50: directional, not conclusive.',
  'compare.metric.leadRate': 'Lead rate',
  'compare.metricNote.leadRate': 'Leads as a share of contacts',
  'compare.metric.contactRate': 'Contact rate',
  'compare.metricNote.contactRate': 'Contacts as a share of interactions',
  'compare.metric.completion': 'Completion',
  'compare.metricNote.completion': 'Responses as a share of interactions',
  'compare.metric.redemption': 'Redemption',
  'compare.metricNote.redemption': 'Delivered as a share of prizes won',
  'compare.metric.consent': 'Marketing consent',
  'compare.metricNote.consent': 'Opt-ins as a share of leads',
  'compare.metric.deliveredPerInteraction': 'Prizes per interaction',
  'compare.metricNote.deliveredPerInteraction': 'Delivered as a share of interactions',
  'compare.metric.unaccountedRate': 'Unaccounted',
  'compare.metricNote.unaccountedRate': 'Units with no destination, of units out (lower is better)',
  'compare.metric.costPerLead': 'Cost per lead',
  'compare.metricNote.costPerLead': 'Distributed value over leads (lower is better)',
  'compare.metric.capturesPerBlock': 'Captures per block',
  'compare.metricNote.capturesPerBlock': 'Average captures per 15-minute block',
  'compare.metric.peakBlock': 'Peak block',
  'compare.metricNote.peakBlock': 'Most captures in one 15-minute block',
  'compare.read.better': '{b} beats {a} on {metric}: {vb} vs {va} ({diff}, 95% CI [{lo}, {hi}] pts, p {p}). The gap is unlikely to be noise.',
  'compare.read.worse': '{b} trails {a} on {metric}: {vb} vs {va} ({diff}, 95% CI [{lo}, {hi}] pts, p {p}). The gap is unlikely to be noise.',
  'compare.read.same': '{b} and {a} are not distinguishable on {metric}: {vb} vs {va} ({diff}, 95% CI [{lo}, {hi}] pts, p {p}). Treat them as equal until the bases grow.',
  'compare.read.noTest': '{b} vs {a} on {metric}: {vb} vs {va} ({diff}). Not a proportion, so no significance test applies.',
  'compare.read.pending': '{metric} is pending on {b} or on {a}; nothing to compare yet.',
  'compare.read.directional': 'One base is under 50, so this reads as a direction, not a conclusion.',
  'compare.projectKicker': 'Event vs event',
  'compare.projectIntro': '{n} of {total} events have data. Pick the reference, then read each metric against it: the margin says how much the base allows, the test says whether the gap holds.',
  'insights.compare.by': 'Group by',
  'insights.compare.mode.project': 'Project',
  'insights.compare.mode.tag': 'Tag',
  'insights.compare.mode.event': 'Event',
  'insights.compare.mode.custom': 'Custom sets',
  'insights.compare.hint.project': 'One group per project in the scope (up to {n}).',
  'insights.compare.hint.tag': 'One group per event or project tag in the scope; an event can sit in several (up to {n}).',
  'insights.compare.hint.event': 'One group per event with data (up to {n}).',
  'insights.compare.hint.custom': 'Build your own sets from the events in the scope (up to 3).',
  'insights.compare.customSet': 'Set {n}',
  'insights.compare.pickEvents': 'Pick events',
  'insights.compare.addSet': '+ Add a set',
  'closing.kicker': 'Event closing',
  'closing.reconciledOn': 'Reconciled on {date}',
  'closing.viewRecord': 'The record',
  'closing.reopen': 'Reopen',
  'closing.reopenTitle': 'Reopen this event?',
  'closing.reopenBody': 'The {n} units returned to the warehouse go back into play at their stations, the brand pool is corrected and the closing record is deleted. You can close again afterwards.',
  'closing.endedKicker': 'The event has ended',
  'closing.endedTitle': '{n} units are still at the stations',
  'closing.endedBody': '{inPlay} still in play and {lapsed} expired or voided are physically there and should come back to the warehouse. {pending} prizes won but not collected stay reserved until they expire.',
  'closing.closeButton': 'Close the event and return leftovers',
  'closing.dialogTitle': 'Close {name}',
  'closing.dialogBody': 'One row per station and premium. "Should be there" is what is still in play plus what expired or was voided. Count what is actually there; whatever is missing is recorded as unaccounted, never as delivered.',
  'closing.note': 'Note for the record',
  'closing.notePlaceholder': 'Who counted, what happened to the missing units, anything the client should know…',
  'closing.confirm': 'Close and return {n} units',
  'closing.toast.closed': 'Event closed: {n} units back in the warehouse',
  'closing.toast.reopened': 'Event reopened',
  'closing.stat.returned': 'Returned to warehouse',
  'closing.stat.returnedHint': '{n} of them added back to the brand pool (won, then expired or voided)',
  'closing.stat.delivered': 'Delivered',
  'closing.stat.deliveredHint': 'of {n} allocated',
  'closing.stat.pending': 'Won, not collected',
  'closing.stat.pendingHint': 'Reserved until they expire; not returned',
  'closing.stat.unaccounted': 'Unaccounted',
  'closing.stat.unaccountedHint': 'Should have been there and were not',
  'closing.col.station': 'Station',
  'closing.col.stock': 'Premium',
  'closing.col.allocated': 'Allocated',
  'closing.col.delivered': 'Delivered',
  'closing.col.pending': 'Pending',
  'closing.col.lapsed': 'Lapsed',
  'closing.col.inPlay': 'In play',
  'closing.col.onHand': 'Should be there',
  'closing.col.counted': 'Counted',
  'closing.col.returned': 'Returns',
  'closing.col.unaccounted': 'Missing',
  'eventReport.hero.reconciled': 'Reconciled {date}',
  'eventReport.hero.open': 'Not closed · {n} still in play',
  'dash.unacc.returned': 'Returned to the warehouse',
  'dash.seg.returned': 'Returned to warehouse',
  'brand.leftovers.close': 'Close and return',
  'insights.filter.tags': 'Tags',
  'insights.filter.noTags': 'No tags on these events or projects yet',
  'insights.filter.projectTag': '{name} · project',
  'insights.picker.askFor': 'Ask for',
  'insights.picker.everything': 'Everything',
  'insights.picker.events': '{n} event(s)',
  'insights.picker.moreProjects': '+{n} more project(s)',
  'insights.picker.lastThree': 'Last three',
  'insights.picker.mostRecent': 'most recent',
  'insights.picker.beatPlan': 'Beat plan',
  'insights.picker.overGoal': 'over goal',
  'insights.picker.search': 'Search {n} events by name, project or date',
  'insights.picker.match': '{n} of {total} events match',
  'insights.picker.selectMatching': 'Select all {n} matching',
  'insights.picker.caption': 'Bar height is contacts captured, on a square-root scale',
  'insights.picker.noMatch': 'No events match',
  'insights.picker.more': '+{n} more not shown',
  'insights.throughput.allSelection': 'Whole selection',
  'insights.throughput.byClock': 'by time of day',
  'dash.thru.nav': '{i} of {n}',
  'dash.thru.title': 'Throughput per capture window',
  'dash.thru.play': 'Play',
  'dash.thru.pause': 'Pause',
  'dash.thru.time': 'Time',
  'dash.thru.perBlock': 'Captures / block',
  'dash.thru.cum': 'Captures so far',
  'dash.thru.prizes': 'Prizes delivered',
  'dash.thru.ofCapture': 'Of the capture',
  'dash.thru.phaseLabel': 'Phase',
  'dash.thru.peakCap': 'Peak {n} per 15 min',
  'dash.thru.capLabel': 'Peak {n} per 15 min · {stations} station(s)',
  'dash.thru.deliveredSoFar': '{n} delivered',
  'dash.thru.completions': '{n} completions',
  'dash.premiums.title': 'Premiums delivered',
  'dash.premiums.headGoal': 'against {goal} planned · {allocated} allocated',
  'dash.premiums.headAllocated': 'of {allocated} allocated',
  'dash.premiums.headExpected': 'against {expected} expected (calculator) · {allocated} allocated',
  'dash.premiums.expected': 'Planned',
  'dash.premiums.allocated': 'Allocated',
  'dash.premiums.actual': 'Delivered',
  'dash.premiums.ofExpected': 'of {n} planned',
  'dash.premiums.ofAllocated': 'of {n} allocated',
  'dash.premiums.headroomNote': 'Faint band is what was allocated, the tick is the 100% mark',
  'dash.premiums.clickToIsolate': 'Click any premium to isolate it',
  'dash.premiums.showingItem': 'Showing {name} · back to all premiums',
  'dash.premiums.where': 'Where {name} went',
  'dash.premiums.lightNote': '{n} of {total} premiums are under 45% delivered, led by {name} at {p}%. Under-delivery ties up merch that another date could have used.',
  'dash.premiums.okNote': 'Every premium is over 45% delivered. The allocation was either right or tight.',
  'dash.unacc.title': 'Unaccounted',
  'dash.unacc.basis': '{n} of {out} units out',
  'dash.unacc.noUnits': 'no units have left the game yet',
  'dash.unacc.body': 'Every unit that left a station has a destination in the ledger: delivered, waiting to be collected, or lapsed. What is left over here is what has no redemption record.',
  'dash.unacc.bodyPending': 'Nothing has left the game yet, so there is nothing to account for. The rate stays pending rather than reading as zero.',
  'dash.unacc.delivered': 'Delivered, operator confirmed',
  'dash.unacc.pending': 'Won, waiting to be collected',
  'dash.unacc.lapsed': 'Expired or voided',
  'dash.unacc.unaccounted': 'Unaccounted, no redemption record',
  'dash.cost.title': 'Cost',
  'dash.cost.offByDefault': 'Off by default',
  'dash.cost.reveal': 'Reveal cost',
  'dash.cost.hide': 'Hide cost',
  'dash.weather.kicker': 'Weather covariate',
  'dash.weather.none': 'Weather was not recorded at this event, so the covariate cannot be computed. The slot stays pending rather than reading as no effect.',
  'dash.weather.oneDay': 'Conditions held at {t}, {d} through the capture window, so weather is a constant here and explains none of the block to block variation.',
  'dash.weather.twoDays': 'Day two ran at {t2}, {d2} against {t1}, {d1} on day one. Read the two days side by side before blaming the crowd for a dip.',
  'dash.weather.noPrior': 'No prior year on file for this fixture, so there is nothing to compare the conditions against yet.',
  'dash.weather.code.clear': 'clear',
  'dash.weather.code.cloudy': 'cloudy',
  'dash.weather.code.fog': 'fog',
  'dash.weather.code.rain': 'rain',
  'dash.weather.code.unknown': 'recorded',
  'dash.atledi.eventRead': 'Event read',
  'dash.atledi.stamp': 'Read from {n} of {total} blocks',
  'dash.atledi.open': 'Open the conversation',
  'dash.atledi.promptClosed': 'What should I change next time?',
  'dash.atledi.promptLive': 'What is at risk right now?',
  'dash.atledi.textSurvey': 'Merch moved faster than the survey filled. {delivered} prizes delivered against {responses} responses, on {interactions} interactions and a redemption rate of {redemption}. Unaccounted sits at {p} of units out, and {pending} prizes are still waiting to be collected rather than lost. If the goal next time is responses rather than reach, hold a portion of the merch out of the game and gate it behind completion.',
  'dash.atledi.textNoSurvey': 'Nothing was asked past the gate here, and it is still a complete report. {out} units left the stations, {delivered} were delivered and confirmed, and {unaccounted} have no redemption record, which is {p}. The argument to this client is inventory discipline, not audience insight. Lead the recap with reconciliation and leave demographics out entirely.',
  'eventReport.show.everything': 'Show everything',
  'eventReport.show.none': 'none',
  'eventReport.funnel.aside': 'Prizes delivered reads straight off the ledger above',
  'eventReport.ledger.kicker': 'Reconciliation · every unit has a destination',
  'eventReport.ledger.basisOpen': '{n} units still in play',
  'eventReport.ledger.basisClosed': 'Every allocated unit has a destination',
  'eventReport.costs.valueNote': 'Enter {items} in My Merch to complete it.',
  'eventReport.costs.valueOk': 'Delivered units multiplied by the unit cost entered in My Merch.',
  'eventReport.health.kicker': 'Raw to clean · this event',
  'eventReport.health.gapsTitle': 'What we cannot compute yet',
  'eventReport.health.blocked': 'blocked',
  'eventReport.footer.part': 'Part of the',
  'eventReport.footer.frames': 'Cleaned frames only. Every percentage carries its own base and margin.',
  'dash.need.unitCosts': '{n} unit cost(s)',
  'dash.need.allocations': 'allocations',
  'dash.need.consumedUnits': 'units out of play',
  'dash.need.prizesWon': 'prizes won',
  'dash.need.contacts': 'contacts',
  'dash.need.interactions': 'interactions',
  'dash.need.leads': 'leads',
  'dash.need.peakBlock': 'a peak block',
  'dash.need.captures': 'captures',
  'dash.need.leadGoal': 'a lead goal',
  'dash.need.distributedValue': 'distributed value',
  'dash.need.footprintTraffic': 'footprint traffic for the activation zone',
  'dash.funnel.interactions': 'Interactions',
  'dash.funnel.contacts': 'Contacts',
  'dash.funnel.leads': 'Leads',
  'dash.funnel.responses': 'Responses',
  'dash.funnel.prizesWon': 'Prizes won',
  'dash.funnel.prizesDelivered': 'Prizes delivered',
  'dash.funnel.note.interactions': 'station flows started',
  'dash.funnel.note.contacts': 'unique people after de-dup',
  'dash.funnel.note.leads': 'contacts with email or phone',
  'dash.funnel.note.responses': 'completed the survey',
  'dash.funnel.note.prizesWon': 'game outcomes awarded',
  'dash.funnel.note.prizesDelivered': 'operator confirmed',
  'dash.funnel.notAsked': 'Not asked',
  'dash.funnel.ofInteractions': 'of interactions',
  'dash.seg.delivered': 'Delivered',
  'dash.seg.pending': 'Won, not yet delivered',
  'dash.seg.lapsed': 'Expired or voided',
  'dash.seg.unaccounted': 'Unaccounted',
  'dash.seg.inPlay': 'Still in play',
  'dash.ledger.allocated': '{n} allocated',
  'dash.ledger.valuePending': 'value pending',
  'dash.ledger.total': 'All premiums',
  'dash.phase.warmup': 'Warm-up',
  'dash.phase.peak': 'Peak',
  'dash.phase.tail': 'Tail',
  'dash.thru.empty': 'No captures recorded yet. The timeline fills in as the first sessions start.',
  'dash.thru.subtitle': '{total} captures on {day} · {blocks} blocks of 15 minutes',
  'dash.thru.tooltip': '{n} captures · {phase}',
  'dash.thru.live': 'live block',
  'dash.thru.peakLine': 'Peak {n}',
  'dash.thru.peakLegend': 'Peak block',
  'dash.thru.warmupNote': 'The first {blocks} blocks carried {n} captures. Yield follows crowd arrival, not staffing.',
  'dash.thru.peakNote': 'Peak block took {peak}. Half of all captures landed inside {n} blocks.',
  'dash.thru.tailNote': 'The tail carried {n} captures. Staffing could taper here without losing yield.',
  'dash.pace.noGoal': 'Set a goal',
  'dash.pace.met': 'Goal met',
  'dash.pace.ahead': '+{d}% vs pace',
  'dash.pace.behind': '{d}% vs pace',
  'dash.pace.ofGoal': '{p}% of goal',
  'dash.pace.redeemed': '{p}% redeemed',
  'dash.pace.noDenominator': 'No denominator',
  'dash.pace.goalLabel': 'of {n} goal',
  'dash.pace.noGoalLabel': 'no goal set',
  'dash.pace.ofWon': 'of {n} won',
  'dash.pace.noneWon': 'no prizes won yet',
  'dash.pace.tracking': 'Tracking to {n} by close · {left} to go',
  'dash.pace.mark': 'Pace mark',
  'dash.wall.title': 'The prize wall',
  'dash.wall.inPlay': 'in play',
  'dash.wall.healthy': 'Healthy',
  'dash.wall.restock': 'Restock · under threshold',
  'dash.wall.out': 'Out of play · re-allocate',
  'dash.wall.delivered': '{d} delivered of {a} allocated',
  'dash.wall.empty': 'No premiums allocated to this event yet.',
  'dash.strip.contacts': 'contacts',
  'dash.strip.responses': '{n} resp',
  'dash.strip.noSurvey': 'no survey',
  'insights.filter.searchEvents': 'Search events',
  'insights.filter.eventsPicked': '{n} events',
  'insights.filter.selectShown': 'All shown',
  'insights.filter.clear': 'Clear',
  'insights.filter.noMatch': 'No events match',
  'insights.scope.none': 'Nothing selected. Pick an event on the line above.',
  'insights.scope.sentence': 'Reading {n} of {total} events, {from} to {to}, across {projects} project(s).',
  'insights.scope.more': '+{n} more not shown',
  'insights.scope.everything': 'Show everything',
  'insights.recap.heroTitle': 'What the selection did',
  'insights.recap.scopeChip': 'Scope · {n} events',
  'insights.recap.contacts': 'Verified contacts',
  'insights.recap.contactsHint': '{n} deployments in the selection',
  'insights.recap.leads': 'Leads',
  'insights.recap.leadsHint': '{p} of contacts · ±{moe} pts',
  'insights.recap.noBase': 'no base yet',
  'insights.recap.delivered': 'Delivered',
  'insights.recap.deliveredHint': 'of {won} won · {p} redeemed · {allocated} allocated',
  'insights.recap.unaccounted': 'Unaccounted',
  'insights.recap.unaccountedHint': 'Left the game with no redemption record · resolve in My Merch',
  'insights.feed.plan': 'Against plan',
  'insights.feed.planText': '{allocated} units were allocated to the selection, {won} were won and {delivered} reached a hand. {pending} are still waiting to be collected.',
  'insights.feed.distribution': 'Distribution',
  'insights.feed.distributionText': '{delivered} units delivered across the selection, {lapsed} expired or voided and {inPlay} still in play. What is still in a box is merch a second date could use.',
  'insights.feed.instrument': 'Instrument',
  'insights.feed.instrumentText': '{surveyed} of {n} events carry a survey past the gate, so demographics rest on {responses} responses rather than the full contact frame.',
  'insights.feed.consent': 'Marketing consent',
  'insights.feed.consentText': '{p} of leads opted in to marketing ({n} people). Only those can be contacted commercially.',
  'insights.feed.consentPending': 'No leads in the selection yet, so the consent rate is pending rather than zero.',
  'insights.throughput.title': 'Throughput across the selection',
  'insights.distribution.byItem': 'Distribution by item',
  'insights.distribution.byItemNote': '{n} units allocated across the selection · segments add up to what left the game',
  'insights.leads.contacts': 'Verified contacts',
  'insights.leads.interactions': 'Interactions',
  'insights.leads.perContact': '{n} per contact',
  'insights.leads.responses': 'Responses',
  'insights.leads.responsesHint': '{p} of interactions completed the survey',
  'insights.leads.consent': 'Marketing consent',
  'insights.leads.consentHint': '{n} of {leads} leads opted in',
  'eventReport.sections.throughput': 'Throughput',
  'eventReport.sections.health': 'Data health',
  'eventReport.funnel.title': 'Capture funnel',
  'eventReport.funnel.note': 'Prizes delivered reads straight off the ledger. If this number and the premiums table ever disagree, that is a bug, not a figure to reconcile by hand.',
  'eventReport.funnel.noSurvey': 'Responses renders as not asked, never as zero: no station at this event ran a survey past the gate.',
  'eventReport.hero.title': 'What the merch did',
  'eventReport.hero.final': 'Final',
  'eventReport.hero.provisional': 'Provisional · event still open',
  'eventReport.hero.unitsOut': 'Units out',
  'eventReport.hero.unitsOutHint': '{allocated} allocated, {inPlay} still in play',
  'eventReport.hero.delivered': 'Delivered',
  'eventReport.hero.deliveredHint': '{p} of {won} prizes won, operator confirmed',
  'eventReport.hero.unaccounted': 'Unaccounted',
  'eventReport.hero.unaccountedHint': '{p} of units out left the game with no redemption record',
  'eventReport.hero.value': 'Distributed value',
  'eventReport.hero.valueHint': '{n} delivered units at their My Merch unit cost',
  'eventReport.hero.valuePending': '{items} has no unit cost, so the total stays pending',
  'eventReport.hero.addCost': 'Add unit cost in My Merch',
  'eventReport.hero.costed': '{costed} of {n} items costed in My Merch',
  'eventReport.ledger.title': 'Premiums, unit by unit',
  'eventReport.ledger.note': 'Every segment is a destination. The bar is what was allocated; what is left uncoloured is still in play.',
  'eventReport.ledger.empty': 'No premiums were allocated to this event.',
  'eventReport.throughput.title': 'Throughput per capture window',
  'eventReport.costs.distributedValue': 'Distributed value',
  'eventReport.costs.redemption': 'Redemption rate',
  'eventReport.costs.captureRate': 'Capture rate',
  'eventReport.health.title': 'Data health',
  'eventReport.health.bridgeHead': '{raw} sessions in, {clean} people you can defend',
  'eventReport.health.raw': 'station sessions started',
  'eventReport.health.removed': 'repeat or anonymous sessions',
  'eventReport.health.clean': 'unique verified contacts',
  'eventReport.health.leads': 'with email or phone',
  'eventReport.health.marketing': 'Marketing consent',
  'eventReport.health.marketingNeed': '{n} leads opted in. Only those can be contacted commercially.',
  'eventReport.health.noLeads': 'No leads yet, so the rate is pending rather than zero.',
  'eventReport.health.costs': 'Unit costs on file',
  'eventReport.notCaptured.title': 'Nothing was captured at this event.',
  'eventReport.notCaptured.body': 'There is not a single recorded interaction. This is not a filter: no play, survey or scan was written. Check the station links and the deployment status.',
  'eventReport.health.costsValue': '{costed} of {n}',
  'eventReport.health.costsNeed': 'Missing on {items}. A missing cost is absent, never zero, so value and cost per lead stay pending.',
  'eventReport.health.costsOk': 'Every item is costed, so value and cost per lead compute.',
  'eventReport.health.inPlay': 'Still in play',
  'eventReport.health.inPlayNeed': 'Units still allocated to stations. Reconciliation completes when they are collected or returned.',
  'eventReport.health.inPlayOk': 'Nothing left in play. Every allocated unit has a destination.',
  'eventReport.health.trafficNeed': 'Needs footprint traffic for the activation zone. Pending, not zero.',
  'liveMonitor.block': 'This block · {n} of peak {peak}',
  'liveMonitor.inPlay': '{n} in play of {a} allocated',
  'liveMonitor.hint.interactions': 'station flows started',
  'liveMonitor.hint.leads': 'contacts with email or phone',
  'liveMonitor.hint.prizes': 'Operator confirmed · {inPlay} still in play of {allocated} allocated',
  'liveMonitor.pace.noGoal': 'No lead goal is set for this event, so Track can count but it cannot tell you whether you are winning. Set one in Plan.',
  'liveMonitor.pace.inherited': 'Target inherited from the project.',
  'liveMonitor.pace.ahead': '{done}% of the capture window has run and {got}% of the lead goal is in. Ahead of pace.',
  'liveMonitor.pace.behind': '{done}% of the capture window has run and {got}% of the lead goal is in. Behind pace, and the gap widens every block you do not close it.',
  'liveMonitor.pace.closed': 'Closed at {n} leads against a goal of {goal}.',
  'liveMonitor.throughput': 'Throughput per capture window',
  'liveMonitor.trackNote': 'No demographics, no cost, no commentary on Track. The field lead is deciding whether to restock and where to move staff. Everything else waits for Report.',
  'liveMonitor.ready.kicker': 'Readiness · checked before doors open',
  'liveMonitor.ready.head': '{n} of {total} checks clear.',
  'liveMonitor.ready.stations': 'Stations configured',
  'liveMonitor.ready.survey': 'Survey deployed on a station',
  'liveMonitor.ready.merch': 'Merch allocated',
  'liveMonitor.ready.costs': 'Unit costs on file',
  'liveMonitor.ready.photos': 'Real product photos (no outline placeholders)',
  'liveMonitor.ready.goals': 'Goals set',
  'liveMonitor.ready.ok': 'Ready',
  'liveMonitor.ready.blocks': 'Blocks a figure',
  'liveMonitor.ready.note': 'Counters stay empty until the first interaction. An empty counter is a pending, not a zero.',
  'projectReport.kpi.events': 'Events',
  'projectReport.kpi.liveNow': 'Live now',
  'projectReport.kpi.eventsHint': '{done} closed, {live} live, {upcoming} upcoming',
  'projectReport.kpi.contacts': 'Contacts',
  'projectReport.kpi.contactsHint': 'Unique people across the project, one bar per capture',
  'projectReport.kpi.pace': 'Pace to goal',
  'projectReport.kpi.paceHint': '{n} of {goal} leads from closed events',
  'projectReport.kpi.paceNoGoal': 'Set lead goals on the events to measure pace',
  'projectReport.kpi.closed': 'Closed',
  'projectReport.kpi.liveProvisional': 'Live, provisional',
  'projectReport.kpi.remaining': 'Remaining',
  'projectReport.kpi.delivered': 'Prizes delivered',
  'projectReport.kpi.deliveredHint': 'Operator confirmed, read off the ledger',
  'projectReport.kpi.unaccounted': 'Unaccounted',
  'projectReport.kpi.clean': 'Clean',
  'projectReport.kpi.accounted': 'Accounted',
  'projectReport.kpi.unaccountedHint': 'Units that left the game with no redemption record',
  'projectReport.kpi.repeat': 'Repeat contacts',
  'projectReport.kpi.repeatPending': 'Needs contacts across the project',
  'projectReport.kpi.newPeople': 'New people',
  'projectReport.kpi.returning': 'Returning',
  'projectReport.captures.title': 'Contacts by capture',
  'projectReport.captures.note': 'One bar per event, in date order. Hover for the lead rate.',
  'projectReport.captures.empty': 'No captures with data yet.',
  'projectReport.captures.tooltip': '{contacts} contacts · {rate} lead rate',
  'projectReport.deployed.title': 'Premiums deployed to date',
  'projectReport.deployed.basis': '{total} total · {left} left',
  'projectReport.deployed.lightNote': '{n} item(s) under 45% deployed across the project',
  'projectReport.deployed.okNote': 'Every item is over 45% deployed across the project',
  'projectReport.weather.title': 'Conditions on the day',
  'projectReport.weather.note': 'Weather is a covariate on throughput, so it belongs beside the lead rate, not in a footnote.',
  'projectReport.weather.days': '{n} days',
  'projectReport.weather.rain': '{n} in rain',
  'projectReport.weather.dry': 'dry',
  'projectReport.weather.leadRate': '{p} lead rate',
  'projectReport.singleNote': '{name} has run once, so everything here is a single observation. Drift needs a second point before it means anything.',
  'projectTrack.kicker': 'Track · project altitude',
  'projectTrack.nothingLive': 'Track only ever follows one event, because a field lead is standing at one station. Nothing is live in this project right now, so there is no live surface to show.',
  'projectTrack.followingLive': 'Following the live event',
  'projectTrack.otherLive': '{n} other event(s) live in this project',
  'projectTrack.openTrack': 'Open event Track',
};

const ES: Dict = {
  // ── Common ─────────────────────────────────────────────────────────
  'common.edit': 'Editar',
  'common.comingSoon': 'Próximamente',
  'common.save': 'Guardar',
  'common.cancel': 'Cancelar',
  'common.done': 'Listo',
  'profile.privacy.eyebrow': 'Privacidad',
  'profile.privacy.profileLabel': 'Tarjeta de perfil',
  'profile.privacy.profileHint': 'Quién puede ver tu nombre, bio y tema.',
  'profile.privacy.activityLabel': 'Cronología de actividad',
  'profile.privacy.activityHint': 'Si tu calendario y actividad aparecen entre cuentas.',
  'profile.privacy.public': 'Público',
  'profile.privacy.private': 'Privado',
  'profile.privacy.saved': 'Preferencia de privacidad guardada',
  'profile.privacy.saveError': 'No se pudo guardar la preferencia',
  'common.close': 'Cerrar',
  'common.delete': 'Eliminar',
  'common.create': 'Crear',
  'common.signOut': 'Cerrar sesión',
  'datePicker.placeholder': 'Selecciona una fecha',
  'datePicker.clear': 'Borrar fecha',
  'datePicker.time': 'Hora',
  'tour.start': 'Hacer recorrido',
  'tour.next': 'Siguiente',
  'tour.prev': 'Atrás',
  'tour.done': 'Listo',
  'tour.progress': '{{current}} de {{total}}',
  'tour.restart': 'Reiniciar los recorridos',
  'tour.restartHint': 'Vuelve a ver las guías al visitar cada página.',
  'tour.restartDone': 'Recorridos reiniciados — se mostrarán de nuevo al navegar.',
  'journeyPreview.title': 'Vista previa en vivo',
  'brandTree.label': 'Explorar proyectos, eventos y estaciones',
  'brandTree.loadError': 'No se pudo cargar',
  'brandTree.empty.projects': 'Sin proyectos aún',
  'brandTree.empty.events': 'Sin eventos',
  'brandTree.empty.stations': 'Sin estaciones',
  'common.unassigned': 'Sin asignar',
  'common.upcoming': 'Próximos',
  'common.live': 'En vivo',
  'common.idle': 'Inactivo',
  'common.export': 'Exportar',
  'common.search': 'Buscar…',
  'common.loading': 'Cargando…',

  // ── TopBar ─────────────────────────────────────────────────────────
  'topbar.workspace': 'CUENTA',
  'topbar.agency': 'AGENCIA',
  'topbar.atledi': 'Atledi',
  'topbar.atlediSoon': 'Atledi llegará pronto',
  'topbar.resetDemo': 'Reiniciar datos de demo',
  'topbar.theme': 'Tema',
  'topbar.language': 'Idioma',
  'topbar.openMenu': 'Abrir menú',
  'topbar.closeMenu': 'Cerrar menú',
  'topbar.help': 'Ayuda y documentación',

  // ── Rejilla de aplicaciones ────────────────────────────────────────
  'apps.open': 'Aplicaciones de Onxite',
  'apps.title': 'Aplicaciones de Onxite',
  'apps.current': 'Estás aquí',
  'apps.comingSoon': 'Pronto',
  'apps.lockedHint': 'No está en tu plan — escríbenos para añadirla',
  'apps.opening': 'Abriendo {app}…',
  'apps.switchFailed': 'No se pudo abrir esa aplicación. Inténtalo otra vez.',

  // ── Selector de workspace (pill de brand alineado a la izquierda) ──
  'topbar.workspaceSwitcher.searchPlaceholder': 'Buscar marcas…',
  'topbar.workspaceSwitcher.empty': 'Sin marcas coincidentes',
  'topbar.workspaceSwitcher.clear': 'Limpiar búsqueda',
  'topbar.workspaceSwitcher.noBrand': 'Elegir marca',

  // ── Árbol de brand del sidebar (marca seleccionada + proyectos/eventos)
  'sidebar.brandTree.projects': 'Proyectos',
  'sidebar.brandTree.events': 'Eventos',
  'sidebar.brandTree.noProjects': 'Sin proyectos aún',
  'sidebar.brandTree.noEvents': 'Sin eventos aún',
  'sidebar.brandTree.noStations': 'Sin estaciones aún',
  'sidebar.brandTree.expand': 'Expandir',
  'sidebar.brandTree.collapse': 'Colapsar',

  // ── Pestañas del perfil de marca (Proyectos · Xotskies · Sobrantes)
  'brand.tabs.projects': 'Proyectos',
  'brand.tabs.xotskies': 'Merch',
  'brand.tabs.inventory': 'Inventario',
  'brand.tabs.leftovers': 'Sobrantes',
  'brand.leftovers.title': 'Inventario sobrante de eventos pasados',
  'brand.leftovers.subtitle': 'Eventos pasados con merch sin usar todavía asignados a sus estaciones.',
  'brand.leftovers.emptyTitle': 'Sin sobrantes',
  'brand.leftovers.emptyBody': 'Ningún evento pasado terminó con unidades disponibles. Cuando un evento cierre con sobrantes aparecerá aquí.',
  'brand.leftovers.noBrand': 'Sin marca seleccionada',
  'brand.leftovers.noBrandBody': 'Elige una marca en el selector del topbar para ver su inventario sobrante.',
  'brand.leftovers.totalLeftover': 'Total sobrante',
  'brand.leftovers.units': 'unidades',
  // Legacy — para deep-links antiguos.
  'brand.tabs.prizes': 'Inventario',
  'brand.xotskies.title': 'Merch de la marca',
  'brand.xotskies.subtitle': 'Catálogo de merch de esta marca. Añade o importa en bloque desde un ZIP.',
  'brand.xotskies.add': 'Añadir merch',
  'brand.xotskies.import': 'Importar en bloque',
  'brand.xotskies.emptyTitle': 'Sin merch aún',
  'brand.xotskies.emptyBody': 'Añade el primer ítem de merch para esta marca o importa un ZIP.',
  'brand.xotskies.noBrand': 'Sin marca seleccionada',
  'brand.xotskies.noBrandBody': 'Elige una marca en el selector del topbar para gestionar su merch.',
  'brand.xotskies.agencyCatalog': 'Disponibles en el catálogo del agency',

  'brand.xotskies.counter.total.label': 'Total',
  'brand.xotskies.counter.total.help':
    'Unidades dadas de alta de este artículo. Reponer lo sube.',
  'brand.xotskies.counter.allocated.label': 'Asign.',
  'brand.xotskies.counter.allocated.help':
    'Comprometido: unidades en juego ahora mismo en eventos abiertos. Vuelven al almacén al cerrar el evento si no se ganaron.',
  'brand.xotskies.counter.delivered.label': 'Entreg.',
  'brand.xotskies.counter.delivered.help':
    'Entregado: en manos de un visitante, según el libro de premios de todos los eventos de esta marca. Esto no vuelve.',
  'brand.xotskies.counter.available.label': 'Dispon.',
  'brand.xotskies.counter.available.help':
    'Lo que la marca posee ahora mismo: el almacén más lo que sigue en juego. Réstale Comprometido para saber lo libre para asignar.',
  'brand.xotskies.agencyCatalogEmpty': 'Ya adoptaste todos el merch del agency, o el catálogo está vacío.',
  'brand.xotskies.deleteAria': 'Eliminar merch',
  'brand.xotskies.deleteBlocked': 'No se puede eliminar el ítem de merch',
  'brand.xotskies.deleteBlockedHint': 'Tiene asignaciones activas en alguna estación — elimina las asignaciones antes de borrarlo.',
  'brand.xotskies.writeOffBlocked': 'No se puede descontar más de {available} unidad(es) disponibles.',

  // ── Theme menu ─────────────────────────────────────────────────────
  'theme.brand': 'Xotski',
  'plan.banner.dismiss': 'Descartar',
  'rightRail.title': 'Panel',
  'planner.drop.xotskiNoStations': 'Añade primero una estación a este evento — el merch vive en las estaciones.',
  'planner.drop.xotskiNoStock': 'No queda {name} por repartir — todas las unidades están asignadas.',
  'planner.drop.xotskiSpread': 'Repartidos {total} × {name} entre {count} estación(es) — ajústalo en el panel.',
  'planner.station.editAllocations': 'Editar merch de esta estación',
  'planner.station.allocationsTitle': 'Merch · {name}',
  'stations.detail.allocationsReadonly': 'Lo que lleva esta estación. Solo lectura aquí — a mitad de evento no es momento de replanificar inventario.',
  'stations.detail.allocationsEditLink': 'Editar en el tablero del proyecto',
  'planner.properties.journeyFromProject': 'Este evento usa el guest journey del proyecto. Cámbialo en la pestaña Design Journey del proyecto y se aplica en todas partes.',
  'planner.gameGlance.title': 'El juego que corre aquí y cuántos huecos tiene',
  'planner.gameGlance.slotOne': 'hueco',
  'planner.gameGlance.slotMany': 'huecos',
  'planner.gameGlance.game.wheel': 'Ruleta de premios',
  'planner.gameGlance.game.scratch': 'Rasca y gana',
  'planner.gameGlance.game.xinkit': 'Xinkit',
  'planner.gameGlance.game.pirinola': 'Pirinola',
  'planner.gameGlance.game.slots': 'Tragamonedas',
  'planner.gameGlance.game.randomDraw': 'Sorteo',
  'planner.gameGlance.game.soccer': 'Tiro penal',
  'planner.gameGlance.game.fromSurvey': 'El juego de la encuesta',
  'access.roles.advanced': 'Avanzado — elegir capacidades una a una',
  'access.roles.advancedHint': 'Solo hace falta cuando un preajuste se acerca pero no encaja. Las capacidades se agrupan por lo que permiten hacer.',
  'assistant.comingSoon': 'Próximamente',
  'entityHome.atledi.comingSoonBody': 'Atledi leerá tus eventos y te dirá qué cambió y qué hacer después. Aún no está activo — aquí es donde vivirá.',
  'eventReport.sections.label': 'Mostrar',
  'eventReport.sections.reach': 'Interactions y leads',
  'eventReport.sections.prizes': 'Premios entregados',
  'eventReport.sections.costs': 'Coste por resultado',
  'eventReport.sections.topAnswers': 'Respuestas top',
  'eventReport.sections.demographics': 'Demografía',
  'liveMonitor.title': 'En vivo ahora',
  'planner.xotskiCarousel.availableInProject': 'Quedan {n} en el pool de este proyecto',
  'auth.login.orContinueWith': 'o continúa con',
  'brandPulse.projectOne': 'proyecto',
  'brandPulse.projectMany': 'proyectos',
  'brandPulse.eventOne': 'evento',
  'brandPulse.eventMany': 'eventos',
  'brandPulse.live': 'en vivo',
  'brandPulse.liveNow': '{name} está corriendo ahora mismo.',
  'brandPulse.nextUp': 'Lo próximo: {name} · {when}',
  'brandPulse.nothingScheduled': 'Nada programado todavía.',
  'brandPulse.watchLive': 'Ver en vivo',
  'brandPulse.openNext': 'Abrirlo',
  'brandPulse.scheduleEvent': 'Programar un evento',
  'sampleGame.cta': 'Ver un juego de muestra',
  'sampleGame.title': 'Un juego de muestra',
  'sampleGame.description': 'Tus colores, premios inventados. No se guarda nada ni se gasta mercancía — es solo para ver lo que verán los invitados.',
  'sampleGame.won': 'Cayó en {name}. Un invitado real recibiría su premio aquí.',
  'sampleGame.again': 'Girar otra vez',
  'onboarding.kit.optionalHint': 'Opcional — puedes añadirlo después.',
  'onboarding.kit.defaultCampaign': 'Mi primera campaña',
  'onboarding.kit.changeLater': 'Todo esto vive en Estudio de marca. Cámbialo cuando quieras.',
  'register.toast.tooMany': 'Demasiados registros desde esta conexión',
  'register.toast.tooManyBody': 'Los registros están limitados un rato. Inténtalo en unos minutos, o inicia sesión si ya tienes cuenta.',
  'prizes.toast.adopted': 'Merch adoptado',
  'prizes.toast.adoptFailed': 'No se pudo adoptar',
  'prizes.toast.restocked': 'Inventario actualizado',
  'prizes.toast.restockFailed': 'No se pudo actualizar el inventario',
  'prizes.toast.removed': 'Quitado de la marca',
  'prizes.toast.removeFailed': 'No se pudo quitar',
  'prizes.counter.total': 'Total',
  'prizes.counter.allocated': 'En estaciones',
  'prizes.counter.delivered': 'Entregados',
  'prizes.counter.available': 'Libres',
  'prizes.restock.placeholder': '+ unidades',
  'prizes.restock.action': 'Actualizar inventario',
  'prizes.restock.hint': 'Un número positivo añade unidades; uno negativo las da de baja.',
  'validation.nameRequired': 'Hace falta un nombre',
  'validation.quantityMin': 'Introduce al menos 1',
  'validation.companyNameRequired': 'Hace falta el nombre de la empresa',
  'validation.contactNameRequired': 'Hace falta el nombre de contacto',
  'validation.industryRequired': 'Elige un sector',
  'validation.campaignNameRequired': 'Hace falta el nombre de la campaña',
  'validation.logoRequired': 'Hace falta un logo',
  'validation.hexColor': 'Usa un color HEX, como #C8E530',
  'forms.imageEditor.useOriginal': 'Usar tal cual',
  'roleForm.description': 'Descripción',
  'roleForm.optional': '(opcional)',
  'roleForm.namePlaceholder': 'admin-de-marca, lector…',
  'roleForm.descriptionPlaceholder': 'Para qué sirve este rol, cuándo asignarlo…',
  'roleForm.descriptionHint': 'Se muestra en la lista de roles y en el chip del miembro.',
  'roleForm.brandScope': 'Marcas ({n})',
  'roleForm.brandScopeHint': 'Quien tenga este rol verá todas estas marcas, y todo lo que cuelga de ellas.',
  'roleForm.noBrands': 'No hay marcas que puedas gestionar.',
  'roleForm.presets': 'Preajustes',
  'roleForm.presetsHint': 'Un preajuste suma sus capacidades a lo ya seleccionado — puedes encadenar varios y luego ajustar.',
  'roleForm.clear': 'Limpiar',
  'roleForm.clearHint': 'Quitar todas las capacidades de este rol',
  'roleForm.titleCreate': 'Crear rol',
  'roleForm.titleEdit': 'Editar rol · {name}',
  'roleForm.titleClone': 'Clonar rol · {name}',
  'roleForm.subtitleCreate': 'Define un rol reutilizable para tu cuenta. Usa las dos listas para conceder capacidades por área.',
  'roleForm.subtitleEdit': 'Actualiza el rol y lo que puede hacer. Los cambios se guardan juntos o no se guardan.',
  'roleForm.subtitleClone': 'Un rol nuevo con las capacidades de «{name}». Elige un nombre y las marcas a las que se aplica esta copia.',
  'roleForm.name': 'Nombre',
  'roleForm.nameRequired': 'Hace falta un nombre',
  'roleForm.companyRequired': 'Elige al menos una marca para este rol',
  'roleForm.created': 'Rol creado',
  'roleForm.updated': 'Rol actualizado',
  'roleForm.save': 'Guardar cambios',
  'roleForm.createClone': 'Crear clon',
  'quickStart.title': 'Tres pasos para un juego en vivo',
  'quickStart.step1': 'Añade merch',
  'quickStart.step1Hint': 'La mercancía que los invitados pueden ganar.',
  'quickStart.step1Cta': 'Añadir',
  'quickStart.step2': 'Crea un evento',
  'quickStart.step2Hint': 'Dónde y cuándo lo vas a repartir.',
  'quickStart.step2Cta': 'Crear',
  'quickStart.step3': 'Obtén el enlace del juego',
  'quickStart.step3Hint': 'Preparamos la estación, el journey y el juego por ti.',
  'quickStart.step3Cta': 'Obtener enlace',
  'quickStart.linkReady': 'Tu juego está en vivo. Comparte esto con los invitados.',
  'quickStart.copy': 'Copiar',
  'quickStart.copied': 'Copiado',
  'quickStart.preview': 'Vista previa',
  'quickStart.failed': 'No se pudo preparar tu juego',
  'quickStart.needEvent': 'Crea un evento primero',
  'quickStart.noTemplate': 'Aún no hay ningún punto de partida de journey disponible',
  'planner.pool.title': 'Comprometer {name} a este proyecto',
  'planner.pool.description': 'El total que este proyecto puede gastar, no una cantidad a añadir. Sus estaciones asignan de aquí, no directamente de la marca.',
  'planner.pool.field': 'Unidades totales para este proyecto',
  'planner.pool.brandHas': 'La marca tiene {n} disponibles',
  'planner.pool.range': 'Entre {min} (ya en las estaciones de este proyecto) y {max} \u2014 a la marca le quedan {free} libres',
  'planner.pool.atLeast': 'Al menos {min}, que ya están en las estaciones de este proyecto',
  'planner.pool.belowAllocated': 'Ya hay {n} en las estaciones de este proyecto',
  'planner.pool.aboveBrand': 'Como mucho {max}: a la marca solo le quedan {free} libres además de lo que ya está en estaciones',
  'planner.pool.currently': 'Ahora: {committed} comprometidas \u00b7 {allocated} en estaciones \u00b7 {remaining} libres',
  'planner.pool.commit': 'Comprometer',
  'planner.pool.commitTip': 'Comprometer unidades de este ítem de merch al proyecto',
  'planner.pool.committed': 'Comprometidas {n} \u00d7 {name} a este proyecto',
  'planner.pool.commitFailed': 'No se pudieron comprometer esas unidades',
  'rightRail.collapse': 'Contraer panel',
  'rightRail.expand': 'Expandir panel',
  'rightRail.empty': 'Selecciona un proyecto, evento o estación para editarlo aquí.',
  'stations.detail.playGameHint': 'Abre la vista previa del juego en vivo — simulación, no consume merch',
  'stations.detail.editJourneyHint': 'Edita este juego en Design Journey',
  'stations.qrPrint.downloadOrPrint': 'Descargar / imprimir',
  'designJourney.tabLabel': 'Design Journey',
  'theme.dark': 'Oscuro',
  'theme.light': 'Claro',

  // ── Dashboard / Home ───────────────────────────────────────────────
  'dashboard.portfolio': 'Portafolio de marcas',
  'dashboard.brandHub': 'Brand Hub',
  'dashboard.brandsLower': 'marcas',
  'dashboard.kpi.liveActivations': 'Activaciones en vivo',
  'dashboard.kpi.spend': 'Gasto',
  'dashboard.kpi.peopleReached': 'Personas alcanzadas (YTD)',
  'dashboard.subBrands': 'Sub-marcas',
  'dashboard.brandManagers': 'Gestores de marca',
  'dashboard.newBrand': 'Nueva marca',
  'dashboard.noBrandsTitle': 'Aún no hay marcas',
  'dashboard.noBrandsBody':
    'Agrega una marca para empezar a correr proyectos en tu cuenta.',
  'dashboard.noManagers':
    'Aún no hay gestores asignados. Invita a tu equipo desde Ajustes.',

  // ── SubBrand card ──────────────────────────────────────────────────
  'subbrand.projects': 'Proyectos',
  'subbrand.events': 'Eventos',
  'subbrand.active': 'Activos',
  'subbrand.ownedBy': 'Gestionado por',
  'subbrand.ytd': 'YTD',

  // ── Brand profile page ─────────────────────────────────────────────
  'brand.workspace': 'Marca',
  'brand.editInStudio': 'Editar en Brand Studio',
  'brand.new.cta': 'Nuevo brand',
  'brand.list.cta': 'Todas las marcas',
  'brandsGallery.title': 'Marcas',
  'brandsGallery.subtitle': 'Todas las marcas en las que puedes trabajar, pintadas con su identidad y con lo que tiene cada una. Abre una para trabajar en ella o edita su identidad en Brand Studio.',
  'brandsGallery.empty': 'Aún no hay marcas.',
  'brandsGallery.root': 'Cuenta',
  'brandsGallery.current': 'Actual',
  'brandsGallery.open': 'Abrir marca',
  'brandsGallery.editIdentity': 'Editar identidad',
  'brandsGallery.noKit': 'Aún sin identidad',
  'brandsGallery.count.projects': 'Proyectos',
  'brandsGallery.count.events': 'Eventos',
  'brandsGallery.count.stations': 'Estaciones',
  'brandsGallery.count.merch': 'Merch',
  'brandsGallery.count.contacts': 'Contactos',
  'brand.new.title': 'Crear un nuevo brand',
  'brand.new.subtitle': 'Solo lo básico — el resto lo personalizás en Brand Studio a continuación.',
  'brand.new.nameLabel': 'Nombre del brand',
  'brand.new.namePlaceholder': 'ej. Acme Soda',
  'brand.new.industryLabel': 'Industria',
  'brand.new.industryPlaceholder': 'ej. Bebidas (opcional)',
  'brand.new.industryPick': 'Elige una (opcional)',
  'brand.new.logoLabel': 'Logotipo de la marca',
  'brand.new.logoPick': 'Subir un logotipo',
  'brand.new.logoReplace': 'Cambiar',
  'brand.new.logoUploading': 'Subiendo…',
  'brand.new.logoHint': 'Un PNG o SVG con el fondo transparente es lo que mejor se ve. Es opcional — también puedes ponerlo luego en Brand Studio.',
  'brand.new.logoFailed': 'No se pudo subir el logotipo',
  'brand.new.studioHint': 'Al crearlo te llevamos directo a Brand Studio para sumar el logo, los colores y las tipografías.',
  'brand.new.submit': 'Crear brand',
  'brand.new.creating': 'Creando…',
  'brand.new.created': 'Brand “{name}” creado',
  'brand.new.failed': 'No se pudo crear el brand',
  'brand.viewTrends': 'Ver tendencias',
  'brand.recentEvents': 'Eventos recientes',
  'brand.recentEvents.count': 'Mostrando {shown} de {total}',
  'common.open': 'Abrir',
  'brand.activeProjects': 'Proyectos activos',
  'brand.newProject': 'Nuevo proyecto',
  'brand.noProjectsTitle': 'Aún no hay proyectos',
  'brand.noProjectsBody':
    'Lanza tu primera campaña para configurar eventos, estaciones y merch.',
  'brand.createProject': 'Crear proyecto',
  'brand.kpi.activeXotskies': 'Merch activo',
  'brand.kpi.distributed': 'Distribuidos (YTD)',
  'brand.kpi.engagement': 'Tasa de interacción',
  'brand.kpi.health': 'Salud de marca',

  // ── Profile page ───────────────────────────────────────────────────
  'profile.tabs.events': 'Eventos',
  'profile.tabs.brands': 'Marcas',
  'profile.tabs.projects': 'Proyectos',
  'profile.tabs.stations': 'Estaciones',
  'profile.tabs.team': 'Equipo',
  'profile.events.upcoming': 'Próximos eventos',
  'profile.events.past': 'Eventos pasados',
  'profile.events.searchPlaceholder': 'Buscar eventos…',
  'profile.events.emptyUpcomingTitle': 'No hay eventos próximos',
  'profile.events.emptyUpcomingSubtitle': 'Crea un proyecto para empezar',
  'profile.events.emptyPastTitle': 'Aún no hay eventos pasados.',
  'profile.brands.emptyTitle': 'Marcas que gestionas',
  'profile.brands.emptySubtitle': 'Aquí verás las marcas asociadas a tu cuenta.',
  'profile.brands.nextEvent': 'Próximo evento',
  'profile.brands.noUpcoming': 'Sin eventos próximos',
  'profile.team.emptyTitle': 'Miembros del equipo',
  'profile.team.emptySubtitle': 'Invita colaboradores para verlos aquí.',
  'profile.activity.emptyTitle': 'Aún no hay actividad',
  'profile.activity.emptySubtitle':
    'Los recursos que crees, edites o elimines aparecerán aquí.',
  'profile.activity.loadErrorTitle': 'No se pudo cargar la actividad',
  'profile.activity.loadErrorSubtitle': 'Inténtalo de nuevo en un momento.',
  'profile.activity.lastActivity': 'Última actividad {date}',
  'profile.activity.historyToggle': 'Historial ({count})',
  'profile.activity.badgeDeleted': 'Eliminado',
  'profile.activity.noun': 'recursos',
  'profile.activity.history.created': 'Creó este {type}',
  'profile.activity.history.updated': 'Editó {fields}',
  'profile.activity.history.updatedGeneric': 'Editó este {type}',
  'profile.activity.history.deleted': 'Eliminó este {type}',
  'profile.activity.history.restored': 'Restauró este {type}',
  'profile.activity.type.brand': 'marca',
  'profile.activity.type.event': 'evento',
  'profile.activity.type.project': 'proyecto',
  'profile.activity.type.station': 'estación',
  'profile.sidebar.experience': 'Mi experiencia',
  'profile.sidebar.contact': 'Información de contacto',
  'profile.sidebar.professional': 'Profesional',
  'profile.sidebar.additional': 'Información adicional',
  'profile.sidebar.preferences': 'Preferencias',
  'profile.sidebar.languages': 'Idiomas',
  'profile.sidebar.interests': 'Intereses',
  'profile.sidebar.editProfile': 'Editar perfil',
  'profile.sidebar.changePassword': 'Cambiar contraseña',
  'profile.sidebar.theme': 'Tema',
  'profile.sidebar.language': 'Idioma',
  'profile.sidebar.noBio':
    'Aún no hay biografía. Haz clic en "Editar perfil" para agregar tu experiencia.',

  // ── Pagination ─────────────────────────────────────────────────────
  'pagination.previous': 'Anterior',
  'pagination.next': 'Siguiente',
  'pagination.previousPage': 'Página anterior',
  'pagination.nextPage': 'Página siguiente',
  'pagination.showing': 'Mostrando',
  'pagination.of': 'de',
  'pagination.items': 'elementos',

  // ── Global search ──────────────────────────────────────────────────
  'globalSearch.placeholder': 'Buscar lo que sea — eventos, marcas, estaciones, merch',
  'globalSearch.clear': 'Limpiar búsqueda',
  'globalSearch.noMatches': 'Sin resultados para',
  'globalSearch.kind.brand': 'Marcas',
  'globalSearch.kind.event': 'Eventos',
  'globalSearch.kind.station': 'Estaciones',
  'globalSearch.kind.stock': 'Merch',
  'globalSearch.subBrand': 'Marca',
  'globalSearch.subEvent': 'Evento',
  'globalSearch.subStation': 'Estación',
  'globalSearch.subStockItem': 'Artículo de merch',

  // ── Right sidebar / AI Assistant ───────────────────────────────────
  'assistant.title': 'Atledi',
  'assistant.collapse': 'Contraer asistente',
  'assistant.expand': 'Abrir asistente',
  // Caso 07 — Etiquetas del launcher + drawer.
  'assistant.launcher.aria': 'Abrir asistente IA',
  'assistant.drawer.aria': 'Asistente IA',
  'assistant.kickoffPending': 'Leyendo contexto de la página…',
  'assistant.placeholder': 'Pregunta lo que quieras sobre esta página…',
  'assistant.send': 'Enviar',
  'assistant.suggestions': 'Sugerencias',
  'assistant.error': 'No pude conectar con el asistente. Intenta de nuevo.',
  'assistant.error.unavailable': 'El asistente no está disponible por el momento. Inténtalo en un momento.',
  'assistant.retry': 'Reintentar',

  // ── Export XLSX ────────────────────────────────────────────────────
  'export.button': 'Exportar XLSX',
  'export.exporting': 'Exportando…',
  'export.nothingTitle': 'Nada para exportar',
  'export.nothingBody': 'La lista está vacía.',
  'export.readyTitle': 'Exportación lista',
  'export.readyBodyOne': 'fila descargada.',
  'export.readyBodyMany': 'filas descargadas.',
  'export.failedTitle': 'Error al exportar',

  // ── Forms / file uploader ──────────────────────────────────────────
  'forms.colorField.pickColor': 'Elegir color',
  'forms.uploader.formats': 'PNG, JPG, WEBP o SVG',
  'forms.uploader.uploadFailed': 'Error al subir',
  'forms.uploader.cancel': 'Cancelar',
  'forms.uploader.edit': 'Editar',
  'forms.uploader.remove': 'Quitar',
  'forms.uploader.retry': 'Reintentar',
  'forms.imageEditor.title': 'Editar imagen',
  'forms.imageEditor.zoomOut': 'Alejar',
  'forms.imageEditor.zoomIn': 'Acercar',
  'forms.imageEditor.rotateLeft': 'Girar izquierda',
  'forms.imageEditor.rotateRight': 'Girar derecha',
  'forms.imageEditor.zoom': 'Zoom',
  'forms.imageEditor.brightness': 'Brillo',
  'forms.imageEditor.contrast': 'Contraste',
  'forms.imageEditor.saturation': 'Saturación',
  'forms.imageEditor.reset': 'Restablecer',
  'forms.imageEditor.cancel': 'Cancelar',
  'forms.imageEditor.apply': 'Aplicar recorte',
  'forms.imageEditor.hint': 'Arrastra para reubicar · Rueda para zoom · Botones ± para control fino',
  'forms.imageEditor.loadError':
    'No se ha podido cargar la imagen. Puede que se haya borrado, o que el servidor de ficheros no responda.',
  'forms.rating.starOne': '1 estrella',
  'forms.rating.starMany': 'estrellas',

  // ── Common buttons / states ────────────────────────────────────────
  'common.saving': 'Guardando…',
  'common.next': 'Siguiente',
  'common.back': 'Atrás',
  'common.continue': 'Continuar',
  'common.skip': 'Omitir',
  'common.submit': 'Enviar',
  'common.confirm': 'Confirmar',
  'common.add': 'Agregar',
  'common.remove': 'Quitar',
  'common.update': 'Actualizar',
  'common.optional': 'Opcional',
  'common.required': 'Obligatorio',
  'common.yes': 'Sí',
  'common.no': 'No',
  'common.retry': 'Reintentar',
  'common.viewAll': 'Ver todo',
  'common.error': 'Algo salió mal',
  'common.tryAgain': 'Inténtalo de nuevo.',
  'common.empty': 'Aún no hay nada aquí',
  'common.copy': 'Copiar',
  'common.copied': 'Copiado',
  'common.draft': 'Borrador',
  'common.active': 'Activo',
  'common.disabled': 'Deshabilitado',
  'common.enabled': 'Habilitado',
  'common.preview': 'Vista previa',
  'common.print': 'Imprimir',
  'common.download': 'Descargar',
  'common.duplicate': 'Duplicar',
  'common.archive': 'Archivar',
  'common.locked': 'Bloqueado',
  'common.unlocked': 'Desbloqueado',

  // ── Onboarding ─────────────────────────────────────────────────────
  'onboarding.brandProfile.title': 'Cuéntanos sobre tu marca',
  'onboarding.brandProfile.subtitle': 'Algunos datos para personalizar tu cuenta.',
  'onboarding.brandProfile.brandName': 'Nombre de la marca',
  'onboarding.brandProfile.brandNamePlaceholder': 'p. ej. Atledi',
  'onboarding.brandProfile.industry': 'Industria',
  'onboarding.brandProfile.industryPlaceholder': 'Selecciona una industria',
  'onboarding.brandProfile.website': 'Sitio web',
  'onboarding.brandProfile.websitePlaceholder': 'https://tumarca.com',
  'onboarding.brandProfile.about': 'Acerca de',
  'onboarding.brandProfile.aboutPlaceholder': 'Breve descripción de lo que hacen.',
  'onboarding.brandProfile.continue': 'Continuar',
  // ── Guía de los primeros días (My Merch) ────────────────────────────────
  'nextSteps.title': 'Lo que viene ahora',
  'nextSteps.subtitle': 'Tu merch es la mitad de la historia — le falta el evento donde se reparte.',
  'nextSteps.merch': 'Sube tu merch',
  'nextSteps.merchHint': 'Los premios y productos por los que van a jugar los invitados.',
  'nextSteps.project': 'Crea un proyecto',
  'nextSteps.projectHint': 'Un proyecto agrupa los eventos de una campaña — una gira, una temporada, un lanzamiento.',
  'nextSteps.event': 'Programa un evento',
  'nextSteps.eventHint': 'El evento es lo que sale a la calle: sus estaciones, su QR y sus premios.',
  'onboarding.profile.title': 'Cuéntanos sobre tu marca',
  'onboarding.profile.subtitle': 'Activaremos tu cuenta gratuita enseguida.',
  'onboarding.profile.companyName': 'Nombre de la empresa',
  'onboarding.profile.companyNamePlaceholder': 'Refresco Lima S.A.',
  'onboarding.profile.yourName': 'Tu nombre',
  'onboarding.profile.yourNamePlaceholder': 'Juana Fundadora',
  'onboarding.profile.industry': 'Industria',
  'onboarding.profile.pickOne': 'Elige una',
  'onboarding.profile.kind': '¿Qué describe mejor lo que haces?',
  'onboarding.profile.kind.Agency': 'Agencia',
  'onboarding.profile.kind.Agency.hint': 'Montas activaciones para otras marcas.',
  'onboarding.profile.kind.Company': 'Marca',
  'onboarding.profile.kind.Company.hint': 'Montas activaciones para tu propia marca.',
  'onboarding.profile.kind.Property': 'Propiedad',
  'onboarding.profile.kind.Property.hint': 'Acoges en tu recinto las activaciones de otros.',
  'onboarding.profile.continue': 'Continuar',
  'onboarding.profile.savedTitle': 'Perfil guardado',
  'onboarding.profile.savedBody': 'Tu plan gratuito está activo. Un paso más.',
  'onboarding.profile.errorTitle': 'No se pudo guardar',
  'onboarding.kit.title': 'Arma tu BrandKit',
  'onboarding.kit.subtitle': 'Usaremos estos valores predeterminados en journeys, juegos e impresos.',
  'onboarding.kit.campaignName': 'Nombre de la campaña',
  'onboarding.kit.campaignNamePlaceholder': 'Tour de Verano 2026',
  'onboarding.kit.primary': 'Primario',
  'onboarding.kit.secondary': 'Secundario',
  'onboarding.kit.font': 'Fuente',
  'onboarding.kit.clientLogo': 'Logo del cliente',
  'onboarding.kit.finish': 'Entrar a Xotski',
  'onboarding.kit.savedTitle': '¡BrandKit guardado!',
  'onboarding.kit.savedBody': 'Bienvenido a Xotski.',
  'onboarding.kit.errorTitle': 'No se pudo guardar el BrandKit',

  // ── Stock ──────────────────────────────────────────────────────────
  'stock.title': 'Merch',
  'stock.subtitle': 'Gestiona los artículos que entregas en tus activaciones.',
  'stock.addButton': 'Añadir merch',
  'stock.empty.title': 'Aún no hay merch',
  'stock.empty.body': 'Agrega tu primer ítem de merch para empezar a activar.',
  'stock.empty.cta': 'Añadir merch',
  'stock.column.name': 'Nombre',
  'stock.column.rating': 'Probabilidad',
  'stock.column.classification': 'Clasificación',
  'stock.column.status': 'Estado',
  'stock.column.actions': 'Acciones',
  'stock.searchPlaceholder': 'Buscar merch…',
  'stock.confirmDelete': '¿Eliminar este ítem de merch?',
  'stock.deletedTitle': 'Merch eliminado',
  'stock.savedTitle': 'Merch guardado',
  'stock.errorTitle': 'No se pudo guardar el ítem de merch',
  'stock.dialog.addTitle': 'Añadir merch',
  'stock.dialog.editTitle': 'Editar merch',
  'stock.dialog.name': 'Nombre',
  'stock.dialog.namePlaceholder': 'Botella muestra, postal, sticker…',
  'stock.dialog.translate': 'Nombre en español',
  'stock.dialog.translatePlaceholder': 'Traducción opcional',
  'stock.dialog.image': 'Imagen',
  'stock.dialog.logo': 'Logo',
  'stock.dialog.rating': 'Probabilidad en el juego',
  'stock.dialog.classification': 'Clasificación',
  'stock.dialog.classificationPlaceholder': 'Bebida, swag, cupón…',
  'stock.dialog.status': 'Estado',
  'stock.list.title': 'Mi Merch',
  'stock.list.subtitle': 'Los artículos de merch que aparecen en tus juegos e impresos.',
  'stock.list.addButton': 'Añadir merch',
  'stock.list.filter': 'Filtro',
  'stock.list.allClassifications': 'Todas las clasificaciones',
  'stock.list.unclassified': 'Sin clasificar',
  'stock.list.itemOne': 'artículo',
  'stock.list.itemMany': 'artículos',
  'stock.list.emptyTitle': 'Sin merch aún',
  'stock.list.emptyBody': 'Agrega los productos y merch que tus invitados pueden ganar.',
  'stock.list.allClassified': 'Todo está clasificado',
  'stock.list.allClassifiedBody': 'Cada artículo tiene una etiqueta SEE / ENGAGE / TRY / ACT.',
  'stock.list.noneInClass': 'No hay merch clasificados como',
  'stock.list.editToSetClass': 'Edita un ítem de merch y asigna su clasificación a',
  'stock.list.editToSetClassSuffix': 'para verlo aquí.',
  'stock.list.createdTitle': 'Merch añadido al inventario',
  'stock.list.updatedTitle': 'Merch actualizado',
  'stock.list.createFailed': 'Error al crear',
  'stock.list.updateFailed': 'Error al actualizar',
  'stock.card.edit': 'Editar',
  'stock.card.delete': 'Eliminar',
  'stock.card.rarityTier': 'Nivel de rareza',
  'stock.card.rarityLabel.1': 'Común',
  'stock.card.rarityLabel.2': 'Poco común',
  'stock.card.rarityLabel.3': 'Raro',
  'stock.card.rarityLabel.4': 'Épico',
  'stock.card.rarityLabel.5': 'Legendario',
  'stock.card.qty': 'Cant.',
  'stock.card.ratingOf': 'Valoración',
  'stock.dialog.editingTitle': 'Editar merch',
  'stock.dialog.addingTitle': 'Añadir merch',
  'stock.dialog.updateHeading': 'Actualizar',
  'stock.dialog.updateHeadingFallback': 'merch',
  'stock.dialog.newHeading': '¿Cuál es el nuevo merch?',
  'stock.dialog.editLead': 'Ajusta lo básico — los cambios se aplican donde sea que aparezca este ítem de merch.',
  'stock.dialog.newLead': 'Define lo básico. Asignarás cantidades a estaciones específicas desde Mi Merch más tarde.',
  'stock.dialog.section.identity': 'Identidad',
  'stock.dialog.section.visuals': 'Visuales',
  'stock.dialog.section.inventory': 'Inventario',
  'stock.dialog.section.funnel': 'Etapa del funnel',
  'stock.dialog.section.rarity': 'Rareza',
  'stock.dialog.field.name': 'Nombre',
  'stock.dialog.field.namePlaceholder': 'p. ej. Pin Joyly esmaltado',
  'stock.dialog.field.translate': 'Nombre en español (traducción)',
  'stock.dialog.field.translatePlaceholder': 'Se muestra cuando la journey corre en en-es',
  'stock.dialog.field.description': 'Descripción',
  'stock.dialog.field.descriptionPlaceholder': 'Una descripción breve mostrada al ganar el premio…',
  'stock.dialog.field.productImage': 'Imagen del producto',
  'stock.dialog.field.wheelLogo': 'Logo en el juego',
  'stock.dialog.field.initialQty': 'Cantidad inicial',
  'stock.dialog.field.unitCost': 'Valor unitario (USD)',
  'stock.dialog.field.unitCostHint': 'Opcional. Lo que vale una unidad. Alimenta el valor distribuido de informes y actas de cierre.',
  'stock.dialog.destination.create': 'Este merch se añadirá a {brand}.',
  'stock.dialog.destination.edit': 'Este merch pertenece a {brand}.',
  'stock.dialog.destination.confirm': 'Sí, es de {brand} (tu cuenta tiene más de una marca).',
  'stock.list.addedTo': 'Añadido a {brand}',
  'stock.list.seeInventory': 'Ver inventario de marca',
  'stock.dialog.field.wheelLogoLibrary': 'Elegir de la biblioteca',
  'stock.dialog.field.wheelLogoLibraryEmpty':
    'Sin logos curados aún. Coloca archivos en ox-platform/uploads/wheel-logos/ para poblar la biblioteca.',
  'stock.library.title': 'Biblioteca de logos',
  'stock.library.count': '{n} logos disponibles. Elige uno y ocupa el hueco del logo; puedes seguir subiendo el tuyo.',
  'stock.library.search': 'Buscar por nombre',
  'stock.library.empty': 'La biblioteca está vacía.',
  'stock.library.noMatch': 'Nada coincide con «{q}».',
  'stock.library.pickedHint': 'De la biblioteca: {name}',
  'stock.outline.pick': 'Elegir silueta',
  'stock.outline.title': 'Siluetas de merch',
  'stock.outline.description': 'Placeholders por tipo de ítem, en tres tintas. La silueta suple hasta que se suba la foto real del producto; los ítems que sigan con silueta se marcan en My Merch y en Track.',
  'stock.outline.empty': 'Aún no hay siluetas cargadas. Suelta el set en ox-platform/uploads/merch-outlines/<categoría>/.',
  'stock.outline.placeholderHint': 'Silueta provisional. Sube la foto real del producto cuando la tengas.',
  'stock.outline.badge': 'Silueta',
  'stock.warehouse.title': 'Qué hay en el almacén',
  'stock.warehouse.ledgerChip': 'Calculado de {n}+ movimientos del libro',
  'stock.warehouse.portfolio': 'Cartera',
  'stock.warehouse.portfolioHint': '{n} ítems · {value} a coste',
  'stock.warehouse.portfolioPending': '{n} ítems · valor pendiente en {k} ítem(s)',
  'stock.warehouse.committed': 'Comprometido',
  'stock.warehouse.committedHint': '{p}% de las unidades en propiedad, en {n} evento(s) abierto(s)',
  'stock.warehouse.restock': 'Necesita reposición',
  'stock.warehouse.restockHint': 'Lo más urgente: {name}',
  'stock.warehouse.restockNone': 'Nada por debajo del umbral',
  'stock.warehouse.unaccounted': 'Sin destino',
  'stock.warehouse.unaccountedHint': '{value} a coste · último cierre {last}',
  'stock.warehouse.costPending': 'coste pendiente',
  'stock.warehouse.never': 'nunca',
  'stock.warehouse.chipAction': 'Acción',
  'stock.warehouse.chipDisposition': 'Necesita disposición',
  'stock.warehouse.action.costs': 'Pon coste unitario a {k} de {n} ítems para desbloquear el valor de la cartera y el coste por lead.',
  'stock.warehouse.action.costsCta': 'Poner costes',
  'stock.warehouse.action.reallocate': 'A {name} no le queda nada en almacén: reasigna lo que sigue en juego o repón.',
  'stock.warehouse.action.reallocateCta': 'Reasignar',
  'stock.warehouse.action.counts': '{n} evento(s) terminado(s) siguen sin recuento de cierre.',
  'stock.warehouse.action.countsCta': 'Hacer recuentos',
  'stock.warehouse.action.moreWaiting': '{n} más esperando',
  'stock.warehouse.action.nothingElse': 'nada más esperando',
  'stock.dispo.kicker': 'Sin destino · necesita una disposición',
  'stock.dispo.head': '{units} unidades en {lots} lote(s) siguen sin disposición',
  'stock.dispo.allClear': 'Cada unidad sin destino tiene ya una disposición registrada',
  'stock.dispo.sub': 'Resolver un lote escribe un movimiento nuevo. El libro es «append only»: el recuento original queda en el registro.',
  'stock.dispo.basis': '{out} unidades salidas · {p}%',
  'stock.dispo.needs': 'Necesita disposición',
  'stock.dispo.blurb': 'Elige lo que pasó de verdad. Hasta entonces cuenta contra el evento.',
  'stock.dispo.kind.off_station': 'Entregado fuera de estación',
  'stock.dispo.kind.operations': 'Uso de operaciones',
  'stock.dispo.kind.write_off': 'Dar de baja',
  'stock.dispo.kind.recount': 'Recuento',
  'stock.dispo.kindBlurb.off_station': 'Se dio en el escenario o en la puerta sin escaneo. Sigue siendo una unidad entregada, sólo que no un resultado del juego.',
  'stock.dispo.kindBlurb.operations': 'Uniforme de staff, muestra o cortesía del recinto. Un hecho de inventario, no merma.',
  'stock.dispo.kindBlurb.write_off': 'Perdido de verdad. Pega en el valor distribuido y en la línea de merma del trimestre.',
  'stock.dispo.kindBlurb.recount': 'Seguían en la caja: las unidades vuelven al almacén.',
  'stock.dispo.units': 'Unidades (hasta {max})',
  'stock.dispo.note': 'Nota',
  'stock.dispo.notePlaceholder': 'Regalo de escenario, petición del organizador…',
  'stock.dispo.appendOnly': 'Esto escribe un movimiento nuevo. Una corrección después es otra fila, nunca una edición.',
  'stock.dispo.confirm': 'Registrar',
  'stock.dispo.savedToast': 'Disposición registrada',
  'stock.dispo.failedToast': 'No se pudo registrar la disposición',
  'stock.movements.title': 'Movimientos recientes',
  'stock.movements.appendOnly': 'Sólo se añade. Una corrección es una fila nueva, nunca una edición.',
  'stock.movements.empty': 'Aún no hay movimientos. Asignaciones, entregas, cierres y disposiciones aparecen aquí según ocurren.',
  'stock.movements.noNote': 'sin nota',
  'stock.movements.noun': 'movimientos',
  'stock.movements.state.warehouse': 'almacén',
  'stock.movements.state.allocated': 'asignado',
  'stock.movements.state.in play': 'en juego',
  'stock.movements.state.won': 'ganado',
  'stock.movements.state.unaccounted': 'sin destino',
  'stock.movements.state.off-station': 'fuera de estación',
  'stock.movements.state.operations': 'operaciones',
  'stock.movements.state.written off': 'dado de baja',
  'stock.dialog.field.restockThreshold': 'Umbral de reabastecimiento',
  'stock.dialog.field.restockPlaceholder': 'Alertar por debajo de…',
  'stock.dialog.field.classification': 'Clasificación (SEE · ENGAGE · TRY · ACT)',
  'stock.dialog.field.classUnset': 'Sin asignar',
  'stock.dialog.field.rarityTier': 'Nivel de rareza (define la probabilidad en el juego)',
  'stock.dialog.weight.weightOn': 'Peso en el juego:',
  'stock.dialog.weight.hint': '1 = común (ganancia frecuente), 5 = legendario (drop raro).',
  'stock.dialog.fixFieldsOne': 'Por favor arregla 1 campo antes de guardar:',
  'stock.dialog.fixFieldsMany': 'Por favor arregla {count} campos antes de guardar:',
  'stock.dialog.cancel': 'Cancelar',
  'stock.dialog.saving': 'Guardando…',
  'stock.dialog.saveChanges': 'Guardar cambios',
  'stock.dialog.addToInventory': 'Agregar al inventario',
  'stock.dialog.error.nameLen': 'El nombre debe tener al menos 2 caracteres',
  'stock.dialog.error.qtyGt0': 'La cantidad inicial debe ser mayor a 0',
  'stock.dialog.error.qtyInt': 'Debe ser un número entero',
  'stock.dialog.error.costNeg': 'El valor unitario no puede ser negativo',
  'stock.dialog.error.costInvalid': 'Escribe un número con hasta dos decimales, o déjalo vacío',
  'stock.dialog.error.threshNeg': 'El umbral no puede ser negativo',
  'stock.dialog.error.threshExceeds': 'El umbral no puede exceder la cantidad inicial',
  'stock.dialog.error.ratingRange': 'La valoración debe estar entre 1 y 5',
  'stock.dialog.classHint.SEE': 'Awareness — solo visibilidad',
  'stock.dialog.classHint.ENGAGE': 'Engagement — pequeño touchpoint',
  'stock.dialog.classHint.TRY': 'Trial — degustación / muestreo',
  'stock.dialog.classHint.ACT': 'Action — conversión de alta intención',

  // ── Stations ───────────────────────────────────────────────────────
  'stations.title': 'Estaciones',
  'stations.subtitle': 'Donde tu equipo entrega merch el día del evento.',
  'stations.addButton': 'Nueva estación',
  'stations.empty.title': 'Aún no hay estaciones',
  'stations.empty.body': 'Agrega una estación para asignar merch y capturar interactions.',
  'stations.empty.cta': 'Crear estación',
  'stations.searchPlaceholder': 'Buscar estaciones…',
  'stations.column.name': 'Nombre',
  'stations.column.event': 'Evento',
  'stations.column.surveys': 'Journeys',
  'stations.column.status': 'Estado',
  'stations.confirmDelete': '¿Eliminar esta estación?',
  'stations.deletedTitle': 'Estación eliminada',
  'stations.savedTitle': 'Estación guardada',
  'stations.dialog.addTitle': 'Nueva estación',
  'stations.dialog.editTitle': 'Editar estación',
  'stations.dialog.name': 'Nombre de la estación',
  'stations.dialog.event': 'Evento',
  'stations.dialog.surveys': 'Journeys',
  'stations.dialog.status': 'Estado',
  'stations.detail.qrCode': 'Código QR',
  'stations.detail.printQr': 'Imprimir QR',
  'stations.detail.copyLink': 'Copiar enlace',
  'stations.detail.linkCopied': 'Enlace copiado al portapapeles',
  'stations.detail.stockAssigned': 'Merch asignado',
  'stations.detail.noStock': 'Sin merch asignados a esta estación.',
  'stations.detail.responses': 'Interactions',
  'stations.detail.noResponses': 'Aún no se han capturado interactions.',
  'stations.print.title': 'Imprimir códigos QR de estaciones',
  'stations.print.instructions': 'Usa el diálogo de impresión de tu navegador para enviar estos QR a una impresora o guardarlos como PDF.',
  'stations.list.subtitleLong': 'Cada QR vive en una estación. Cada estación pertenece a un evento.',
  'stations.list.emptyDesc': 'Crea estaciones dentro de los eventos de un proyecto.',
  'stations.list.goToProjects': 'Ir a proyectos',
  'stations.list.allStations': 'Todas las estaciones',
  'stations.list.column.survey': 'Journey',
  'stations.list.column.actions': 'Acciones',
  'stations.list.allocate': 'Abrir estación',
  'stations.list.column.allocated': 'Asignado',
  'stations.list.allocatedCell': '{units} uds · {items} premios',
  'stations.list.allocatedNone': 'Sin asignar',
  'stations.list.allocatedLink': 'Cambiar en Plan',
  'stations.list.qrShort': 'QR',
  'stations.detail.notFound': 'Estación no encontrada',
  'stations.detail.back': 'Atrás',
  'stations.detail.eventLabel': 'Evento',
  'stations.detail.surveyLabel': 'Journey',
  'stations.detail.edit': 'Editar',
  'stations.detail.viewQr': 'Ver código QR',
  'stations.detail.allocationsTitle': 'Merch en esta estación',
  'stations.detail.stock': 'Merch',
  'stations.detail.loadingStocks': 'Cargando catálogo de merch…',
  'stations.detail.allAllocated': 'Todo el merch de la marca ya está asignado a esta estación',
  'stations.detail.noStocksForBrand': 'Esta marca aún no tiene merch',
  'stations.detail.createStocksCta': 'Crear merch',
  'stations.detail.available': 'disponibles',
  'stations.detail.pickStock': 'Elige un artículo de merch',
  'stations.detail.quantity': 'Cantidad',
  'stations.detail.qtyError': 'Debe ser ≥ 1',
  'stations.detail.overrideRating': 'Sobrescribir probabilidad',
  'stations.detail.usesDefault': 'Usa el valor por defecto',
  'stations.detail.wheelWeight': 'Peso en el juego:',
  'stations.detail.allocate': 'Asignar',
  'stations.detail.noAllocations': 'Aún no hay asignaciones.',
  'stations.detail.stockCol': 'Merch',
  'stations.detail.ratingCol': 'Probabilidad',
  'stations.detail.availTotalCol': 'Disponible / total',
  'stations.detail.out': 'Agotado',
  'stations.detail.removeAria': 'Quitar',
  'stations.detail.allocatedToast': 'Merch asignado',
  'stations.detail.allocateFailed': 'Error al asignar',
  'stations.detail.missingData': 'Datos faltantes',
  'stations.detail.brandPool': 'Inventario de marca',
  'stations.detail.exceedsBrandPool': 'Excede el inventario de marca',
  // PRD §9 v2 — Event Prize Pool picker (ES).
  'eventPrizePool.title': 'Premios elegibles para este evento',
  'eventPrizePool.subtitle':
    'Selecciona qué premios del brand pueden asignarse a las stations de este evento. Vacío = se permiten todos los premios del brand (retro-compatibilidad).',
  'eventPrizePool.brandEmpty':
    'El brand aún no tiene premios adoptados. Abre Brand Studio → Premios para adoptar del catálogo de la agency.',
  'eventPrizePool.noPoolHint':
    'No hay premios seleccionados — las stations verán el catálogo completo del brand.',
  'eventPrizePool.unitsRemain': 'unidades',
  'eventPrizePool.savedToast': 'Pool de premios actualizado',
  'eventPrizePool.saveFailed': 'No se pudo actualizar el pool de premios',
  // ── Station Detail v2 (header + tabs) ────────────────────────────
  'stations.detail.inventoryTab': 'Merch',
  'stations.detail.responsesTab': 'Interactions',
  'stations.detail.dashboardTab': 'Dashboard',
  'stations.detail.usedCol': 'Usado',
  'stations.detail.totalAllocated': 'Total',
  'stations.detail.totalUsed': 'Usado',
  'stations.detail.totalRemaining': 'Restante',
  'stations.detail.noSurveyAssigned': 'Aún no hay journey asignado a esta station.',
  'stations.detail.responsesOverTime': 'Interactions en el tiempo',
  'stations.detail.stockDistribution': 'Merch entregado por item',
  'stations.detail.inventoryDonut': 'Inventario usado vs restante',
  'stations.detail.topAnswers': 'Interactions más comunes',
  'stations.detail.dashboardEmpty': 'Aún no hay datos suficientes para esta gráfica — vuelve cuando empiecen a llegar interactions.',
  'stations.dash.heroTitle': 'Qué pasó en esta estación',
  'stations.dash.usedChip': '{pct}% del merch usado',
  'stations.dash.interactions': 'Interactions',
  'stations.dash.interactionsHint': 'sesiones de encuesta iniciadas en esta estación',
  'stations.dash.unitsOut': 'Unidades salidas',
  'stations.dash.ofAllocated': 'de {n} asignadas',
  'stations.dash.remainingHint': 'disponibles para los juegos',
  'stations.dash.last24h': 'Últimas 24 h',
  'stations.dash.last7dHint': '{n} en los últimos 7 días',
  'stations.dash.chip': '{n} interactions · por {unit}',
  'stations.dash.unitHour': 'hora',
  'stations.dash.unitDay': 'día',
  'stations.dash.peakCap': 'Pico {n} por {unit}',
  'stations.dash.peakWhen': '{unit} más activa',
  'stations.dash.perUnit': 'Pico por {unit}',
  'stations.dash.total': 'Total',
  'stations.dash.avg': 'Media por bloque',
  'stations.dash.tooltip': '{n} interactions · {phase}',
  'stations.dash.legendPeak': 'Bloque pico',
  'stations.dash.rowUsed': '{used} de {total}',
  'stations.dash.soldOut': 'Agotado',
  'stations.dash.answered': '{n} respuestas',
  'stations.detail.responseTimestamp': 'Cuándo',
  'stations.detail.responseSummary': 'Vista previa',
  'stations.detail.prevPage': 'Página anterior',
  'stations.detail.nextPage': 'Página siguiente',
  'stations.editDialog.title': 'Editar estación',
  'stations.editDialog.description': 'Renombra o reasigna la journey. Las asignaciones se mantienen.',
  'stations.editDialog.stationName': 'Nombre de la estación',
  'stations.editDialog.nameRequired': 'El nombre de la estación es obligatorio',
  'stations.editDialog.survey': 'Journey',
  'stations.editDialog.surveyDisabledTitle': 'La journey se configura en la pestaña Design Journey del evento. Abre el evento para cambiarla.',
  'stations.editDialog.noSurvey': '— sin journey —',
  'stations.editDialog.template': 'journey',
  'stations.editDialog.global': 'global',
  'stations.editDialog.surveyHintPre': 'Configurado en la pestaña',
  'stations.editDialog.surveyHintLink': 'Design Journey',
  'stations.editDialog.surveyHintPost': 'del evento — aplica a todas las estaciones de este evento.',
  'stations.editDialog.confirmHeader': 'Eliminar estación',
  'stations.editDialog.confirmBody': 'La estación y sus asignaciones de merch se ocultarán de los listados.',
  'stations.editDialog.confirmInstruction': 'Haz clic en',
  'stations.editDialog.confirmInstructionEmph': 'Confirmar eliminación',
  'stations.editDialog.confirmInstructionEnd': 'para proceder.',
  'stations.editDialog.deleting': 'Eliminando…',
  'stations.editDialog.confirmDelete': 'Confirmar eliminación',
  'stations.editDialog.delete': 'Eliminar',
  'stations.editDialog.cancel': 'Cancelar',
  'stations.editDialog.save': 'Guardar',
  'stations.editDialog.updatedToast': 'Estación actualizada',
  'stations.editDialog.updateFailed': 'Error al actualizar',
  'stations.editDialog.deletedToast': 'Estación eliminada',
  'stations.editDialog.deleteFailed': 'Error al eliminar',
  'stations.qrPrint.noSurvey': 'Esta estación aún no tiene journey. Empieza uno en la pestaña Design Journey del evento.',
  'stations.qrPrint.back': 'Atrás',
  'stations.qrPrint.title': 'Código QR',
  'stations.qrPrint.shortUrl': 'URL corta',
  'stations.qrPrint.sharedNote': 'Esta estación comparte un QR con otras — el código de abajo abre el selector de estación compartido. Al escanearlo, el consumidor elige primero su estación.',
  'stations.qrPrint.fullUrl': 'URL completa',
  'stations.qrPrint.downloadSvg': 'Descargar SVG',
  'stations.qrPrint.print': 'Imprimir',
  'stations.qrPrint.openNewTab': 'Abrir en nueva pestaña',

  // ── Events ─────────────────────────────────────────────────────────
  'events.detail.startDate': 'Inicio',
  'events.detail.endDate': 'Fin',
  'events.detail.address': 'Dirección',
  'events.detail.codeRef': 'Código de referencia',
  'events.detail.sectionCount': 'Secciones',
  'events.detail.stations': 'Estaciones',
  'events.detail.noStations': 'Aún no hay estaciones en este evento.',
  'events.detail.addStation': 'Agregar estación',
  'events.detail.savedTitle': 'Evento guardado',
  'events.detail.deletedTitle': 'Evento eliminado',
  'events.detail.confirmDelete': '¿Eliminar este evento?',
  'events.dialog.editTitle': 'Editar evento',
  'events.dialog.name': 'Nombre del evento',
  'events.dialog.address': 'Dirección',
  'events.dialog.startDate': 'Fecha de inicio',
  'events.dialog.endDate': 'Fecha de fin',
  'events.dialog.sectionCount': 'Número de secciones',
  'events.dialog.codeRef': 'Código de referencia',
  'events.dialog.status': 'Estado',
  'events.survey.title': 'Design Journey',
  'events.survey.subtitle': 'Diseña el guest journey de este evento.',
  'events.survey.outputs': 'Salidas',
  'events.survey.preview': 'Vista previa',
  'events.survey.savedTitle': 'Configuración guardada',
  'events.survey.endpoint': 'URL del endpoint',
  'events.survey.endpointPlaceholder': 'https://hooks.example.com/onxite',
  'events.survey.translatable': 'Traducible',
  'events.survey.translatableHint': 'Muestra el selector de idioma a los respondientes.',
  'events.survey.locked': 'Bloquear journey',
  'events.survey.lockedHint': 'Evita ediciones accidentales al cuerpo de la journey.',
  'events.survey.preview.empty': 'Agrega pasos y componentes para ver la vista previa.',
  'events.detail.notFound': 'Evento no encontrado',
  'events.detail.backToProject': 'Volver al proyecto',
  'events.detail.delete': 'Eliminar evento',
  'events.detail.deleteTitle': '¿Eliminar «{name}»?',
  'events.detail.deleteBody':
    'El evento desaparece del proyecto y de todos los listados, y sus enlaces públicos dejan de funcionar. Las respuestas ya recogidas se conservan. Sus estaciones NO se eliminan: siguen en el listado de Estaciones hasta que las borres allí.',
  'events.detail.deletedToast': 'Evento «{name}» eliminado',
  'events.detail.deleteFailed': 'No se pudo eliminar el evento',
  'events.detail.unknown': 'Desconocido',
  'events.detail.tbd': 'Por definir',
  'events.detail.sectionOne': 'sección',
  'events.detail.sectionMany': 'secciones',
  'events.detail.editEvent': 'Editar evento',
  'events.detail.viewReport': 'Ver reporte',
  // Caso 10 — Atajo al monitor en vivo desde el header del evento.
  'events.detail.openLiveMonitor': 'Monitor en vivo',
  'events.detail.liveTitle': 'Monitoreo en vivo',
  'events.detail.reportTitle': 'Reporte del evento',
  // Caso 12 — Card de metas del evento.
  'goals.title': 'Metas y avance',
  'goals.description':
    'Define metas por evento. El avance se calcula con las mismas métricas del reporte.',
  'goals.empty': 'Aún no hay metas. Elige una métrica abajo para empezar.',
  'goals.targetPlaceholder': 'Meta',
  'goals.editor.eventTitle': 'Metas de este evento',
  'goals.editor.eventHint': 'Se heredan enteras del proyecto salvo que fijes una aquí. La meta del evento manda sobre la del proyecto; deja un campo vacío para seguir midiendo sin puntuar.',
  'goals.editor.projectTitle': 'Metas del proyecto',
  'goals.editor.projectHint': 'Cada evento del proyecto las hereda enteras (no se reparten). Un evento puede pisar cualquiera desde su panel de propiedades.',
  'goals.editor.own': 'Este evento',
  'goals.editor.inherited': 'Del proyecto',
  'goals.editor.inheritedHint': 'Escribe una cifra para pisarla en este evento.',
  'goals.editor.useProject': 'Usar la del proyecto',
  'goals.editor.remove': 'Quitar la meta',
  'goals.editor.none': 'Sin meta',
  'goals.editor.saved': 'Meta guardada',
  'goals.editor.removed': 'Meta quitada',
  'goals.editor.failed': 'No se pudo guardar la meta',
  'goals.readOnlyHint': 'Las metas se fijan en el planner del proyecto (Plan) y en el panel de propiedades del evento. Esta tarjeta sólo enseña el avance.',
  'goals.editInPlan': 'Editar metas en Plan',
  'goals.source.project': 'del proyecto',
  'goals.metric.samples': 'Muestras',
  'goals.metric.responses': 'Interactions',
  'goals.metric.leads': 'Leads',
  'goals.metric.prizesDelivered': 'Premios entregados',
  'goals.metric.completionRate': 'Tasa de finalización',
  'goals.status.on-track': 'En camino',
  'goals.status.at-risk': 'En riesgo',
  'goals.status.met': 'Cumplida',
  // Caso 11 — Vista móvil del operador del booth.
  'operator.title': 'Modo operador',
  'operator.edit.cta': 'Editar disponibilidad',
  'operator.edit.unlockTitle': 'Código de la estación',
  'operator.edit.unlockBody': 'Pide a quien organiza el código de 6 dígitos de esta estación. Desbloquea la edición en este teléfono durante 8 horas.',
  'operator.edit.codeLabel': 'Código',
  'operator.edit.unlock': 'Desbloquear',
  'operator.edit.wrongCode': 'Código incorrecto',
  'operator.edit.tooMany': 'Demasiados intentos — espera un minuto y vuelve a probar.',
  'operator.edit.unlocked': 'Edición desbloqueada',
  'operator.edit.editing': 'Editando',
  'operator.edit.saved': 'Guardado',
  'operator.edit.saveFailed': 'No se pudo guardar',
  'operator.edit.notEnabled': 'La edición no está activada para esta estación. Pídeselo a quien organiza.',
  'operator.back': 'Volver a estaciones',
  'operator.invalidId': 'Identificador de estación inválido.',
  'operator.unknownEvent': 'Evento desconocido',
  'operator.surveyActive': 'Journey activa: {name}',
  'operator.noSurvey': 'Sin journey asignada aún.',
  'operator.cta.redeem': 'Canjear',
  'operator.cta.showQr': 'Mostrar QR',
  'operator.stock.title': 'Premios restantes',
  'operator.stock.totalRemaining': '{count} en total',
  'operator.stock.left': 'restan',
  'operator.stock.empty': 'No hay premios asignados a esta estación.',
  'operator.stock.lowAlert':
    '{count} premio(s) por agotarse — avisa al coordinador.',
  'stations.detail.operatorMode': 'Modo operador',
  'stations.detail.operatorEdit.label': 'Los operadores pueden editar la disponibilidad y sacar premios de los juegos',
  'stations.detail.operatorEdit.hint': 'Lo desbloquean en su teléfono con el código de esta estación. Sin él, la vista de operador es sólo lectura.',
  'stations.detail.operatorEdit.code': 'Código de la estación',
  'stations.detail.operatorEdit.copy': 'Copiar código',
  'stations.detail.operatorEdit.copied': 'Código copiado',
  'stations.detail.operatorEdit.rotate': 'Código nuevo',
  'stations.detail.operatorEdit.rotated': 'Código nuevo generado — el anterior ya no vale',
  'stations.detail.operatorEdit.failed': 'No se pudo actualizar el acceso del operador',
  // Caso 11 v3 — Tokens de acceso del operador.
  'stations.detail.generateOperatorLink': 'Enlace de operador',
  'stations.detail.tokenReady': 'Enlace de operador listo',
  'stations.detail.tokenWarning':
    'Esta URL da acceso al modo operador. Cópiala ahora — no la mostraremos otra vez.',
  'stations.detail.tokenExpiresAt': 'Expira {when}',
  'stations.detail.tokenCopy': 'Copiar URL',
  'stations.detail.tokenCopied': 'Copiado al portapapeles.',
  'stations.detail.tokenCopyFailed':
    'No se pudo copiar. Selecciona la URL manualmente.',
  'stations.detail.tokenMintFailed':
    'No se pudo generar el enlace de operador.',
  // Caso 11 v4 — login del operador (intercambio de token).
  'operatorLogin.title': 'Iniciando tu sesión…',
  'operatorLogin.subtitle': 'Intercambiando tu enlace por una sesión de operador.',
  'operatorLogin.errorTitle': 'No se pudo iniciar tu sesión',
  'operatorLogin.missingToken':
    'Este enlace no tiene el token de acceso.',
  'operatorLogin.exchangeFailed':
    'El enlace es inválido, fue revocado, o expiró. Pide uno nuevo al manager.',
  // Caso 16 — Etiquetas de migas de pan.
  'breadcrumbs.brand': 'Marca',
  'breadcrumbs.project': 'Proyecto',
  'breadcrumbs.event': 'Evento',
  'breadcrumbs.settings': 'Ajustes',
  // Caso 16 — Chip de tipo de entidad junto al título (EntityTypeBadge).
  'entityType.project': 'Proyecto',
  'entityType.event': 'Evento',
  'entityType.station': 'Estación',

  // ── Caso 09 — Panel de demografía ───────────────────────────────
  'demographics.title': 'Demografía',
  'demographics.subtitle':
    '{count} contactos con datos demográficos registrados.',
  'demographics.ageRange': 'Rango de edad',
  'demographics.gender': 'Género',
  'demographics.ethnicity': 'Etnia',
  'demographics.city': 'Ciudades principales',
  'demographics.state': 'Estados principales',
  'demographics.language': 'Idiomas principales',
  'demographics.genderValue.female': 'Femenino',
  'demographics.genderValue.male': 'Masculino',
  'demographics.genderValue.non-binary': 'No binario',
  'demographics.genderValue.other': 'Otro',
  'demographics.genderValue.undisclosed': 'No declarado',
  'demographics.brand.noEvents':
    'Esta marca aún no tiene eventos — la demografía aparecerá cuando un evento corra con una journey que recolecte edad, género o ubicación.',
  'demographics.brand.noData':
    'Se revisaron {count} evento(s); ninguno tiene interactions demográficas todavía. Asegúrate de que las journeys de la marca recolecten edad, género o ubicación.',
  // Caso 10 — Monitor en vivo.
  'liveMonitor.invalidId': 'Identificador de evento inválido.',
  'liveMonitor.back': 'Volver al reporte',
  'liveMonitor.live': 'EN VIVO',
  'liveMonitor.refreshing': 'Actualizando…',
  // Caso 10 v2 — Indicador de transporte (WS push vs polling).
  'liveMonitor.transport.live': 'Tiempo real',
  'liveMonitor.transport.polling': 'Sondeo',
  'liveMonitor.unknown': 'Evento',
  'liveMonitor.kpi.interactions': 'Interacciones',
  'liveMonitor.kpi.last24h': 'Últimas 24 h',
  'liveMonitor.kpi.leads': 'Leads',
  'liveMonitor.kpi.prizesDistributed': 'Premios entregados',
  'liveMonitor.alert.lowStock':
    'Solo quedan {count} premio(s) en todas las estaciones.',
  'eventReport.openLiveMonitor': 'Abrir monitor en vivo',
  'events.detail.stationsTab': 'Estaciones',
  'events.detail.surveyTab': 'Design Journey',
  // Aviso de siguiente paso: estaciones listas → a diseñar el Xurvey.
  'events.djNudge.title': 'Estaciones listas — ahora diseña tu Xurvey',
  'events.djNudge.body':
    'Tus {n} estación(es) corren la encuesta por defecto. Dale tu marca, pasos y premios en el Design Journey.',
  'events.djNudge.cta': 'Design Journey',
  // Design Journey — la marca no ha escrito un texto legal que esta encuesta pide.
  'designJourney.consentMissing.title': 'Falta un texto legal de esta marca',
  'designJourney.consentMissing.general':
    'Esta encuesta tiene un paso de consentimiento, pero la marca todavía no ha escrito sus términos y condiciones. A quien responda se le pediría aceptar un documento que no existe.',
  'designJourney.consentMissing.photo':
    'Esta encuesta pide consentimiento de imagen, pero la marca todavía no ha escrito ese texto. A quien responda se le pediría aceptar que le fotografíen sin nada que leer.',
  'designJourney.consentMissing.both':
    'Esta encuesta pide consentimiento —también el de imagen— y la marca no ha escrito ninguno de los dos textos. A quien responda se le pediría aceptar documentos que no existen.',
  'designJourney.consentMissing.cta': 'Escríbelos en Ajustes › Marca',
  // Design Journey — la capa legal derivada del despliegue (sept 2026).
  'designJourney.tab.steps': 'Journeys',
  'designJourney.tab.consent': 'Consentimiento',
  'designJourney.legalLocked': 'Se gestiona solo, a partir de lo que recoge esta encuesta y de la página Legal de la marca. No se puede ocultar a mano.',
  'designJourney.consent.title': 'Consentimiento',
  'designJourney.consent.description':
    'Lo que se le pedirá aceptar al invitado se deriva: contacto, móvil y premio de la encuesta; verificación de edad, marketing y SMS de la página Legal y consentimiento de la marca. Sólo la fotografía y los menores se deciden por despliegue.',
  'designJourney.consent.blockedTitle': 'Este despliegue aún no puede activarse',
  'designJourney.consent.cta': 'Abrir Legal y consentimiento',
  'designJourney.consent.photos': 'Fotografía en esta activación',
  'designJourney.consent.photos.none': 'Sin fotos ni vídeo',
  'designJourney.consent.photos.ambient': 'Sólo ambiental (público, ambiente): aviso',
  'designJourney.consent.photos.featured': 'Destacada (personas, imagen): cesión aparte',
  'designJourney.consent.minors': 'Puede haber menores',
  'designJourney.consent.minorsDescription': 'Apaga la cesión de imagen destacada en el flujo digital. Usa la cesión en papel del tutor.',
  'designJourney.consent.instrument.notice': 'Aviso de privacidad (siempre)',
  'designJourney.consent.instrument.terms': 'Casilla de Términos y condiciones',
  'designJourney.consent.instrument.rules': 'Enlace a las Reglas oficiales',
  'designJourney.consent.instrument.marketing': 'Opt-in de marketing por email',
  'designJourney.consent.instrument.sms': 'Opt-in de SMS (TCPA)',
  'designJourney.consent.instrument.media': 'Cesión de imagen destacada',
  'designJourney.consent.instrument.ageGate': 'Verificación de edad antes del consentimiento',
  'designJourney.consent.why.noPrize': 'Este despliegue no tiene premio.',
  'designJourney.consent.why.noContact': 'La encuesta no recoge contacto: no hay a quién escribir.',
  'designJourney.consent.why.noMobile': 'Necesita un móvil recogido con un bloque de contacto.',
  'designJourney.consent.why.brandOff': 'Apagado en la página Legal de la marca.',
  'designJourney.consent.why.noFeatured': 'La fotografía no está en «destacada».',
  'designJourney.consent.why.minors': 'Apagada porque puede haber menores.',
  'designJourney.consent.brandEmpty': 'Esta marca aún no tiene documentos legales; el invitado sólo ve los de Onxite.',
  'designJourney.consent.descriptionXotski': 'Lo que se le pide aceptar al invitado sale de la encuesta y de la página Legal y consentimiento de la marca: la verificación de edad, Términos y Privacidad, el opt-in de marketing por email y, con juego de premio, las Reglas oficiales. Aquí no hay nada que fijar por evento.',
  'designJourney.consent.setHereNote': 'Este evento tiene un ajuste propio de fotos o de menores, por eso se ve aquí. Los eventos de Xotski normalmente no lo necesitan: pon la fotografía en «Sin fotos ni vídeo» y apaga los menores, y en cuanto se guarde desaparece.',
  // Settings › Brand — textos legales de la empresa (migración 089).
  'settings.terms.title': 'Términos y condiciones',
  'settings.terms.description':
    'Cada marca tiene los suyos — quien responde tiene que leer los de la marca que hace la activación, no los de la agencia. Se pegan por marca. Cada paso de consentimiento de tus encuestas desplegadas los lee de aquí y los enseña a nombre de tu marca — las encuestas no pueden reescribirlos.',
  'settings.terms.brand': 'Marca a la que pertenecen estos textos',
  'settings.terms.general.label': 'Términos y condiciones generales',
  'settings.terms.general.hint':
    'Lo que acepta quien responde sobre el tratamiento de sus datos. Se enseña como un enlace que abre el texto completo.',
  'settings.terms.general.placeholder': 'Pega aquí tus términos y condiciones…',
  'settings.terms.photo.label': 'Consentimiento de imagen',
  'settings.terms.photo.hint':
    'Un permiso aparte: que se le fotografíe o grabe, y qué uso se dará a esas imágenes. Sólo lo piden las encuestas cuyo paso de consentimiento lo marque.',
  'settings.terms.photo.placeholder': 'Pega aquí tu consentimiento de imagen…',
  'settings.terms.empty': 'Sin rellenar',
  'settings.terms.chars': '{n} caracteres',
  'settings.terms.legacyUrl':
    'Esta marca conserva una URL de términos anterior ({url}). Se sigue usando hasta que pegues el texto de arriba.',
  // Settings › Legal & Consent — la página legal de la marca (co_company_legal, 116).
  'settings.section.legal': 'Legal y consentimiento',
  'settings.section.legalDesc': 'Términos, privacidad, reglas oficiales y consentimiento de imagen, por marca',
  'settings.brand.legalMoved':
    'Los textos legales se han movido a Legal y consentimiento: una página por marca que leen todas las aplicaciones (Xotski, Xamplin, Xurvey). Los ajustes de marca se quedan con identidad e inventario.',
  'settings.brand.legalMovedCta': 'Abrir Legal y consentimiento',
  'settings.legal.title': 'Legal y consentimiento',
  'settings.legal.subtitle':
    'Los instrumentos legales de la marca, en un solo sitio. Cada paso de consentimiento de una encuesta desplegada lee de aquí sus documentos y los enseña a nombre de la marca. Las encuestas no pueden reescribirlos. El aviso en pantalla y la casilla de aceptación son siempre de Onxite y no se editan aquí.',
  'settings.legal.roles.title': 'Roles sobre los datos',
  'settings.legal.roles.brand': 'marca = responsable',
  'settings.legal.roles.app': 'Xotski = encargado',
  'settings.legal.roles.description':
    'La marca que hace la activación es dueña de los datos del invitado y de sus términos legales. Xotski los trata por cuenta de la marca. Por eso cada texto se guarda por marca, no por agencia ni por aplicación.',
  'settings.legal.brandHint': 'Los textos se guardan en esta marca. Una encuesta desplegada para ella saca exactamente estos.',
  'settings.legal.legacyNotice':
    'Esta marca sigue usando los textos pegados en los ajustes de marca antiguos. Guarda una vez aquí y pasan a la página Legal y consentimiento para siempre.',
  'settings.legal.readOnly': 'Puedes leer esta página pero no cambiarla: editar exige el permiso de edición de marca.',
  'settings.legal.tag.brand': 'lo aporta la marca',
  'settings.legal.tag.brandLink': 'enlace o texto de la marca',
  'settings.legal.tag.ifPrize': 'sólo si hay premio',
  'settings.legal.tag.ifPhotos': 'sólo si hay fotos',
  'settings.legal.tag.config': 'configuración',
  'settings.legal.tag.onxite': 'estándar de Onxite',
  'settings.legal.field.url': 'URL',
  'settings.legal.field.text': 'Texto completo',
  'settings.legal.legalName.title': 'Razón social',
  'settings.legal.legalName.description':
    'El nombre registrado de la sociedad detrás de la marca (p. ej. Felix Technologies Inc.). Se usa en los reconocimientos y en la cesión de imagen de menores; el invitado ve el nombre de la marca.',
  'settings.legal.legalName.placeholder': 'Nombre registrado de la sociedad',
  'settings.legal.terms.title': 'Términos y condiciones generales',
  'settings.legal.terms.description':
    'Lo que el invitado acepta sobre el tratamiento de sus datos. Se enseña como un enlace que abre el texto completo; se acepta con la casilla del paso de consentimiento.',
  'settings.legal.terms.urlHint': 'Preferido: un enlace mantiene el documento al día y aligera el alta.',
  'settings.legal.terms.textHint': 'Alternativa si los términos no están publicados. Con texto, el enlace no se usa.',
  'settings.legal.privacy.title': 'Política de privacidad',
  'settings.legal.privacy.description':
    'La política vigente de la marca. La pantalla de consentimiento la enlaza como referencia: la privacidad es aviso, no consentimiento, así que no lleva casilla propia.',
  'settings.legal.privacy.urlHint': 'Si la marca no tiene política, activa abajo los términos estándar de Onxite.',
  'settings.legal.rules.title': 'Reglas oficiales (Official Rules)',
  'settings.legal.rules.description':
    'Obligatorias siempre que la activación tenga un juego con premio. La marca es la patrocinadora de registro; Onxite aporta una plantilla que la marca adapta y pega aquí. Sin ellas, un despliegue con premio no se puede activar.',
  'settings.legal.rules.urlHint': 'Enlace a las reglas publicadas.',
  'settings.legal.rules.textHint': 'O pega aquí las reglas adaptadas.',
  'settings.legal.rules.placeholder': 'Pega aquí las reglas oficiales\u2026',
  'settings.legal.media.title': 'Consentimiento de imagen',
  'settings.legal.media.description':
    'Un permiso aparte: que se le fotografíe o grabe, y qué uso se dará a esas imágenes. No puede ser un enlace: se concede en el flujo. Sólo lo piden los despliegues que toman fotos.',
  'settings.legal.media.textHint': 'El texto de cesión propio de la marca. Déjalo vacío para adoptar la cesión genérica de Onxite de abajo.',
  'settings.legal.media.adoptLabel': 'Adoptar la cesión de imagen genérica de Onxite',
  'settings.legal.media.adoptDescription':
    'La marca la adopta como propia y asume la responsabilidad exclusiva del uso de las imágenes.',
  'settings.legal.ageGate.title': 'Verificación de edad',
  'settings.legal.ageGate.description':
    'Para marcas reguladas (alcohol). Se exige antes de la pantalla de consentimiento, con un registro de declaración inmutable. La marca garantiza que el ajuste es el correcto para la jurisdicción del recinto.',
  'settings.legal.ageGate.requireLabel': 'Exigir verificación de edad',
  'settings.legal.ageGate.requireDescription': 'Con esto encendido y con paso de edad en la encuesta del evento, el invitado de Xotski ve primero un botón («Tengo 21 años o más» o «Tengo 18 años o más») antes de la pantalla de consentimiento, y un toque lo lleva al siguiente paso; la pantalla no le deja seguir sin él. Xotski nunca pide la fecha de nacimiento. Con esto apagado, el invitado de Xotski no ve ninguna pregunta de edad. Las encuestas desplegadas para esta marca deben incluir un paso de verificación de edad.',
  'settings.legal.ageGate.minAge': 'Edad legal para esta activación',
  'settings.legal.ageGate.minAgeHint': '18 o 21, según la jurisdicción.',
  'settings.legal.marketing.title': 'Canales de marketing',
  'settings.legal.marketing.description':
    'El consentimiento de email y el de SMS son instrumentos distintos: dos casillas, dos registros. Ninguno se deriva del otro. Una casilla sólo aparece cuando la encuesta recoge el campo correspondiente.',
  'settings.legal.marketing.emailLabel': 'Opt-in de marketing por email',
  'settings.legal.marketing.emailDescription': 'Sólo aparece cuando la encuesta recoge datos de contacto.',
  'settings.legal.marketing.smsLabel': 'Opt-in de SMS (TCPA PEWC)',
  'settings.legal.marketing.smsDescription': 'Sólo aparece cuando la encuesta recoge un número móvil. Usa la redacción de consentimiento previo expreso por escrito.',
  'settings.legal.standard.title': 'Respaldo estándar (Standard Tier)',
  'settings.legal.standard.description':
    'Si la marca no aporta documentos, la activación corre con los términos estándar de Onxite, operativos a nombre de la marca.',
  'settings.legal.standard.label': 'Usar los términos estándar de Onxite para esta marca',
  'settings.legal.standard.labelDescription': 'El representante confirma que puede obligar a la marca y adopta los términos estándar.',
  'settings.legal.lastSaved': 'Guardado por última vez {when}',
  'settings.legal.toBrandSettings': 'Ajustes de marca',
  'settings.legal.save': 'Guardar textos legales',
  'settings.legal.textLimit.counter': '{n} / {max} caracteres',
  'settings.legal.textLimit.over': 'Demasiado largo: hasta {max} caracteres. Acórtalo para poder guardar.',
  'settings.legal.textLimit.saveBlocked': 'Un texto legal pegado pasa de {max} caracteres. Acórtalo para poder guardar.',
  'settings.legal.textLimit.toast': '«{field}» pasa de {max} caracteres. Acórtalo y vuelve a guardar.',
  'settings.legal.fieldError.url': 'Revisa el enlace de «{field}»: tiene que ser una dirección completa que empiece por https://.',
  'settings.legal.fieldError.generic': 'Revisa «{field}» y vuelve a guardar.',
  'settings.legal.savedToast': 'Textos legales guardados',
  'settings.legal.other.title': 'Otras aplicaciones y eventos anteriores',
  'settings.legal.other.inUse': 'En uso',
  'settings.legal.other.description': 'El consentimiento de imagen, el opt-in de SMS y los términos estándar de Onxite. Un evento de Xotski no los necesita, pero las demás aplicaciones que hacen encuestas para esta marca (Xamplin, xeck-in, DiD, Xurvey) y los eventos anteriores los leen de esta misma página, así que siguen aquí y se pueden editar. Lo que esté encendido aquí sigue aplicándose en todas partes, también en Xotski. No se borra nada.',
  'settings.legal.marketing.emailTitle': 'Marketing por email',
  'settings.legal.marketing.emailOnlyDescription': 'El opt-in de email que puede marcar el invitado. Es un consentimiento distinto del de SMS, que está más abajo, en «Otras aplicaciones y eventos anteriores».',
  'settings.legal.sms.title': 'Marketing por SMS (TCPA)',
  'settings.legal.sms.description': 'Un consentimiento aparte del de email, con redacción de consentimiento previo, expreso y por escrito: dos casillas, dos registros, y uno nunca se deduce del otro.',
  'settings.legal.privacy.urlHintPanel': 'Si la marca no tiene política, los términos estándar de Onxite están más abajo, en «Otras aplicaciones y eventos anteriores».',
  // Reconocimientos del representante (Exhibit F) + historial legal.
  'legalAck.summary': 'Resumen (generado por el sistema)',
  'legalAck.name': 'Nombre',
  'legalAck.title': 'Cargo',
  'legalAck.titlePlaceholder': 'p. ej. Directora de marketing',
  'legalAck.recordNote': 'Este reconocimiento se guarda como registro inmutable con tu nombre, cargo, la configuración mostrada y la hora. Cualquier cambio posterior exige uno nuevo.',
  'legalAck.error': 'No se pudo registrar el reconocimiento',
  'legalAck.setup.title': 'Confirma tu configuración legal',
  'legalAck.setup.intro': 'Estás configurando los instrumentos legales que los participantes verán y aceptarán en las activaciones de {brand}. Revisa el resumen antes de confirmar.',
  'legalAck.setup.check.authority': 'Estoy autorizado/a a actuar en nombre de {brand} y a obligarla con esta configuración.',
  'legalAck.setup.check.controller': '{brand} es la Responsable del tratamiento de los datos de consumidores recogidos en sus activaciones. Onxite los trata como proveedor de servicios conforme a los Términos del cliente.',
  'legalAck.setup.check.documents': 'Donde {brand} aportó sus propios documentos, garantiza que son exactos, lícitos y suficientes. Donde adoptó los instrumentos genéricos de Onxite, los adopta como propios y asume la responsabilidad exclusiva de su uso.',
  'legalAck.setup.confirm': 'Confirmar y guardar',
  'legalAck.export.title': 'Vas a tomar posesión de datos de consumidores',
  'legalAck.export.intro': 'Vas a exportar datos a nivel de consumidor ({subject}). Estos datos pertenecen a {brand} como Responsable del tratamiento. Una vez exportados, salen de la plataforma de Onxite y de sus controles de seguridad.',
  'legalAck.export.point.security': '{brand} es responsable de proteger estos datos y de su uso lícito, conforme al consentimiento que dieron los participantes.',
  'legalAck.export.point.rights': 'Las solicitudes de derechos (acceso, supresión, oposición) sobre esta copia las atiende {brand}. Onxite sigue atendiéndolas para los datos que permanecen en la plataforma.',
  'legalAck.export.point.marketing': 'El uso para marketing se limita a quienes dieron el opt-in de marketing aparte. El consentimiento de email y el de SMS son distintos: el export marca cada uno por registro, y enviar SMS a un número sin su propio consentimiento no está permitido.',
  'legalAck.export.point.logged': 'Esta exportación queda registrada (quién, qué, cuándo) y el registro está disponible para {brand} en su propio expediente de cumplimiento.',
  'legalAck.export.check.authority': 'Estoy autorizado/a a recibir estos datos en nombre de {brand}.',
  'legalAck.export.check.custody': 'Entiendo que {brand} asume la responsabilidad de esta copia de los datos desde la exportación.',
  'legalAck.export.confirm': 'Exportar datos',
  'settings.legal.ack.own': 'Propios',
  'settings.legal.ack.ownUrl': 'Documentos propios en URL',
  'settings.legal.ack.ownText': 'Documentos propios (texto pegado)',
  'settings.legal.ack.standard': 'Términos estándar de Onxite (Standard Tier)',
  'settings.legal.ack.generic': 'Cesión genérica de Onxite adoptada',
  'settings.legal.ack.none': 'No aportado',
  'settings.legal.history.title': 'Reconocimientos',
  'settings.legal.history.description': 'Cada acto legal que un representante firmó para esta marca, el más reciente primero. Es tu propio registro de cumplimiento; nada de esto se edita ni se borra.',
  'settings.legal.history.empty': 'Aún no hay reconocimientos. Guardar esta página registra el primero.',
  'settings.legal.history.type.legal_setup_ack': 'Reconocimiento de configuración legal',
  'settings.legal.history.type.data_export_ack': 'Reconocimiento de exportación de datos',
  'settings.legal.history.type.media_adopt_generic_ack': 'Cesión de imagen genérica adoptada',
  'settings.legal.history.type.standard_tier_ack': 'Términos estándar de Onxite adoptados',
  'settings.legal.history.type.dpa': 'Acuerdo de tratamiento de datos (DPA)',
  'settings.legal.history.type.data_controller_ack': 'Reconocimiento de Responsable del tratamiento',
  'contacts.exportAckSubject': 'Exportación de contactos · {n} registros',
  'contacts.exportFields': 'Campos de contacto (nombre, email, teléfono, consentimientos) y una columna por pregunta de encuesta',
  'events.detail.touchpoints': 'Estaciones / Touchpoints',
  'events.detail.eventSurvey': 'Journey del evento:',
  'events.detail.sharedByStations': '· compartida por todas las estaciones',
  'events.detail.noEventSurveyPre': 'Aún no hay journey del evento — empieza uno en la pestaña',
  'events.detail.noEventSurveyTab': 'Design Journey',
  'events.detail.noEventSurveyPost': '.',
  'events.detail.newStation': 'Nueva estación',
  'events.detail.stationName': 'Nombre de la estación',
  'events.detail.stationNamePlaceholder': 'Barra principal',
  'events.detail.surveyAutoPre': 'asignada automáticamente desde la configuración de journey de este evento (definida en la pestaña',
  'events.detail.surveyAutoTab': 'Design Journey',
  'events.detail.surveyAutoPost': '). Todas las estaciones del evento comparten la misma journey.',
  'events.detail.surveyLabel': 'Journey:',
  'events.detail.createStation': 'Crear estación',
  'events.detail.stationCreated': 'Estación creada',
  'events.detail.createFailed': 'Error al crear',
  'events.detail.surveyNone': 'ninguna',
  'events.detail.outOfSync': 'desincronizada',
  'events.detail.noSurvey': 'sin journey',
  'events.detail.outOfSyncTitle': 'Desincronizada — la journey del evento es',
  'events.detail.outOfSyncBody': 'Usa "Re-sincronizar estaciones" en Design Journey.',
  'events.detail.noSurveyTitle': 'Sin journey asignada. Usa "Re-sincronizar estaciones" en Design Journey.',
  'events.detail.editStationAria': 'Editar estación',
  'events.detail.editBtn': 'Editar',
  'events.detail.allocateStock': 'Ver',
  'events.detail.qrShort': 'QR',
  'events.editDialog.title': 'Editar evento',
  'events.editDialog.description': 'Ajusta el horario, dirección, estado y capacidad de este evento.',
  'events.editDialog.name': 'Nombre del evento',
  'events.editDialog.address': 'Dirección',
  'events.editDialog.starts': 'Inicia',
  'events.editDialog.ends': 'Termina',
  'events.editDialog.sections': 'Secciones',
  'events.editDialog.codeRef': 'Código',
  'events.editDialog.statusField': 'Estado',
  'events.editDialog.confirmHeader': 'Eliminar',
  'events.editDialog.confirmBody': 'El evento, sus estaciones y asignaciones de merch se ocultarán de todos los listados.',
  'events.editDialog.confirmInstruction': 'Haz clic en',
  'events.editDialog.confirmInstructionEmph': 'Confirmar eliminación',
  'events.editDialog.confirmInstructionEnd': 'para proceder.',
  'events.editDialog.deleting': 'Eliminando…',
  'events.editDialog.confirmDelete': 'Confirmar eliminación',
  'events.editDialog.delete': 'Eliminar',
  'events.editDialog.cancel': 'Cancelar',
  'events.editDialog.save': 'Guardar',
  'events.editDialog.updatedToast': 'Evento actualizado',
  'events.editDialog.updateFailed': 'Error al actualizar',
  'events.editDialog.deletedToast': 'Evento eliminado',
  'events.editDialog.deleteFailed': 'Error al eliminar',
  'events.editDialog.error.nameRequired': 'El nombre del evento es obligatorio',
  'events.editDialog.error.startRequired': 'La fecha y hora de inicio es obligatoria',
  'events.editDialog.error.startInvalid': 'Fecha de inicio inválida',
  'events.editDialog.error.endRequired': 'La fecha y hora de fin es obligatoria',
  'events.editDialog.error.endInvalid': 'Fecha de fin inválida',
  'events.editDialog.error.endAfterStart': 'El fin debe ser posterior al inicio',
  'events.editDialog.error.outOfRange': 'Las fechas deben estar dentro del rango del proyecto',
  // ── Despliegue de Xurvey (protocolo §2) ────────────────────────────
  'xurveyDeploy.title': 'Encuesta de Xurvey',
  'xurveyDeploy.subtitle': 'Elige una encuesta publicada y ponla en marcha en las estaciones de este evento. Cada estación tiene su propio enlace, y es lo que ata cada respuesta al sitio del que vino.',
  'xurveyDeploy.surveyLabel': 'Encuesta',
  'xurveyDeploy.deployAll': 'Desplegar en todas las estaciones',
  'xurveyDeploy.deployOne': 'Desplegar',
  'xurveyDeploy.open': 'Abrir',
  'xurveyDeploy.qr': 'QR',
  'xurveyDeploy.done': 'En marcha en {n} estación(es)',
  'xurveyDeploy.pending': '{n} estación(es) sin desplegar',
  'xurveyDeploy.noStations': 'Este evento todavía no tiene estaciones. Añade una y podrá ponerse en marcha.',
  'xurveyDeploy.noSurveys': 'Ninguna encuesta publicada apunta a Xotski. Un borrador no se puede poner en marcha: publícalo antes en Xurvey.',
  'xurveyDeploy.overridesTitle': 'Cómo se ve en este evento',
  'xurveyDeploy.overridesHint': 'Sólo el diseño: las preguntas son de la encuesta.',
  'xurveyDeploy.overridesSaved': 'Guardado en todas las estaciones del evento',
  'xurveyDeploy.appliesTo': 'Se aplica a {n} estación(es) de este evento',
  'xurveyDeploy.fromTemplate': 'La de la encuesta',
  'xurveyDeploy.colorPrimary': 'Primario',
  'xurveyDeploy.colorBg': 'Fondo',
  'xurveyDeploy.colorText': 'Texto',
  'xurveyDeploy.font': 'Fuente',
  'xurveyDeploy.celebration': 'Celebración',
  'xurveyDeploy.celebration.confetti': 'Confeti',
  'xurveyDeploy.celebration.fireworks': 'Fuegos artificiales',
  'xurveyDeploy.celebration.sparks': 'Destellos',
  'xurveyDeploy.celebration.none': 'Ninguna',
  'xurveyDeploy.game': 'Juego',
  'xurveyDeploy.logo': 'Logo',
  'xurveyDeploy.background': 'Imagen de fondo',
  'xurveyDeploy.locales': 'Idiomas que se ofrecen aquí',
  'xurveyDeploy.previewTitle': 'Vista previa de la encuesta',
  'xurveyDeploy.previewOpen': 'Abrir la vista previa en otra pestaña',
  'xurveyDeploy.previewNotDeployed': 'Todavía no hay nada que previsualizar: despliega la encuesta en una estación y aquí aparece su enlace.',

  // ── Design Journey (estándar de UI para las apps consumidoras) ─────
  'designJourney.title': 'Cómo se ve en este evento',
  'designJourney.cardTitle': 'Design Journey',
  'designJourney.subtitle': 'Sólo el diseño. Se guarda en los {n} enlaces de estación de este evento.',
  'designJourney.saved': 'Guardado en {n} estación(es)',
  'designJourney.autosaving': 'Guardando…',
  'designJourney.autosavePending': 'Sin guardar · se guarda solo',
  'designJourney.autosaved': 'Guardado a las {hora}',
  'designJourney.autosaveOn': 'Se guarda solo',
  // XCS-15 (épica #333): el autoguardado se para tras un rechazo y el guardado informa por evento.
  'designJourney.autosavePaused': 'Sin guardar · en pausa hasta que cambies algo',
  'designJourney.autosaveWaiting': 'Sin guardar · el servidor pidió esperar; vuelve a intentarlo a las {hora}',
  'designJourney.saveReport.noneSaved': 'El diseño no se guardó',
  'designJourney.saveReport.someLinks': 'Guardado en {ok} de {total} enlaces de este evento',
  'designJourney.saveReport.events': 'Guardado en {ok} de {total} eventos',
  'designJourney.saveReport.eventReason': '{name}: {reason}',
  'designJourney.saveReport.more': 'y {n} más',
  'designJourney.saveReport.unnamedEvent': 'Un evento',
  'designJourney.saveError.officialRulesMissing': 'Este enlace tiene premio y la marca no tiene Reglas oficiales. Añádelas en la página Legal de la marca o quita el juego.',
  'designJourney.saveError.ageGateMissing': 'La marca exige verificación de edad y esta encuesta no tiene ese paso. Añádelo a la encuesta o desactiva la verificación en la página Legal.',
  'designJourney.saveError.drawingNeedsContact': 'Un sorteo necesita datos de contacto para avisar al ganador. Recoge datos de contacto o usa premio inmediato.',
  'designJourney.saveError.forbidden': 'Tu rol no puede cambiar este enlace.',
  'designJourney.saveError.notFound': 'Este enlace ya no existe. Recarga la página.',
  'designJourney.saveError.network': 'No se pudo conectar con el servidor. Prueba de nuevo en un momento.',
  'designJourney.saveError.server': 'El servidor tuvo un problema. Prueba de nuevo en un momento.',
  'designJourney.saveError.unknown': 'El servidor no aceptó el cambio.',
  'designJourney.saveError.rateLimited': 'Demasiados cambios en poco tiempo. Espera un minuto y vuelve a intentarlo.',
  'designJourney.saveError.sessionExpired': 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
  'designJourney.saveError.conflict': 'Esta encuesta cambió en otro sitio o está bloqueada. Recarga la página y vuelve a intentarlo.',
  'designJourney.notAllDeployed': 'Guardar se aplica a los enlaces en marcha. {n} estación(es) todavía no corren esta encuesta: despliégalas para incluirlas.',
  'designJourney.notDeployed': 'Despliega antes la encuesta en una estación: el diseño es del enlace, y todavía no hay ninguno.',
  'designJourney.previewTitle': 'Vista previa',
  'xurveyDeploy.deployHeading': 'Encuesta y enlaces',
  'designJourney.steps': 'Journeys',
  'designJourney.abandonedHere': '{n} se fueron en este paso',
  'designJourney.completion': '{ok} de {total} llegan al final ({pct}%)',
  'designJourney.median': 'suelen tardar {s}s',
  'designJourney.stepsHint': 'Arrastra para reordenar, pulsa para ir, el ojo para ocultar. Las preguntas se editan en Xurvey.',
  'designJourney.reorder': 'Reordenar',
  'designJourney.hide': 'Ocultar en este evento',
  'designJourney.cannotHideLast':
    'Es el único paso que queda a la vista: ocultarlo dejaría el QR sin nada que responder.',
  'designJourney.show': 'Volver a mostrar',
  'designJourney.tab.apariencia': 'Apariencia',
  'designJourney.tab.estilo': 'Estilo',
  'designJourney.tab.form': 'Form',
  'designJourney.fromSurvey': 'La de la encuesta',
  'designJourney.backToTheme': 'Volver al color de la encuesta',
  'designJourney.transparent': 'Dejarlo transparente',
  'designJourney.logo': 'Logo',
  'designJourney.background': 'Fondo',
  'designJourney.backgroundSize': 'Encuadre',
  'designJourney.opacity': 'Opacidad',
  'designJourney.celebration': 'Celebración',
  'dj.testQr.button': 'QR de prueba',
  'dj.testQr.title': 'QR de prueba — pruébalo en un móvil de verdad',
  'dj.testQr.body':
    'Un QR imprimible que funciona en cualquier móvil pero no puede hacer de evento real: 10 jugadas, 24 horas, los premios se enseñan pero no se entregan, y nada se guarda.',
  'dj.testQr.create': 'Crear QR de prueba',
  'dj.testQr.createAgain': 'El último QR de prueba caducó — crea uno nuevo',
  'dj.testQr.created': 'QR de prueba listo',
  'dj.testQr.copy': 'Copiar enlace',
  'dj.testQr.copied': 'Enlace copiado',
  'dj.testQr.limits': '10 jugadas · 24 h · premios de mentira',
  'dj.testQr.expires': 'caduca {when}',
  'dj.testQr.activate': 'Activar — salir en vivo',
  'dj.testQr.activateHint': '¿Recorrido probado? Activar quita la marca de prueba, entrega premios de verdad y empieza a guardar respuestas.',
  'dj.testQr.activated': 'Despliegue activado',
  'dj.testQr.activatedBody': 'El QR ya está en vivo: premios reales y respuestas guardadas.',
  'dj.testQr.failed': 'No se pudo completar la acción',
  'designJourney.watermark': 'Logo de marca de agua del juego',
  'designJourney.watermarkHint':
    'Se ve tenue dentro de los juegos — mosaico del rasca, valla LED del fútbol, superficie del pirinola, bola del xinkit. Vacío = se usa el logo de cabecera.',
  'designJourney.game': 'Juego',
  'designJourney.gameHint': 'La encuesta dice que aquí hay un juego; este evento elige cuál. Se guarda en los enlaces de estación, no en la encuesta.',
  'designJourney.noGames': 'Tu plan todavía no incluye ningún juego.',
  'designJourney.celebration.confetti': 'Confeti',
  'designJourney.celebration.fireworks': 'Fuegos artificiales',
  'designJourney.celebration.sparks': 'Destellos',
  'designJourney.celebration.none': 'Ninguna',
  'designJourney.locales': 'Idiomas que se ofrecen aquí',
  'designJourney.localesHint': 'Sólo los que la encuesta ya tiene. Ofrecer uno que no trae daría una pantalla a medio traducir.',
  'designJourney.palette': 'Paleta',
  'designJourney.typography': 'Tipografía',
  'designJourney.fontDisplay': 'Títulos',
  'designJourney.fontBody': 'Cuerpo',
  'designJourney.textOpacity': 'Opacidad del texto',
  'designJourney.textOpacityHint': 'Por debajo del 40% el texto baja de 3:1 sobre casi cualquier fondo y deja de leerse en un evento.',
  'designJourney.container': 'Tarjeta',
  'designJourney.cardColor': 'Color de la tarjeta',
  'designJourney.border': 'Borde',
  'designJourney.borderColor': 'Color del borde',
  'designJourney.radiusTop': 'Redondeo ↑',
  // ── Estilos guardados ────────────────────────────────────────────────
  'designJourney.questionsCount': '{n} de {total} preguntas',
  'designJourney.stepAutoHidden': 'Todas sus preguntas están ocultas, así que este paso no se verá.',
  'designJourney.tab.cartel': 'Cartel',
  'designJourney.screens': 'Pantallas y mando',
  'designJourney.screenGet': 'Obtener los enlaces de pantalla y mando',
  'designJourney.screenHint': 'Un mando para los {n} puntos de recogida de este evento.',
  'designJourney.screenNeedsDeploy': 'Despliega antes la encuesta en las estaciones: sin enlaces no hay nada que enseñar ni que encender.',
  'designJourney.screenUrl': 'Pantalla del recinto',
  'designJourney.screenUrlHint': 'Ábrela en la tele del recinto. Hasta que el evento esté en marcha, el QR sale difuminado con «empieza pronto».',
  'designJourney.controlUrl': 'Mando del agente',
  'designJourney.controlUrlHint': 'Mándaselo a quien esté en el sitio. Sin login: el enlace más el código de abajo.',
  'designJourney.controlCode': 'Código del evento',
  'designJourney.controlWarning': 'Apagar cierra la recogida de respuestas en todos los puntos de este evento. Las pantallas cambian solas, sin recargar.',
  'designJourney.screenOn': 'En marcha',
  'designJourney.screenOff': 'Apagado',
  'designJourney.screenFailed': 'No se pudo crear el mando.',
  'designJourney.posterPreview': 'El cartel de este despliegue',
  'designJourney.posterBackgroundImage': 'Fondo del cartel',
  'designJourney.posterPng': 'Descargar PNG',
  'designJourney.posterPdf': 'PDF / imprimir',
  'designJourney.posterDownloadHint': 'PNG a 2160×2880 para imprimir o para redes. El PDF sale del diálogo del navegador y conserva el QR vectorial — nítido a cualquier tamaño.',
  'designJourney.posterQrLogo': 'Logo en el centro del QR',
  'designJourney.posterQrLogoHint': 'Ocupa un quinto del código y éste se dibuja con corrección de errores alta, así que se sigue escaneando.',
  'designJourney.posterOpen': 'Abrir a tamaño completo',
  'designJourney.posterDownload': 'Allí se descarga en PNG o PDF para imprimir.',
  'designJourney.posterHint': 'El cartel viene de la encuesta; aquí se pisa para ESTE evento. Vacío = lo que traiga la encuesta. El QR es el de esta estación.',
  'designJourney.posterTitle': 'Titular',
  'designJourney.posterDescription': 'Descripción',
  'designJourney.posterCta': 'Texto bajo el QR',
  'designJourney.posterBackground': 'Color de fondo',
  'designJourney.posterText': 'Color del texto',
  'designJourney.posterQrColor': 'Color del QR',
  'designJourney.posterQrBackground': 'Fondo del QR',
  'designJourney.presets': 'Estilos guardados',
  'designJourney.presetSave': 'Guardar este diseño',
  'designJourney.presetNameHint': 'Lo encontrarás con este nombre en todos los eventos de tu empresa y sus marcas.',
  'designJourney.presetNamePlaceholder': 'Verano 2026 · Joyly',
  'designJourney.presetName': 'Nombre del estilo (p. ej. «Verano 2026»)',
  'designJourney.presetsEmpty': 'Todavía no hay estilos guardados. Diseña éste y guárdalo: podrás aplicarlo en cualquier otro evento.',
  'designJourney.presetsShow': 'Ver los {n} estilos guardados',
  'designJourney.presetsHide': 'Ocultar la lista',
  'designJourney.presetApply': 'Aplicar aquí',
  'designJourney.presetApplied': 'Aplicado',
  'designJourney.presetPick': 'Elige uno de los {n} estilos guardados…',
  'designJourney.presetApplyElsewhere': 'Aplicar a…',
  // Aplicar un estilo guardado a otros eventos, sin abrirlos uno a uno.
  'applyStyle.title': 'Aplicar «{name}» a otros eventos',
  'applyStyle.description':
    'Viaja sólo la apariencia — colores, tipografía, logo, fondo, celebración. Los pasos ocultos, su orden y el cartel de cada evento se quedan como están.',
  'applyStyle.noEvents': 'Todavía no hay eventos a los que aplicarlo.',
  'applyStyle.noProject': 'Sin proyecto',
  'applyStyle.allOfProject': 'Todos',
  'applyStyle.noneOfProject': 'Ninguno',
  'applyStyle.cta': 'Aplicar a {n} eventos',
  'applyStyle.applied': 'Estilo aplicado a {n} enlaces en vivo, de {e} eventos.',
  'applyStyle.skipped': '{n} de los eventos elegidos no tienen encuesta desplegada todavía, así que ahí no cambió nada.',
  'applyStyle.noneApplied':
    'No cambió nada: ninguno de los eventos elegidos tiene encuesta desplegada todavía.',
  'applyStyle.failed': 'No se pudo aplicar el estilo en todos',
  'applyStyle.partial': 'Estilo aplicado a {ok} de {total} eventos',
  'applyStyle.partialHint': 'Los eventos a los que no llegó se quedan marcados, con el motivo debajo de cada uno.',
  'designJourney.presetShared': 'compartido',
  'designJourney.presetDelete': 'Retirar estilo',
  'designJourney.presetDeleteConfirm': '¿Retirar «{name}»? Los eventos ya diseñados con él conservan su aspecto.',
  'designJourney.presetAppliedHint': 'Aplicado a la previa. No llega a los enlaces del evento hasta que pulses Guardar.',
  'designJourney.presetSaveFailed': 'No se pudo guardar el estilo.',
  'designJourney.presetDeleteFailed': 'No se pudo retirar el estilo.',
  'designJourney.posterPresets': 'Dise\u00f1os de cartel guardados',
  'designJourney.posterPresetSave': 'Guardar este dise\u00f1o',
  'designJourney.posterPresetNameHint': 'Fondo, colores y aspecto del QR. Los textos del cartel se quedan en cada evento. Lo encontrar\u00e1s con este nombre en todos los eventos de tu empresa y sus marcas.',
  'designJourney.posterPresetNamePlaceholder': 'Cartel Verano 2026 \u00b7 Joyly',
  'designJourney.posterPresetsEmpty': 'Todav\u00eda no hay dise\u00f1os de cartel guardados. Dise\u00f1a \u00e9ste y gu\u00e1rdalo: podr\u00e1s reutilizarlo en cualquier otro evento.',
  'designJourney.posterPresetPick': 'Elige uno de los {n} dise\u00f1os guardados\u2026',
  'designJourney.posterPresetDelete': 'Retirar dise\u00f1o',
  'designJourney.posterPresetDeleteConfirm': '\u00bfRetirar \u00ab{name}\u00bb? Los eventos que ya lo usan conservan su cartel.',
  'designJourney.posterPresetSaveFailed': 'No se pudo guardar el dise\u00f1o del cartel.',
  'designJourney.posterPresetDeleteFailed': 'No se pudo retirar el dise\u00f1o del cartel.',
  'applyPoster.title': 'Aplicar el cartel \u00ab{name}\u00bb a otros eventos',
  'applyPoster.description': 'S\u00f3lo viaja el dise\u00f1o del cartel: fondo, colores y aspecto del QR. Cada evento conserva sus textos, sus pasos y la apariencia de su encuesta.',
  'applyPoster.applied': 'Dise\u00f1o de cartel aplicado a {n} enlaces vivos, en {e} eventos.',
  'applyPoster.noneApplied': 'Ninguno de los eventos elegidos tiene a\u00fan una encuesta desplegada, as\u00ed que no cambi\u00f3 nada.',
  'applyPoster.failed': 'No se pudo aplicar el dise\u00f1o del cartel en todos',
  'applyPoster.partial': 'Dise\u00f1o de cartel aplicado a {ok} de {total} eventos',
  'designJourney.stepFrame': 'Marco del paso',
  'designJourney.layoutFit': 'Altura',
  'designJourney.layoutAlign': 'Alineación',
  'designJourney.navigation': 'Navegación',
  'designJourney.navLayout': 'Botones',
  // El botón bajo el QR del premio — a dónde manda a quien acaba de jugar.
  // Clima del evento — pronóstico por delante, registro propio de lo vivido.
  // Saltos rápidos al principio del tab Plan — se hizo largo y escondía cosas.
  'plan.anchors.label': 'Ir a',
  'plan.anchors.stations': 'Estaciones',
  'plan.anchors.weather': 'Clima',
  'plan.anchors.goals': 'Objetivos',
  'plan.anchors.staff': 'Personal',
  // El fin del evento se elige como DURACIÓN, no como una segunda fecha.
  'eventForm.duration': 'Duración',
  'eventForm.hours': '{n} horas',
  'eventForm.endsAt': 'Termina el {fecha}',
  'eventForm.durationOther': 'Actual: {h} h',
  'weather.title': 'Clima',
  'weather.recorded': 'Registrado',
  'weather.noLocation':
    'No hemos podido situar este evento en el mapa. Añade un recinto o una ciudad a la dirección y el pronóstico aparece.',
  'weather.outOfRange':
    'No hay clima que enseñar: el pronóstico llega a 15 días vista, y este evento o queda más allá o ya ocurrió antes de que empezáramos a registrarlo.',
  'weather.footnote':
    'Temperaturas en °F y viento en mph. Los días ya vividos quedan registrados y no vuelven a cambiar; el resto es pronóstico y cambiará.',
  'weather.code.clear': 'Despejado',
  'weather.code.partly': 'Parcialmente nublado',
  'weather.code.cloudy': 'Nublado',
  'weather.code.fog': 'Niebla',
  'weather.code.drizzle': 'Llovizna',
  'weather.code.rain': 'Lluvia',
  'weather.code.showers': 'Chubascos',
  'weather.code.snow': 'Nieve',
  'weather.code.storm': 'Tormenta',
  'weather.code.unknown': '—',
  'designJourney.prizeLink': 'Después del juego',
  'designJourney.prizeLinkUrl': 'Enlace del botón',
  'designJourney.defaultLocale': 'Arranca en',
  'designJourney.defaultLocaleHint': 'El idioma con el que abre la encuesta en este evento; el visitante puede cambiarlo.',
  'designJourney.prizeLinkStyle': 'Mostrarlo como',
  'designJourney.prizeLinkStyle.link': 'Enlace',
  'designJourney.prizeLinkStyle.button': 'Botón',
  'designJourney.prizeLinkStyle.iconButton': 'Botón con icono',
  'designJourney.welcome': 'Bienvenida',
  'designJourney.welcomeImage': 'Imagen o logo',
  'designJourney.welcomeRole': 'Colocación',
  'designJourney.welcomeRole.hero': 'Imagen de cabecera',
  'designJourney.welcomeRole.logo': 'Logo centrado',
  'designJourney.welcomeRole.background': 'Fondo',
  'designJourney.welcomeHint': 'Sólo para este evento; la encuesta conserva su imagen.',
  'designJourney.presetModified': 'modificado después',
  'designJourney.prizeLinkPlaceholder': 'https://tu-marca.com/oferta',
  'designJourney.prizeLinkLabel': 'Texto del botón',
  'designJourney.prizeLinkLabelPlaceholder': 'Ver más',
  'designJourney.prizeLinkHint':
    'Sale bajo el QR del premio, cuando ya han jugado. Vacío no pinta botón; sin tocarlo, manda el enlace de la propia encuesta.',
  'designJourney.gameLockedHint': 'Todavía no está en tu plan — abre Facturación para ver cómo añadirlo.',
  'designJourney.progress': 'Progreso',
  // Cómo le dice el runtime a quien responde por dónde va.
  'designJourney.paginado.numeric': 'Número de paso (1 de 7)',
  'designJourney.paginado.dots': 'Puntos',
  'designJourney.paginado.bar': 'Barra de progreso',
  'designJourney.paginado.none': 'Nada',
  'designJourney.localesLast': 'Una encuesta necesita al menos un idioma.',
  // ── Projects ───────────────────────────────────────────────────────
  'project.detail.kpi.events': 'Eventos',
  'project.detail.kpi.stations': 'Estaciones',
  'project.detail.kpi.responses': 'Interactions',
  'project.detail.events': 'Eventos',
  'project.detail.noEvents': 'Aún no hay eventos en este proyecto.',
  'project.detail.newEvent': 'Nuevo evento',
  'project.detail.editProject': 'Editar proyecto',
  'project.detail.deleteProject': 'Eliminar proyecto',
  'project.detail.confirmDelete': '¿Eliminar este proyecto?',
  'project.detail.deletedTitle': 'Proyecto eliminado',
  'project.detail.savedTitle': 'Proyecto guardado',
  'project.detail.notFound': 'Proyecto no encontrado.',
  'project.detail.backToBrand': 'Volver a la marca',

  // ── Contacts ───────────────────────────────────────────────────────
  'contacts.title': 'Contactos',
  'contacts.subtitle': 'Interactions de journeys capturadas en tus estaciones.',
  'contacts.empty.title': 'Aún no hay interactions',
  'contacts.empty.body': 'Lanza una journey en una estación y verás interactions aquí.',
  'contacts.searchPlaceholder': 'Buscar contactos…',
  'contacts.column.name': 'Nombre',
  'contacts.column.email': 'Correo',
  'contacts.column.phone': 'Teléfono',
  'contacts.column.event': 'Evento',
  'contacts.column.date': 'Fecha',
  'contacts.column.source': 'Fuente',
  'contacts.exportFilename': 'contactos',

  // ── Insights ───────────────────────────────────────────────────────
  'insights.title': 'Insights',
  'insights.subtitle': 'Desempeño de distribución por marca, evento e inventario.',
  'insights.kpi.totalResponses': 'Interactions totales',
  'insights.kpi.uniqueContacts': 'Contactos únicos',
  'insights.kpi.activeStations': 'Estaciones activas',
  'insights.kpi.completionRate': 'Tasa de finalización',
  'insights.responsesByDay': 'Interactions por día',
  'insights.responsesByEvent': 'Interactions por evento',
  'insights.responsesByStation': 'Interactions por estación',
  'insights.topStock': 'Merch principales',
  'insights.empty.title': 'Aún no hay suficientes datos',
  'insights.empty.body': 'Una vez que empiecen a llegar interactions, verás tus insights aquí.',
  'insights.notCaptured.title': 'No se capturó nada.',
  'insights.notCaptured.body': 'Los {n} evento(s) de esta selección no tienen ni una interacción registrada. No es un filtro que excluya datos: no se escribió ninguna partida, encuesta ni escaneo. Revisa los enlaces de las estaciones y el estado del despliegue antes de leer cualquier gráfica.',

  // ── Settings ───────────────────────────────────────────────────────
  'settings.title': 'Ajustes',
  'settings.subtitle': 'Preferencias y ajustes de tu cuenta.',
  'settings.section.account': 'Cuenta',
  'settings.section.workspace': 'Cuenta',
  'settings.section.notifications': 'Notificaciones',
  'settings.section.danger': 'Zona de peligro',
  'settings.workspace.name': 'Nombre de la cuenta',
  'settings.workspace.timezone': 'Zona horaria',
  'settings.workspace.language': 'Idioma predeterminado',
  'settings.notifications.email': 'Notificaciones por correo',
  'settings.notifications.product': 'Actualizaciones del producto',
  'settings.danger.deleteWorkspace': 'Eliminar cuenta',
  'settings.danger.deleteWorkspaceHint': 'Esta acción no se puede deshacer.',
  'settings.savedTitle': 'Ajustes guardados',

  // ── Access manager ─────────────────────────────────────────────────
  'access.title': 'Administrador de accesos',
  'access.subtitle': 'Invita a tu equipo y controla quién puede hacer qué.',
  'access.tabs.members': 'Miembros',
  'access.tabs.roles': 'Roles',
  'access.tabs.grants': 'Concesiones',
  'access.members.invite': 'Invitar miembro',
  'access.members.empty.title': 'Aún no hay miembros',
  'access.members.empty.body': 'Invita a tu equipo a colaborar en tus activaciones.',
  'access.members.column.name': 'Nombre',
  'access.members.column.email': 'Correo',
  'access.members.column.role': 'Rol',
  'access.members.column.status': 'Estado',
  'access.members.dialog.inviteTitle': 'Invitar miembro',
  'access.members.dialog.editTitle': 'Editar miembro',
  'access.members.dialog.email': 'Correo',
  'access.members.dialog.role': 'Rol',
  'access.members.dialog.firstName': 'Nombre',
  'access.members.dialog.lastName': 'Apellido',
  'access.members.savedTitle': 'Miembro guardado',
  'access.members.removedTitle': 'Miembro eliminado',
  'access.members.confirmRemove': '¿Quitar a este miembro?',
  'access.roles.new': 'Nuevo rol',
  'access.roles.empty.title': 'Aún no hay roles',
  'access.roles.empty.body': 'Crea un rol para agrupar permisos para tu equipo.',
  'access.roles.column.name': 'Nombre',
  'access.roles.column.members': 'Miembros',
  'access.roles.column.permissions': 'Permisos',
  'access.roles.dialog.newTitle': 'Nuevo rol',
  'access.roles.dialog.editTitle': 'Editar rol',
  'access.roles.dialog.name': 'Nombre del rol',
  'access.roles.dialog.description': 'Descripción',
  'access.roles.dialog.permissions': 'Permisos',
  'access.roles.savedTitle': 'Rol guardado',
  'access.roles.deletedTitle': 'Rol eliminado',
  'access.roles.confirmDelete': '¿Eliminar este rol?',
  'access.grants.new': 'Nueva concesión',
  'access.grants.empty.title': 'Aún no hay concesiones',
  'access.grants.empty.body': 'Otorga a un miembro acceso a una marca, proyecto o evento específico.',
  'access.grants.column.member': 'Miembro',
  'access.grants.column.scope': 'Alcance',
  'access.grants.column.role': 'Rol',
  'access.grants.dialog.newTitle': 'Nueva concesión',
  'access.grants.dialog.editTitle': 'Editar concesión',
  'access.grants.dialog.member': 'Miembro',
  'access.grants.dialog.scope': 'Alcance',
  'access.grants.dialog.role': 'Rol',
  'access.grants.savedTitle': 'Concesión guardada',
  'access.grants.deletedTitle': 'Concesión eliminada',
  'access.grants.confirmDelete': '¿Quitar esta concesión?',
  'access.permissions.available': 'Disponibles',
  'access.permissions.selected': 'Seleccionados',
  'access.permissions.searchPlaceholder': 'Buscar permisos…',

  // ── Brand identity ─────────────────────────────────────────────────
  'brandIdentity.title': 'Identidad de marca',
  'brandIdentity.subtitle': 'Logo, colores y voz que definen tu marca.',
  'brandIdentity.logo': 'Logo',
  'brandIdentity.primaryColor': 'Color primario',
  'brandIdentity.accentColor': 'Color de acento',
  'brandIdentity.voice': 'Voz de la marca',
  'brandIdentity.voicePlaceholder': 'Algunos adjetivos que describen cómo habla tu marca.',
  'brandIdentity.savedTitle': 'Identidad de marca guardada',

  // ── Brand studio (new page) ────────────────────────────────────────
  'brandStudio.new.title': 'Crear una nueva marca',
  'brandStudio.new.subtitle': 'Configura lo esencial. Podrás refinar todo después.',
  'brandStudio.new.brandName': 'Nombre de la marca',
  'brandStudio.new.brandNamePlaceholder': 'p. ej. Atledi',
  'brandStudio.new.logo': 'Logo',
  'brandStudio.new.primaryColor': 'Color primario',
  'brandStudio.new.accentColor': 'Color de acento',
  // @deprecated sidebar.calculator y calculator.*: la página usa merchPlanner.* desde T-2026-10-21-mp-s1-motor-planner; se borran en una limpieza aparte.
  'brandStudio.new.create': 'Crear marca',
  'brandStudio.new.createdTitle': 'Marca creada',

  // ── Agency ─────────────────────────────────────────────────────────
  'agency.title': 'Perfil de la agencia',
  'agency.subtitle': 'Cómo se ve tu agencia ante marcas y socios.',
  'agency.section.basics': 'Básicos',
  'agency.section.team': 'Equipo',
  'agency.section.brands': 'Marcas',
  'agency.name': 'Nombre de la agencia',
  'agency.tagline': 'Eslogan',
  'agency.about': 'Acerca de',
  'agency.aboutPlaceholder': 'Una descripción breve de tu agencia.',
  'agency.logo': 'Logo',
  'agency.website': 'Sitio web',
  'agency.savedTitle': 'Agencia guardada',
  'agency.team.empty': 'Invita a tu equipo desde el Administrador de accesos.',
  'agency.brands.empty': 'Aún no hay marcas vinculadas a esta agencia.',
  'agency.home.title': 'Panel de agencia',
  'agency.home.subtitle': 'Rastrea la actividad en todas las marcas que gestionas.',
  'agency.home.activeBrands': 'Marcas activas',
  'agency.home.liveEvents': 'Eventos en vivo',
  'agency.home.totalResponses': 'Interactions totales',
  'agency.home.teammates': 'Miembros del equipo',

  // ── Billing ────────────────────────────────────────────────────────
  'billing.title': 'Facturación',
  'billing.subtitle': 'Planes, uso y facturas.',
  'billing.currentPlan': 'Plan actual',
  'billing.changePlan': 'Cambiar plan',
  'billing.upgrade': 'Mejorar',
  'billing.downgrade': 'Bajar plan',
  'billing.cancelPlan': 'Cancelar plan',
  'billing.invoices': 'Facturas',
  'billing.noInvoices': 'Aún no hay facturas.',
  'billing.usageTitle': 'Uso',
  'billing.usage.responses': 'Interactions',
  'billing.usage.stations': 'Estaciones',
  'billing.usage.events': 'Eventos',
  'billing.usage.teammates': 'Miembros',
  'billing.usage.usedOf': 'usados de',
  'billing.pricing.title': 'Planes',
  'billing.pricing.subtitle': 'Elige el plan que se adapta a tus activaciones.',
  'billing.pricing.month': '/ mes',
  'billing.pricing.year': '/ año',
  'billing.pricing.choosePlan': 'Elegir plan',
  'billing.pricing.currentPlan': 'Plan actual',
  'billing.pricing.contactSales': 'Contactar ventas',
  'billing.upgradeDialog.title': 'Mejorar plan',
  'billing.upgradeDialog.confirm': 'Confirmar mejora',
  'billing.upgradeDialog.cancel': 'Cancelar',
  'billing.checkout.successTitle': 'Pago exitoso',
  'billing.checkout.successBody': 'Tu plan se actualizó. ¡Bienvenido a bordo!',
  'billing.checkout.failedTitle': 'Pago fallido',
  'billing.checkout.failedBody': 'Algo salió mal al procesar tu pago.',
  'billing.checkout.returnToBilling': 'Volver a facturación',
  'billing.mock.title': 'Checkout de prueba',
  'billing.mock.subtitle': 'Este es un flujo de prueba — no se cobrará ningún pago real.',
  'billing.mock.pay': 'Pagar ahora',
  'billing.mock.cancel': 'Cancelar',

  // ── Public survey ──────────────────────────────────────────────────
  'publicSurvey.continue': 'Continuar',
  'publicSurvey.back': 'Atrás',
  'publicSurvey.submit': 'Enviar',
  'publicSurvey.finish': 'Finalizar',
  'publicSurvey.thanks.title': '¡Gracias por tu interaction!',
  'publicSurvey.thanks.body': 'Tus interactions han sido registradas.',
  'publicSurvey.thanks.replay': 'Girar de nuevo',
  'publicSurvey.notFound.title': 'Journey no encontrada',
  'publicSurvey.notDeployed': 'Esta estación no tiene ninguna encuesta en marcha ahora mismo. Si gestionas este evento, despliégala desde su pestaña de Encuesta.',
  'publicSurvey.notFound.body': 'Verifica el enlace o el QR e intenta de nuevo.',
  'publicSurvey.required': 'Este campo es obligatorio.',
  'publicSurvey.invalidEmail': 'Ingresa un correo válido.',
  'publicSurvey.invalidPhone': 'Ingresa un teléfono válido.',
  'publicSurvey.wheel.spin': 'Girar la ruleta',
  'publicSurvey.wheel.spinning': 'Girando…',
  'publicSurvey.wheel.youWon': 'Ganaste',
  'publicSurvey.wheel.tryAgain': 'Intentar de nuevo',
  'publicSurvey.wheel.claim': 'Reclama tu premio',
  'publicSurvey.loading': 'Cargando journey…',

  // ── Sidebar ────────────────────────────────────────────────────────
  'sidebar.home': 'Inicio',
  'sidebar.myXotskies': 'My Merch',
  'sidebar.brandInventory': 'Inventario de marca',
  'sidebar.calculator': 'Calculadora',
  'calculator.title': 'Calculadora',
  'calculator.subtitle': 'Ni quedarse sin nada ni pedir de más: de la asistencia esperada a las unidades a pedir por premio, con la cuenta a la vista. Guarda el resultado en un evento y Plan, Track y el informe miden contra él.',
  'calculator.inputs': 'Entradas',
  'calculator.attendance': 'Asistencia esperada por día',
  'calculator.attendanceHint': 'Personas en el recinto, no jugadores.',
  'calculator.captureRate': 'Tasa de captura',
  // @deprecated — sustituida por calculator.captureRateHintStart (T-2026-10-23-xotski-calculadora-arreglos)
  'calculator.captureRateHint': 'Parte de los asistentes que juegan. Tus eventos pasados rondan el 30–40%.',
  'calculator.days': 'Días de evento',
  'calculator.buffer': 'Colchón',
  'calculator.bufferHint': 'Extra sobre las unidades esperadas.',
  'calculator.prizes': 'Premios en el juego',
  'calculator.noStock': 'La marca aún no tiene merch. Dalo de alta en',
  'calculator.rarity': 'rareza {n}',
  'calculator.pendingTitle': 'Pendiente',
  'calculator.pendingBody': 'Falta: {needs}. El resultado no se adivina: rellena esto y aparece.',
  'calculator.need.attendance': 'la asistencia esperada',
  'calculator.need.captureRate': 'una tasa de captura entre 1 y 100%',
  'calculator.need.days': 'al menos un día',
  'calculator.need.buffer': 'un colchón de 0% o más',
  'calculator.need.items': 'al menos un premio en el juego',
  'calculator.results': 'Pedido por premio',
  'calculator.col.prize': 'Premio',
  'calculator.col.weight': 'Peso',
  'calculator.col.probability': 'Probabilidad',
  'calculator.col.expected': 'Esperado',
  'calculator.col.order': 'Pedido',
  'calculator.col.threshold': 'Reponer en',
  'calculator.total': 'Total · {plays} partidas',
  'calculator.working': 'La cuenta',
  'calculator.working.plays': 'partidas = {a} asistentes × {c} captura × {d} día(s) = {plays}',
  'calculator.working.weight': 'probabilidad por premio = peso ÷ {sum}, con peso = (6 − rareza)², la misma curva que usa el juego',
  'calculator.working.order': 'pedido = ceil(partidas × probabilidad × (1 + {b} colchón))',
  // @deprecated — sustituida por calculator.working.restock (T-2026-10-23-xotski-calculadora-arreglos)
  'calculator.working.threshold': 'reponer en = ceil(pedido ÷ {d} día(s)): un día de consumo',
  'calculator.saveTitle': 'Guardar en un evento',
  // @deprecated — sustituida por calculator.saveBodyEvent (T-2026-10-23-xotski-calculadora-arreglos)
  'calculator.saveBody': 'Escribe el pedido como unidades esperadas de cada premio en ese evento, repartidas entre sus estaciones. Los premios sin asignación en el evento se saltan: asígnalos antes en Plan.',
  'calculator.project': 'Proyecto',
  'calculator.event': 'Evento',
  'calculator.pickProject': 'Elige un proyecto…',
  'calculator.pickEvent': 'Elige un evento…',
  'calculator.saveCta': 'Guardar el esperado en el evento',
  'calculator.savedToast': 'Esperado guardado en {n} asignación(es)',
  'calculator.savedSkipped': '{n} premio(s) saltados: sin asignación en este evento.',
  'calculator.saveFailed': 'No se pudo guardar el esperado',
  'calculator.captureRateHintStart': 'Parte de los asistentes que juegan. El 35% es sólo un punto de partida, no un dato de tu marca: ajústalo al evento.',
  'calculator.hours': 'Horas abiertas por día',
  'calculator.hoursHint': 'Se rellena con el evento al elegirlo. Sólo se usa para la guía de reposición.',
  'calculator.eventSection': 'Evento (opcional)',
  'calculator.eventSectionHint': 'Elige uno para planificar sólo el merch que juega allí, con la rareza fijada en cada estación.',
  'calculator.leftOut.title': 'Fuera del plan',
  'calculator.leftOut.notAllocated': 'sin asignación en este evento',
  'calculator.leftOut.hidden': 'oculto de los juegos en todas sus estaciones',
  'calculator.leftOut.removed': 'retirado de la marca; el juego aún puede entregarlo mientras siga asignado',
  'calculator.leftOut.allocate': 'Asignar merch en Plan',
  'calculator.working.restock': 'reponer en = ceil(pedido ÷ {d} día(s) ÷ {h} h ÷ estaciones con ese merch): más o menos una hora de entregas en una estación. Es sólo una guía: no se guarda.',
  'calculator.working.restockPending': 'reponer en: añade las horas abiertas por día para verlo.',
  'calculator.saveBodyEvent': 'Escribe el pedido como unidades esperadas de cada merch de este plan en el evento elegido, repartidas entre sus estaciones.',
  'calculator.saveNeedsEvent': 'Elige un evento arriba para guardar.',
  'calculator.listTooLong': 'Este evento o esta marca tienen más de 500 asignaciones o merch, así que la calculadora no los ve todos: no planifica ni guarda este evento.',
  'calculator.allocationsError': 'No se pudieron cargar las asignaciones de este evento, así que la calculadora no sabe qué juega aquí. No planifica ni guarda este evento: recarga la página o vuelve a intentarlo en un momento.',
  'sidebar.merchPlanner': 'Planificador de merch',
  'merchPlanner.title': 'Planificador de merch',
  'merchPlanner.subtitle': 'Cuánta merch pedir para un evento, con la cuenta a la vista: invitados, quién juega, quién viene a recoger y un colchón. Las probabilidades son las del propio juego.',
  'merchPlanner.planFor': 'Evento (opcional)',
  'merchPlanner.planForHint': 'Elige uno para planificar sólo la merch que juega allí, estación por estación, con la rareza fijada en cada una.',
  'merchPlanner.project': 'Proyecto',
  'merchPlanner.pickProject': 'Elige un proyecto…',
  'merchPlanner.event': 'Evento',
  'merchPlanner.noEvent': 'Sin evento todavía: planificar con toda la merch de la marca',
  'merchPlanner.eventNotFound': 'Ese evento no está disponible. Elige uno de tus proyectos.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.inputs': 'Entradas',
  'merchPlanner.events': 'Eventos',
  'merchPlanner.guests': 'Invitados por evento',
  'merchPlanner.guestsHint': 'Personas en el evento, no jugadores.',
  'merchPlanner.shareWhoPlay': 'Parte que juega',
  'merchPlanner.shareWhoPlayHint': 'Parte de los invitados que juega. El 40% es un punto de partida, no un dato tuyo: Xotski todavía no registra la asistencia.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.claimRate': 'Tasa de canje',
  'merchPlanner.claimRateHint': 'Ganadores que vienen a recoger su premio. Es la misma tasa de canje del informe del evento.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.buffer': 'Colchón',
  'merchPlanner.bufferHint': 'Extra sobre lo que esperas entregar.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.hours': 'Horas de apertura',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.hoursFromEvent': 'Del evento: {h} h. Escribe otra cifra para cambiarla en este evento.',
  'merchPlanner.hoursMultiDay': 'Este evento abarca {h} horas en varios días: escribe las horas que está abierto.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.stations': 'Estaciones por evento',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.stationsFromEvent': '{n} estación(es) con merch en el juego, del evento.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.minutesPerHandout': 'Minutos por entrega',
  'merchPlanner.minutesPerHandoutHint': 'Tiempo en la estación para entregar un premio.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.draftNote': 'Tus entradas se guardan en este dispositivo hasta que cierres sesión.',
  'merchPlanner.prizes': 'Premios en el juego',
  'merchPlanner.noMerch': 'La marca aún no tiene merch. Dalo de alta en',
  'merchPlanner.rarity': 'rareza {n}',
  'merchPlanner.rarityRange': 'rareza {min}–{max}',
  'merchPlanner.cooldown': 'uno cada {n} min',
  'merchPlanner.duplicateRows': '{n} filas de este premio en {station}: el juego sólo usa la primera.',
  'merchPlanner.stationNoMerch': '{station} no tiene merch en el juego: el plan reparte sus invitados entre las demás estaciones.',
  'merchPlanner.notInGame.title': 'Fuera del plan',
  'merchPlanner.notInGame.body': 'No se llevan parte del plan. Asígnalos al evento para contarlos.',
  'merchPlanner.notInGame.unallocated': 'sin asignación en este evento',
  'merchPlanner.notInGame.hidden': 'oculto en los juegos en todas sus estaciones',
  'merchPlanner.notInGame.disabled': 'retirado de la marca',
  'merchPlanner.notInGame.disabledStillPlays': 'Mientras siga asignado aquí, el juego aún puede entregarlo. Quita su asignación en Asignar premios.',
  'merchPlanner.loadError': 'No se pudo cargar la merch, las estaciones o las asignaciones de este evento, así que el planificador no sabe qué juega aquí. No planifica ni guarda esto: recarga la página o vuelve a intentarlo en un momento.',
  'merchPlanner.listTooLong': 'Aquí hay más de 500 asignaciones, estaciones o artículos de merch, así que el planificador no los ve todos. No planifica ni guarda esto.',
  'merchPlanner.pendingTitle': 'Pendiente',
  'merchPlanner.pendingBody': 'Falta: {needs}. El resultado no se adivina: rellena esto y aparece.',
  'merchPlanner.need.events': 'al menos un evento',
  'merchPlanner.need.guests': 'los invitados por evento',
  'merchPlanner.need.shareWhoPlay': 'una parte que juega entre 1 y 100%',
  'merchPlanner.need.claimRate': 'una tasa de canje entre 1 y 100%',
  'merchPlanner.need.buffer': 'un colchón de 0% o más',
  'merchPlanner.need.stations': 'al menos una estación',
  'merchPlanner.need.items': 'al menos un premio en el juego',
  'merchPlanner.need.hours': 'las horas de apertura',
  'merchPlanner.need.minutesPerHandout': 'minutos por entrega, más de 0 y hasta 60',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.results': 'Qué pedir',
  'merchPlanner.col.prize': 'Premio',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.col.weight': 'Peso',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.col.chance': 'Probabilidad',
  'merchPlanner.col.planned': 'Previsto',
  'merchPlanner.col.order': 'A pedir',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.col.perEvent': '≈ Por evento',
  'merchPlanner.col.restock': 'Reponer en (por estación)',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.total': 'Total · {players} jugadores · {claims} canjes',
  'merchPlanner.restockGuide': 'Reponer en = más o menos una hora de entregas en una estación. Sólo orientativo: no se guarda.',
  'merchPlanner.restockPending': 'Reponer en necesita las horas de apertura.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.capacity.ok': '{points} estación(es) entregan hasta {capacity} por hora; tu hora punta necesita unos {peak}. Cubierto.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.capacity.short': '{points} estación(es) entregan hasta {capacity} por hora; tu hora punta necesita unos {peak}: añade {extra} más.',
  'merchPlanner.capacity.pending': 'Capacidad: falta {needs}.',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.working': 'La cuenta',
  'merchPlanner.working.players': 'jugadores = {events} evento(s) × {guests} invitados × {share} que juega = {players}',
  'merchPlanner.working.claims': 'canjes = {players} jugadores × {claim} de canje = {claims}',
  'merchPlanner.working.order': 'a pedir = ⌈{claims} canjes × (1 + {buffer} de colchón)⌉ = {order}',
  'merchPlanner.working.chance': 'probabilidad por premio = su peso entre los pesos de cada estación, con peso = (6 − rareza)², la misma curva del juego; los invitados se reparten a partes iguales entre estaciones',
  'merchPlanner.working.split': 'previsto y a pedir se reparten por premio según la probabilidad, así que las filas suman los totales',
  // @deprecated (S1b): sin uso, se conserva por compatibilidad
  'merchPlanner.saveTitle': 'Guardar en el evento',
  'merchPlanner.saveBody': 'Escribe las unidades a pedir de cada premio, con el colchón, como unidades esperadas en este evento, repartidas entre las estaciones que lo tienen. Track y el informe del evento miden contra ellas los premios entregados. La merch fuera del plan se pone a 0.',
  'merchPlanner.saveNeedsEvent': 'Elige un evento para guardar el plan en él.',
  'merchPlanner.savedNow': 'Guardado ahora en el evento: {n} unidad(es) esperada(s).',
  'merchPlanner.saveCta': 'Guardar el pedido en el evento',
  'merchPlanner.savedToast': 'Unidades esperadas guardadas en {n} asignación(es)',
  'merchPlanner.savedSkipped': '{n} premio(s) saltados: sin asignación en este evento.',
  'merchPlanner.saveFailed': 'No se pudieron guardar las unidades esperadas',
  'merchPlanner.entry.planMerch': 'Planificar merch',
  'merchPlanner.entry.saveFirst': 'Guarda antes el evento',
  'merchPlanner.entry.fromDrawer': 'Planificar este evento',
  'merchPlanner.entry.applyFirst': 'Aplica o cancela antes tus cambios',
  'merchPlanner.crowdTitle': 'Tu público',
  'merchPlanner.guestsInAll': '{n} invitados en total',
  'merchPlanner.guestsFewer': 'Menos invitados',
  'merchPlanner.guestsMore': 'Más invitados',
  'merchPlanner.shareWords.few': 'Se acercan unos pocos curiosos.',
  'merchPlanner.shareWords.typical': 'Juegan unos {n} de cada 10 invitados. Lo normal en un stand con juego.',
  'merchPlanner.shareWords.most': 'Juega casi todo el público, como en una activación estrella.',
  'merchPlanner.claimLabel': 'Ganadores que recogen su premio',
  'merchPlanner.bufferLabel': 'Colchón de seguridad',
  'merchPlanner.buffer.tight': 'Justo',
  'merchPlanner.buffer.lean': 'Ajustado',
  'merchPlanner.buffer.safe': 'Seguro',
  'merchPlanner.buffer.generous': 'Generoso',
  'merchPlanner.dayTitle': 'El día',
  'merchPlanner.eventsLess': 'Un evento menos',
  'merchPlanner.eventsMore': 'Un evento más',
  'merchPlanner.stationsLabel': 'Estaciones o staff de campo (BAs)',
  'merchPlanner.stationsBaHint': 'Cada BA con su propio código QR es un punto de entrega más.',
  'merchPlanner.stationsLess': 'Una estación o BA menos',
  'merchPlanner.stationsMore': 'Una estación o BA más',
  // @deprecated (S1b r2): sin uso, se conserva por compatibilidad
  'merchPlanner.stationsLocked': 'Del evento. Las estaciones o BAs se añaden o se quitan en el plan del proyecto.',
  'merchPlanner.hoursOpen': 'Horas abiertas por día',
  'merchPlanner.hoursLess': 'Una hora menos',
  'merchPlanner.hoursMore': 'Una hora más',
  'merchPlanner.hoursEventChip': 'Evento · {h} h',
  'merchPlanner.timePerHandout': 'Tiempo por entrega',
  'merchPlanner.clock.minuteUp': 'Un minuto más',
  'merchPlanner.clock.minuteDown': 'Un minuto menos',
  'merchPlanner.clock.secondUp': 'Quince segundos más',
  'merchPlanner.clock.secondDown': 'Quince segundos menos',
  'merchPlanner.clock.caption': 'MIN:SEG',
  'merchPlanner.clock.value': '{m} minutos {s} segundos por entrega',
  'merchPlanner.clock.hint': 'Un invitado juega, gana y se va con su premio. Son {n} entregas por hora y por estación o BA.',
  'merchPlanner.savedOnDevice': 'Guardado en este dispositivo',
  'merchPlanner.result.order': 'Pedido',
  'merchPlanner.result.piecesPerEvent': 'piezas de merch, unas {n} por evento',
  'merchPlanner.result.piecesOne': 'piezas de merch para este evento',
  'merchPlanner.result.players': 'Jugadores',
  'merchPlanner.result.claims': 'Premios recogidos',
  'merchPlanner.result.buffer': 'Colchón',
  'merchPlanner.split.title': 'Cómo se reparte el pedido',
  'merchPlanner.split.hint': 'La merch más rara sale menos, así que necesitas menos.',
  'merchPlanner.col.rarity': 'Rareza',
  'merchPlanner.rarityWord.1': 'Muy común',
  'merchPlanner.rarityWord.2': 'Común',
  'merchPlanner.rarityWord.3': 'Media',
  'merchPlanner.rarityWord.4': 'Rara',
  'merchPlanner.rarityWord.5': 'Muy rara',
  'merchPlanner.restockWhen': 'cuando queden {n}',
  'merchPlanner.workingToggle': 'De dónde sale {n}',
  'merchPlanner.capacity.avgOk': '{points} estación(es) o BA(s) entregan hasta {capacity} por hora; una hora media necesita unos {peak}. Cubierto de media.',
  'merchPlanner.capacity.avgShort': '{points} estación(es) o BA(s) entregan hasta {capacity} por hora; una hora media necesita unos {peak}: añade {extra} más.',
  'merchPlanner.sim.title': 'Simulador de hora punta',
  'merchPlanner.sim.subtitle': 'Un día de evento a cámara rápida: los invitados llegan, hacen cola, ganan y se van.',
  'merchPlanner.sim.crowdLabel': 'Cómo llega el público',
  'merchPlanner.sim.steady': 'Público constante',
  'merchPlanner.sim.rush': 'Pico a media jornada',
  'merchPlanner.sim.play': 'Reproducir el día',
  'merchPlanner.sim.resume': 'Seguir',
  'merchPlanner.sim.again': 'Otra vez',
  'merchPlanner.sim.pause': 'Pausa',
  'merchPlanner.sim.reset': 'Reiniciar',
  'merchPlanner.sim.clock': 'Reloj del evento',
  'merchPlanner.sim.doorsOpen': 'Abren puertas',
  'merchPlanner.sim.close': 'Cierre',
  'merchPlanner.sim.progress': '{n} % del día',
  'merchPlanner.sim.station': 'Estación o BA {n}',
  'merchPlanner.sim.served': 'atendidos {n}',
  'merchPlanner.sim.noLine': 'sin cola',
  'merchPlanner.sim.noWait': 'sin espera',
  'merchPlanner.sim.wait': 'unos {n} min',
  'merchPlanner.sim.waitCaption': 'de espera en la cola',
  'merchPlanner.sim.moreStations': '+{n} estaciones o BAs más, {q} personas esperando',
  'merchPlanner.sim.played': 'Han jugado',
  'merchPlanner.sim.merchLeft': 'Merch que queda para este evento',
  'merchPlanner.sim.longestLine': 'Cola más larga del día',
  'merchPlanner.sim.people': '{n} personas',
  'merchPlanner.sim.okOne': 'Una estación o BA da abasto, incluso en el momento de más gente.',
  'merchPlanner.sim.okMany': 'Tus {n} estaciones o BAs dan abasto, incluso en el momento de más gente.',
  'merchPlanner.sim.short': 'Se forma cola en el momento de más gente: unos {peak} jugadores por hora frente a {capacity} que puedes atender.',
  'merchPlanner.sim.addOne': 'Suma 1 estación o BA',
  'merchPlanner.sim.addStations': 'Suma {n} estaciones o BAs',
  // @deprecated (S1b r2): sin uso, se conserva por compatibilidad
  'merchPlanner.sim.addInPlan': 'Suma estaciones o BAs en el plan del proyecto',
  'merchPlanner.sim.pending': 'Al simulador le falta {needs}.',
  'merchPlanner.sim.dayOver': 'Fin del día: han jugado {played}, cola más larga {line}.',
  'merchPlanner.sim.reducedMotion': 'Este dispositivo reduce el movimiento: Reproducir enseña el final del día.',
  'merchPlanner.hoursChip': '{n} h',
  'merchPlanner.col.merch': 'Merch',
  'merchPlanner.stationsLockedBa': 'Del evento. Para sumar BAs, añade una estación por cada uno en el proyecto; cada estación tiene su propio código QR.',
  'merchPlanner.sim.addInPlanBa': 'Para sumar BAs, añade una estación por cada uno en el proyecto; cada estación tiene su propio código QR.',
  'sidebar.brandStudio': 'Estudio de marca',
  'sidebar.projects': 'Proyectos',
  'sidebar.insights': 'Métricas',
  'sidebar.contacts': 'Contactos',
  'sidebar.redeem': 'Canjear premio',
  'sidebar.profile': 'Perfil',
  'sidebar.accessManager': 'Gestor de acceso',
  'sidebar.settings': 'Configuración',
  'sidebar.admin': 'Admin',
  'sidebar.collapse': 'Colapsar menú',
  'sidebar.expand': 'Expandir menú',
  'sidebar.profileMenu.workspace': 'Cuenta',
  'sidebar.profileMenu.workspaceSublabel': 'Plan · cupos · marcas · facturación',
  'sidebar.profileMenu.plan': 'Plan',
  'sidebar.profileMenu.planSublabel': 'Cupos · marcas · facturación',
  'sidebar.profileMenu.language': 'Idioma',
  'sidebar.profileMenu.languagePickerSoon': 'Selector de idioma próximamente',
  'sidebar.profileMenu.openMenu': 'abrir menú',
  'sidebar.favorites': 'Favoritos',
  'sidebar.workspace': 'Cuenta',
  'sidebar.notifications': 'Notificaciones',
  'sidebar.billing': 'Facturación',
  'sidebar.followup': 'Seguimiento',
  'sidebar.integrations': 'Integraciones',
  'sidebar.surveyBuilder': 'Design Journey',

  // ── Inventario de marca (PRD §9, modelo de inventario en dos niveles) ──
  'brandInventory.title': 'Inventario de marca',
  'brandInventory.subtitle':
    'Pool de artículos a nivel de marca. Asígnalos a proyectos, regrésalos al cerrar un evento o márcalos como baja.',
  'brandInventory.noun': 'artículos',
  'brandInventory.filterByState': 'Filtrar por estado',
  'brandInventory.allStates': 'Todos',
  'brandInventory.empty': 'Ningún artículo coincide con estos filtros.',
  'brandInventory.emptyHint': 'Captura uno desde el campo o créalo desde el admin.',
  'brandInventory.emptyNotEnabled': 'Inventario de marca no está habilitado',
  'brandInventory.emptyNotEnabledHint':
    'Habilítalo en Ajustes › Marca › Inventario para empezar a registrar artículos.',
  'brandInventory.estimated': 'estimado',
  'brandInventory.col.name': 'Nombre',
  'brandInventory.col.quantity': 'Cant.',
  'brandInventory.col.state': 'Estado',
  'brandInventory.col.rating': 'Rareza',
  'brandInventory.col.tags': 'Etiquetas',
  'brandInventory.state.unassigned': 'En almacén',
  'brandInventory.state.allocated': 'Asignado (en juego)',
  'brandInventory.state.distributed': 'Entregados',
  'brandInventory.state.claimed': 'Reclamado',
  'brandInventory.state.returned': 'Devueltos al cierre',
  'brandInventory.basis': 'Cifras del libro de premios de los {n} evento(s) de la marca y de sus asignaciones vivas. Los mismos hechos que el informe de cada evento.',
  'brandInventory.goToMerch': 'Abrir My Merch',
  'brandInventory.emptyNoMerch': '{brand} aún no tiene merch',
  'brandInventory.emptyNoMerchHint': 'Da de alta ítems en My Merch; aparecen aquí en cuanto existen, con lo que pasó con cada unidad que salió.',
  'brandInventory.emptyFilter': 'Ningún ítem está en el estado «{state}»',
  'brandInventory.emptyFilterHint': 'La marca tiene {n} ítem(s); ninguno tiene unidades en este estado. Elige otra ficha o «Todos».',
  'brandInventory.rarity': 'Rareza {n}/5',
  'brandInventory.col.total': 'Total',
  'brandInventory.state.pending': 'Pendientes de recoger',
  'brandInventory.state.lapsed': 'Caducados / anulados',
  'brandInventory.state.unaccounted': 'Sin destino',
  'brandInventory.state.written_off': 'Dado de baja',

  // ── Entity Home (PRD §7.1) ─────────────────────────────────────
  'entityHome.atledi.label': 'Atledi',
  'entityHome.atledi.youFallback': 'equipo',
  'entityHome.atledi.greeting.morning':
    'Buen día, {name}. Tres días en la semana — ¿qué activación sigue en la lista?',
  'entityHome.atledi.greeting.afternoon':
    'Buenas tardes, {name}. A media tarde sube la tasa de captura. ¿Reviso tus estaciones en vivo?',
  'entityHome.atledi.greeting.evening':
    'Buenas noches, {name}. La ventana de preparación para mañana empieza ahora. ¿Armamos el brief del próximo evento?',
  // Brand home (Entity Home simplificado)
  'brandHome.cooking': '¿Qué se cocina hoy, {name}?',
  'brandHome.addXotskies': 'Agregar Merch',
  'brandHome.addXotskiesHint': 'Premios y merch para tu inventario',
  'brandHome.addEvents': 'Agregar eventos',
  'brandHome.addEventsHint': 'Programa una activación',
  'brandHome.addProject': 'Agregar un proyecto',
  'brandHome.addProjectHint': 'Agrupa eventos en una campaña',
  'brandHome.jumpBack': 'Retoma tus proyectos',
  'brandHome.askAtledi': 'Pregúntale algo más a Atledi…',
  'brandHome.status.planning': 'Planeación',
  'brandHome.status.wrapped': 'Cerrado',
  // Formulario de creación de evento (panel derecho)
  'eventForm.project': 'Proyecto',
  'eventForm.noProjects': 'Crea un proyecto primero — los eventos viven dentro de un proyecto.',
  'eventForm.name': 'Nombre del evento',
  'eventForm.namePlaceholder': 'p. ej. Noche de apertura',
  'eventForm.address': 'Dirección',
  'eventForm.addressPlaceholder': 'Sede o ubicación',
  'eventForm.starts': 'Inicia',
  'eventForm.ends': 'Termina',
  'eventForm.create': 'Crear evento',
  // ── Alta avanzada (montar evento / proyecto) ─────────────────────────
  'scaffold.advanced': 'Avanzado — estaciones y premios',
  'scaffold.events': 'Eventos',
  'scaffold.eventsHint': 'Se reparten por el rango de fechas del proyecto.',
  'scaffold.stations': 'Estaciones por evento',
  'scaffold.stationsHint': 'Cada una tiene su QR y su parte de los premios.',
  'scaffold.prizes': 'Premios a repartir',
  'scaffold.noPrizes': 'Esta marca todavía no tiene premios. Puedes añadirlos al evento después.',
  'scaffold.left': 'quedan {n}',
  'scaffold.perEvent': 'por evento',
  'scaffold.total': 'en total',
  'scaffold.oneStation': 'las {n} en la única estación',
  'scaffold.split': '{n} en cada una de las {s} estaciones',
  'scaffold.splitUneven': '{hi} en {hiN}, {lo} en {loN}',
  'scaffold.fewerThanStations': 'sólo {n} para {s} estaciones — alguna se queda sin',
  'scaffold.overStock': 'Son {total} unidades y en la marca sólo quedan {left}.',
  'scaffold.summary': 'Crea {events} evento(s) y {stations} estación(es).',
  'scaffold.datesNote': 'Los eventos se reparten por el rango de fechas del proyecto.',
  'scaffold.built': 'Montados {events} evento(s) con {stations} estación(es)',
  'scaffold.projectOnly': 'El proyecto se creó, pero sus eventos no',
  // ── Formulario de alta de proyecto ───────────────────────────────────
  'projectForm.name': 'Nombre del proyecto',
  'projectForm.namePlaceholder': 'p. ej. Gira de Primavera 2026',
  'projectForm.codeRef': 'Código de referencia',
  'projectForm.codeRefPlaceholder': 'p. ej. GIRA26',
  'projectForm.starts': 'Fecha de inicio',
  'projectForm.ends': 'Fecha de fin',
  'projectForm.optional': 'Opcional.',
  'projectForm.rangeNote': 'El rango de fechas del proyecto manda sobre las fechas que pueden tener sus eventos. Puedes ponerlo o cambiarlo después desde la ficha del proyecto.',
  'projectForm.create': 'Crear proyecto',
  'projectForm.creating': 'Creando…',
  'projectForm.created': 'Proyecto «{name}» creado',
  'projectForm.createFailed': 'No se pudo crear el proyecto',
  'projectForm.badRange': 'La fecha de fin debe ser igual o posterior a la de inicio',
  // ── Calendario ───────────────────────────────────────────────────────
  'sidebar.calendar': 'Calendario',
  'calendar.prev': 'Mes anterior',
  'calendar.next': 'Mes siguiente',
  'calendar.today': 'Hoy',
  'calendar.countThisMonth': '{n} eventos este mes',
  'calendar.countThisMonthOne': '1 evento este mes',
  'calendar.empty': 'Esta marca todavía no tiene eventos. Usa el + de cualquier día para programar el primero.',
  'calendar.noBrand': 'Elige una marca para ver su calendario.',
  'calendar.more': '+{n} más',
  'calendar.newOnDay': 'Nuevo evento · {day}',
  'calendar.newOnDayShort': 'Nuevo evento este día',
  'calendar.phase.live': 'En vivo',
  'calendar.phase.upcoming': 'Próximo',
  'calendar.phase.edit': 'En preparación',
  'calendar.phase.done': 'Terminado',
  'calendar.view.month': 'Mes',
  'calendar.view.week': 'Semana',
  'calendar.countThisWeek': '{n} eventos esta semana',
  'calendar.countThisWeekOne': '1 evento esta semana',
  'calendar.weekEmpty': 'Esta semana no hay nada.',
  'calendar.summary.title': 'Evento',
  'calendar.summary.open': 'Abrir el evento',
  'calendar.summary.unnamedPrize': 'Premio sin nombre',
  'calendar.summary.gone': 'Ese evento ya no está disponible.',
  'calendar.summary.noDates': 'Todavía sin fechas',
  'calendar.summary.stations': '{n} estaciones',
  'calendar.summary.prizes': 'Premios',
  'calendar.summary.left': 'quedan {left} de {total}',
  // ── Campo de dirección (Google Places + mapa) ────────────────────────
  'address.placeholder': 'Busca un recinto o una calle…',
  'address.hint': 'Elige una sugerencia o pon el marcador en el mapa, para que el sitio se pueda localizar y no sólo leer.',
  'address.pinned': 'Señalado en {lat}, {lng}',
  'address.noKey': 'Texto libre. Las sugerencias y el mapa necesitan configurar VITE_GOOGLE_MAPS_API_KEY.',
  'address.openMap': 'Señalar en el mapa',
  'address.mapTitle': '¿Dónde es?',
  'address.mapHint': 'Arrastra el marcador o pulsa en cualquier punto del mapa. La dirección se saca del marcador.',
  'address.resolving': 'Buscando la dirección…',
  'address.nothingYet': 'Todavía no hay ningún punto elegido.',
  'address.useThis': 'Usar esta ubicación',
  'scaffold.venue': 'Dirección de todos los eventos',
  // ── Borrar una marca (selector de espacio de trabajo) ────────────────
  'brandDelete.action': 'Borrar {name}',
  'brandDelete.title': '¿Borrar «{name}»?',
  'brandDelete.counting': 'Comprobando qué contiene esta marca…',
  'brandDelete.body': 'Contiene {projects} proyecto(s) y {events} evento(s). Todos dejan de estar disponibles con ella.',
  'brandDelete.typeName': 'Escribe «{name}» para confirmar',
  'brandDelete.recoverable': 'No se destruye nada: la marca deja de aparecer en las listas y sus datos siguen en la base, así que un administrador puede recuperarla.',
  'brandDelete.confirm': 'Borrar la marca',
  'brandDelete.deleting': 'Borrando…',
  'brandDelete.done': 'Marca «{name}» borrada',
  'brandDelete.failed': 'No se pudo borrar la marca',
  // ── Estudio de fondo (el mismo motor que Xurvey) ─────────────────────
  'designJourney.designBackground': 'Diseñar el fondo',
  'bgStudio.title': 'Diseñar el fondo',
  'bgStudio.hue': 'Matiz',
  'bgStudio.harmony': 'Armonía',
  'bgStudio.gradient': 'Degradado',
  'bgStudio.angle': 'Ángulo',
  'bgStudio.pattern': 'Trama',
  'bgStudio.noPattern': 'Sin trama',
  'bgStudio.patternSize': 'Tamaño de trama',
  'bgStudio.patternOpacity': 'Opacidad de trama',
  'bgStudio.blend': 'Fusión',
  'bgStudio.vignette': 'Viñeta',
  'bgStudio.glow': 'Resplandor',
  'bgStudio.grain': 'Grano',
  'bgStudio.another': 'Otra variación',
  'bgStudio.applyPalette': 'Aplicar también los colores',
  'bgStudio.use': 'Usar este fondo',
  'bgStudio.applying': 'Aplicando…',
  'bgStudio.savedAs': 'Se guarda como imagen · {format} 1080×1920 · ~{kb} KB',
  'bgStudio.lightened': 'aclarado para que el botón se vea',
  'bgStudio.textContrast': 'Contraste del texto',
  'bgStudio.buttonContrast': 'Contraste del botón',
  'designJourney.projectScope': 'Lo que diseñes aquí se aplica a {deployments}, en {events} de este proyecto. Desplegar una encuesta en un evento se hace desde ese evento.',
  'designJourney.oneDeployment': '1 despliegue vivo',
  'designJourney.nDeployments': '{n} despliegues vivos',
  'designJourney.oneEvent': '1 evento',
  'designJourney.nEvents': '{n} eventos',
  'eventForm.outOfRange': 'Las fechas del evento deben estar dentro del rango del proyecto',
  'eventForm.badRange': 'La fecha de fin debe ser posterior a la de inicio',
  'entityHome.kpi.moments': 'Momentos capturados',
  'entityHome.kpi.throughput': 'Flujo (throughput)',
  'entityHome.kpi.inventoryHealth': 'Salud de inventario',
  'entityHome.kpi.contacts': 'Contactos capturados',
  'entityHome.kpi.comingSoon': 'Métricas en vivo, próximamente',
  'entityHome.kpi.aboveThreshold': 'Artículos que aún tienen unidades',
  'entityHome.kpi.totalDeduped': 'Contactos únicos totales',
  'entityHome.nextEvent.label': 'Mi próximo evento',
  'entityHome.nextEvent.openCta': 'Abrir evento',
  'entityHome.nextEvent.noneTitle': 'No tienes eventos próximos agendados',
  'entityHome.nextEvent.noneHint':
    'Crea un proyecto para agendar tu primera activación.',

  // ── Project Environment (PRD §7.2) ─────────────────────────────
  'project.atledi.label': 'Atledi',
  'project.atledi.emptyProject':
    '{name} aún no tiene eventos. ¿Te armo el primero a partir del brief de marca?',
  'project.atledi.liveActivity':
    '{name} está en vivo en {live} evento(s). Vigila la tasa de captura contra el flujo esperado.',
  'project.atledi.scheduled':
    '{name} tiene {total} evento(s) agendado(s). Cierra el brief 6 días antes para pegar la ventana de temporada.',
  'project.panels.nextEvent.label': 'Próximo evento',
  'project.panels.nextEvent.tbd': 'Fecha por definir',
  'project.panels.nextEvent.empty': 'No hay eventos próximos agendados.',
  'project.panels.nextEvent.open': 'Abrir evento',
  'project.panels.myXotskies.label': 'My Merch',
  'project.panels.myXotskies.itemsTotal': 'artículos en total',
  'project.panels.myXotskies.lowStock': 'bajos',
  'project.panels.myXotskies.open': 'Abrir My Merch',
  'project.panels.reporters.label': 'Reporteros',
  'project.panels.reporters.comingSoon':
    'La vista de Reporter llega en la Fase 2 — onboarding por magic-link para personal de campo.',
  'project.capacity.label': 'Capacidad',
  'project.capacity.green': 'En curso',
  'project.capacity.yellow': 'Ajustado',
  'project.capacity.red': 'Insuficiente',
  'project.capacity.pendingShort': 'Pendiente',
  'project.capacity.pending':
    'Aún no tenemos tasa histórica de captura para este proyecto. Corre una revisión manual o espera a que Atledi aprenda tras el primer evento.',
  'project.capacity.summary.green':
    'Inventario {inventory} ≥ esperado {expected} × buffer 15%. Estás bien provisto.',
  'project.capacity.summary.yellow':
    'Inventario {inventory} entre el esperado {expected} y el buffer del 15%. Considera reforzar.',
  'project.capacity.summary.red':
    'Inventario {inventory} está por debajo del esperado {expected}. Refuerza o suma una BA.',

  // ── Caso 15 v2 — Banner de plan en la cabecera del shell ───────
  'plan.banner.viewPlans': 'Ver planes',
  'plan.banner.upgradeNow': 'Actualizar plan',
  'plan.banner.expiringSoon': 'Tu plan está por vencer.',
  'plan.banner.limitNear':
    'Te estás acercando a uno de los límites de tu plan.',
  'plan.banner.limitCritical':
    'Estás por quedarte sin cupo — actualiza ahora para que tu evento siga corriendo.',
  'plan.banner.expired':
    'Tu plan ha vencido — tu cuenta volvió a Free.',
  'plan.banner.day': 'día',
  'plan.banner.days': 'días',

  // ── Caso 13 — Integraciones CRM salientes ────────────────────────
  'integrations.title': 'Integraciones',
  'integrations.subtitle':
    'Envía cada contacto nuevo a tu CRM o cualquier webhook en tiempo real.',
  'integrations.newWebhook': 'Nuevo webhook',
  'integrations.empty':
    'Aún no hay integraciones. Agrega un webhook para sincronizar contactos.',
  'integrations.row.enabled': 'Activo',
  'integrations.row.disabled': 'Inactivo',

  'integrations.stats.title': 'Estado de sincronización',
  'integrations.stats.subtitle':
    'Conteos de la cola saliente para esta cuenta.',
  'integrations.state.PENDING': 'Pendientes',
  'integrations.state.SENT': 'Enviados',
  'integrations.state.FAILED': 'Fallidos',
  'integrations.state.SKIPPED': 'Omitidos',
  'integrations.dispatch.run': 'Despachar ahora',
  'integrations.dispatch.running': 'Despachando…',
  'integrations.dispatch.lastRun': 'Última corrida',

  'integrations.oauth.title': 'HubSpot · Mailchimp',
  'integrations.oauth.subtitle':
    'Los conectores OAuth se habilitarán cuando las apps de Onxite estén registradas con cada proveedor.',
  'integrations.oauth.comingSoon': 'Próximamente',

  'integrations.list.title': 'Integraciones configuradas',
  'integrations.list.subtitle':
    'Cada fila dispara un POST firmado al capturar un nuevo contacto.',

  'integrations.editor.newTitle': 'Nuevo webhook',
  'integrations.editor.editTitle': 'Editar webhook',
  'integrations.editor.subtitle':
    'Onxite firma cada payload con HMAC-SHA256. Verifica el header X-Onxite-Signature en tu endpoint.',
  'integrations.editor.name': 'Nombre',
  'integrations.editor.namePlaceholder': 'ej. Webhook del CRM de Ventas',
  'integrations.editor.url': 'URL del webhook',
  'integrations.editor.secret': 'Secreto compartido',
  'integrations.editor.secretPlaceholder':
    'Opcional — se usa para firmar los payloads',
  'integrations.editor.secretHelp':
    'Cuando está presente, cada request agrega X-Onxite-Signature: sha256=<hex>.',
  'integrations.editor.consentOnly':
    'Sólo enviar contactos con consentimiento de marketing',
  'integrations.editor.enabled': 'Activo',
  // Caso 13 v2 — editor multi-proveedor.
  'integrations.new.webhook': 'Nuevo webhook',
  'integrations.new.hubspot': 'Nuevo HubSpot',
  'integrations.new.mailchimp': 'Nuevo Mailchimp',
  'integrations.editor.webhookHelp':
    'Onxite firma cada payload con HMAC-SHA256. Verifica el header X-Onxite-Signature en tu endpoint.',
  'integrations.editor.hubspotHelp':
    'Crea una Private App en HubSpot con scope crm.objects.contacts.write y pega su access token abajo.',
  'integrations.editor.mailchimpHelp':
    'Pega una API key de Mailchimp y el Audience ID destino. Los contactos se sincronizan como miembros de la audiencia.',
  'integrations.editor.secretSaved': '•••••••• (guardado — deja vacío para conservar)',
  'integrations.editor.hubspotAccessToken': 'Access token de Private App',
  'integrations.editor.hubspotAccessTokenHelp':
    'HubSpot → Settings → Integrations → Private Apps. Se guarda cifrado.',
  'integrations.editor.mailchimpApiKey': 'API key',
  'integrations.editor.mailchimpApiKeyHelp':
    'Mailchimp → Account → Extras → API keys. El sufijo de datacenter (-us21) es obligatorio. Se guarda cifrado.',
  'integrations.editor.mailchimpAudienceId': 'Audience ID',
  'integrations.editor.mailchimpAudienceIdHelp':
    'Mailchimp → Audience → Settings → Audience name and defaults.',

  // ── Caso 08 — Constructor de encuestas embebible (página host) ────
  // Caso 08 — workspace: lista / importar / exportar / clonar / template.
  'sidebar.copyright.product': 'Un producto de',

  // ── Navegación inferior móvil ──────────────────────────────────────
  'mobileNav.label': 'Navegación principal',
  'mobileNav.more': 'Más',
  'mobileNav.close': 'Cerrar menú',

  // ── Centro de Ayuda (solo chrome — el contenido vive en helpContent.ts) ─
  'help.title': 'Centro de Ayuda',
  'help.subtitle': 'Explora qué hace cada pantalla, qué datos lleva y para qué sirve. Usa el buscador o salta a una categoría.',
  'help.searchPlaceholder': 'Buscar temas, palabras clave…',
  'help.whatItDoes': 'Qué hace',
  'help.whatData': 'Qué datos lleva',
  'help.whatFor': 'Para qué sirve',
  'help.related': 'Relacionado',
  'help.goToPage': 'Ir a la página',
  'help.noResults': 'Ningún tema coincide con tu búsqueda.',
  'help.resultsCount': '{count} resultados',
  'help.hintAria': 'Abrir ayuda de esta página',

  // ── Notificaciones ─────────────────────────────────────────────────
  'notifications.planAlert.title': 'Tu plan está casi lleno',
  'notifications.planAlert.body': 'Tu cuenta ha agotado casi uno de los límites de su plan. Amplíalo antes del próximo evento para que no te corte a mitad.',
  'notifications.banner.markRead': 'Marcar como leída',
  'notifications.banner.moreCount': 'más',
  'notifications.banner.systemSender': 'Sistema',
  'notifications.bell.title': 'Notificaciones',
  'notifications.bell.empty': 'Aún no tienes notificaciones.',
  'notifications.bell.allCaughtUp': 'Al día',
  'notifications.bell.unreadCount': '{{count}} sin leer',
  'notifications.compose.pageTitle': 'Enviar una notificación',
  'notifications.compose.pageSubtitle': 'Escribe un mensaje y elige a quién enviárselo. También recibirán un push.',
  'notifications.compose.messageTitle': 'Mensaje',
  'notifications.compose.messageDescription': 'El título se muestra en el push y como encabezado del banner.',
  'notifications.compose.titleLabel': 'Título',
  'notifications.compose.titlePlaceholder': 'Un título corto…',
  'notifications.compose.bodyLabel': 'Cuerpo',
  'notifications.compose.bodyPlaceholder': 'Añade detalle. Acepta emoji y saltos de línea 🎉',
  'notifications.compose.emailCopy': 'Enviar también por email',
  'notifications.compose.send': 'Enviar ({{count}})',
  'notifications.compose.recipientsTitle': 'Destinatarios',
  'notifications.compose.recipientsDescription': 'Personas dentro de tu árbol de compañía.',
  'notifications.compose.searchPlaceholder': 'Buscar por nombre o email…',
  'notifications.compose.available': 'disponibles',
  'notifications.compose.noCandidates': 'No hay personas disponibles para notificar.',
  'notifications.compose.titleRequired': 'El título es obligatorio',
  'notifications.compose.bodyRequired': 'El cuerpo es obligatorio',
  'notifications.compose.recipientsRequired': 'Selecciona al menos un destinatario',
  'notifications.toast.sent': 'Notificación enviada a {{count}} persona(s)',
  'notifications.toast.sendFailed': 'No se pudo enviar la notificación',
  'notifications.push.disabledTitle': 'Notificaciones push desactivadas',
  'notifications.push.insecureContextBody':
    'Este URL de desarrollo no es HTTPS ni localhost, así que el navegador bloquea el service worker que recibe los push. Abre la app por HTTPS o vía http://localhost para activarlo.',
  'notifications.push.permissionDeniedBody':
    'Bloqueaste las notificaciones para este sitio. Reactívalas desde la configuración del navegador (candado → Notificaciones) y recarga.',

  // ── Importación masiva de stock ────────────────────────────────────
  'stock.list.importButton': 'Importar',
  'stock.import.title': 'Importar merch en bloque',
  'stock.import.subtitle': 'Sube un ZIP con stock.xlsx y una carpeta images/.',
  'stock.import.dropzone': 'Suelta un archivo ZIP aquí o haz click para seleccionar',
  'stock.import.dropzoneHint': 'Acepta .zip (máx 50 MB)',
  'stock.import.downloadTemplate': 'Descargar plantilla',
  'stock.import.parsing': 'Leyendo ZIP…',
  'stock.import.parseFailed': 'No se pudo leer este ZIP',
  'stock.import.preview': 'Vista previa',
  'stock.import.previewCount': '{count} filas · {errors} errores',
  'stock.import.col.image': 'Imagen',
  'stock.import.col.name': 'Nombre',
  'stock.import.col.classification': 'Clase',
  'stock.import.col.rating': 'Rating',
  'stock.import.col.qty': 'Cantidad',
  'stock.import.col.status': 'Estado',
  'stock.import.status.new': 'Nuevo',
  'stock.import.status.duplicate': 'Ya existe',
  'stock.import.status.error': 'Error',
  'stock.import.action.replace': 'Reemplazar existente',
  'stock.import.action.createNew': 'Crear como nuevo',
  'stock.import.execute': 'Importar {count} items',
  'stock.import.cancel': 'Cancelar',
  'stock.import.progress.upload': 'Subiendo imágenes',
  'stock.import.progress.create': 'Creando merch',
  'stock.import.progress.replace': 'Actualizando existentes',
  'stock.import.summarySuccess': '{created} creados · {updated} actualizados',
  'stock.import.summaryMixed': '{created} creados · {updated} actualizados · {failed} fallidos',
  'stock.import.summaryAllFailed': 'La importación falló para las {count} filas',
  'stock.import.done': 'Listo',
  'stock.import.error.noXlsx': 'No se encontró archivo .xlsx en el ZIP',
  'stock.import.error.multipleXlsx': 'Más de un archivo .xlsx — deja solo stock.xlsx',
  'stock.import.error.missingNameCol': 'Falta la columna requerida: Name',
  'stock.import.error.emptySheet': 'El xlsx no tiene filas de datos',
  'stock.import.error.nameRequired': 'El nombre es obligatorio',
  'stock.import.error.nameTooShort': 'El nombre debe tener al menos 2 caracteres',
  'stock.import.error.ratingRange': 'El rating debe estar entre 1 y 5',
  'stock.import.error.classificationInvalid': 'La clasificación debe ser SEE, ENGAGE, TRY o ACT',
  'stock.import.error.imageNotFound': 'Imagen "{file}" no encontrada en images/',
  'stock.import.error.logoNotFound': 'Logo "{file}" no encontrado en images/',
  'stock.import.error.qtyNotInt': 'La cantidad inicial debe ser un entero',
  'stock.import.error.zipTooLarge': 'El ZIP supera los 50 MB',
  'stock.import.error.notAZip': 'El archivo debe ser un .zip',

  // ── Favoritos ──────────────────────────────────────────────────────
  'favorites.added': 'Añadido a favoritos',
  'favorites.removed': 'Quitado de favoritos',
  'favorites.addTitle': 'Añadir a favoritos',
  'favorites.removeTitle': 'Quitar de favoritos',

  // ── Auth · Login ───────────────────────────────────────────────────
  'auth.login.welcomeBack': 'Bienvenido de nuevo',
  'auth.login.subtitle': 'Entra para gestionar tus campañas. ¿Nuevo por aquí? Crea tu cuenta abajo.',
  'auth.login.noAccount': 'Todavía no hay ninguna cuenta con este correo.',
  'auth.login.createWithEmail': 'Crear tu cuenta con este correo →',
  'auth.login.modeOtp': 'Código por correo',
  'auth.login.modePassword': 'Contraseña',
  'auth.login.emailLabel': 'Correo electrónico',
  'auth.login.email': 'Correo',
  'auth.login.password': 'Contraseña',
  'auth.login.continueWithEmail': 'Continuar con correo',
  'auth.login.signIn': 'Iniciar sesión',
  'auth.login.forgotPassword': '¿Olvidaste tu contraseña?',
  'auth.forgot.title': 'Recupera tu contraseña',
  'auth.forgot.subtitle': 'Ingresa tu correo y te enviaremos un código de 6 dígitos a tu correo de recuperación.',
  'auth.forgot.submit': 'Enviar código',
  'auth.forgot.sentTitle': 'Código enviado',
  'auth.forgot.sentBody': 'Revisa tu bandeja para el código de 6 dígitos.',
  'auth.forgot.failed': 'No se pudo enviar el código',
  'auth.forgot.backToLogin': 'Volver a iniciar sesión',
  'auth.reset.title': 'Define una nueva contraseña',
  'auth.reset.subtitleFor': 'Ingresa el código de 6 dígitos enviado a {email} y tu nueva contraseña.',
  'auth.reset.subtitleNoEmail': 'Falta el correo. Solicita un nuevo código.',
  'auth.reset.requestNew': 'Solicitar un nuevo código',
  'auth.reset.otpLabel': 'Código de recuperación',
  'auth.reset.newPasswordLabel': 'Nueva contraseña',
  'auth.reset.confirmPasswordLabel': 'Confirmar nueva contraseña',
  'auth.reset.submit': 'Actualizar contraseña',
  'auth.reset.successTitle': 'Contraseña actualizada',
  'auth.reset.successBody': 'Ya puedes iniciar sesión con tu nueva contraseña.',
  'auth.reset.failed': 'No se pudo restablecer la contraseña',
  'auth.reset.errOtpLength': 'El código debe tener 6 dígitos.',
  'auth.reset.errPasswordShort': 'La contraseña debe tener al menos 8 caracteres.',
  'auth.reset.errMismatch': 'Las contraseñas no coinciden.',
  'auth.login.demoTipLabel': 'Tip demo:',
  'auth.login.demoTipBody': 'cualquier correo sirve. El OTP siempre es',
  'auth.login.curious': '¿Solo curioso?',
  'auth.login.seePlans': 'Ver planes',
  'auth.login.newHere': '¿Nuevo por aquí?',
  'auth.login.pendingPlan': 'Entra y pasas directamente al pago de {plan}.',
  'auth.login.createAccount': 'Crea tu cuenta',
  'auth.login.heroTitle': 'Inventario y engagement, juntos',
  'auth.login.heroBody': 'Gestiona cada evento, estación y giro de premio en tiempo real. Journeys con tu marca, métricas al instante y juegos que tus invitados recordarán.',
  'auth.toast.devHint': 'Pista dev',
  'auth.toast.otpFailed': 'No se pudo enviar el OTP',
  'auth.toast.invalidCredentials': 'Credenciales inválidas',
  'auth.toast.alreadySignedIn': 'Cuenta en uso',
  'auth.toast.alreadySignedInBody':
    'Ya tienes sesión iniciada en otro dispositivo o navegador. Notificamos a esa sesión de este intento.',
  'auth.footer.copyrightPrefix': '© {year} Xotski by ',
  'auth.footer.copyrightSuffix': '. Todos los derechos reservados.',

  // ── Auth · OTP ─────────────────────────────────────────────────────
  'otp.checkInbox': 'Revisa tu bandeja',
  'otp.subtitlePrefix': 'Enviamos un código de 6 dígitos a',
  'otp.verifyCode': 'Verificar código',
  'otp.didntReceive': '¿No lo recibiste?',
  'otp.resendIn': 'Reenviar en {seconds}s',
  'otp.resendNow': 'Reenviar ahora',
  'otp.toast.welcome': '¡Bienvenido!',
  'otp.toast.signedIn': 'Sesión iniciada',
  'otp.toast.invalidExpired': 'Código inválido o expirado',
  'otp.toast.newCodeSent': 'Nuevo código enviado',
  'otp.toast.newCodeBody': 'Sigue siendo 123456.',

  // ── Auth · Register (wizard 3 pasos) ───────────────────────────────
  // ── Registro v2 (alta rediseñada, sept 2026) ──
  'register.step.create': 'Crear cuenta',
  'register.step.org': 'Tu organización',
  'register.step.ready': 'A punto',
  'register.acct.title': 'Crea tu cuenta',
  'register.acct.subtitle': 'Empieza a montar activaciones en minutos.',
  'register.acct.fullName': 'Nombre completo',
  'register.acct.fullNamePlaceholder': 'p. ej. Marco Reyes',
  'register.acct.email': 'Correo de trabajo',
  'register.acct.password': 'Contraseña',
  'register.acct.passwordPlaceholder': 'Mínimo 8 caracteres',
  'register.acct.consentPre': 'Acepto los ',
  'register.acct.consentTerms': 'Términos',
  'register.acct.consentMid': ' y la ',
  'register.acct.consentPrivacy': 'Política de privacidad',
  'register.acct.consentPost':
    ', y consiento que Xotski procese datos de activación en nombre de mi organización.',
  'register.acct.create': 'Crear cuenta',
  'register.org.kicker': 'Paso 2 de 3 — tu organización',
  'register.org.title': 'Configura tu organización',
  'register.org.body':
    'La organización que creas es la dueña de la cuenta: los miembros entran bajo ella. Las marcas cuelgan de ella y son dueñas del almacén, los proyectos, y los eventos y estaciones dentro de ellos.',
  'register.org.kindLabel': 'Estoy creando esto como…',
  'register.org.kind.brand': 'Marca',
  'register.org.kind.brandHint': 'Activaciones de nuestros propios productos',
  'register.org.kind.agency': 'Agencia',
  'register.org.kind.agencyHint': 'Activaciones para marcas cliente',
  'register.org.kind.other': 'Otro',
  'register.org.kind.otherHint': 'Recinto, distribuidor o socio',
  'register.org.orgName.brand': 'Empresa matriz',
  'register.org.orgName.agency': 'Nombre de la agencia',
  'register.org.orgName.other': 'Nombre de la organización',
  'register.org.orgPlaceholder.brand': 'p. ej. Joyly Beverages Inc.',
  'register.org.orgPlaceholder.agency': 'p. ej. Matraka',
  'register.org.orgPlaceholder.other': 'p. ej. Lone Star Venues',
  'register.org.industry.brand': 'Industria',
  'register.org.industry.agency': 'Industria principal a la que sirves',
  'register.org.industry.other': 'Industria',
  'register.org.industryPlaceholder': 'Elige una industria…',
  'register.org.companySize': 'Tamaño de la empresa',
  'register.org.companySizePlaceholder': 'Elige…',
  'register.org.region': 'Región principal',
  'register.org.regionPlaceholder': 'p. ej. Los Ángeles, CA',
  'register.org.brand.brand': 'Marca(s) de esta cuenta',
  'register.org.brand.agency': 'Marcas que representas',
  'register.org.brand.other': 'Marca(s) de esta cuenta',
  'register.org.brandPlaceholder.brand': 'Añade una marca…',
  'register.org.brandPlaceholder.agency': 'Añade una marca cliente…',
  'register.org.brandPlaceholder.other': 'Añade una marca…',
  'register.org.brandHint.brand':
    'Añade la marca para la que abres esta cuenta. Podrás añadir más después.',
  'register.org.brandHint.agency':
    'Añade la marca cliente para la que montas activaciones. Podrás añadir más después.',
  'register.org.brandHint.other':
    'Añade la marca para la que abres esta cuenta. Podrás añadir más después.',
  'register.org.continue': 'Continuar',
  'register.industry.bevNonAlc': 'Bebidas (sin alcohol)',
  'register.industry.beerWineSpirits': 'Cerveza, vino y destilados',
  'register.industry.foodCpg': 'Alimentación y gran consumo',
  'register.industry.snacks': 'Snacks y confitería',
  'register.industry.beauty': 'Belleza y cuidado personal',
  'register.industry.health': 'Salud y bienestar',
  'register.industry.tech': 'Tecnología y electrónica',
  'register.industry.automotive': 'Automoción',
  'register.industry.retail': 'Retail y moda',
  'register.industry.hospitality': 'Hostelería y restauración rápida',
  'register.industry.cannabis': 'Cannabis y CBD',
  'register.industry.other': 'Otro',
  'validation.fullNameMin': 'Escribe tu nombre completo',
  'validation.consentRequired': 'Debes aceptar los términos para continuar',
  'validation.orgNameMin': 'El nombre debe tener al menos 2 caracteres',
  'validation.brandNameMin': 'El nombre de la marca debe tener al menos 2 caracteres',
  'validation.industrySelect': 'Elige una industria',
  'validation.companySizeSelect': 'Elige un tamaño de empresa',
  'validation.emailInvalid': 'Escribe un correo electrónico válido',
  'validation.passwordMin6': 'La contraseña debe tener al menos 6 caracteres',
  'validation.passwordMin8': 'La contraseña debe tener al menos 8 caracteres',
  // Avisos de los atributos HTML (required, type, min/max…) que antes daba el
  // navegador en el idioma del ordenador. Los usa `nativeValidation.ts`.
  'validation.required': 'Este campo es obligatorio',
  'validation.urlInvalid': 'Escribe una dirección web válida',
  'validation.invalid': 'Revisa este valor',
  'validation.number': 'Escribe un número',
  'validation.pattern': 'Usa el formato que se pide',
  'validation.maxLength': 'Usa como máximo {n} caracteres',
  'validation.minLength': 'Usa al menos {n} caracteres',
  'validation.min': 'Escribe {min} o más',
  'validation.max': 'Escribe {max} o menos',
  'validation.wholeNumber': 'Escribe un número entero',
  'validation.pickOne': 'Elige una opción',
  'register.step.company': 'Marca',
  'register.step.account': 'Cuenta',
  'register.step.verify': 'Verificar',
  'register.orWithSocial': 'Regístrate con',
  'register.orWithSocialHint': 'Al registrarte con una red social no pediremos código por correo.',
  'register.orWithEmail': 'O con correo',
  'register.company.title': 'Crea tu cuenta',
  'register.company.subtitle': 'Cuéntanos el nombre de tu empresa — te activamos un plan Free para empezar.',
  'register.company.companyName': 'Nombre de la empresa',
  'register.company.companyNamePlaceholder': 'Acme S.A.',
  'register.company.continue': 'Continuar',
  'register.account.title': 'Tu cuenta',
  'register.account.subtitlePrefix': 'Serás el propietario de',
  'register.account.firstname': 'Nombre',
  'register.account.lastname': 'Apellido',
  'register.account.email': 'Correo de trabajo',
  'register.account.password': 'Contraseña',
  'register.account.passwordHint': 'Mínimo 8 caracteres.',
  'register.account.confirmPassword': 'Confirma la contraseña',
  'register.account.continue': 'Enviar código de verificación',
  'register.verify.title': 'Verifica tu correo',
  'register.verify.subtitlePrefix': 'Enviamos un código de 6 dígitos a',
  'register.verify.verify': 'Crear mi cuenta',
  'register.verify.didntReceive': '¿No lo recibiste?',
  'register.verify.resendIn': 'Reenviar en {seconds}s',
  'register.verify.resendNow': 'Reenviar ahora',
  'register.haveAccount': '¿Ya tienes cuenta?',
  'register.pendingPlan': 'En cuanto verifiques tu correo pasas directamente al pago de {plan}.',
  'register.signIn': 'Inicia sesión',
  'register.hero.title': 'Listo en minutos',
  'register.hero.body': 'Plan Free: 1 marca, 2 journeys, 2 estaciones, 5 ítems de merch. Actualiza cuando quieras.',
  'register.toast.devHint': 'Pista dev',
  'register.toast.welcome': '¡Bienvenido, {name}!',
  'register.toast.welcomeBody': 'Tu cuenta está lista.',
  'register.toast.initiateFailed': 'No pudimos iniciar el registro',
  'register.toast.emailTaken': 'Ese correo ya está registrado',
  'register.toast.emailTakenBody': 'Mejor inicia sesión.',
  'register.toast.invalidOtp': 'Código inválido o expirado',

  // ── Auth · Pending access ──────────────────────────────────────────
  'pendingAccess.title': 'Esperando acceso',
  'pendingAccess.body': 'Tu cuenta ya está creada, pero un admin debe asignarte un rol. Mantén esta página abierta — vuelve a entrar cuando lo haga.',
  'pendingAccess.signedInAs': 'Iniciaste sesión como',

  // ── Onboarding shell ───────────────────────────────────────────────
  'onboarding.step.account': 'Cuenta',
  'onboarding.step.brandProfile': 'Perfil de marca',
  'onboarding.step.brandKit': 'Kit de marca',

  // ── Edit profile dialog ────────────────────────────────────────────
  'editProfile.eyebrow': 'Editar perfil',
  'editProfile.title': 'Actualiza tus datos',
  'editProfile.subtitle': 'Visible en toda tu cuenta. El correo y el rol no se pueden cambiar aquí.',
  'editProfile.section.visuals': 'Visuales',
  'editProfile.section.identity': 'Identidad',
  'editProfile.section.professional': 'Profesional',
  'editProfile.section.tags': 'Etiquetas',
  'editProfile.field.avatar': 'Avatar',
  'editProfile.field.cover': 'Imagen de portada',
  'editProfile.field.firstName': 'Nombre',
  'editProfile.field.lastName': 'Apellido',
  'editProfile.field.username': 'Usuario (@handle)',
  'editProfile.field.phone': 'Teléfono',
  'editProfile.field.bio': 'Biografía',
  'editProfile.field.bioPlaceholder': 'Una frase corta sobre lo que construyes.',
  'editProfile.field.title': 'Cargo',
  'editProfile.field.titlePlaceholder': 'Gestor de marca',
  'editProfile.field.location': 'Ubicación',
  'editProfile.field.locationPlaceholder': 'CDMX, México',
  'editProfile.field.studiedAt': 'Estudiaste en',
  'editProfile.tag.placeholder': 'Escribe y presiona Enter…',
  'editProfile.tag.remove': 'Quitar {tag}',
  'editProfile.save': 'Guardar cambios',

  // ── Change password dialog ─────────────────────────────────────────
  'changePassword.eyebrow': 'Seguridad',
  'changePassword.title': 'Cambiar contraseña',
  'changePassword.subtitle': 'Mínimo 8 caracteres. Tu sesión seguirá activa.',
  'changePassword.field.current': 'Contraseña actual',
  'changePassword.field.new': 'Nueva contraseña',
  'changePassword.field.confirm': 'Confirmar nueva contraseña',
  'changePassword.show': 'Mostrar contraseña',
  'changePassword.hide': 'Ocultar contraseña',
  'changePassword.hint.lengthOk': 'Largo correcto.',
  'changePassword.hint.lengthShort': '{count}/8 caracteres — faltan {missing}.',
  'changePassword.hint.match': 'Las contraseñas coinciden.',
  'changePassword.hint.noMatch': 'Las contraseñas aún no coinciden.',
  'changePassword.error.length': 'La nueva contraseña debe tener al menos 8 caracteres.',
  'changePassword.error.mismatch': 'La nueva contraseña y la confirmación no coinciden.',
  'changePassword.submit': 'Actualizar contraseña',
  'changePassword.toast.updated': 'Contraseña actualizada',

  // ── Pricing page ───────────────────────────────────────────────────
  'pricing.back': 'Volver',
  'pricing.heroTitle': 'Planes para cada marca',
  'pricing.heroBody': 'Mismo SKU, dos lecturas: marcas o merch. Elige el plan que se ajusta a tu hoja de ruta.',
  'pricing.checkoutFailed': 'Falló el checkout',
  'billingPage.alacarte.seatPlanner': '+1 asiento Planner',
  'billingPage.alacarte.seatPlannerHint': 'El banco de trabajo Plan/Test/Track. Una persona, un asiento.',
  'billingPage.alacarte.seatEventManager': '+1 asiento Event Manager',
  'billingPage.alacarte.seatEventManagerHint': 'Gestión de inventario y operación de eventos.',
  'billingPage.alacarte.seatEditor': '+1 asiento de editor',
  'billingPage.alacarte.seatEditorHint': 'Dos incluidos en XK1, tres en XK2, diez en Enterprise. Una persona, un asiento.',
  'billingPage.alacarte.coterm': 'Todo lo anual renueva junto el {date} — lo añadido a mitad de término se prorratea hasta esa fecha.',
  'billingPage.alacarte.title': 'À la carte',
  'billingPage.alacarte.desc': 'Crece este año sin cambiar de plan.',
  'billingPage.alacarte.event': '+1 evento',
  'billingPage.alacarte.eventHint': 'Suma un evento a tu cupo anual. Vale por un año.',
  'billingPage.alacarte.unlimited': 'Eventos ilimitados',
  'billingPage.alacarte.unlimitedHint': 'Tus eventos dejan de contar por el resto del término.',
  'billingPage.alacarte.unlimitedNeedsXk2': 'Add-on de XK2 — sube primero y desbloquéalo.',
  'billingPage.alacarte.buyFor': 'Añadir — {price}',
  'billingPage.alacarte.goXk2': 'Ver XK2',
  'billingPage.addonFailed': 'No se pudo iniciar la compra',
  'pricing.startFree': 'Empieza gratis',
  'pricing.alreadyFree': 'Ya tienes el Freemium — toda cuenta empieza ahí.',
  'pricing.startingAt': 'Desde {price}',
  'pricing.talkToUs': 'Hablemos',
  'pricing.talkToUsBody': 'Nos pondremos en contacto para diseñar un plan a tu medida.',
  'pricing.planUnavailable': 'Plan no disponible',
  'pricing.missingTier': 'Falta el plan "{slug}"',

  // ── Price card ─────────────────────────────────────────────────────
  'priceCard.tier.pilot': 'Pilot',
  'priceCard.tier.scale': 'Scale ★',
  'priceCard.tier.program': 'Program',
  'priceCard.tier.enterprise': 'Enterprise',
  'priceCard.period.year': '/año',
  'priceCard.period.month': '/mes',
  'priceCard.mostPopular': '★ Más popular',
  'priceCard.planAria': 'Plan {tier}',
  'priceCard.selected': 'Seleccionado',
  'priceCard.talkToUs': 'Hablemos',
  'priceCard.select': 'Elegir {tier}',
  'priceCard.line.inBrands': 'En marcas',
  'priceCard.line.inCapacity': 'En capacidad',
  'priceCard.line.inEvents': 'En eventos',
  'priceCard.line.perUnit': 'Por unidad',
  'priceCard.brandsOne': '{count} marca',
  'priceCard.brandsMany': '{count} marcas',
  'priceCard.xotskiesValue': '{count} ítems de merch',
  'priceCard.editorSeatOne': '{count} asiento de editor (transferible)',
  'priceCard.editorSeatMany': '{count} asientos de editor (transferibles)',
  'priceCard.editorSeatsUnlimited': '{count} asientos de editor (transferibles)',
  'priceCard.unlimitedReporters': 'Reporteros ilimitados (gratis)',
  'priceCard.tier1Games': 'Juegos branded Tier 1',
  'priceCard.tier2Games': 'Biblioteca de juegos Tier 2 incluida',
  'priceCard.realtime': 'Dashboard en tiempo real + Inventario inteligente',
  'priceCard.enterpriseExtras': 'SSO + Bitácora + API + CSM dedicado',
  'priceCard.feature.notifications': 'Notificaciones push',
  'priceCard.feature.activityLog': 'Bitácora de actividad',
  'priceCard.feature.stockImport': 'Importar merch por CSV',
  'priceCard.feature.aiAssistant': 'Asistente IA',
  'priceCard.feature.userManagement': 'Gestión de usuarios',

  // ── Upgrade dialog ─────────────────────────────────────────────────
  'upgradeDialog.title': 'Alcanzaste tu límite de {resource}',
  'upgradeDialog.body': 'Tu plan {plan} incluye {limit} {resource}. Mejora tu plan para seguir creando.',
  'upgradeDialog.maybeLater': 'Quizás después',
  'upgradeDialog.seePlans': 'Ver planes',
  'upgradeDialog.resource.projects': 'proyectos',
  'upgradeDialog.resource.groups': 'grupos de eventos',
  'upgradeDialog.resource.events': 'eventos',
  'upgradeDialog.resource.stations': 'estaciones',
  'upgradeDialog.resource.stocks': 'merch',
  'upgradeDialog.resource.brands': 'marcas',
  'upgradeDialog.resource.users': 'usuarios',
  'upgradeDialog.resource.roles': 'roles',
  'upgradeDialog.resource.surveys': 'journeys',

  // ── Billing page ───────────────────────────────────────────────────
  'billingPage.title': 'Facturación',
  'billingPage.subtitle': 'Monitorea uso, administra tu plan y descarga facturas.',
  'billingPage.currentBadge': 'Plan actual',
  'billingPage.currentTitle': 'Plan actual',
  'billingPage.currentDesc': 'El endpoint de detalles del plan aún no está expuesto en el backend.',
  'billingPage.changePlan': 'Cambiar plan',
  'billingPage.currentRenews': 'Renueva el {date}',
  'billingPage.currentFree': 'Plan gratuito · sin fecha de renovación. Sube de plan cuando necesites más eventos.',
  'billingPage.currentAnnual': 'Plan anual',
  'billingPage.currentPerYear': '{price} al año',
  'billingSuccess.confirming': 'Confirmando tu pago…',
  'billingSuccess.readyTitle': 'Pago recibido',
  'billingSuccess.readyBody': 'Tu nuevo plan ya está activo. Te llevamos a Facturación…',
  'billingSuccess.timeoutTitle': 'Todavía en proceso',
  'billingSuccess.timeoutBody': 'Stripe está tardando más de lo normal en confirmar. Tu factura aparecerá en Facturación en breve.',
  'billingSuccess.goToBilling': 'Ir a Facturación',
  'billingPage.usageTitle': 'Uso',
  'billingPage.usageDesc': 'Cuánto has consumido de tu plan.',
  'billingPage.invoicesTitle': 'Facturas',
  'billingPage.invoicesDesc': 'Historial de compras de tu plan.',
  'billingPage.noInvoices': 'Aún no hay facturas.',
  'billingPage.col.date': 'Fecha',
  'billingPage.col.plan': 'Plan',
  'billingPage.col.amount': 'Monto',
  'billingPage.col.status': 'Estado',
  'billingPage.invoice.paid': 'Pagada',
  'billingPage.invoice.pending': 'Pendiente',
  'billingPage.invoice.status.active': 'Activa',
  'billingPage.invoice.status.expired': 'Expirada',
  'billingPage.invoice.status.refunded': 'Reembolsada',
  'billingPage.invoice.status.exhausted': 'Agotada',
  'billingPage.resource.brands': 'Marcas',
  'billingPage.resource.users': 'Usuarios',
  'billingPage.resource.roles': 'Roles',
  'billingPage.resource.surveys': 'Journeys',
  'billingPage.resource.responsesPerSurvey': 'Interactions / journey',
  'billingPage.resource.projects': 'Proyectos',
  'billingPage.resource.eventGroups': 'Grupos de eventos',
  'billingPage.resource.events': 'Eventos',
  'billingPage.resource.stations': 'Estaciones',
  'billingPage.resource.stocks': 'Xotskies',
  'billingPage.featuresTitle': 'Características del plan',
  'billingPage.featuresDesc': 'Lo que está activo en tu cuenta ahora mismo.',
  'billingPage.feature.notifications': 'Notificaciones push',
  'billingPage.feature.activeLog': 'Bitácora de actividad',
  'billingPage.feature.aiAssistant': 'Asistente IA',
  'billingPage.feature.stockImport': 'Importar merch por CSV',
  'billingPage.feature.userManagement': 'Gestión de usuarios',
  'billingPage.gamesTitle': 'Juegos habilitados',
  'billingPage.gamesDesc': 'Mecánicas de juego incluidas en tu plan.',
  'billingPage.gamesEmpty': 'Este plan no incluye mecánicas de juego.',

  // ── Checkout result ────────────────────────────────────────────────
  'checkoutResult.successTitle': '¡Listo!',
  'checkoutResult.successBody': 'Tu plan ya está activo y las cuotas se actualizaron.',
  'checkoutResult.seeBilling': 'Ver facturación',
  'checkoutResult.backToDashboard': 'Volver al panel',
  'checkoutResult.cancelTitle': 'Checkout cancelado',
  'checkoutResult.cancelBody': 'No se realizó ningún cargo. Puedes elegir otro plan cuando quieras.',
  'checkoutResult.seePlans': 'Ver planes',

  // ── Mock checkout page ─────────────────────────────────────────────
  'mockCheckout.paymentConfirmed': 'Pago confirmado',
  'mockCheckout.confirmationFailed': 'Falló la confirmación',
  'mockCheckout.invalidSession': 'Sesión de checkout inválida',
  'mockCheckout.backToPlans': 'Volver a planes',
  'mockCheckout.badge': 'Checkout demo',
  'mockCheckout.planTitle': 'Plan {plan}',
  'mockCheckout.planDesc': 'Se cobrará hoy y el mismo día cada mes. Cancela cuando quieras.',
  'mockCheckout.subtotal': 'Subtotal',
  'mockCheckout.tax': 'Impuestos',
  'mockCheckout.taxIncluded': 'Incluidos',
  'mockCheckout.totalDue': 'Total a pagar hoy',
  'mockCheckout.pay': 'Pagar {amount}',
  'mockCheckout.cancelGoBack': 'Cancelar y volver',
  'mockCheckout.mockEnv': 'Entorno demo — no se mueve dinero real.',
  'mockCheckout.whatsIn': '¿Qué incluye {plan}?',

  // ── Settings page (v2 list-style page) ─────────────────────────────
  'settings.comingSoon': 'Próximamente',
  'settings.section.accountDesc': 'Datos de la empresa, zona horaria, idioma',
  'settings.section.access': 'Gestión de accesos',
  'settings.section.brand': 'Marca',
  'tags.settingsSection': 'Etiquetas',
  'tags.settingsSectionDesc': 'Ordena el vocabulario de etiquetas: renombrar, recolorear y retirar.',
  'settings.section.brandDesc': 'Perfiles de marca (identidad), opt-in de Inventario de marca y página pública',
  'settings.section.workspaceType': 'Tipo de cuenta',
  'settings.section.workspaceDesc': 'Marca o agencia, y qué tiene cada una',
  'settings.workspace.title': 'Tipo de cuenta',
  'settings.workspace.subtitle': 'Qué tipo de cuenta es. Se elige al darse de alta y lo leen todos los productos de Onxite.',
  'settings.workspace.accountType': 'Tipo de cuenta',
  'settings.workspace.currentFor': 'Para {name}.',
  'settings.workspace.current': 'Actual',
  'settings.workspace.brand': 'Marca',
  'settings.workspace.brandBody': 'Hace activaciones de sus propios productos. Abre directamente en su marca, sin selector de por medio.',
  'settings.workspace.agency': 'Agencia',
  'settings.workspace.agencyBody': 'Hace activaciones para marcas cliente. Lleva varias marcas y tiene el selector de marca en la barra superior.',
  'settings.workspace.notRoot': 'Sólo la cuenta principal puede cambiar su tipo de cuenta.',
  'settings.workspace.keepsData': 'Cambiar el tipo conserva todos los proyectos, eventos, marcas y contactos. Una agencia con más de una marca tiene que quedarse con una sola antes de pasar a cuenta de marca.',
  'settings.workspace.confirmTitle': '¿Pasar esta cuenta a {type}?',
  'settings.workspace.confirmToAgency': 'La cuenta podrá llevar varias marcas y el selector de marca aparece en la barra superior. No se borra nada.',
  'settings.workspace.confirmToBrand': 'La cuenta abre directamente en su única marca y el selector desaparece. No se borra nada.',
  'settings.workspace.confirm': 'Cambiar',
  'settings.workspace.savedToast': 'Tipo de cuenta actualizado',
  'settings.workspace.errorToast': 'No se pudo cambiar el tipo de cuenta',
  'settings.brand.title': 'Ajustes de marca',
  'settings.brand.subtitle': 'Perfiles y ajustes por marca. Afecta la experiencia de la marca en Xotski.',
  'settings.brand.profilesTitle': 'Perfiles de marca',
  'settings.brand.profilesDescription': 'Cada marca de tu cuenta con su identidad: logotipo, paleta y tipografía. Edita cualquiera en Brand Studio; las encuestas, los juegos y los carteles en vivo se pintan con ella.',
  'settings.brand.profileEdit': 'Editar identidad',
  'settings.brand.profileOpen': 'Abrir marca',
  'settings.brand.profileNoKit': 'Aún sin identidad: configúrala en Brand Studio.',
  'settings.brand.profileRoot': 'Cuenta',
  'settings.brand.profileFont': 'Tipografía',
  'settings.brand.profileNoIndustry': 'Sin industria',
  'settings.brand.swatchesLabel': 'Paleta de {brand}',
  'settings.brand.swatchPrimary': 'Principal',
  'settings.brand.swatchSecondary': 'Secundario',
  'settings.brand.swatchAccent': 'Acento',
  'settings.brand.swatchBackground': 'Fondo',
  'settings.brand.swatchText': 'Texto',
  'settings.brand.inventoryTitle': 'Inventario de marca',
  'settings.brand.inventoryDescription':
    'Modelo de inventario en dos niveles (PRD §9): los artículos viven en un pool a nivel de marca y se asignan a almacenes de proyecto. Está apagado hasta que lo enciendas por marca.',
  'settings.brand.lockedBadge': 'Plan bloqueado',
  'settings.brand.noBrands': 'Aún no hay marcas visibles para tu cuenta.',
  'settings.brand.savedToast': 'Guardado',
  'settings.brand.publicPageTitle': 'Página pública de la marca',
  'settings.brand.publicPageDescription': 'Una URL estable (/b/slug) que ya puedes imprimir en los QR. Queda cerrada al equipo de la marca y a su agencia hasta que la abras; los demás ven «aún no disponible» y un camino a iniciar sesión. Se pinta con el kit de Brand Studio.',
  'settings.brand.publicPageNoSlug': 'Aún sin página pública: dale un slug.',
  'settings.brand.publicPageSlug': 'Slug de la página pública de {brand}',
  'settings.brand.publicPageSlugPlaceholder': 'mi-marca',
  'settings.brand.publicPageOpen': 'Abrir la página pública de {brand}',
  'settings.brand.publicPageIsOpen': 'Abierta a todos',
  'settings.brand.publicPageIsGated': 'Sólo el equipo',
  'publicBrand.notFoundTitle': 'Esta página no existe',
  'publicBrand.notFoundBody': 'Revisa el enlace o el código QR que seguiste.',
  'publicBrand.gatedTitle': 'Aún no disponible',
  'publicBrand.gatedBody': 'La página de esta marca se está preparando. Si eres del equipo, inicia sesión para verla.',
  'publicBrand.gatedSignedIn': 'Tu cuenta no forma parte del equipo de esta marca.',
  'publicBrand.signIn': 'Iniciar sesión',
  'publicBrand.openBody': 'Escanea, juega y recoge tu premio en el stand.',
  'publicBrand.cta': 'Encuéntranos en el evento',
  'publicBrand.poweredBy': 'Con la tecnología de Xotski',
  'settings.brand.errorToast': 'No pudimos guardar el cambio',
  'settings.section.accessDesc': 'Invita miembros, asigna roles, comparte accesos',
  'settings.section.billing': 'Facturación',
  'settings.section.billingDesc': 'Plan, uso y facturas',
  'settings.section.followup': 'Correos de seguimiento',
  'settings.section.followupDesc': 'Plantillas, cupones y nutrición post-evento',
  'settings.section.integrations': 'Integraciones CRM',
  'settings.section.integrationsDesc': 'Conecta HubSpot, Mailchimp o un webhook',
  'settings.section.crm': 'Integraciones CRM',
  'settings.section.crmDesc': 'Conecta HubSpot, Salesforce o un webhook',
  'settings.section.api': 'API keys',
  'settings.section.apiDesc': 'Genera llaves para acceso programático',
  'settings.section.notificationsDesc': 'Alertas por correo de inventario y eventos',
  'settings.section.activeLog': 'Active Log',
  'settings.section.activeLogDesc': 'Historial de auditoría de cada cambio en tu cuenta',

  // ── Insights page (v2 distribution overview) ───────────────────────
  'insights.resetFilters': 'Limpiar filtros',
  'insights.filter.brand': 'Marca',
  'insights.filter.event': 'Evento',
  'insights.filter.project': 'Proyecto',
  // Deprecado: ver la nota en la versión en inglés.
  'insights.filter.classification': 'Clasificación',
  'insights.filter.from': 'Desde',
  'insights.filter.to': 'Hasta',
  'insights.filter.allBrands': 'Todas las marcas ({count})',
  'insights.filter.allEvents': 'Todos los eventos ({count})',
  'insights.filter.allProjects': 'Todos los proyectos ({count})',
  'insights.filter.all': 'Todas',
  'insights.filter.unclassified': 'Sin clasificar',
  'insights.kpi.distributed': 'Distribuidos',
  'insights.kpi.distributedHint': '{rate}% de lo asignado',
  'insights.kpi.remaining': 'Restante en estaciones',
  'insights.kpi.remainingHint': '{total} asignados en total',
  'insights.kpi.value': 'Valor distribuido',
  'insights.kpi.valueHint': 'Basado en costo unitario',
  'insights.kpi.activeEvents': 'Eventos activos',
  'insights.kpi.activeEventsHintOne': '{brands} marca · {events} evento en alcance',
  'insights.kpi.activeEventsHintMany': '{brands} marcas · {events} eventos en alcance',
  'insights.restockAlertOne': '{count} estación al o bajo umbral de reposición.',
  'insights.restockAlertMany': '{count} estaciones al o bajo umbral de reposición.',
  'insights.restockRefill': 'Reabastece antes del próximo evento.',
  'insights.chart.byBrand': 'Distribución por marca',
  'insights.chart.topEvents': 'Eventos top por unidades distribuidas',
  'insights.chart.topStocks': 'Top 10 ítems de merch (unidades distribuidas)',
  'insights.chart.byClassification': 'Unidades distribuidas por clasificación',
  'insights.chart.byRarity': 'Unidades distribuidas por rareza',
  'insights.chart.usedAvailable': 'Merch usado vs disponible (top 10)',
  'insights.chart.used': 'Usado',
  'insights.chart.available': 'Disponible',
  'insights.chart.empty': 'Sin datos para los filtros actuales',
  'insights.rarity.common': 'Común',
  'insights.rarity.uncommon': 'Poco común',
  'insights.rarity.rare': 'Raro',
  'insights.rarity.epic': 'Épico',
  'insights.rarity.legendary': 'Legendario',
  'insights.rarity.tier': 'Tier {n}',
  'insights.unclassifiedLabel': 'Sin clasificar',

  // ── Contacts page (v2 list) ────────────────────────────────────────
  'contacts.responsesCount': '{count} interactions',
  'contacts.col.when': 'Cuándo',
  'contacts.col.survey': 'Journey',
  'contacts.col.station': 'Estación',
  'contacts.col.source': 'Origen',
  'contacts.col.prize': 'Premio',
  'contacts.noun': 'interactions',
  // ── Caso 01 — Leads CRM (integración real /co-contacts) ────────────
  'contacts.export': 'Exportar CSV',
  'contacts.applySearch': 'Buscar',
  'contacts.filter.consentOnly': 'Solo con consentimiento',
  'contacts.totalCount': '{count} contactos',
  'contacts.col.name': 'Nombre',
  'contacts.col.contact': 'Contacto',
  'contacts.col.interactions': 'Visitas',
  'contacts.col.prizes': 'Premios',
  'contacts.col.lastSeen': 'Última visita',
  'contacts.col.consent': 'Consentimiento',
  'contacts.anonymousName': 'Participante anónimo',
  'contacts.anonymousSnippet': 'Sin identidad capturada',
  'contacts.consent.yes': 'Sí',
  'contacts.consent.no': 'No',
  'contacts.consent.sms': 'SMS',
  'contacts.detail.title': 'Detalle del contacto',
  'contacts.detail.back': 'Volver a contactos',
  'contacts.detail.invalidId': 'Identificador inválido.',
  'contacts.detail.delete': 'Eliminar (GDPR)',
  'contacts.detail.deleteConfirm':
    '¿Eliminar permanentemente los datos de {name}? Esto no se puede deshacer.',
  'contacts.detail.engagement.title': 'Engagement',
  'contacts.detail.engagement.interactions': 'Visitas',
  'contacts.detail.engagement.prizes': 'Premios ganados',
  'contacts.detail.engagement.firstSeen': 'Primera visita',
  'contacts.detail.engagement.lastSeen': 'Última visita',
  'contacts.detail.demographics.title': 'Demografía',
  'contacts.detail.demographics.age': 'Edad',
  'contacts.detail.demographics.gender': 'Género',
  'contacts.detail.demographics.city': 'Ciudad',
  'contacts.detail.demographics.state': 'Estado',
  'contacts.detail.demographics.zip': 'Código postal',
  'contacts.detail.demographics.language': 'Idioma',
  'contacts.detail.controls.title': 'Controles',
  'contacts.detail.controls.revokeConsent': 'Revocar consentimiento',
  'contacts.detail.controls.grantConsent': 'Conceder consentimiento',
  'contacts.detail.controls.tags': 'Etiquetas',
  'contacts.detail.controls.add': 'Añadir',
  'contacts.detail.controls.addTagPlaceholder': 'Añade una etiqueta…',
  'contacts.detail.controls.removeTag': 'Quitar etiqueta',
  'contacts.detail.timeline.title': 'Interacciones ({count})',
  'contacts.detail.timeline.empty': 'Aún no hay interacciones registradas.',
  'contacts.detail.timeline.atEvent': 'Evento #{id}',
  'contacts.detail.timeline.unknownEvent': 'Evento desconocido',
  'contacts.detail.timeline.station': 'Estación #{id}',
  'contacts.detail.timeline.prize': 'Premio #{id}',

  // ── Caso 02 — Reporte de resultados por evento ─────────────────────
  'eventReport.invalidId': 'Identificador de evento inválido.',
  'eventReport.backToEvent': 'Volver al evento',
  'eventReport.print': 'Imprimir / Guardar PDF',
  'eventReport.printedOn': 'Informe generado el {date}',
  'eventReport.createShare': 'Crear enlace público',
  'eventReport.shareCopied': 'Enlace copiado al portapapeles',
  'eventReport.publicFooter':
    'Resultados agregados — no se comparten datos individuales de participantes.',
  'eventReport.publicInvalid': 'Enlace inválido.',
  'eventReport.publicNotFound.title': 'Este reporte no está disponible',
  'eventReport.publicNotFound.body':
    'El enlace puede haber expirado o sido revocado. Pide uno nuevo al organizador.',
  'eventReport.reach.interactions': 'Interacciones',
  'eventReport.reach.last24h': 'Últimas 24 h',
  'eventReport.reach.distinctContacts': 'Contactos únicos',
  'eventReport.reach.leads': 'Leads capturados',
  'eventReport.prizes.title': 'Premios',
  'eventReport.prizes.allocated': 'Asignados',
  'eventReport.prizes.distributed': 'Entregados',
  'eventReport.prizes.remaining': 'Restantes',
  'eventReport.prizes.value': 'Valor entregado',
  'eventReport.costs.title': 'Costo por resultado',
  'eventReport.costs.perInteraction': 'Por interacción',
  'eventReport.costs.perLead': 'Por lead',
  'eventReport.topAnswers.title': 'Interactions top',
  'eventReport.demographics.title': 'Demografía',
  'surveyDemographics.title': 'A qui\u00e9n llegamos',
  'surveyDemographics.coverage': '{n} respuestas completas \u00b7 {links} despliegues',
  'surveyDemographics.noLinks': 'Todav\u00eda no hay encuesta desplegada en estas estaciones.',
  'surveyDemographics.empty': 'A\u00fan no hay respuestas completas. Las gr\u00e1ficas aparecen en cuanto alguien termina la journey.',
  'surveyDemographics.noChoice': 'Estas journeys no hacen preguntas de una sola respuesta, as\u00ed que no hay nada que graficar. A\u00f1ade edad, g\u00e9nero, origen o una pregunta de s\u00ed/no a un paso.',
  'surveyDemographics.persona': 'El visitante compuesto',
  'surveyDemographics.personaNote': 'La respuesta m\u00e1s frecuente a cada pregunta demogr\u00e1fica, una al lado de otra.',
  'surveyDemographics.per100': '{label} \u00b7 100 personas',
  'surveyDemographics.largest': 'Franja mayor: {label}, el {pct}% de las respuestas.',
  'surveyDemographics.lean': 'M\u00e1s {label}',
  'surveyDemographics.even': 'Reparto parejo',
  'surveyDemographics.sayYes': 'dicen s\u00ed',
  'surveyDemographics.answered': '{n} respondieron',
  'surveyDemographics.multiple': 'se pod\u00edan marcar varias',
  'surveyDemographics.other': 'Otros',
  'surveyDemographics.fromSurvey': 'de \u00ab{name}\u00bb',

  // ── Caso 03 — Canje de premios (pantalla operador + stats) ─────────
  'redemption.title': 'Canjear un premio',
  'redemption.subtitle': 'Escribe o escanea el código del consumidor y confirma la entrega.',
  'redemption.codeLabel': 'Código de canje',
  'redemption.lookUp': 'Buscar',
  'redemption.scan': 'Escanear',
  // Caso 11 v2 — Overlay del scanner por cámara.
  'operator.scanner.title': 'Escanear código QR',
  'operator.scanner.hint': 'Apunta la cámara al QR del consumidor.',
  'operator.scanner.unsupported':
    'Este navegador no admite escaneo por cámara. Ingresa el código manualmente.',
  'operator.scanner.cameraError':
    'No se pudo acceder a la cámara. Revisa el permiso e inténtalo de nuevo.',
  'redemption.preview.title': 'Premio listo para entregar',
  'redemption.preview.code': 'Código',
  'redemption.preview.stockId': 'Premio',
  'redemption.preview.station': 'Estación',
  'redemption.preview.won': 'Ganado',
  'redemption.preview.expires': 'Expira',
  'redemption.preview.confirm': 'Confirmar entrega',
  'redemption.preview.confirming': 'Confirmando…',
  'redemption.preview.state.delivered': 'Ya fue canjeado.',
  'redemption.preview.state.expired': 'Este canje expiró.',
  'redemption.preview.state.voided': 'Este canje fue anulado.',
  'redemption.success.title': '¡Entregado!',
  'redemption.success.body': 'Código {code} confirmado.',
  'redemption.error.generic': 'No se pudo canjear. Intenta de nuevo.',
  'redemption.error.tooShort': 'Los códigos tienen 6+ caracteres.',
  'redemption.stats.title': 'Canje de premios',
  'redemption.stats.won': 'Ganados',
  'redemption.stats.delivered': 'Entregados',
  'redemption.stats.expired': 'Expirados',
  'redemption.stats.rate': 'Tasa',

  // ── Caso 04 — Seguimiento post-evento ──────────────────────────────
  'followup.title': 'Correo de seguimiento',
  'followup.subtitle':
    'Configura el correo que se envía a participantes con consentimiento tras su evento.',
  'followup.enabled': 'Activa',
  'followup.disabled': 'Apagada',
  'followup.newTemplate': 'Nueva plantilla',
  'followup.confirmDelete': '¿Eliminar esta plantilla?',
  'followup.templates.title': 'Plantillas',
  'followup.templates.description':
    'Gana la coincidencia más específica: primero la del evento, luego la de marca.',
  'followup.templates.empty':
    'Aún no hay plantillas. Crea una para empezar a nutrir.',
  'followup.editor.newTitle': 'Nueva plantilla',
  'followup.editor.editTitle': 'Editar plantilla',
  'followup.editor.name': 'Nombre',
  'followup.editor.nameRequired': 'El nombre es requerido — Guardar se habilita cuando lo llenas.',
  'followup.editor.scope': 'Aplicar a',
  'followup.editor.scopeHint': 'Las plantillas específicas de evento ganan sobre la brand-wide para ese evento.',
  'followup.scope.brandWide': 'Todos los eventos (brand-wide)',
  'followup.scope.event': 'Evento',
  'followup.toast.created': 'Plantilla creada',
  'followup.toast.updated': 'Plantilla actualizada',
  'followup.toast.deleted': 'Plantilla eliminada',
  'followup.toast.saveError': 'No se pudo guardar la plantilla',
  'followup.toast.deleteError': 'No se pudo eliminar la plantilla',
  'followup.editor.subject': 'Asunto',
  'followup.editor.body': 'Cuerpo (HTML)',
  'followup.editor.variables':
    'Variables: {{firstName}} {{lastName}} {{prizeName}} {{couponCode}} {{eventName}} {{unsubscribeUrl}}',
  'followup.editor.delay': 'Demora de envío (horas)',
  'followup.defaults.subject': '¡Gracias por participar!',
  'followup.defaults.bodyHtml':
    '<p>Hola {{firstName}},</p><p>Gracias por visitarnos en {{eventName}}. Usa el código <strong>{{couponCode}}</strong> en tu próxima compra.</p><p><a href="{{unsubscribeUrl}}">Cancelar suscripción</a></p>',

  // ── Caso 14 — Checklist de primeros pasos ─────────────────────────
  'firstSteps.title': 'Pon en marcha tu primera activación',
  'firstSteps.step.brand.title': 'Añade tu marca',
  'firstSteps.step.brand.description': 'Logo + colores para que cada pantalla lleve tu identidad.',
  'firstSteps.step.brand.cta': 'Añadir marca',
  'firstSteps.step.event.title': 'Crea tu primer evento',
  'firstSteps.step.event.description': 'Fechas, lugar y a qué marca pertenece.',
  'firstSteps.step.event.cta': 'Crear evento',
  'firstSteps.step.station.title': 'Añade una estación',
  'firstSteps.step.station.description':
    'Un booth o touchpoint del evento. Genera el QR.',
  'firstSteps.step.identity.title': 'Ponle tu cara',
  'firstSteps.step.identity.description':
    'Añade tu logo y tus colores para que la encuesta que ve el invitado se parezca a tu marca. Opcional — funciona igual sin esto.',
  'firstSteps.step.identity.cta': 'Personalizar',
  'firstSteps.step.station.cta': 'Añadir estación',
  'common.dismiss': 'Descartar',

  // ── Caso 14 v1 — Asistente modal guiado ────────────────────────
  'firstEventWizard.title': 'Lancemos tu primer Xotski',
  'firstEventWizard.subtitle':
    'Tres pasos y estás en vivo: marca, evento, estación.',
  'firstEventWizard.progress': 'Avance del setup',
  'firstEventWizard.dontShowAgain': 'No mostrar esto otra vez',
  'firstEventWizard.step.brand.title': 'Crea tu marca',
  'firstEventWizard.step.brand.description':
    'Logo, colores y tono — la identidad que heredan tus journeys.',
  'firstEventWizard.step.brand.cta': 'Abrir brand studio',
  'firstEventWizard.step.event.title': 'Crea tu primer evento',
  'firstEventWizard.step.event.description':
    'Una activación en vivo: cuándo y dónde corre.',
  'firstEventWizard.step.event.cta': 'Crear evento',
  'firstEventWizard.step.station.title':
    'Añade una estación e imprime el QR',
  'firstEventWizard.step.station.description':
    'Un booth, kiosko o touchpoint donde los invitados escanean para jugar.',
  'firstEventWizard.step.station.cta': 'Añadir estación',
  // Caso 14 v2 — Creación inline de marca.
  'firstEventWizard.brand.nameLabel': 'Nombre de la marca',
  'firstEventWizard.brand.namePlaceholder': 'ej. Atledi',
  'firstEventWizard.brand.createSubmit': 'Crear marca',
  'firstEventWizard.brand.openStudio': 'Abrir Brand Studio completo →',
  'firstEventWizard.brand.created': 'Marca creada.',
  'firstEventWizard.brand.createFailed': 'No se pudo crear la marca.',

  // ── Project detail page ────────────────────────────────────────────
  'project.status.planning': 'Planificación',
  'project.status.live': 'En vivo',
  'project.status.wrapped': 'Cerrado',
  'project.backToBrand': 'Volver a la marca',
  'project.editProject': 'Editar proyecto',
  'project.eventsCardTitle': 'Eventos',
  'project.newEvent': 'Nuevo evento',
  'project.viewTrends': 'Ver tendencias',

  // ── Caso 12 parte 2 — Tendencias entre eventos ──────────────────
  'trends.title': 'Tendencias de eventos',
  'trends.subtitle':
    'Compara cómo desempeñó cada activación lado a lado.',
  'trends.empty': 'Aún no hay suficientes eventos para comparar.',
  'trends.chart.title': 'Métricas por evento',
  'trends.chart.subtitle':
    'Las barras se agrupan por evento para detectar patrones semana a semana.',
  'trends.table.title': 'Tabla detallada',
  'trends.table.event': 'Evento',
  'trends.table.date': 'Fecha',
  'trends.table.status': 'Estado',
  'trends.metric.responses': 'Interactions',
  'trends.metric.leads': 'Leads',
  'trends.metric.prizes': 'Premios entregados',
  'trends.metric.prizesDistributed': 'Premios distribuidos',
  'project.field.eventName': 'Nombre del evento',
  'project.field.eventNamePlaceholder': 'Brooklyn Block Party',
  'project.field.address': 'Dirección',
  'project.field.addressPlaceholder': '123 Wythe Ave, Brooklyn NY',
  'project.field.starts': 'Inicia',
  'project.field.ends': 'Termina',
  'project.field.sections': 'Estaciones',
  'project.field.codeRef': 'Código',
  'project.field.codeRefPlaceholder': 'LOLL26',
  'project.createEvent': 'Crear evento',
  'project.eventsEmpty': 'Aún no hay eventos. Agrega uno para colocar estaciones.',
  'project.noAddress': 'Sin dirección',
  'project.tbd': 'Por definir',
  'project.noun.events': 'eventos',
  'project.notFound': 'Proyecto no encontrado',
  'project.eventCreatedToast': 'Evento creado',
  'project.eventCreateFailedToast': 'Falló crear',
  'project.updatedToast': 'Proyecto "{name}" actualizado',
  'project.updateFailedToast': 'No se pudo actualizar el proyecto',
  'project.deletedToast': 'Proyecto "{name}" eliminado',
  'project.deleteFailedToast': 'No se pudo eliminar el proyecto',
  'project.edit.title': 'Editar proyecto',
  'project.edit.description': 'Actualiza el nombre, código, estado y fechas. Los eventos se mantienen.',
  'project.edit.field.name': 'Nombre del proyecto',
  'project.edit.field.codeRef': 'Código de referencia',
  'project.edit.field.codeRefPlaceholder': 'JST26',
  'project.edit.field.status': 'Estado',
  'project.edit.note': 'las fechas del proyecto se derivan de los eventos. Edita cada evento para cambiar el calendario.',
  'project.edit.noteLabel': 'Nota:',
  'project.edit.field.startDate': 'El proyecto inicia',
  'project.edit.field.endDate': 'El proyecto termina',
  'project.edit.rangeNoteLabel': 'Rango de fechas:',
  'project.edit.rangeNote': 'esta ventana rige las fechas permitidas en cada evento del proyecto. Déjalo vacío para derivarlo de los eventos.',
  'project.edit.rangeInvalid': 'El fin del proyecto debe ser posterior al inicio.',
  // Pestañas del workspace de proyecto
  'project.tab.plan': 'Planificar',
  'project.tab.track': 'Seguir',
  'project.tab.measure': 'Informe',
  // La insignia de estado — la columna guarda Active/Disabled/Finished y se
  // pintaba en crudo, así que se quedaba en inglés con la app en español.
  'events.status.active': 'Activo',
  'events.status.disabled': 'Desactivado',
  'events.status.finished': 'Terminado',
  // Pestaña PLAN — Planner
  'planner.xotskiCarousel.title': 'Merch',
  'planner.xotskiCarousel.empty': 'Esta marca aún no tiene merch.',
  'planner.xotskiCarousel.availableTip': '{n} disponibles en el pool de la marca',
  'planner.xotskiCarousel.outOfStock': 'Sin stock — repón para poder asignarlo',
  'planner.xotskiCarousel.out': 'Sin stock',
  'planner.stationSource.label': '+ Estación',
  'planner.reporters.title': 'Reporteros',
  'planner.reporters.emailPlaceholder': 'correo@dominio.com',
  'planner.reporters.namePlaceholder': 'Nombre (opcional)',
  'planner.reporters.inviteToSelected': 'Invitar al evento seleccionado',
  'planner.reporters.selectEventFirst': 'Selecciona un evento para invitar',
  'planner.reporters.empty': 'Aún no hay reporteros. Invita uno arriba o arrástralo a un evento.',
  'planner.reporters.dragHint': 'Arrastra a un evento para (re)invitar',
  'planner.reporters.invitedToast': 'Reportero invitado',
  'planner.reporters.inviteFailedToast': 'No se pudo invitar al reportero',
  'planner.reporters.statusConfirmed': 'Confirmado',
  'planner.reporters.statusPending': 'Pendiente',
  'planner.reporters.statusRevoked': 'Revocado',
  'planner.reporters.statusExpired': 'Expirado',
  'planner.reporters.remove': 'Quitar reportero',
  'planner.reporters.removed': 'Reportero eliminado',
  'planner.reporters.removeFailed': 'No se pudo eliminar el reportero',
  'planner.alloc.available': 'quedan {n}',
  'planner.alloc.increase': 'Aumentar cantidad',
  'planner.alloc.decrease': 'Disminuir cantidad',
  'planner.alloc.atCeiling': 'No quedan más unidades en el brand',
  'planner.alloc.remove': 'Quitar del evento',
  'planner.alloc.removed': 'Merch eliminado del evento',
  'planner.alloc.removeFailed': 'No se pudo eliminar el ítem de merch',
  'planner.alloc.qtyFailed': 'No se pudo actualizar la cantidad',
  'planner.alloc.qtyInput': 'Cantidad — escribí un número o usá los botones',
  'planner.alloc.availableOf': '{available} de {quantity} disponibles',
  'planner.alloc.soldOut': 'Agotado',
  'planner.alloc.hidden': 'Oculto de los juegos',
  'planner.alloc.drawer.open': 'Asignar premios',
  'planner.alloc.drawer.title': 'Asignar premios · {event}',
  'planner.alloc.drawer.hint': 'Unidades por premio y estación, descontadas del pool de la marca. Subir está topado por lo que le queda a la marca; 0 quita la asignación. Una celda con varias filas del mismo premio se edita en la estación.',
  'planner.alloc.drawer.prize': 'Premio',
  'planner.alloc.drawer.left': 'quedan {n} en la marca',
  'planner.alloc.drawer.over': 'Sobran {n} por encima',
  'planner.alloc.drawer.multi': '{n} filas · se edita en la estación',
  'planner.alloc.drawer.noStations': 'Este evento aún no tiene estaciones. Añade una en el tablero primero.',
  'planner.alloc.drawer.noStock': 'La marca aún no tiene merch. Dalo de alta en',
  'planner.alloc.drawer.changes': '{n} cambio(s)',
  'planner.alloc.drawer.apply': 'Aplicar',
  'planner.alloc.drawer.applied': '{n} asignación(es) actualizadas',
  'planner.alloc.drawer.failed': 'Algunas asignaciones no se guardaron',
  'planner.alloc.cooling': 'En espera',
  'planner.alloc.settings': 'Ajustes del premio',
  'planner.alloc.availableInput': 'Unidades que quedan',
  'planner.alloc.mode.label': 'Cómo sale en los juegos',
  'planner.alloc.mode.weight': 'Por peso',
  'planner.alloc.mode.cooldown': 'Cada N minutos',
  'planner.alloc.mode.weightHint': 'Rareza 1 = el más común (peso 25) · 5 = el más raro (peso 1)',
  'planner.alloc.mode.cooldownHint': 'No vuelve a salir hasta que pasen estos minutos desde la última vez que se ganó.',
  'planner.alloc.mode.cooldownMinutes': 'Minutos entre premios',
  'planner.alloc.hideFromGames': 'Sacar de los juegos',
  'planner.alloc.showInGames': 'Volver a los juegos',
  'planner.alloc.saveFailed': 'No se pudieron guardar los ajustes del premio',
  'planner.station.refresh': 'Actualizar disponibilidad',
  'planner.station.refreshed': 'Disponibilidad actualizada',
  'planner.station.actions': 'Acciones de la estación',
  'planner.events.actions': 'Acciones del evento',
  'planner.properties.open': 'Propiedades',
  // Editor de allocations por estación (en línea)
  'planner.station.expand': 'Expandir estación',
  'planner.station.collapse': 'Contraer estación',
  'planner.station.sliceCount': '{count} slices',
  'planner.station.empty': 'Aún no hay merch aquí. Suelta uno en el evento para surtir cada estación.',
  'planner.station.drag': 'Arrastra para reordenar',
  'planner.station.duplicate': 'Duplicar como otro slice',
  'planner.station.duplicated': 'Slice duplicado',
  'planner.station.duplicateFailed': 'No se pudo duplicar el slice',
  'planner.station.reorderFailed': 'No se pudo guardar el nuevo orden',
  'planner.station.preview': 'Previsualizar el juego',
  'planner.station.previewTitle': 'Preview · {name}',
  'planner.station.previewNoSurvey': 'Aún no hay guest journey — diséñalo en la pestaña Design Journey del proyecto y se aplica aquí.',
  'planner.alloc.groupedHint': '{n} asignaciones · {total} unidades en total. Editá las cantidades por station en el detalle de la station.',
  'planner.alloc.removeGroupTitle': '¿Quitar “{name}” de este evento?',
  'planner.alloc.removeGroupBody': 'Esto elimina las {n} asignaciones de “{name}” en todas las stations de este evento ({total} unidades en total) y lo saca de sus juegos. Las unidades vuelven al pool de la marca; los premios ya ganados se conservan. Si querés quitarlo de una sola station, hacelo desde el detalle de esa station.',
  'planner.properties.title': 'Propiedades del evento',
  'planner.properties.noSelection': 'Selecciona un evento para editar sus propiedades.',
  'planner.properties.savedToast': 'Evento guardado',
  'planner.properties.saveFailedToast': 'No se pudo guardar el evento',
  'planner.properties.endAfterStart': 'El fin debe ser posterior al inicio.',
  'planner.properties.outOfRange': 'Las fechas deben estar dentro del rango del proyecto.',
  'planner.properties.save': 'Guardar',
  'planner.properties.sectionsReadonly': 'Solo lectura — igual al número de estaciones del evento',
  'planner.properties.sectionsHint': 'solo lectura',
  'planner.properties.surveyTemplate': 'Punto de partida del journey',
  'planner.properties.pickTemplate': 'Elige un punto de partida…',
  'planner.properties.applyingTemplate': 'Aplicando…',
  'planner.events.title': 'Eventos en planificación',
  'planner.events.empty': 'No hay eventos por planificar. Usa “Nuevos eventos” para crear.',
  'planner.events.collapse': 'Colapsar',
  'planner.events.expand': 'Expandir',
  'planner.events.openConfig': 'Abrir config',
  'planner.events.merch': 'Merch',
  'planner.events.reporters': 'Reporteros',
  'planner.events.dropXotskiHint': 'Arrastra un ítem de merch aquí para asignarlo.',
  'planner.events.xotskiRollupEmpty': 'Aún no hay merch — suelta uno en una estación abajo para agregarlo.',
  'planner.events.dropReporterHint': 'Arrastra un reportero aquí para invitarlo.',
  'planner.events.stationCount': '{count} estación(es)',
  'planner.events.dropStationHint': 'Arrastra el chip “+ Estación” aquí para añadir una.',
  'planner.events.openStationConfig': 'Abrir configuración de la estación',
  'planner.events.removeStation': 'Quitar estación',
  'planner.events.lastStation': 'Un evento debe mantener al menos una estación',
  'planner.events.stationRemoved': 'Estación eliminada',
  'planner.events.stationRemoveFailed': 'No se pudo eliminar la estación',
  'planner.phase.live': 'En vivo',
  'planner.phase.upcoming': 'Próximo',
  'planner.phase.edit': 'En edición',
  'planner.phase.done': 'Completado',
  'eventForm.stationNote': 'El evento nace con su primera estación, lista para recoger respuestas. Esa estación cuenta en tu plan.',
  'planner.spreadAll.cta': 'Repartir mis premios aquí',
  'planner.spreadAll.done': 'Premios repartidos en este evento ({n}).',
  'planner.spreadAll.nothing': 'No hay nada que repartir: sin merch libre, o las estaciones ya tienen estos premios.',
  'planner.spreadAll.noStock': 'Todavía no tienes premios: añade uno en My Merch.',
  'planner.newEvent': 'Nuevo evento',
  'planner.batch.open': 'Varios de golpe',
  'planner.batch.title': 'Crear varios eventos',
  'planner.batch.description': 'Elige cuántos eventos crear. Los nombres y fechas se completan automáticamente y cada evento nace con una estación.',
  'planner.batch.countLabel': '¿Cuántos eventos?',
  'planner.batch.noRange': 'sin rango de proyecto definido',
  'planner.batch.previewNames': 'Nombres: {first} … {last}',
  'planner.batch.previewRange': 'Fechas repartidas en {range}',
  'planner.batch.previewStation': 'Cada evento empieza con una estación.',
  'planner.batch.cancel': 'Cancelar',
  'planner.batch.create': 'Crear eventos',
  'planner.batch.createdToast': '{count} evento(s) creados',
  'planner.batch.failedToast': 'No se pudieron crear los eventos',
  'planner.drop.xotskiDone': '{name} asignado a todas las estaciones',
  'planner.drop.xotskiToStation': '{name} asignado a esta estación',
  'planner.drop.xotskiUseStation': 'Suelta el ítem de merch sobre una estación para asignarlo.',
  'planner.drop.xotskiOnStationExists': '{name} ya está en esta estación — usa +/- o duplícalo',
  'planner.drop.reporterDone': '{name} invitado',
  'planner.drop.stationDone': 'Estación añadida',
  'planner.drop.failed': 'Falló al soltar',
  'planner.drop.noStations': 'Este evento aún no tiene estaciones.',
  'planner.drop.allStationsFailed': 'No se pudo asignar a ninguna estación.',
  'planner.drop.partial': 'Asignado, pero {failed} de {total} estaciones fallaron.',
  'planner.drop.here': 'Soltar aquí',
  'planner.drop.xotskiExists': '{name} ya está en este evento — usa +/- para cambiar la cantidad',
  'planner.drop.reporterExists': '{name} ya está invitado a este evento',
  'sharedQr.pick.title': '¿Dónde estás?',
  'sharedQr.pick.body': 'Elige tu estación para empezar.',
  'sharedQr.loading.title': 'Cargando…',
  'sharedQr.loading.body': 'Obteniendo las estaciones de este QR.',
  'sharedQr.invalid.title': 'QR inválido',
  'sharedQr.invalid.body': 'Este QR compartido no es válido o fue eliminado.',
  'sharedQr.empty.title': 'Sin estaciones',
  'sharedQr.empty.body': 'Este QR aún no tiene estaciones asignadas.',
  'sharedQr.manager.title': 'QR compartido',
  'sharedQr.manager.subtitle': 'Un QR para varias estaciones — el consumidor elige su estación primero.',
  'sharedQr.manager.create': 'Crear QR compartido',
  'sharedQr.manager.empty': 'Este evento aún no tiene QR compartidos.',
  'sharedQr.manager.copy': 'Copiar enlace',
  'sharedQr.manager.copied': 'Enlace copiado',
  'sharedQr.manager.delete': 'Eliminar',
  'sharedQr.manager.removed': 'QR compartido eliminado',
  'sharedQr.manager.removeFailed': 'No se pudo eliminar el QR compartido',
  'sharedQr.manager.dialogTitle': 'Crear un QR compartido',
  'sharedQr.manager.dialogBody': 'Elige 2 o más estaciones para compartir un QR. Cada una conserva sus merch.',
  'sharedQr.manager.nameLabel': 'Nombre',
  'sharedQr.manager.defaultName': 'QR compartido',
  'sharedQr.manager.pickStations': 'Estaciones',
  'sharedQr.manager.needTwo': 'Este evento necesita al menos 2 estaciones para compartir un QR.',
  'sharedQr.manager.cancel': 'Cancelar',
  'sharedQr.manager.createBtn': 'Crear',
  'sharedQr.manager.createdToast': 'QR compartido creado',
  'sharedQr.manager.createFailed': 'No se pudo crear el QR compartido',
  // Pestaña TRACK
  'track.empty': 'Este proyecto aún no tiene eventos.',
  'track.noDate': 'Sin fecha',
  'track.edit': 'Editar',
  'track.view': 'Ver',
  'track.group.live': 'En vivo ahora',
  'track.group.upcoming': 'Próximos',
  'track.group.edit': 'En edición',
  'track.group.done': 'Completados',
  // Pestaña MEASURE
  'measure.range': 'Rango de fechas',
  'measure.events': 'Eventos',
  'measure.live': 'En vivo ahora',
  'measure.stockChartTitle': 'Merch repartidos en el proyecto',
  'measure.noStockData': 'Aún no se ha repartido ningún ítem de merch a una estación.',
  'measure.quantity': 'Asignado',
  'measure.available': 'Disponible',
  'project.edit.confirmTitle': '¿Eliminar este proyecto?',
  'project.edit.confirmBody': 'Sus eventos, estaciones y asignaciones de merch se eliminarán permanentemente.',
  'project.edit.deleting': 'Eliminando…',
  'project.edit.confirmDelete': 'Confirmar eliminación',
  'project.edit.deleteProject': 'Eliminar proyecto',
  'project.edit.cancel': 'Cancelar',
  'project.edit.saving': 'Guardando…',
  'project.edit.saveChanges': 'Guardar cambios',

  // ── Access Manager ─────────────────────────────────────────────────
  'accessManager.title': 'Gestor de accesos',
  'accessManager.scope': 'alcance: {app}',
  'accessManager.subtitle': 'Administra miembros, roles y permisos externos para tu cuenta y agencias hijas. Los listados y nuevas entradas se limitan a la app {app} — cambia de app para gestionar otros módulos.',
  'accessManager.tab.members': 'Miembros',
  'accessManager.tab.roles': 'Roles',
  'accessManager.tab.grants': 'Compartido externo',
  'accessManager.noAccess.title': 'Sin acceso',
  'accessManager.noAccess.body':
    'No tienes permiso para ver miembros, roles ni el compartido externo. Pide a tu administrador que te conceda al menos uno.',
  'accessManager.currentApp': 'actual',

  // ── Members tab ────────────────────────────────────────────────────
  'members.title': 'Miembros',
  'memberForm.titleAdd': 'Agregar miembro',
  'memberForm.titleEdit': 'Editar miembro',
  'memberForm.subtitle': 'Los miembros nuevos se crean en {company}. El rol que elijas decide qué parte de la empresa puede ver.',
  'memberForm.firstName': 'Nombre',
  'memberForm.lastName': 'Apellidos',
  'memberForm.email': 'Correo',
  'memberForm.phone': 'Teléfono (opcional)',
  'memberForm.password': 'Contraseña inicial (opcional — si no, se le manda una invitación)',
  'memberForm.role': 'Rol',
  'memberForm.noRole': 'Sin rol',
  'memberForm.roleHint': 'El alcance de empresa del rol es lo que limita lo que esta persona ve al entrar.',
  'memberForm.submitCreate': 'Crear miembro',
  'memberForm.submitSave': 'Guardar',
  'memberForm.errRequired': 'Hacen falta el correo y el nombre',
  'memberForm.errNoWorkspace': 'Falta el contexto de la cuenta — vuelve a entrar',
  'memberForm.created': 'Miembro creado',
  'memberForm.existed': 'Esa persona ya tenía cuenta — se le ha dado acceso a la app',
  'memberForm.updated': 'Miembro actualizado',
  'members.addMember': 'Agregar miembro',
  'members.empty.title': 'Aún no hay miembros',
  'members.empty.body': 'Agrega al primer miembro de tu equipo.',
  'members.confirmDelete': '¿Eliminar suavemente a {email}?',
  'members.deletedToast': 'Miembro removido',
  'members.col.name': 'Nombre',
  'members.col.email': 'Correo',
  'members.col.company': 'Marca',
  'members.col.role': 'Rol',
  'members.col.actions': 'Acciones',
  'members.youBadge': 'tú',
  'members.openRoleTitle': 'Abrir rol "{role}" en la pestaña de roles',
  'members.cantDeleteSelf': 'No puedes eliminar tu propia cuenta',
  'members.setPassword.action': 'Cambiar contraseña',
  'members.setPassword.title': 'Cambiar contraseña',
  'members.setPassword.description': 'Define una nueva contraseña para {name}. La usará en su próximo inicio de sesión.',
  'members.setPassword.newPassword': 'Nueva contraseña',
  'members.setPassword.confirm': 'Confirmar contraseña',
  'members.setPassword.submit': 'Guardar contraseña',
  'members.setPassword.show': 'Mostrar contraseña',
  'members.setPassword.hide': 'Ocultar contraseña',
  'members.setPassword.minLength': 'La contraseña debe tener al menos {n} caracteres.',
  'members.setPassword.mismatch': 'Las contraseñas no coinciden.',
  'members.setPassword.successToast': 'Contraseña actualizada para {name}',
  'members.setPassword.errorToast': 'No se pudo cambiar la contraseña',

  // ── Roles tab ──────────────────────────────────────────────────────
  'roles.title': 'Roles',
  'roles.createRole': 'Crear rol',
  'roles.empty.title': 'Aún no hay roles',
  'roles.empty.body': 'Crea roles para asignar paquetes de permisos reutilizables.',
  'roles.confirmDelete': '¿Eliminar rol {name}?',
  'roles.deletedToast': 'Rol eliminado',
  'roles.templateBadge': 'plantilla',
  'roles.permsCount': '{count} permiso(s)',
  'roles.cloneTitle': 'Crear un nuevo rol con estos mismos permisos',
  'roles.clone': 'Clonar',
  'roles.readOnlyTitle': 'Las plantillas del sistema son de solo lectura. Clona para personalizar.',
  'roles.readOnlyLabel': 'Plantilla de solo lectura',

  // ── Grants tab ─────────────────────────────────────────────────────
  'grants.title': 'Compartido externo',
  'grants.comingSoonNotice': 'Compartido externo estará disponible próximamente. Esta funcionalidad aún no está habilitada — ¡pronto la tendrás!',
  'grants.shareAccess': 'Compartir acceso',
  'grants.confirmRevoke': '¿Revocar acceso compartido con {email}?',
  'grants.revokedToast': 'Acceso revocado',
  'grants.tab.outgoing': 'Otorgados por mí',
  'grants.tab.incoming': 'Otorgados a mí',
  'grants.empty.outgoingTitle': 'No hay accesos activos',
  'grants.empty.incomingTitle': 'No hay accesos entrantes',
  'grants.empty.outgoingBody': 'Comparte el acceso a una de tus empresas con un usuario externo.',
  'grants.empty.incomingBody': 'Aún no han compartido acceso contigo.',
  'grants.user': 'usuario #{id}',
  'grants.expires': 'expira {date}',
  'grants.permsCount': '{count} permiso(s)',
  'grants.grantedBy': 'otorgado por {email}',
  'grants.revoke': 'Revocar',

  // ── Brand Identity page (additional descriptive variants) ──────────
  'brandIdentity.subtitleLong': 'Valores por defecto a nivel marca para logo, colores, tipografía y nombre de campaña. Las personalizaciones por evento (recolección de datos, age gate, juego, premios) viven en la Configuración de Journey de cada evento.',
  'brandIdentity.saveChanges': 'Guardar cambios',
  'brandIdentity.savedToast': 'Identidad de marca guardada',
  'brandIdentity.saveFailedToast': 'Falló al guardar',

  // ── Brand Studio (new) ─────────────────────────────────────────────
  'brandStudio.title': 'Brand Studio',
  'brandStudio.editing': 'Editando',
  'brandStudio.newBrandInherit': 'Nueva marca · heredada de',
  'brandStudio.back': 'Volver',
  'brandStudio.deleting': 'Eliminando…',
  'brandStudio.confirmDelete': 'Confirmar eliminación',
  'brandStudio.deleteBrand': 'Eliminar marca',
  'brandStudio.cancel': 'Cancelar',
  'brandStudio.saving': 'Guardando…',
  'brandStudio.saveChanges': 'Guardar cambios',
  'brandStudio.savePublish': 'Guardar y publicar',
  'brandStudio.confirmDeleteTitle': '¿Eliminar esta marca?',
  'brandStudio.confirmDeleteBody': 'Todos sus proyectos, eventos, estaciones, merch y journeys serán removidos permanentemente. Haz clic en',
  'brandStudio.confirmDeleteEmph': 'Confirmar eliminación',
  'brandStudio.confirmDeleteEnd': 'otra vez para continuar.',
  'brandStudio.brandCreated': 'Marca "{name}" creada',
  'brandStudio.brandUpdated': 'Marca "{name}" actualizada',
  'brandStudio.brandDeleted': 'Marca "{name}" eliminada',
  'brandStudio.createFailed': 'No se pudo crear la marca',
  'brandStudio.updateFailed': 'No se pudo actualizar la marca',
  'brandStudio.kitSaveFailed': 'La marca se guardó pero el kit (colores / defaults de experiencia) no se pudo persistir',
  'brandStudio.deleteFailed': 'No se pudo eliminar la marca',
  'brandStudio.error.nameRequired': 'El nombre de la marca es obligatorio',
  'brandStudio.error.primaryHex': 'El color primario debe ser un valor hex (#RGB o #RRGGBB)',
  'brandStudio.error.secondaryHex': 'El color secundario debe ser un valor hex',
  'brandStudio.error.textHex': 'El color de texto debe ser un valor hex',
  'brandStudio.fixOne': 'Corrige {count} problema antes de guardar:',
  'brandStudio.fixMany': 'Corrige {count} problemas antes de guardar:',
  'brandStudio.tab.identity': 'Identidad',
  'brandStudio.tab.experience': 'Experiencia',
  'brandStudio.tab.lookFeel': 'Estilo',
  'brandStudio.tab.game': 'Juego',
  'brandStudio.tab.prizes': 'Premios',
  'brandStudio.tab.output': 'Salida',
  'brandStudio.tab.notSaved': 'Aún no se guarda',
  'brandStudio.tab.notSavedHint': 'Esta pestaña todavía no tiene dónde persistir. Esto se ajusta por evento en Design Journey.',
  'brandStudio.lookFeel.partialNote': 'Hoy sólo la celebración se guarda con la marca. El estilo de botón, el texto de consentimiento, el CTA del juego y la redirección tras el canje se ajustan por evento en Design Journey.',
  'brandStudio.livePreview': 'Vista previa en vivo',
  'brandStudio.previewFooter': 'La vista previa se actualiza al cambiar los ajustes — bienvenida, CTA del juego y pantalla de premio comparten estos estilos.',
  // Caso 05 v1 — Simulación de animación de celebración
  'brandStudio.game.celebration': 'Animación de celebración',
  'brandStudio.game.test': 'Probar celebración',
  'brandStudio.game.themeHint.confetti': 'Lluvia de papel — se siente festivo.',
  'brandStudio.game.themeHint.fireworks': 'Mayor impacto — explosiones radiales.',
  'brandStudio.game.themeHint.sparks': 'Brillo sutil — minimalista y elegante.',
  // Identity tab
  'brandStudio.identity.hint': 'Sube tu logo primero — en el futuro Atledi sugerirá colores de marca automáticamente. Por ahora, define tu paleta de 5 colores.',
  'brandStudio.identity.brandName': 'Nombre de la marca',
  'brandStudio.identity.brandNameHint': 'Cómo aparecerá esta marca en Xotski.',
  'brandStudio.identity.brandNamePlaceholder': 'p. ej. Joyly',
  'brandStudio.identity.industry': 'Industria',
  'brandStudio.identity.industryHint': 'Opcional. Ayuda a Atledi a adaptar plantillas.',
  'brandStudio.identity.industryPlaceholder': 'Bebidas',
  'brandStudio.identity.campaignName': 'Nombre de campaña',
  'brandStudio.identity.campaignNameHint': 'Usado en URLs de QR, etiquetas de estación y reportes.',
  'brandStudio.identity.campaignNamePlaceholder': 'p. ej. Joyly Spring Tour 2026',
  'brandStudio.identity.brandLogo': 'Logo de marca',
  'brandStudio.identity.brandLogoHint': 'Heredado de la empresa hasta que se sobrescriba.',
  'brandStudio.identity.noLogo': 'Sin logo',
  'brandStudio.identity.qrThumb': 'Thumbnail del QR',
  'brandStudio.identity.qrThumbHint': 'Imagen cuadrada dentro del QR. Si está vacío usa el logo.',
  'brandStudio.identity.noThumb': 'Sin thumb',
  'brandStudio.identity.backgroundImage': 'Imagen de fondo',
  'brandStudio.identity.backgroundImageHint': 'Foto a sangre detrás de la journey. Vacío = color de fondo sólido.',
  'brandStudio.identity.noImage': 'Sin imagen',
  'brandStudio.identity.palette': 'Paleta de 5 colores',
  'brandStudio.identity.color.primary': 'Primario',
  'brandStudio.identity.color.primaryDesc': 'Headers, fondo del QR, superficies principales',
  'brandStudio.identity.color.secondary': 'Secundario',
  'brandStudio.identity.color.secondaryDesc': 'Superficies de apoyo, bordes, divisores',
  'brandStudio.identity.color.accent': 'Acento',
  'brandStudio.identity.color.accentDesc': 'Botones, estados activos, primer plano del QR',
  'brandStudio.identity.color.background': 'Fondo',
  'brandStudio.identity.color.backgroundDesc': 'Fondos de tarjetas y contenedores',
  'brandStudio.identity.color.text': 'Texto',
  'brandStudio.identity.color.textDesc': 'Texto principal en superficies de marca',
  'brandStudio.identity.brandFont': 'Tipografía de marca',
  // Experience tab
  'brandStudio.experience.hint': 'Las journeys bilingües logran hasta 34% más finalización en eventos multiculturales.',
  'brandStudio.experience.surveyLanguage': 'Idioma de la journey',
  'brandStudio.experience.english': 'Inglés',
  'brandStudio.experience.spanish': 'Español',
  'brandStudio.experience.bilingualHint': 'Modo bilingüe — los usuarios eligen el idioma al inicio',
  'brandStudio.experience.contactInfo': 'Datos de contacto',
  'brandStudio.experience.collectContact': 'Recolectar datos de contacto',
  'brandStudio.experience.fullName': 'Nombre completo',
  'brandStudio.experience.email': 'Correo',
  'brandStudio.experience.phone': 'Teléfono',
  'brandStudio.experience.ageGate': 'Filtro de edad',
  'brandStudio.experience.disabled': 'Desactivado',
  'brandStudio.experience.demographics': 'Demografía',
  'brandStudio.experience.ageRange': 'Rango de edad',
  'brandStudio.experience.gender': 'Género',
  'brandStudio.experience.ethnicity': 'Etnia',
  'brandStudio.experience.nationality': 'Nacionalidad',
  'brandStudio.experience.pronouns': 'Pronombres',
  'brandStudio.experience.customQuestions': 'Preguntas personalizadas',
  'brandStudio.experience.customQuestionsLabel': '{count} pregunta(s) personalizada(s) agregada(s)',
  'brandStudio.experience.customQuestionsAccessOn': 'Tu plan permite preguntas personalizadas. El editor llega en Fase 4.',
  'brandStudio.experience.customQuestionsAccessOff': 'Actualiza tu plan para agregar preguntas personalizadas al flujo del consumidor.',
  // Look & Feel tab
  'brandStudio.look.hint': 'Estilos de botón y celebraciones consistentes construyen reconocimiento de marca.',
  'brandStudio.look.buttonStyle': 'Estilo de botón',
  'brandStudio.look.next': 'Siguiente',
  'brandStudio.look.celebration': 'Tema de celebración',
  'brandStudio.look.noAnimation': 'Sin animación',
  'brandStudio.look.consent': 'Texto de consentimiento',
  'brandStudio.look.consentHint': 'Mostrado antes de la recolección de datos. Debe cumplir tu política de privacidad.',
  'brandStudio.look.gameCTAEn': 'CTA del juego (EN)',
  'brandStudio.look.gameCTAEnPlaceholder': 'Spin to win!',
  'brandStudio.look.gameCTAEs': 'CTA del juego (ES)',
  'brandStudio.look.gameCTAEsPlaceholder': '¡Gira para ganar!',
  // Game tab
  'brandStudio.game.hint': 'Cada juego destaca tu marca de forma distinta. La Ruleta de premios viene en todos los planes; los premium se desbloquean con upgrades.',
  'brandStudio.game.chooseYourGame': 'Elige tu juego',
  'brandStudio.game.brandIntegration': 'Integración de marca',
  'brandStudio.brandIntegration': 'Integración de marca',
  'brandStudio.game.wheel.name': 'Ruleta',
  'brandStudio.game.wheel.desc': 'Gira para ganar — el clásico. Logo en el centro, colores de marca en los segmentos.',
  'brandStudio.game.wheel.s1': 'Logo en el centro de la ruleta',
  'brandStudio.game.wheel.s2': 'Color de marca en los segmentos',
  'brandStudio.game.wheel.s3': 'Foto del premio llena la pantalla de revelación',
  'brandStudio.game.scratch.name': 'Tarjeta para raspar',
  'brandStudio.game.scratch.desc': 'Raspa para revelar — táctil y satisfactorio.',
  'brandStudio.game.scratch.s1': 'Marca de agua del logo bajo la superficie',
  'brandStudio.game.scratch.s2': 'Degradado del color de marca',
  'brandStudio.game.scratch.s3': 'Foto del premio revelada abajo',
  'brandStudio.game.pirinola.name': 'Pirinola',
  'brandStudio.game.pirinola.desc': 'Gira la pirinola — clásico cultural con un giro digital.',
  'brandStudio.game.pirinola.s1': 'Colores de marca en las caras',
  'brandStudio.game.pirinola.s2': 'Logo en la cara superior',
  'brandStudio.game.pirinola.s3': 'Nombre del premio en la cara ganadora',
  'brandStudio.game.random.name': 'Sorteo aleatorio',
  'brandStudio.game.random.desc': 'Revelación estilo lotería — suspenso y sorpresa.',
  'brandStudio.game.random.s1': 'Animación con color de marca',
  'brandStudio.game.random.s2': 'Logo en el encabezado',
  'brandStudio.game.random.s3': 'Tarjeta de premio con foto del producto',
  'brandStudio.game.xinkit.name': 'Xinkit',
  'brandStudio.game.xinkit.desc': 'Suelta la bola — diversión basada en física con pegs personalizados.',
  'brandStudio.game.xinkit.s1': 'Color de marca en pegs y tablero',
  'brandStudio.game.xinkit.s2': 'Logo en la bola',
  'brandStudio.game.xinkit.s3': 'Casillas de premio abajo',
  'brandStudio.game.soccer.name': 'Balón de Fútbol',
  'brandStudio.game.soccer.desc': 'Toca para patear el balón al arco — los slots dentro revelan el premio.',
  'brandStudio.game.soccer.s1': 'Color de marca en el balón y el marco del arco',
  'brandStudio.game.soccer.s2': 'Logo en los slots de premio',
  'brandStudio.game.soccer.s3': 'Ideal para marcas deportivas y activaciones de bebidas',
  'brandStudio.game.loteria.name': 'Lotería',
  'brandStudio.game.loteria.desc':
    'Juego de mesa folclórico mexicano. Tablero de 4×3. Completa una fila para ganar. La mayor resonancia cultural para eventos latinos.',
  'brandStudio.game.loteria.s1': 'Colores de marca en la fila ganadora y el banner de Jackpot',
  'brandStudio.game.loteria.s2': 'Tablero auto-marcado · mayor tiempo de juego de todos',
  'brandStudio.game.loteria.s3': 'Ancla de festival; ideal para activaciones en mercado hispano',
  'brandStudio.game.slots.name': 'Tragamonedas',
  'brandStudio.game.slots.desc':
    'Tres rodillos de íconos de premio. Toca para girar; alinea la fila central para ganar.',
  'brandStudio.game.slots.s1': 'Colores de marca en el gabinete y el marco de rodillos',
  'brandStudio.game.slots.s2': 'Íconos de premio en cada rodillo',
  'brandStudio.game.slots.s3': 'Mecánica de alta energía; ideal para stands estilo arcade',
  'brandStudio.gamePicker.selectedGame': 'Juego seleccionado',
  'brandStudio.gamePicker.tierRequired': 'Plan requerido',
  'publicSurvey.soccer.kick': '¡Patea!',
  'publicSurvey.soccer.kicking': 'Pateando…',
  'publicSurvey.soccer.goal': '¡GOL!',
  'publicSurvey.soccer.fieldAlt': 'Campo de fútbol con arco',
  'publicSurvey.loteria.label': 'Lotería',
  'publicSurvey.loteria.cardCalled': 'Carta cantada',
  'publicSurvey.loteria.getReady': 'Prepárate…',
  'publicSurvey.loteria.start': '¡Lotería!',
  'publicSurvey.loteria.hint': 'Las cartas se marcan solas · Completa una fila para ganar',
  // PRD §18 — Experiencia de juego unificada (labels compartidos; sin emojis)
  'publicSurvey.games.youWon': 'Ganaste',
  'publicSurvey.games.tapToPlay': 'Toca para jugar',
  'publicSurvey.games.sound': 'Sonido',
  'publicSurvey.games.soundOn': 'Sonido activado',
  'publicSurvey.games.soundOff': 'Sonido desactivado',
  'publicSurvey.games.wheel.action': 'Gira la ruleta',
  'publicSurvey.games.wheel.busy': 'Girando…',
  'publicSurvey.games.wheel.hub': 'GIRA',
  'publicSurvey.games.scratch.action': 'Raspa para revelar',
  'publicSurvey.games.scratch.hint': 'Raspa con tu dedo',
  'publicSurvey.games.scratch.revealed': '¡Revelado!',
  'publicSurvey.games.slots.action': 'Jala',
  'publicSurvey.games.slots.busy': 'Girando…',
  'publicSurvey.games.xinkit.action': 'Suelta la bola',
  'publicSurvey.games.xinkit.busy': 'Cayendo…',
  'publicSurvey.games.xinkit.tagline': 'Suelta · Rebota · Gana',
  'publicSurvey.games.pirinola.action': 'Gira la pirinola',
  'publicSurvey.games.pirinola.busy': 'Girando…',
  'publicSurvey.games.randomDraw.action': 'Saca una carta',
  'publicSurvey.games.randomDraw.busy': 'Barajando…',
  'publicSurvey.games.randomDraw.done': '¡Revelado!',
  'publicSurvey.games.loteria.waiting': 'Esperando…',
  'brandStudio.game.wheelTaglineEn': 'Tagline del juego (EN)',
  'brandStudio.game.wheelTaglineEs': 'Tagline del juego (ES)',
  'brandStudio.game.wheelTaglineEnPlaceholder': 'Spin to win!',
  'brandStudio.game.wheelTaglineEsPlaceholder': '¡Gira para ganar!',
  'brandStudio.game.heroImage': 'Imagen hero del juego',
  'brandStudio.game.heroImageHint': 'Opcional. Foto de producto o banner detrás del juego.',
  'brandStudio.game.noHero': 'Sin hero',
  'brandStudio.game.tier.free': 'Gratis',
  'brandStudio.game.tier.premium': 'Premium',
  'brandStudio.game.tier.pro': 'Pro',
  // Prizes tab
  'brandStudio.prizes.hint': '3–5 premios con rareza variada funcionan mejor. Raro (1–2 estrellas) crea emoción; común (4–5 estrellas) mantiene a todos ganando.',
  'brandStudio.prizes.inventory': 'Inventario de premios',
  'brandStudio.prizes.selected': '{count} seleccionados',
  'brandStudio.prizes.units': '{count} unidades',
  'brandStudio.prizes.revealPreview': 'Vista previa de revelación de premio',
  'brandStudio.prizes.youWon': '¡Ganaste!',
  'brandStudio.prizes.fallback': 'Premio',
  'brandStudio.prizes.claim': 'Muestra esta pantalla al staff del evento para reclamar tu premio',
  'brandStudio.prizes.rare': 'Raro',
  'brandStudio.prizes.medium': 'Medio',
  'brandStudio.prizes.common': 'Común',
  'brandStudio.prizes.inv.tshirt': 'Playera personalizada',
  'brandStudio.prizes.inv.hat': 'Gorra snapback',
  'brandStudio.prizes.inv.sticker': 'Pack de stickers',
  'brandStudio.prizes.inv.tote': 'Tote bag de lona',
  'brandStudio.prizes.inv.bottle': 'Botella de agua',
  'brandStudio.prizes.inv.hoodie': 'Hoodie premium',
  // Output tab
  'brandStudio.output.hint': 'Cada estación tiene su propio QR — los colores y logo configurados aquí se aplican automáticamente.',
  'brandStudio.output.universalQr': 'Código QR universal',
  'brandStudio.output.embedLogo': 'El QR incluye tu logo embebido',
  'brandStudio.output.uploadLogo': 'Sube un logo para embeberlo en el QR',
  'brandStudio.output.shareableLink': 'Enlace para compartir',
  'brandStudio.output.uniquePerStation': 'Cada estación tiene su propio enlace y QR',
  'brandStudio.output.uniquePerStationBody': 'Los enlaces públicos se generan por estación, no por marca. Ve a {path} para descargar QRs individuales. Los ajustes de marca configurados aquí se aplican automáticamente.',
  'brandStudio.output.path': 'Proyectos → Evento → Estación',
  'brandStudio.output.downloadPng': 'Descargar PNG',
  'brandStudio.output.downloadPngHint': 'Disponible tras guardar y conectar a una estación',
  'brandStudio.output.printDisplay': 'Imprimir',
  'brandStudio.output.upload': 'Subir',
  'brandStudio.output.clear': 'Limpiar',
  // Preview
  'brandStudio.preview.yourBrand': 'Tu marca',
  'brandStudio.preview.brandWorkspace': 'Marca',
  'brandStudio.preview.fallbackCampaign': 'Muestreo de {brand}',
  'brandStudio.preview.continue': 'Continuar',
  'brandStudio.preview.spinToWin': 'Gira y gana',

  // ── Public-survey end-user surface ─────────────────────────────────
  'publicSurvey.finishPage.title': '¡Gracias por participar!',
  'publicSurvey.finishPage.body': 'Vuelve cuando quieras — siempre hay más por desbloquear.',
  'publicSurvey.missingSurveyOrStation': 'Falta la journey o la estación.',
  // Caso 15 — Pantallas terminales por plan-cap / lock.
  'publicSurvey.locked.limitExhausted':
    'Esta experiencia llegó al límite por ahora. ¡Gracias por participar!',
  'publicSurvey.locked.planExpired':
    'Esta experiencia ya no está disponible. Contacta al organizador para más información.',
  'publicSurvey.locked.manualDisable':
    'Esta experiencia está temporalmente deshabilitada por el organizador.',
  'publicSurvey.ageGate.eyebrow': 'Verificación de edad',
  'publicSurvey.ageGate.question': '¿Tienes {age} años o más?',
  'publicSurvey.ageGate.body': 'Necesitas confirmar para continuar.',
  'publicSurvey.ageGate.yes': 'Sí',
  'publicSurvey.ageGate.no': 'No',
  'publicSurvey.continueArrow': 'Continuar →',
  'publicSurvey.prizeReveal.claimHint': 'Muestra esta pantalla en la barra / estación para reclamar tu premio.',
  'publicSurvey.prizeReveal.directDelivery': 'El personal del evento te entregará tu premio directamente. No necesitas código.',
  'publicSurvey.photoFilm.eyebrow': 'Aviso',
  'publicSurvey.photoFilm.title': 'Fotografía y filmación',
  'publicSurvey.photoFilm.body': 'Al participar en este evento reconoces que podrías ser fotografiado o filmado para uso de marketing.',
  'publicSurvey.photoFilm.bodyWithBrand': 'Al participar en {brand} reconoces que podrías ser fotografiado o filmado para uso de marketing.',
  'publicSurvey.photoFilm.notACheckbox': 'Esto es un aviso, no un consentimiento — no requiere checkbox.',
  'publicSurvey.photoFilm.acknowledge': 'Entiendo',
  'publicSurvey.prizeReveal.continueExternal': 'Continuar al sitio de la marca →',
  'publicSurvey.prizeReveal.brandedThankYou': 'Gracias por jugar — mantén esta pantalla lista cuando el embajador de la marca venga a entregar tu premio.',
  'insights.tabs.recap': 'Resumen Atledi',
  'insights.tabs.throughput': 'Throughput',
  'insights.tabs.distribution': 'Distribución',
  'insights.tabs.demographics': 'Demografía',
  'insights.tabs.leads': 'Leads',
  'insights.leads.eyebrow': 'Exportación CSV locked',
  'insights.leads.title': 'Captura y seguimiento de leads',
  'insights.leads.body': 'Las identidades de leads — nombres, contacto, demografía y consentimiento de marketing — viven en la superficie Contactos junto con la exportación CSV de 22 columnas locked (PRD §10.1).',
  'insights.leads.cta': 'Abrir Contactos',
  'insights.exportPdf': 'Exportar PDF',
  'insights.pdfPaid': 'PDF · Pago',
  'insights.recap.eyebrow': 'Resumen de Atledi',
  'insights.recap.narrative': 'Narrativa',
  'insights.recap.narrativePlaceholder':
    'La narrativa en vivo llega con el endpoint Atledi/Sonnet en Fase 4. Cada evento tendrá una historia de 2–3 oraciones de lo que acaba de pasar, en la voz de marca configurada en Brand Studio.',
  'insights.recap.kpis': 'Resumen de KPIs',
  'insights.recap.recommendation': 'Recomendación',
  'insights.recap.recommendationPlaceholder':
    'Una próxima acción concreta con nivel de confianza aparecerá aquí cuando se conecte el endpoint de recap.',
  'insights.recap.confidence.low': 'Baja',
  'insights.recap.confidence.medium': 'Media',
  'insights.recap.confidence.high': 'Alta',
  'insights.kpi.moments': 'Momentos',
  'insights.kpi.throughput': 'Throughput',
  'insights.kpi.inventory': 'Inventario',
  'legal.eyebrow': 'Legal · Reconocimiento único',
  'legal.brandObligations': 'Obligaciones de la marca',
  'legal.xotskiObligations': 'Obligaciones de Xotski',
  'legal.xotskiObligationsBody':
    'Xotski procesa datos personales solo bajo tus instrucciones documentadas, los mantiene en las regiones que selecciones y te notifica cualquier violación dentro de 24 horas de detección.',
  'legal.signerName': 'Nombre del firmante',
  'legal.signerTitle': 'Cargo del firmante',
  'legal.later': 'Recordarme más tarde',
  'legal.submitting': 'Guardando…',
  'legal.accept': 'Acepto',
  'reporterAdmin.eyebrow': 'Reporteros',
  'reporterAdmin.title': 'Reporters de campo para este evento',
  'reporterAdmin.invite': 'Invitar reporter',
  'reporterAdmin.freeUnlimited': 'Gratis · Ilimitados',
  'reporterAdmin.paidOnly': 'Pago',
  'reporterAdmin.paidHint': 'Actualiza tu plan para invitar reporters con enlaces mágicos de 24h — reporters ilimitados gratis en todos los planes pagos.',
  'reporterAdmin.loading': 'Cargando reporters…',
  'reporterAdmin.empty': 'Aún no hay reporters. Envía un enlace mágico a tu primer BA.',
  'reporterAdmin.expiresAt': 'expira {date}',
  'reporterAdmin.state.active': 'Activo',
  'reporterAdmin.state.revoked': 'Revocado',
  'reporterAdmin.state.expired': 'Expirado',
  'reporterAdmin.reissueHint': 'Re-emitir enlace (copia la URL nueva)',
  'reporterAdmin.revokeHint': 'Revocar enlace activo',
  'reporterAdmin.revoked': 'Enlace de reporter revocado',
  'reporterAdmin.reissuedCopied': 'Nuevo enlace copiado al portapapeles',
  'reporterAdmin.invitedCopied': 'URL mágica copiada al portapapeles',
  'reporterAdmin.capReached': 'Límite de reviewers alcanzado',
  'reporterAdmin.capReachedBody': 'Tu plan cubre un número de reviewers gratis. Mejora de plan para invitar más — XK2 en adelante tiene reviewers ilimitados.',
  'reporterAdmin.inviteError': 'No se pudo enviar la invitación',
  'reporterAdmin.copied': 'Copiado',
  'reporterAdmin.copy': 'Copiar URL',
  'reporterAdmin.modal.eyebrow': 'PRD §12 · Vista del Reporter',
  'reporterAdmin.modal.title': 'Invitar a un reporter de campo',
  'reporterAdmin.modal.body': 'Recibe un enlace mágico de 24h a una superficie de 4 tabs — sin cuenta ni contraseña.',
  'reporterAdmin.modal.email': 'Correo',
  'reporterAdmin.modal.nameOptional': 'Nombre (opcional)',
  'reporterAdmin.modal.namePlaceholder': 'Nombre del BA',
  'reporterAdmin.modal.send': 'Enviar invitación',
  'reporterAdmin.modal.sending': 'Enviando…',
  'reporterAdmin.modal.successTo': 'Enlace mágico enviado a {email}',
  'reporterAdmin.modal.magicUrl': 'URL mágica',
  'reporterAdmin.modal.expiresHint': 'El enlace expira {date}',
  'reporter.tabs.today': 'Hoy',
  'reporter.tabs.inventory': 'Inventario',
  'reporter.tabs.notes': 'Notas',
  'reporter.tabs.claims': 'Reclamos',
  'reporter.header.eyebrow': 'Vista del reporter',
  'reporter.header.expiresAt': 'El enlace expira {date}',
  'reporter.verifying.title': 'Verificando tu enlace…',
  'reporter.verifying.body': 'Un momento mientras abrimos tu evento.',
  'reporter.invalidLink.title': 'Este enlace no es válido',
  'reporter.invalidLink.body': 'Pídele a la marca un nuevo enlace.',
  'reporter.expired.title': 'Enlace expirado',
  'reporter.expired.body': 'Los enlaces de reporter duran 24 horas. Pide uno nuevo a la marca.',
  'reporter.terms.eyebrow': 'Términos del reporter',
  'reporter.terms.title': 'Antes de empezar en {event}',
  'reporter.terms.body': 'Vas a iniciar como reporter de la marca. Confirma estos puntos antes de capturar notas:',
  'reporter.terms.point1': 'Las notas y fotos solo se comparten con la marca organizadora.',
  'reporter.terms.point2': 'No compartas el enlace ni capturas de pantalla con códigos de premio.',
  'reporter.terms.point3': 'Puedes dejar de reportar en cualquier momento cerrando esta pestaña.',
  'reporter.terms.checkbox': 'Entiendo y quiero comenzar a reportar.',
  'reporter.terms.accept': 'Comenzar a reportar',
  'reporter.terms.submitting': 'Guardando…',
  'reporter.today.scheduleTitle': 'Agenda',
  'reporter.today.scheduleBody': 'Estaciones y horarios para {event}. Vista en vivo llega con la cola offline en Fase 3.',
  'reporter.today.atlediEyebrow': 'Briefing de Atledi',
  'reporter.today.atlediTitle': 'Tu foco ahora mismo',
  'reporter.today.atlediBody': 'Tips de capacidad + foco para la primera hora desde Atledi. Llega en Fase 4.',
  'reporter.today.atlediBriefing.morning': 'Buen día en {event}. Despliega los posters en cada entrada y enciende los lectores QR — la primera hora suele rendir 30–40% de las capturas del día.',
  'reporter.today.atlediBriefing.afternoon': 'Empuje de tarde en {event}. Refresca las estaciones agotadas, camina por la barra y re-engancha a los grupos que aún no escanearon.',
  'reporter.today.atlediBriefing.evening': 'Flujo nocturno en {event}. Vigila los timers de premios y avisa cualquier expiración cercana antes de quedarte sin inventario.',
  'reporter.today.atlediBriefing.night': 'Turno tarde en {event}. Cuenta lo que queda, captura fotos de cierre y deja notas para el manager mañana.',
  'reporter.inventory.title': 'Inventario de marca',
  'reporter.inventory.body': 'Artículos asignados a {event} con ajustes rápidos para dañados o descartados.',
  'reporter.notes.title': 'Notas de campo',
  'reporter.notes.body': 'Notas de texto + fotos para el manager. Toca "+" abajo a la derecha para agregar.',
  'reporter.notes.offlineTitle': 'Offline primero',
  'reporter.notes.offlineBody': 'Las notas sin señal se almacenan en IndexedDB y sincronizan cuando vuelve la conexión.',
  'reporter.claims.title': 'Verificación de reclamo',
  'reporter.claims.body': 'Escanea el QR del invitado o teclea el código para marcar un premio entregado en {event}.',
  'brandStudio.look.atlediTone': 'Voz de Atledi',
  'brandStudio.look.atlediToneHint': 'Da forma a la narrativa de resumen + bienvenida que Atledi escribe para esta marca.',
  'brandStudio.look.atlediTone.playful': 'Divertida',
  'brandStudio.look.atlediTone.professional': 'Profesional',
  'brandStudio.look.atlediTone.warm': 'Cálida',
  'brandStudio.look.atlediTone.bold': 'Audaz',
  'brandStudio.look.atlediTone.custom': 'Personalizada',
  'brandStudio.look.atlediToneCustomPlaceholder': 'Describe la voz — ej. "concisa, basada en datos, sin signos de exclamación".',
  'brandStudio.look.consentVariant': 'Variante de consentimiento',
  'brandStudio.look.consentVariantHint': 'Anónima (A) omite PII y marketing. PII (B) recopila contacto y muestra el opt-in de marketing por separado.',
  'brandStudio.look.consentVariantA': 'A · Anónima',
  'brandStudio.look.consentVariantADesc': 'Sin PII ni marketing. Solo aceptación de T&C.',
  'brandStudio.look.consentVariantB': 'B · PII + opt-in de marketing',
  'brandStudio.look.consentVariantBDesc': 'Recopila contacto + checkbox de marketing opcional por separado.',
  'publicSurvey.claim.preparing': 'Generando tu código de canje…',
  'publicSurvey.claim.codeLabel': 'Código de canje',
  'publicSurvey.claim.showToBa':
    'Muestra este código al promotor antes de que termine el tiempo.',
  'publicSurvey.claim.expiredAskBa':
    'El tiempo se agotó. Pide al promotor que lo extienda.',
  'publicSurvey.wheel.emptyTitle': 'No quedan premios',
  'publicSurvey.wheel.emptyBody': 'Todos los premios de esta estación ya fueron reclamados.',

  // ── Active Log (auditoría) ─────────────────────────────────────────
  'activeLog.title': 'Registro de actividad',
  'activeLog.subtitle': 'Cada cambio en tu cuenta — quién, cuándo, dónde.',
  'sidebar.activeLog': 'Auditoría',
  'activeLog.noAccessTitle': 'Sin acceso a auditoría',
  'activeLog.noAccessBody': 'Tu rol no tiene acceso al registro de auditoría.',
  'activeLog.empty.title': 'Sin actividad aún',
  'activeLog.empty.body': 'Cuando el equipo trabaje, cada acción relevante aparecerá aquí.',
  'activeLog.filter.search': 'Buscar',
  'activeLog.filter.searchPlaceholder': 'Descripción, URL, usuario…',
  'activeLog.filter.action': 'Acción',
  'activeLog.filter.actionAll': 'Todas las acciones',
  'activeLog.filter.entity': 'Entidad',
  'activeLog.filter.userId': 'ID de usuario',
  'activeLog.filter.eventId': 'ID de evento',
  'activeLog.filter.from': 'Desde',
  'activeLog.filter.to': 'Hasta',
  'activeLog.filter.apply': 'Aplicar',
  'activeLog.filter.reset': 'Limpiar',
  'activeLog.table.when': 'Cuándo',
  'activeLog.table.user': 'Usuario',
  'activeLog.table.action': 'Acción',
  'activeLog.table.entity': 'Entidad',
  'activeLog.table.description': 'Descripción',
  'activeLog.pager.summary': 'Mostrando {from}–{to} de {total}',
  'activeLog.pager.prev': 'Página anterior',
  'activeLog.pager.next': 'Página siguiente',
  'activeLog.detail.id': 'ID',
  'activeLog.detail.companyId': 'Marca',
  'activeLog.detail.user': 'Usuario',
  'activeLog.detail.email': 'Correo',
  'activeLog.detail.method': 'Método HTTP',
  'activeLog.detail.appType': 'App',
  'activeLog.detail.eventId': 'ID de evento',
  'activeLog.detail.ip': 'Dirección IP',
  'activeLog.detail.url': 'URL',
  'activeLog.detail.userAgent': 'User agent',
  'activeLog.detail.description': 'Descripción',
  'activeLog.detail.oldData': 'Datos previos',
  'activeLog.detail.newData': 'Datos nuevos',

  // ── Paywall (servicio premium) ─────────────────────────────────────
  'paywall.badge.premium': 'Premium',
  'paywall.badge.locked': 'Bloqueado',
  'paywall.cta.viewPlans': 'Ver planes',
  'paywall.activeLog.title': 'Active Log es un servicio premium',
  'paywall.activeLog.description':
    'Audita cada acción de tu equipo: quién creó, editó o eliminó qué, desde dónde y cuándo. Activa este servicio en tu plan para empezar a ver el historial completo.',
  'paywall.activeLog.perk1': 'Historial granular de cada cambio (antes/después)',
  'paywall.activeLog.perk2': 'Filtrado por usuario, entidad, fecha e IP',
  'paywall.activeLog.perk3': 'Exportación y retención extendida',
  'paywall.notifications.title': 'Notificaciones es un servicio premium',
  'paywall.notifications.description':
    'Envía mensajes en tiempo real (push, in-app y email) a tu equipo y a tus operadores en estación. Activa este servicio en tu plan para empezar a comunicarte.',
  'paywall.notifications.perk1': 'Push instantáneo a dispositivos de operadores',
  'paywall.notifications.perk2': 'Email opcional al mismo destinatario',
  'paywall.notifications.perk3': 'Segmentación por usuarios o grupos',
  'paywall.aiAssistant.title': 'AI Assistant es un servicio premium',
  'paywall.aiAssistant.description':
    'Haz preguntas sobre tus proyectos, eventos, journeys y interactions en lenguaje natural. El asistente transmite interactions basadas en tus datos en vivo.',
  'paywall.aiAssistant.perk1':
    'Preguntas y interactions instantáneas sobre tus eventos, estaciones y interactions',
  'paywall.aiAssistant.perk2':
    'Insights al alcance sin exportar a una hoja de cálculo',
  'paywall.aiAssistant.perk3':
    'Siempre basado en los datos que tu plan habilita',
  // ── Etiquetas ──────────────────────────────────────────────────────
  'tags.picker.add': 'Etiquetar',
  'tags.picker.more': 'Añadir',
  'tags.picker.placeholder': 'Busca o crea una etiqueta…',
  'tags.picker.create': 'Crear «{name}»',
  'tags.picker.emptyVocab': 'Aún no hay etiquetas. Escribe un nombre y crea la primera.',
  'tags.picker.noMatches': 'No coincide nada. Escribe otro nombre para crearlo.',
  'tags.filter.label': 'Etiquetas',
  'tags.filter.clear': 'Quitar',
  'tags.filter.andHint':
    'Sale sólo lo que lleva todas las etiquetas marcadas — cada una estrecha la lista.',
  'tags.filter.oneResult': '1 resultado',
  'tags.filter.nResults': '{n} resultados',
  'tags.saveFailed': 'No se pudieron guardar las etiquetas',
  'tags.type.project': 'Proyectos',
  'tags.type.event': 'Eventos',
  'tags.type.station': 'Estaciones',
  'tags.manager.title': 'Etiquetas',
  'tags.manager.subtitle':
    'El vocabulario de etiquetas de esta marca. Etiquetar se hace en cada lista — aquí se ordena: renombrar, recolorear y retirar lo que ya no sirve.',
  'tags.manager.noBrand': 'Elige antes una marca',
  'tags.manager.noBrandBody': 'Las etiquetas son de una marca, así que no hay nada que enseñar hasta que elijas una.',
  'tags.manager.empty': 'Aún no hay etiquetas',
  'tags.manager.emptyBody': 'Crea la primera al etiquetar un {type} — van apareciendo aquí solas.',
  'tags.manager.count': '{n} etiquetas',
  'tags.manager.unused': 'sin usar',
  'tags.manager.usedOne': 'en 1',
  'tags.manager.usedMany': 'en {n}',
  'tags.manager.rename': 'Renombrar {name}',
  'tags.manager.remove': 'Retirar {name}',
  'tags.manager.removeTitle': '¿Retirar «{name}»?',
  'tags.manager.removeBodyOne':
    'Se quitará de 1 {type} además del vocabulario. No cambia nada más de ella.',
  'tags.manager.removeBodyMany':
    'Se quitará de {n} {type} además del vocabulario. No cambia nada más de ellos.',
  'tags.manager.removeUnused': 'No la lleva nada, así que sólo desaparece del vocabulario.',
  'tags.manager.removeConfirm': 'Retirar',
  'tags.manager.removed': '«{name}» retirada',
  // ── Mis dispositivos ───────────────────────────────────────────────
  'sessions.title': 'Mis dispositivos',
  'sessions.settingsDesc': 'Mira dónde está iniciada tu sesión y cierra lo que no reconozcas.',
  'sessions.subtitle':
    'Tu cuenta puede estar iniciada en {n} dispositivos a la vez — portátil, móvil, la tablet del evento. Al entrar en un cuarto se cierra el que lleve más tiempo sin usarse, para que nadie se quede fuera en mitad de una activación.',
  'sessions.count': '{n} de {max} dispositivos',
  'sessions.thisDevice': 'Este dispositivo',
  'sessions.unknownDevice': 'Dispositivo no reconocido',
  'sessions.lastSeen': 'usado {when}',
  'sessions.now': 'ahora mismo',
  'sessions.minutesAgo': 'hace {n} min',
  'sessions.hoursAgo': 'hace {n} h',
  'sessions.close': 'Cerrar',
  'sessions.closed': 'Dispositivo cerrado',
  'sessions.closeFailed': 'No se pudo cerrar ese dispositivo',
  'sessions.sharedHint':
    'Esta cuenta se ha usado hoy en {n} dispositivos. Si es tu equipo de campo, no necesitan tu asiento — invítalos como reporteros y tendrán su propio enlace, sin una contraseña que ir pasando.',
  'sessions.atLimit':
    'Estás en {n} dispositivos. Si entras en otro sitio se cerrará el que lleves más tiempo sin usar.',

  // ── Dashboards (Insights · informe y Track de evento · informe de proyecto) ──────────────────────────────────────────────
  'dash.pending': 'Pendiente',
  'dash.measured': 'medido',
  'dash.needs': 'falta {what}',
  'dash.basis': 'n = {n} · ±{moe} pts',
  'compare.pickDefaultGroups': 'Los {n} primeros',
  'compare.pickEvents': 'Eventos a comparar (hasta {max})',
  'compare.pickGroups': 'Grupos a comparar (hasta {max})',
  'compare.pickDefault': 'Los {n} más recientes',
  'compare.searchGroups': 'Buscar grupos',
  'compare.groupsPicked': '{n} grupos',
  'project.tab.compare': 'Comparar',
  'insights.tabs.compare': 'Comparar',
  'compare.needTwo': 'Elige al menos dos grupos con datos para compararlos.',
  'compare.baseline': 'Referencia',
  'compare.ref': 'referencia',
  'compare.eventsN': '{n} evento(s)',
  'compare.capped': 'Sólo se comparan los primeros {n} grupos; acota el alcance o la agrupación.',
  'compare.chartNoteProportion': 'Las barras llevan su margen de error al 95 %. Las diferencias se contrastan con la referencia con un test z de dos proporciones.',
  'compare.chartNoteNoTest': 'No es una proporción: se compara como diferencia simple, sin test de significación.',
  'compare.col.metric': 'Métrica',
  'compare.col.context': 'Contexto',
  'compare.context': '{events} evento(s) · {interactions} interacciones · {contacts} contactos · {leads} leads · {delivered} entregados',
  'compare.ns': 'n.s.',
  'compare.footnote': 'Cada porcentaje lleva su base (n) y su margen al 95 %. Las etiquetas dan la diferencia frente a la referencia: p < 0,05 quiere decir que la diferencia difícilmente es ruido; n.s., que podría serlo. La virgulilla (~) marca una base menor de 50: orienta, no concluye.',
  'compare.metric.leadRate': 'Tasa de leads',
  'compare.metricNote.leadRate': 'Leads como parte de los contactos',
  'compare.metric.contactRate': 'Tasa de contacto',
  'compare.metricNote.contactRate': 'Contactos como parte de las interacciones',
  'compare.metric.completion': 'Completado',
  'compare.metricNote.completion': 'Respuestas como parte de las interacciones',
  'compare.metric.redemption': 'Canje',
  'compare.metricNote.redemption': 'Entregados como parte de los premios ganados',
  'compare.metric.consent': 'Consentimiento de marketing',
  'compare.metricNote.consent': 'Opt-ins como parte de los leads',
  'compare.metric.deliveredPerInteraction': 'Premios por interacción',
  'compare.metricNote.deliveredPerInteraction': 'Entregados como parte de las interacciones',
  'compare.metric.unaccountedRate': 'Sin destino',
  'compare.metricNote.unaccountedRate': 'Unidades sin destino sobre las salidas (menos es mejor)',
  'compare.metric.costPerLead': 'Coste por lead',
  'compare.metricNote.costPerLead': 'Valor distribuido entre leads (menos es mejor)',
  'compare.metric.capturesPerBlock': 'Capturas por bloque',
  'compare.metricNote.capturesPerBlock': 'Capturas medias por bloque de 15 minutos',
  'compare.metric.peakBlock': 'Bloque pico',
  'compare.metricNote.peakBlock': 'Máximo de capturas en un bloque de 15 minutos',
  'compare.read.better': '{b} supera a {a} en {metric}: {vb} frente a {va} ({diff}, IC95 [{lo}, {hi}] pts, p {p}). La diferencia difícilmente es ruido.',
  'compare.read.worse': '{b} queda por debajo de {a} en {metric}: {vb} frente a {va} ({diff}, IC95 [{lo}, {hi}] pts, p {p}). La diferencia difícilmente es ruido.',
  'compare.read.same': '{b} y {a} no se distinguen en {metric}: {vb} frente a {va} ({diff}, IC95 [{lo}, {hi}] pts, p {p}). Trátalos como iguales hasta que crezcan las bases.',
  'compare.read.noTest': '{b} frente a {a} en {metric}: {vb} frente a {va} ({diff}). No es una proporción, así que no hay test de significación.',
  'compare.read.pending': '{metric} está pendiente en {b} o en {a}; aún no hay nada que comparar.',
  'compare.read.directional': 'Una de las bases es menor de 50, así que esto se lee como una dirección, no como una conclusión.',
  'compare.projectKicker': 'Evento contra evento',
  'compare.projectIntro': '{n} de {total} eventos tienen datos. Elige la referencia y lee cada métrica contra ella: el margen dice cuánto permite la base, el test si la diferencia se sostiene.',
  'insights.compare.by': 'Agrupar por',
  'insights.compare.mode.project': 'Proyecto',
  'insights.compare.mode.tag': 'Etiqueta',
  'insights.compare.mode.event': 'Evento',
  'insights.compare.mode.custom': 'Conjuntos a medida',
  'insights.compare.hint.project': 'Un grupo por proyecto del alcance (hasta {n}).',
  'insights.compare.hint.tag': 'Un grupo por etiqueta de evento o de proyecto del alcance; un evento puede estar en varios (hasta {n}).',
  'insights.compare.hint.event': 'Un grupo por evento con datos (hasta {n}).',
  'insights.compare.hint.custom': 'Arma tus propios conjuntos con los eventos del alcance (hasta 3).',
  'insights.compare.customSet': 'Conjunto {n}',
  'insights.compare.pickEvents': 'Elige eventos',
  'insights.compare.addSet': '+ Añadir un conjunto',
  'closing.kicker': 'Cierre del evento',
  'closing.reconciledOn': 'Conciliado el {date}',
  'closing.viewRecord': 'El acta',
  'closing.reopen': 'Reabrir',
  'closing.reopenTitle': '¿Reabrir este evento?',
  'closing.reopenBody': 'Las {n} unidades devueltas al almacén vuelven a estar en juego en sus estaciones, se corrige el pool de la marca y el acta se borra. Después se puede volver a cerrar.',
  'closing.endedKicker': 'El evento terminó',
  'closing.endedTitle': '{n} unidades siguen en las estaciones',
  'closing.endedBody': '{inPlay} aún en juego y {lapsed} caducadas o anuladas están físicamente allí y deben volver al almacén. {pending} premios ganados sin recoger siguen reservados hasta que caduquen.',
  'closing.closeButton': 'Cerrar el evento y devolver sobrantes',
  'closing.dialogTitle': 'Cerrar {name}',
  'closing.dialogBody': 'Una fila por estación y premio. «Debería haber» es lo que sigue en juego más lo caducado o anulado. Cuenta lo que hay de verdad; lo que falte queda como sin destino, nunca como entregado.',
  'closing.note': 'Nota para el acta',
  'closing.notePlaceholder': 'Quién contó, qué pasó con lo que falta, lo que el cliente deba saber…',
  'closing.confirm': 'Cerrar y devolver {n} unidades',
  'closing.toast.closed': 'Evento cerrado: {n} unidades de vuelta en el almacén',
  'closing.toast.reopened': 'Evento reabierto',
  'closing.stat.returned': 'Devueltas al almacén',
  'closing.stat.returnedHint': '{n} de ellas repuestas al pool de la marca (ganadas y luego caducadas o anuladas)',
  'closing.stat.delivered': 'Entregadas',
  'closing.stat.deliveredHint': 'de {n} asignadas',
  'closing.stat.pending': 'Ganadas sin recoger',
  'closing.stat.pendingHint': 'Reservadas hasta que caduquen; no se devuelven',
  'closing.stat.unaccounted': 'Sin destino',
  'closing.stat.unaccountedHint': 'Debían estar y no estaban',
  'closing.col.station': 'Estación',
  'closing.col.stock': 'Premio',
  'closing.col.allocated': 'Asignado',
  'closing.col.delivered': 'Entregado',
  'closing.col.pending': 'Pendiente',
  'closing.col.lapsed': 'Caducado',
  'closing.col.inPlay': 'En juego',
  'closing.col.onHand': 'Debería haber',
  'closing.col.counted': 'Recuento',
  'closing.col.returned': 'Devuelve',
  'closing.col.unaccounted': 'Falta',
  'eventReport.hero.reconciled': 'Conciliado el {date}',
  'eventReport.hero.open': 'Sin cerrar · {n} aún en juego',
  'dash.unacc.returned': 'Devueltas al almacén',
  'dash.seg.returned': 'Devuelto al almacén',
  'brand.leftovers.close': 'Cerrar y devolver',
  'insights.filter.tags': 'Etiquetas',
  'insights.filter.noTags': 'Estos eventos y proyectos aún no llevan etiquetas',
  'insights.filter.projectTag': '{name} · proyecto',
  'insights.picker.askFor': 'Pídeme',
  'insights.picker.everything': 'Todo',
  'insights.picker.events': '{n} evento(s)',
  'insights.picker.moreProjects': '+{n} proyecto(s) más',
  'insights.picker.lastThree': 'Los tres últimos',
  'insights.picker.mostRecent': 'más recientes',
  'insights.picker.beatPlan': 'Batieron el plan',
  'insights.picker.overGoal': 'sobre objetivo',
  'insights.picker.search': 'Busca entre {n} eventos por nombre, proyecto o fecha',
  'insights.picker.match': '{n} de {total} eventos coinciden',
  'insights.picker.selectMatching': 'Elegir los {n} que coinciden',
  'insights.picker.caption': 'La altura es contactos captados, en escala de raíz cuadrada',
  'insights.picker.noMatch': 'Ningún evento coincide',
  'insights.picker.more': '+{n} más sin mostrar',
  'insights.throughput.allSelection': 'Toda la selección',
  'insights.throughput.byClock': 'por hora del día',
  'dash.thru.nav': '{i} de {n}',
  'dash.thru.title': 'Throughput por ventana de captura',
  'dash.thru.play': 'Reproducir',
  'dash.thru.pause': 'Pausa',
  'dash.thru.time': 'Hora',
  'dash.thru.perBlock': 'Capturas / bloque',
  'dash.thru.cum': 'Capturas hasta aquí',
  'dash.thru.prizes': 'Premios entregados',
  'dash.thru.ofCapture': 'De la captura',
  'dash.thru.phaseLabel': 'Fase',
  'dash.thru.peakCap': 'Pico {n} por 15 min',
  'dash.thru.capLabel': 'Pico {n} por 15 min · {stations} estación(es)',
  'dash.thru.deliveredSoFar': '{n} entregados',
  'dash.thru.completions': '{n} capturas',
  'dash.premiums.title': 'Premios entregados',
  'dash.premiums.headGoal': 'frente a {goal} previstos · {allocated} asignados',
  'dash.premiums.headAllocated': 'de {allocated} asignados',
  'dash.premiums.headExpected': 'frente a {expected} esperados (calculadora) · {allocated} asignados',
  'dash.premiums.expected': 'Previsto',
  'dash.premiums.allocated': 'Asignado',
  'dash.premiums.actual': 'Entregado',
  'dash.premiums.ofExpected': 'de {n} previstos',
  'dash.premiums.ofAllocated': 'de {n} asignados',
  'dash.premiums.headroomNote': 'La banda tenue es lo asignado; la marca, el 100 %',
  'dash.premiums.clickToIsolate': 'Pulsa un premio para aislarlo',
  'dash.premiums.showingItem': 'Viendo {name} · volver a todos',
  'dash.premiums.where': 'Adónde fue {name}',
  'dash.premiums.lightNote': '{n} de {total} premios están por debajo del 45 % entregado, empezando por {name} con el {p} %. Lo que no se entrega es merch que otra fecha podría haber usado.',
  'dash.premiums.okNote': 'Todos los premios superan el 45 % entregado. La asignación fue justa o acertada.',
  'dash.unacc.title': 'Sin destino',
  'dash.unacc.basis': '{n} de {out} unidades salidas',
  'dash.unacc.noUnits': 'aún no ha salido ninguna unidad',
  'dash.unacc.body': 'Cada unidad que salió de una estación tiene destino en el libro: entregada, esperando a que la recojan, o caducada. Lo que queda aquí es lo que no tiene registro de canje.',
  'dash.unacc.bodyPending': 'Aún no ha salido nada del juego, así que no hay nada que cuadrar. La tasa queda pendiente en vez de leerse como cero.',
  'dash.unacc.delivered': 'Entregados, confirmados por el operador',
  'dash.unacc.pending': 'Ganados, esperando a que los recojan',
  'dash.unacc.lapsed': 'Caducados o anulados',
  'dash.unacc.unaccounted': 'Sin destino, sin registro de canje',
  'dash.cost.title': 'Coste',
  'dash.cost.offByDefault': 'Oculto por defecto',
  'dash.cost.reveal': 'Ver el coste',
  'dash.cost.hide': 'Ocultar el coste',
  'dash.weather.kicker': 'Clima como covariable',
  'dash.weather.none': 'En este evento no se registró el clima, así que la covariable no se puede calcular. Queda pendiente, no como «sin efecto».',
  'dash.weather.oneDay': 'Se mantuvo a {t}, {d} durante toda la ventana de captura, así que aquí el clima es una constante y no explica la variación entre bloques.',
  'dash.weather.twoDays': 'El segundo día corrió a {t2}, {d2} frente a {t1}, {d1} del primero. Mira los dos días lado a lado antes de culpar al público de un bajón.',
  'dash.weather.noPrior': 'No hay año anterior registrado para esta cita, así que todavía no hay con qué comparar las condiciones.',
  'dash.weather.code.clear': 'despejado',
  'dash.weather.code.cloudy': 'nublado',
  'dash.weather.code.fog': 'niebla',
  'dash.weather.code.rain': 'lluvia',
  'dash.weather.code.unknown': 'registrado',
  'dash.atledi.eventRead': 'Lectura del evento',
  'dash.atledi.stamp': 'Leído de {n} de {total} bloques',
  'dash.atledi.open': 'Abrir la conversación',
  'dash.atledi.promptClosed': '¿Qué debería cambiar la próxima vez?',
  'dash.atledi.promptLive': '¿Qué está en riesgo ahora mismo?',
  'dash.atledi.textSurvey': 'La mercancía se movió más rápido de lo que se llenó la encuesta. {delivered} premios entregados frente a {responses} respuestas, sobre {interactions} interacciones y una tasa de canje del {redemption}. Lo sin destino es el {p} de las unidades salidas, y {pending} premios siguen esperando a que los recojan, no perdidos. Si la próxima vez el objetivo son respuestas y no alcance, reserva parte del merch fuera del juego y dalo sólo al completar.',
  'dash.atledi.textNoSurvey': 'Aquí no se preguntó nada más allá del consentimiento, y aun así es un informe completo. Salieron {out} unidades de las estaciones, {delivered} se entregaron y confirmaron, y {unaccounted} no tienen registro de canje, que es el {p}. El argumento para este cliente es disciplina de inventario, no lectura de audiencia. Abre el resumen con la conciliación y deja fuera la demografía.',
  'eventReport.show.everything': 'Verlo todo',
  'eventReport.show.none': 'nada',
  'eventReport.funnel.aside': 'Los premios entregados salen directamente del libro de arriba',
  'eventReport.ledger.kicker': 'Conciliación · cada unidad tiene destino',
  'eventReport.ledger.basisOpen': '{n} unidades aún en juego',
  'eventReport.ledger.basisClosed': 'Cada unidad asignada tiene destino',
  'eventReport.costs.valueNote': 'Introduce {items} en Mi Merch para completarlo.',
  'eventReport.costs.valueOk': 'Unidades entregadas por el coste unitario de Mi Merch.',
  'eventReport.health.kicker': 'De bruto a limpio · este evento',
  'eventReport.health.gapsTitle': 'Lo que aún no se puede calcular',
  'eventReport.health.blocked': 'bloqueado',
  'eventReport.footer.part': 'Parte del ecosistema',
  'eventReport.footer.frames': 'Sólo marcos limpios. Cada porcentaje lleva su propia base y su margen.',
  'dash.need.unitCosts': '{n} coste(s) unitario(s)',
  'dash.need.allocations': 'asignaciones',
  'dash.need.consumedUnits': 'unidades fuera de juego',
  'dash.need.prizesWon': 'premios ganados',
  'dash.need.contacts': 'contactos',
  'dash.need.interactions': 'interacciones',
  'dash.need.leads': 'leads',
  'dash.need.peakBlock': 'un bloque pico',
  'dash.need.captures': 'capturas',
  'dash.need.leadGoal': 'un objetivo de leads',
  'dash.need.distributedValue': 'el valor distribuido',
  'dash.need.footprintTraffic': 'el tráfico de la zona de activación',
  'dash.funnel.interactions': 'Interacciones',
  'dash.funnel.contacts': 'Contactos',
  'dash.funnel.leads': 'Leads',
  'dash.funnel.responses': 'Respuestas',
  'dash.funnel.prizesWon': 'Premios ganados',
  'dash.funnel.prizesDelivered': 'Premios entregados',
  'dash.funnel.note.interactions': 'sesiones iniciadas en estación',
  'dash.funnel.note.contacts': 'personas únicas tras deduplicar',
  'dash.funnel.note.leads': 'contactos con email o teléfono',
  'dash.funnel.note.responses': 'completaron la encuesta',
  'dash.funnel.note.prizesWon': 'resultados del juego',
  'dash.funnel.note.prizesDelivered': 'confirmados por el operador',
  'dash.funnel.notAsked': 'No se preguntó',
  'dash.funnel.ofInteractions': 'de las interacciones',
  'dash.seg.delivered': 'Entregados',
  'dash.seg.pending': 'Ganados, sin entregar',
  'dash.seg.lapsed': 'Caducados o anulados',
  'dash.seg.unaccounted': 'Sin destino',
  'dash.seg.inPlay': 'Aún en juego',
  'dash.ledger.allocated': '{n} asignados',
  'dash.ledger.valuePending': 'valor pendiente',
  'dash.ledger.total': 'Todos los premios',
  'dash.phase.warmup': 'Arranque',
  'dash.phase.peak': 'Pico',
  'dash.phase.tail': 'Cola',
  'dash.thru.empty': 'Aún no hay capturas. La línea se llena con las primeras sesiones.',
  'dash.thru.subtitle': '{total} capturas el {day} · {blocks} bloques de 15 minutos',
  'dash.thru.tooltip': '{n} capturas · {phase}',
  'dash.thru.live': 'bloque en curso',
  'dash.thru.peakLine': 'Pico {n}',
  'dash.thru.peakLegend': 'Bloque pico',
  'dash.thru.warmupNote': 'Los primeros {blocks} bloques trajeron {n} capturas. El ritmo lo marca la llegada del público, no el personal.',
  'dash.thru.peakNote': 'El bloque pico hizo {peak}. La mitad de las capturas cayó en {n} bloques.',
  'dash.thru.tailNote': 'La cola trajo {n} capturas. El personal podría reducirse aquí sin perder captura.',
  'dash.pace.noGoal': 'Fija un objetivo',
  'dash.pace.met': 'Objetivo cumplido',
  'dash.pace.ahead': '+{d}% sobre el ritmo',
  'dash.pace.behind': '{d}% sobre el ritmo',
  'dash.pace.ofGoal': '{p}% del objetivo',
  'dash.pace.redeemed': '{p}% canjeados',
  'dash.pace.noDenominator': 'Sin denominador',
  'dash.pace.goalLabel': 'de {n} de objetivo',
  'dash.pace.noGoalLabel': 'sin objetivo',
  'dash.pace.ofWon': 'de {n} ganados',
  'dash.pace.noneWon': 'aún sin premios ganados',
  'dash.pace.tracking': 'Al cierre, {n} · faltan {left}',
  'dash.pace.mark': 'Marca de ritmo',
  'dash.wall.title': 'El muro de premios',
  'dash.wall.inPlay': 'en juego',
  'dash.wall.healthy': 'Bien',
  'dash.wall.restock': 'Reponer · bajo el umbral',
  'dash.wall.out': 'Fuera de juego · reasignar',
  'dash.wall.delivered': '{d} entregados de {a} asignados',
  'dash.wall.empty': 'Este evento aún no tiene premios asignados.',
  'dash.strip.contacts': 'contactos',
  'dash.strip.responses': '{n} resp',
  'dash.strip.noSurvey': 'sin encuesta',
  'insights.filter.searchEvents': 'Buscar eventos',
  'insights.filter.eventsPicked': '{n} eventos',
  'insights.filter.selectShown': 'Todos los mostrados',
  'insights.filter.clear': 'Quitar',
  'insights.filter.noMatch': 'Ningún evento coincide',
  'insights.scope.none': 'Nada seleccionado. Elige un evento en la línea de arriba.',
  'insights.scope.sentence': 'Leyendo {n} de {total} eventos, del {from} al {to}, en {projects} proyecto(s).',
  'insights.scope.more': '+{n} más sin mostrar',
  'insights.scope.everything': 'Ver todo',
  'insights.recap.heroTitle': 'Qué hizo la selección',
  'insights.recap.scopeChip': 'Alcance · {n} eventos',
  'insights.recap.contacts': 'Contactos verificados',
  'insights.recap.contactsHint': '{n} despliegues en la selección',
  'insights.recap.leads': 'Leads',
  'insights.recap.leadsHint': '{p} de los contactos · ±{moe} pts',
  'insights.recap.noBase': 'aún sin base',
  'insights.recap.delivered': 'Entregados',
  'insights.recap.deliveredHint': 'de {won} ganados · {p} canjeados · {allocated} asignados',
  'insights.recap.unaccounted': 'Sin destino',
  'insights.recap.unaccountedHint': 'Salieron del juego sin registro de canje · resuélvelo en Mi Merch',
  'insights.feed.plan': 'Contra el plan',
  'insights.feed.planText': 'Se asignaron {allocated} unidades a la selección, se ganaron {won} y {delivered} llegaron a una mano. {pending} siguen esperando a que las recojan.',
  'insights.feed.distribution': 'Distribución',
  'insights.feed.distributionText': '{delivered} unidades entregadas en la selección, {lapsed} caducadas o anuladas y {inPlay} aún en juego. Lo que sigue en una caja es merch que otra fecha podría usar.',
  'insights.feed.instrument': 'Instrumento',
  'insights.feed.instrumentText': '{surveyed} de {n} eventos llevan encuesta más allá del consentimiento, así que la demografía se apoya en {responses} respuestas y no en todos los contactos.',
  'insights.feed.consent': 'Consentimiento de marketing',
  'insights.feed.consentText': 'El {p} de los leads aceptó marketing ({n} personas). Sólo a ellas se les puede escribir con fines comerciales.',
  'insights.feed.consentPending': 'Aún no hay leads en la selección, así que la tasa de consentimiento está pendiente, no a cero.',
  'insights.throughput.title': 'Throughput de la selección',
  'insights.distribution.byItem': 'Distribución por artículo',
  'insights.distribution.byItemNote': '{n} unidades asignadas en la selección · los segmentos suman lo que salió del juego',
  'insights.leads.contacts': 'Contactos verificados',
  'insights.leads.interactions': 'Interacciones',
  'insights.leads.perContact': '{n} por contacto',
  'insights.leads.responses': 'Respuestas',
  'insights.leads.responsesHint': 'El {p} de las interacciones completó la encuesta',
  'insights.leads.consent': 'Consentimiento de marketing',
  'insights.leads.consentHint': '{n} de {leads} leads aceptaron',
  'eventReport.sections.throughput': 'Throughput',
  'eventReport.sections.health': 'Salud del dato',
  'eventReport.funnel.title': 'Embudo de captura',
  'eventReport.funnel.note': 'Los premios entregados salen directamente del libro. Si esta cifra y la tabla de premios no cuadran alguna vez, es un fallo, no algo que conciliar a mano.',
  'eventReport.funnel.noSurvey': 'Las respuestas salen como «no se preguntó», nunca como cero: ninguna estación de este evento corrió una encuesta más allá del consentimiento.',
  'eventReport.hero.title': 'Qué hizo la mercancía',
  'eventReport.hero.final': 'Final',
  'eventReport.hero.provisional': 'Provisional · evento abierto',
  'eventReport.hero.unitsOut': 'Unidades salidas',
  'eventReport.hero.unitsOutHint': '{allocated} asignadas, {inPlay} aún en juego',
  'eventReport.hero.delivered': 'Entregados',
  'eventReport.hero.deliveredHint': '{p} de {won} premios ganados, confirmados por el operador',
  'eventReport.hero.unaccounted': 'Sin destino',
  'eventReport.hero.unaccountedHint': 'El {p} de las unidades salidas dejó el juego sin registro de canje',
  'eventReport.hero.value': 'Valor distribuido',
  'eventReport.hero.valueHint': '{n} unidades entregadas a su coste unitario de Mi Merch',
  'eventReport.hero.valuePending': '{items} no tiene coste unitario, así que el total sigue pendiente',
  'eventReport.hero.addCost': 'Añade el coste unitario en Mi Merch',
  'eventReport.hero.costed': '{costed} de {n} artículos con coste en Mi Merch',
  'eventReport.ledger.title': 'Premios, unidad a unidad',
  'eventReport.ledger.note': 'Cada segmento es un destino. La barra es lo asignado; lo que queda sin color sigue en juego.',
  'eventReport.ledger.empty': 'Este evento no tuvo premios asignados.',
  'eventReport.throughput.title': 'Throughput por ventana de captura',
  'eventReport.costs.distributedValue': 'Valor distribuido',
  'eventReport.costs.redemption': 'Tasa de canje',
  'eventReport.costs.captureRate': 'Tasa de captura',
  'eventReport.health.title': 'Salud del dato',
  'eventReport.health.bridgeHead': '{raw} sesiones dentro, {clean} personas que puedes defender',
  'eventReport.health.raw': 'sesiones iniciadas en estación',
  'eventReport.health.removed': 'sesiones repetidas o anónimas',
  'eventReport.health.clean': 'contactos únicos verificados',
  'eventReport.health.leads': 'con email o teléfono',
  'eventReport.health.marketing': 'Consentimiento de marketing',
  'eventReport.health.marketingNeed': '{n} leads aceptaron. Sólo a ellos se les puede escribir con fines comerciales.',
  'eventReport.health.noLeads': 'Aún no hay leads, así que la tasa está pendiente, no a cero.',
  'eventReport.health.costs': 'Costes unitarios registrados',
  'eventReport.notCaptured.title': 'En este evento no se capturó nada.',
  'eventReport.notCaptured.body': 'No hay ni una interacción registrada. No es un filtro: no se escribió ninguna partida, encuesta ni escaneo. Revisa los enlaces de las estaciones y el estado del despliegue.',
  'eventReport.health.costsValue': '{costed} de {n}',
  'eventReport.health.costsNeed': 'Falta en {items}. Un coste que falta es ausente, nunca cero, así que el valor y el coste por lead siguen pendientes.',
  'eventReport.health.costsOk': 'Todos los artículos tienen coste, así que el valor y el coste por lead se calculan.',
  'eventReport.health.inPlay': 'Aún en juego',
  'eventReport.health.inPlayNeed': 'Unidades aún asignadas a estaciones. La conciliación se completa cuando se recogen o se devuelven.',
  'eventReport.health.inPlayOk': 'Nada queda en juego. Cada unidad asignada tiene destino.',
  'eventReport.health.trafficNeed': 'Necesita el tráfico de la zona de activación. Pendiente, no cero.',
  'liveMonitor.block': 'Este bloque · {n} del pico {peak}',
  'liveMonitor.inPlay': '{n} en juego de {a} asignados',
  'liveMonitor.hint.interactions': 'sesiones iniciadas en estación',
  'liveMonitor.hint.leads': 'contactos con email o teléfono',
  'liveMonitor.hint.prizes': 'Confirmados por el operador · {inPlay} aún en juego de {allocated} asignados',
  'liveMonitor.pace.noGoal': 'Este evento no tiene objetivo de leads, así que Track puede contar pero no decirte si vas ganando. Fija uno en Plan.',
  'liveMonitor.pace.inherited': 'Meta heredada del proyecto.',
  'liveMonitor.pace.ahead': 'Ha corrido el {done}% de la ventana de captura y ya está el {got}% del objetivo de leads. Por delante del ritmo.',
  'liveMonitor.pace.behind': 'Ha corrido el {done}% de la ventana de captura y sólo está el {got}% del objetivo de leads. Por detrás del ritmo, y el hueco crece con cada bloque que no se cierra.',
  'liveMonitor.pace.closed': 'Cerró con {n} leads frente a un objetivo de {goal}.',
  'liveMonitor.throughput': 'Throughput por ventana de captura',
  'liveMonitor.trackNote': 'En Track no hay demografía, ni coste, ni comentario. El jefe de campo decide si repone y dónde mueve al personal. Lo demás espera al informe.',
  'liveMonitor.ready.kicker': 'Preparación · revisada antes de abrir puertas',
  'liveMonitor.ready.head': '{n} de {total} comprobaciones en orden.',
  'liveMonitor.ready.stations': 'Estaciones configuradas',
  'liveMonitor.ready.survey': 'Encuesta desplegada en una estación',
  'liveMonitor.ready.merch': 'Mercancía asignada',
  'liveMonitor.ready.costs': 'Costes unitarios registrados',
  'liveMonitor.ready.photos': 'Fotos reales de producto (sin siluetas provisionales)',
  'liveMonitor.ready.goals': 'Objetivos fijados',
  'liveMonitor.ready.ok': 'Listo',
  'liveMonitor.ready.blocks': 'Bloquea una cifra',
  'liveMonitor.ready.note': 'Los contadores siguen vacíos hasta la primera interacción. Un contador vacío es un pendiente, no un cero.',
  'projectReport.kpi.events': 'Eventos',
  'projectReport.kpi.liveNow': 'En directo',
  'projectReport.kpi.eventsHint': '{done} cerrados, {live} en directo, {upcoming} por venir',
  'projectReport.kpi.contacts': 'Contactos',
  'projectReport.kpi.contactsHint': 'Personas únicas en el proyecto, una barra por captura',
  'projectReport.kpi.pace': 'Ritmo al objetivo',
  'projectReport.kpi.paceHint': '{n} de {goal} leads de los eventos cerrados',
  'projectReport.kpi.paceNoGoal': 'Fija objetivos de leads en los eventos para medir el ritmo',
  'projectReport.kpi.closed': 'Cerrados',
  'projectReport.kpi.liveProvisional': 'En directo, provisional',
  'projectReport.kpi.remaining': 'Restante',
  'projectReport.kpi.delivered': 'Premios entregados',
  'projectReport.kpi.deliveredHint': 'Confirmados por el operador, leídos del libro',
  'projectReport.kpi.unaccounted': 'Sin destino',
  'projectReport.kpi.clean': 'Limpio',
  'projectReport.kpi.accounted': 'Con destino',
  'projectReport.kpi.unaccountedHint': 'Unidades que salieron del juego sin registro de canje',
  'projectReport.kpi.repeat': 'Contactos repetidos',
  'projectReport.kpi.repeatPending': 'Necesita contactos en el proyecto',
  'projectReport.kpi.newPeople': 'Personas nuevas',
  'projectReport.kpi.returning': 'Que vuelven',
  'projectReport.captures.title': 'Contactos por captura',
  'projectReport.captures.note': 'Una barra por evento, en orden de fecha. Pasa el cursor para ver la tasa de leads.',
  'projectReport.captures.empty': 'Aún no hay capturas con datos.',
  'projectReport.captures.tooltip': '{contacts} contactos · {rate} de tasa de leads',
  'projectReport.deployed.title': 'Premios desplegados hasta hoy',
  'projectReport.deployed.basis': '{total} en total · quedan {left}',
  'projectReport.deployed.lightNote': '{n} artículo(s) por debajo del 45 % desplegado en el proyecto',
  'projectReport.deployed.okNote': 'Todos los artículos superan el 45 % desplegado en el proyecto',
  'projectReport.weather.title': 'Condiciones del día',
  'projectReport.weather.note': 'El clima es una covariable del throughput, así que va junto a la tasa de leads y no en una nota al pie.',
  'projectReport.weather.days': '{n} días',
  'projectReport.weather.rain': '{n} in de lluvia',
  'projectReport.weather.dry': 'seco',
  'projectReport.weather.leadRate': '{p} de tasa de leads',
  'projectReport.singleNote': '{name} se ha celebrado una vez, así que todo lo de arriba es una sola observación. La deriva necesita un segundo punto para significar algo.',
  'projectTrack.kicker': 'Track · altitud de proyecto',
  'projectTrack.nothingLive': 'Track sólo sigue a un evento, porque un jefe de campo está en una estación. Ahora mismo no hay nada en directo en este proyecto, así que no hay superficie en directo que enseñar.',
  'projectTrack.followingLive': 'Siguiendo el evento en directo',
  'projectTrack.otherLive': '{n} evento(s) más en directo en este proyecto',
  'projectTrack.openTrack': 'Abrir el Track del evento',
};

const DICTS: Record<AppLanguage, Dict> = { en: EN, es: ES };

export type TranslationKey = keyof typeof EN;

export function translate(language: AppLanguage, key: string): string {
  return DICTS[language]?.[key] ?? EN[key] ?? key;
}
